import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { authHeaders } from './lib/apiAuth';
import { openBillingPortal } from './lib/billing';
import ReactDOM from "react-dom";

// ── Extracted shared modules ─────────────────────────────────
import { T, SPARKY_SERVER_URL, SUBJECTS, EXAM_SUBJECTS, ALL_YEARS, EXAM_YEARS, YEARS,
         PLAN_ACCESS, PLANS, NAPLAN_CRITERIA, MIN_QUESTIONS_FOR_PCT, MOODS,
         WRITING_PROMPTS, WRITING_CRITERIA, BLOCKED_PATTERNS, REDIRECT_RESPONSES,
         LUMI_SAFE_SYSTEM, profileKey, isInteractiveEnabled, isWritingSubject, EXAM_WRITING,
         HSC_SUBJECTS, VCE_SUBJECTS } from './components/shared/constants';
import { MultilineText, LumiMarkdown, renderInline, Btn, ErrorBoundary, Card, Badge,
         ProgressRing, ProgressBar, DifficultyBadge, Confetti, RadarChart } from './components/shared/ui';

// ── Extracted components ─────────────────────────────────────
import LumiTutor from './components/LumiTutor';
import { isSafeInput, getSafeRedirect, speakText, cleanSpeech, fallbackSpeak,
         detectTopic, detectDiagram } from './components/LumiTutor/helpers';
import VoiceWave from './components/LumiTutor/VoiceWave';
import AnimatedConceptDiagram from './components/AnimatedConceptDiagram';
import WritingPractice from './components/WritingPractice';
import { ExamInteractive, interactiveCount } from './components/ExamInteractive';
import ParentDashboard from './components/ParentDashboard';
import { learnXP } from './lib/topicProgress';
import StudyPlanner from './components/StudyPlanner';
import LessonReader from './components/LessonReader';
import LearnSection, { resumeLearnAt } from './components/LearnSection';
import { TodayHome, PracticeHub, MeScreen, LearnerPicker, TrialEnded, learnYearOf, Icon, ICONS } from './components/StudentHome';
import { LumiOrb as Lumi, LUMI_STATES } from './components/LumiOrb'; // the purple orb is Lumi everywhere (the old teal robot is gone)
import NaplanItemRenderer from './components/QuestionRenderers/NaplanItemRenderer';
import GlowUp, { GlowUpCard } from './components/GlowUp';
import { recordMistake, getCounts as getGlowUpCounts } from './lib/glowUp';
import { dailyGoal } from './lib/weeklyReport.mjs';
import { groupByPassage } from './lib/passageUnits';
import ScratchPad from './components/ScratchPad';
import { entitlements } from './lib/entitlements';
import { applyTopic, matchesFilter, filterLabel, areaFilter, groupTopics } from './lib/examTopics';
import { getPapers, getPaper, getSectionName, hasMock } from './lib/examStructure';
import { ITEM_TYPES, itemType, isMcqItem, isCorrectResponse, responseText, answerText } from './components/QuestionRenderers/naplanItems';
import { BANKS, loadBanks, banksLoaded } from './lib/banks';
import { syncSessionCookie } from './lib/sessionCookie';
import { EXAM_TESTLETS, TESTLET_SIZE, LEVEL_NAME, buildTestlet, routeNextLevel, routeProficiency } from './lib/naplanExam';
// Expansion questions (procedural numeracy/language + authored reading, scripts/naplan-expand/) are tens of thousands
// of items, so they are NOT in the bundle: each year+subject is a JSON file under /naplan/extra/, fetched when the year
// is chosen (and awaited before a session starts). Reading files store each passage once; questions point to it.
const NAPLAN_EXTRA = {};          // "y5-reading" -> loaded questions (passage text filled back in)
const naplanExtraPending = {};    // "y5-reading" -> in-flight fetch promise
const NAPLAN_EXTRA_SUBJECTS = ["numeracy", "language", "reading", "writing"];
const naplanExtraKey = (year, subject) => `${year}-${subject}`;
function loadNaplanExtra(year, subject) {
  const k = naplanExtraKey(year, subject);
  if (NAPLAN_EXTRA[k]) return Promise.resolve(NAPLAN_EXTRA[k]);
  if (!naplanExtraPending[k]) {
    naplanExtraPending[k] = fetch(`/naplan/extra/${k}.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const passages = (d && d.passages) || {};
        NAPLAN_EXTRA[k] = ((d && d.questions) || []).map((q) => (q.p ? { ...q, passage: passages[q.p] } : q));
        return NAPLAN_EXTRA[k];
      })
      .catch(() => { NAPLAN_EXTRA[k] = []; delete naplanExtraPending[k]; return []; }); // offline: base banks only, retry later
  }
  return naplanExtraPending[k];
}
const naplanExtraReady = (year, subject) => Array.isArray(NAPLAN_EXTRA[naplanExtraKey(year, subject)]);
const getNaplanExtra = (year, subject) => NAPLAN_EXTRA[naplanExtraKey(year, subject)] || [];
const allLoadedNaplanExtra = () => Object.values(NAPLAN_EXTRA).flat();
// The other exams' banks (ICAS, ACER, IGNITE, Selective, OC, VIC; scripts/exam-expand/) work the same way: one file
// per program+year+section under /exam/extra/, listed in manifest.json so a section without a file is never fetched.
const EXAM_EXTRA = {};            // "icas-y5-numeracy" -> loaded questions
const examExtraPending = {};
let examManifest = null, examManifestPending = null;
const examExtraKey = (prog, year, subject) => `${prog}-${year}-${subject}`;
function loadExamManifest() {
  if (examManifest) return Promise.resolve(examManifest);
  if (!examManifestPending) {
    examManifestPending = fetch('/exam/extra/manifest.json')
      .then((r) => (r.ok ? r.json() : {}))
      .then((m) => { examManifest = m && typeof m === 'object' ? m : {}; return examManifest; })
      .catch(() => { examManifestPending = null; return {}; }); // offline: inline bank only, retry later
  }
  return examManifestPending;
}
function loadExamExtra(prog, year, subject) {
  const k = examExtraKey(prog, year, subject);
  if (EXAM_EXTRA[k]) return Promise.resolve(EXAM_EXTRA[k]);
  if (!examExtraPending[k]) {
    examExtraPending[k] = loadExamManifest()
      .then((m) => {
        if (!m[k]) return null; // no expansion file for this section
        return fetch(`/exam/extra/${k}.json`).then((r) => (r.ok ? r.json() : null));
      })
      .then((d) => {
        const passages = (d && d.passages) || {};
        EXAM_EXTRA[k] = ((d && d.questions) || []).map((q) => (q.p ? { ...q, passage: passages[q.p] } : q));
        return EXAM_EXTRA[k];
      })
      .catch(() => { delete examExtraPending[k]; return []; }); // offline: inline bank only, retry later
  }
  return examExtraPending[k];
}
const examExtraReady = (prog, year, subject) => Array.isArray(EXAM_EXTRA[examExtraKey(prog, year, subject)]);
const getExamExtra = (prog, year, subject) => subject === "all"
  ? Object.entries(EXAM_EXTRA).filter(([k]) => k.startsWith(`${prog}-${year}-`)).flatMap(([, qs]) => qs)
  : EXAM_EXTRA[examExtraKey(prog, year, subject)] || [];
const allLoadedExamExtra = () => Object.values(EXAM_EXTRA).flat();
const examExtraCount = (prog, subject) => Object.entries(examManifest || {})
  .filter(([k]) => k.startsWith(prog + '-') && (!subject || k.endsWith('-' + subject))).reduce((s, [, v]) => s + (v.questions || 0), 0);
import { trackEvent } from './utils/telemetry';
import { motion, AnimatePresence } from 'framer-motion';
import { celebrate, celebrateExpert } from './utils/celebrate';

// ── Supabase auth + trial limits (env-gated; falls back to legacy flow) ──
import { supabase, supabaseEnabled } from './lib/supabase';
import { startAccountSync } from './lib/accountSync';
import LoginScreen from './components/Auth/LoginScreen';
import SetNewPassword from './components/Auth/SetNewPassword';
import EmailVerifyBanner from './components/Auth/EmailVerifyBanner';
import UpgradeModal from './components/UpgradeModal';
import {
  TRIAL_LIMITS, getTrialUsage, incrementTrialUsage, recordTopicOpened,
  canStartQuestion, canOpenTopic,
} from './lib/trialLimits';
import { getSubjectDisplayName } from './utils/subjectNames';

const STRIPE_PUBLISHABLE_KEY = "pk_live_51TIpsoPqoCXj8WyhT1ST1QSUifjv6KTaonGXnAO3niGUJoRBhT0x77ZFJhFrQXfgLcMIEK4ai0zHxw7DGrlC4lPq00El58zaD3";

// ── Display helper: convert progress key slug (e.g. "naplan-y5-reading") to readable label
function subjectDisplayName(slug) {
  if (!slug || typeof slug !== 'string') return slug || '';
  const parts = slug.split('-');
  if (parts.length < 3) return slug;
  const examMap = { naplan:'NAPLAN', selective_nsw:'NSW Selective', selective:'Selective', oc:'OC Test', acer:'ACER', ignite:'IGNITE', vic_selective:'VIC Selective', icas:'ICAS', hsc:'HSC', vce:'VCE' };
  const yearMap = { y3:'Year 3', y4:'Year 4', y5:'Year 5', y6:'Year 6', y7:'Year 7', y8:'Year 8', y9:'Year 9', y10:'Year 10', y11:'Year 11', y12:'Year 12' };
  const subjectMap = { reading:'Reading', numeracy:'Numeracy', writing:'Writing', language:'Language Conventions', thinking:'Thinking Skills', maths:'Mathematics', english:'English', science:'Science', biology:'Biology', chemistry:'Chemistry', physics:'Physics', hass:'HASS', economics:'Economics', legal:'Legal Studies', business:'Business Studies', psychology:'Psychology' };
  const exam = examMap[parts[0]] || parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const year = yearMap[parts[1]] || parts[1].toUpperCase();
  const subject = subjectMap[parts[2]] || parts[2].charAt(0).toUpperCase() + parts[2].slice(1);
  return exam + ' ' + year + ' – ' + subject;
}


// ╔══════════════════════════════════════════════════════════════╗
// ║  EmpirioP — Premium Edition                       ║
// ║  All Years · All Subjects · Adaptive · AI-Powered           ║
// ╚══════════════════════════════════════════════════════════════╝

const EXAM_PROGRAMS = [
  {
    id: "naplan",
    label: "NAPLAN",
    icon: "📋",
    color: "#0D9488",
    desc: "National Literacy & Numeracy Assessment · Years 3, 5, 7 & 9",
    years: ["y3","y5","y7","y9"],
    subjects: ["Reading","Writing","Language Conventions","Numeracy"],
    format: "Online MCQ + Written",
    date: "March 2027",
    available: true,
  },
  {
    id: "selective_nsw",
    label: "NSW Selective High School",
    icon: "🏆",
    color: "#8B5CF6",
    desc: "NSW Selective High School Placement Test · Year 6 students",
    years: ["y6"],
    subjects: ["Reading","Mathematical Reasoning","Thinking Skills","Writing"],
    format: "4 sections · Computer-based",
    date: "2027 date to be announced",
    available: true,
  },
  {
    id: "oc",
    label: "OC Test",
    icon: "⭐",
    color: "#F59E0B",
    desc: "NSW Opportunity Class Placement Test · Year 4 students",
    years: ["y4"],
    subjects: ["Reading","Mathematical Reasoning","Thinking Skills"],
    format: "3 sections · No writing · Computer-based",
    date: "2027 date to be announced",
    available: true,
  },
  {
    id: "acer",
    label: "ACER Scholarship",
    icon: "🔬",
    color: "#10B981",
    desc: "ACER Scholarship Test · Years 5–6 (Level 1, entry to Year 7)",
    years: ["y5","y6"],
    subjects: ["Written Expression","Humanities & Reading","Mathematics"],
    format: "4 parts · Written + MCQ",
    date: "27 February 2027",
    available: true,
  },
  {
    id: "ignite",
    label: "IGNITE (SA)",
    icon: "💡",
    color: "#EC4899",
    desc: "SA Gifted & Talented Program · HAST by ACER · Year 6 students",
    years: ["y6"],
    subjects: ["Reading Comprehension","Written Expression","Mathematical Reasoning","Abstract Reasoning"],
    format: "4 parts · SA only",
    date: "February 2027",
    available: true,
  },
  {
    id: "vic_selective",
    label: "VIC Selective Entry",
    icon: "🎓",
    color: "#3B82F6",
    desc: "Victorian Selective Entry · Melbourne High, MacRob, Nossal, Suzanne Cory · Year 8 students",
    years: ["y8"],
    subjects: ["Reading & Verbal Reasoning","Mathematics & Quantitative Reasoning","Writing"],
    format: "3 sections · ACER administered",
    date: "2027 date to be announced",
    available: true,
  },
  {
    id: "icas",
    label: "ICAS",
    icon: "🌟",
    color: "#EF4444",
    desc: "International Competitions & Assessments · Years 2–12",
    years: ["y3","y5","y7","y9"],
    subjects: ["English","Mathematics","Science","Digital Technologies","Writing","Spelling Bee"],
    format: "6 subjects · Independent MCQ per subject",
    date: "2027 date to be announced",
    available: false, // hidden on owner request 2026-11-03; set back to true to show ICAS again
  },
  {
    id: "hsc",
    label: "NSW HSC",
    icon: "🎓",
    color: "#3B82F6",
    desc: "Higher School Certificate · Year 12 · NSW & ACT",
    years: ["y12"],
    subjects: ["English Advanced","English Standard","Mathematics Advanced","Extension 1","Biology","Chemistry","Physics","Modern History","Legal Studies","Economics","Business Studies"],
    format: "12 subjects · NESA syllabus-aligned",
    date: "Oct–Nov 2026",
    available: true,
  },
  {
    id: "vce",
    label: "VIC VCE",
    icon: "🏅",
    color: "#7C3AED",
    desc: "Victorian Certificate of Education · Year 12 · Victoria",
    years: ["y12"],
    subjects: ["Legal Studies","Economics","Accounting","Business Management","Global Politics","History: Revolutions"],
    format: "13 subjects · VCAA Units 3 & 4",
    date: "Oct–Nov 2026",
    available: true,
  },
];

// ── COMPREHENSIVE QUESTION BANK ────────────────────────────────
// Format: { id, topic, criteria, difficulty (1=bronze,2=silver,3=gold),
//           year (array), q, options, answer (index), explanation, hint }

const QB = BANKS.QB;

const ALL_QUESTIONS = BANKS.ALL;

const EXAM_QUESTIONS = BANKS.EXAM;
// The question banks are JSON files behind the content gate, filled in place by loadBanks() after sign-in (src/lib/banks.js)
const NAPLAN_Y3_QUESTIONS = BANKS.naplan.y3;
const NAPLAN_Y5_QUESTIONS = BANKS.naplan.y5;
const NAPLAN_Y7_QUESTIONS = BANKS.naplan.y7;
const NAPLAN_Y9_QUESTIONS = BANKS.naplan.y9;


// ── ADAPTIVE DIFFICULTY ENGINE ─────────────────────────────────
// ── ADAPTIVE DIFFICULTY ENGINE ─────────────────────────────────
// Bucket system: Bronze (1) → Silver (2) → Gold (3)
// After 2 consecutive correct at current level → promote to next level
// After 2 consecutive wrong → demote to previous level
// Session state is tracked via adaptiveState object passed in

// The full pool for one exam section is built once and reused (the subject page asks for several per render);
// a NAPLAN entry is rebuilt when that section's expansion file has loaded. Callers copy before reordering.
const POOL_CACHE = new Map();
function buildPool(subject, yearId, topicFilter, examProg) {
  const key = `${examProg}|${subject}|${yearId}|${examProg === "naplan" ? getNaplanExtra(yearId, subject).length : getExamExtra(examProg, yearId, subject).length}`;
  let base = POOL_CACHE.get(key);
  if (!base) { base = buildFullPool(subject, yearId, examProg); POOL_CACHE.set(key, base); }
  return topicFilter ? base.filter(q => matchesFilter(q, topicFilter)) : base;
}
function buildFullPool(subject, yearId, examProg) {
  let pool = [];
  if (examProg === "naplan") {
    const subjectMap = { reading: ["r","y3r","n3r","y5r","n5r","y7r","n7r","y9r","n9r"], writing: ["w","y3w","n3w","y5w","n5w","y7w","n7w","y9w","n9w"], language: ["l","y3l","n3l","y5l","n5l","y7l","n7l","y9l","n9l"], numeracy: ["n","y3n","n3n","y5n","n5n","y7n","n7n","y9n","n9n"] };
    const prefixes = subjectMap[subject] || [];
    const extraQ = ALL_QUESTIONS.filter(q => {
      const matchSubject = prefixes.some(p => q.id.startsWith(p));
      const matchYear = q.year && q.year.includes(yearId);
      return matchSubject && matchYear;
    });
    const subjectLabel = { reading:"Reading", writing:"Writing", language:"Language Conventions", numeracy:"Numeracy" };
    const enriched = extraQ.map(q => q.criteria ? q : { ...q, criteria: `${subjectLabel[subject] || subject} — ${q.topic}` });
    pool = [...(QB[subject] || []).filter(q => q.year && q.year.includes(yearId)), ...enriched];
    const y3Pool = NAPLAN_Y3_QUESTIONS.filter(q => q.subject === subject && q.year.includes(yearId));
    pool = [...pool, ...y3Pool];
    const y5Pool = NAPLAN_Y5_QUESTIONS.filter(q => q.subject === subject && q.year.includes(yearId));
    pool = [...pool, ...y5Pool];
    const y7Pool = NAPLAN_Y7_QUESTIONS.filter(q => q.subject === subject && q.year.includes(yearId));
    pool = [...pool, ...y7Pool];
    const y9Pool = NAPLAN_Y9_QUESTIONS.filter(q => q.subject === subject && q.year.includes(yearId));
    pool = [...pool, ...y9Pool];
    const extraPool = getNaplanExtra(yearId, subject).filter(q => q.subject === subject && q.year.includes(yearId));
    pool = [...pool, ...extraPool];
  } else {
    pool = EXAM_QUESTIONS.filter(q => {
      const matchExam = q.exam === examProg;
      const matchSubject = subject === "all" || q.subject === subject;
      const matchYear = !yearId || (q.year && q.year.includes(yearId));
      return matchExam && matchSubject && matchYear;
    });
    pool = pool.map(q => q.criteria ? q : { ...q, criteria: `${q.topic}` });
    // expansion bank for this exam + year + section (already carries area/topic, see scripts/exam-expand/)
    if (yearId) pool = [...pool, ...getExamExtra(examProg, yearId, subject).filter(q => (subject === "all" || q.subject === subject) && q.year.includes(yearId))];
  }
  // clean topic names and topic areas (src/lib/examTopics.js); a filter can be one topic or a whole area
  pool = pool.map(q => applyTopic(q, examProg));
  // Only item types the app can render (scripts/naplan-expand/ITEM_SCHEMA.md); an unknown type never reaches a screen.
  pool = pool.filter(q => ITEM_TYPES.includes(itemType(q)));
  const seen = new Set();
  pool = pool.filter(q => { if (seen.has(q.id)) return false; seen.add(q.id); return true; });
  return pool;
}

// ── Cross-session question tracking ─────────────────────────
// Tracks which question IDs a student has already seen per subject+year
// Ensures a fresh set is served each new practice session
function getSessionKey(subject, yearId, examProg) {
  return `lumio_seen_${examProg}_${subject}_${yearId}`;
}
function getSeenIds(subject, yearId, examProg) {
  try {
    const raw = localStorage.getItem(getSessionKey(subject, yearId, examProg));
    return new Set(JSON.parse(raw || '[]'));
  } catch(_) { return new Set(); }
}
function markIdsSeen(ids, subject, yearId, examProg) {
  try {
    const key = getSessionKey(subject, yearId, examProg);
    const existing = getSeenIds(subject, yearId, examProg);
    ids.forEach(id => existing.add(id));
    localStorage.setItem(key, JSON.stringify([...existing]));
  } catch(_) {}
}
function resetSeenIds(subject, yearId, examProg) {
  try { localStorage.removeItem(getSessionKey(subject, yearId, examProg)); } catch(_) {}
}

function getAdaptiveQuestions(subject, yearId, topicFilter, count = 10, examProg = "naplan", opts = {}) {
  let fullPool = buildPool(subject, yearId, topicFilter, examProg);
  // Single-choice-only screens (the diagnostic) ask for mcq items so a typed item can never reach them.
  if (opts.mcqOnly) fullPool = fullPool.filter(isMcqItem);
  // Exclude questions seen in previous sessions — serve a fresh set
  const seenIds = getSeenIds(subject, yearId, examProg);
  // Also exclude questions used in the diagnostic test (within last 30 days)
  let diagData = { ids: [], ts: 0 };
  try { diagData = JSON.parse(localStorage.getItem('lumio_diagnostic_used') || '{"ids":[],"ts":0}'); } catch(_) {}
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  const filterDiag = Date.now() - (diagData.ts || 0) < thirtyDays;
  const diagIds = new Set(Array.isArray(diagData.ids) ? diagData.ids : []);
  let pool = fullPool.filter(q => !seenIds.has(q.id)).filter(q => !filterDiag || !diagIds.has(q.id));
  // If we've seen most/all questions, reset and start fresh
  if (pool.length < count) {
    resetSeenIds(subject, yearId, examProg);
    pool = fullPool.filter(q => !filterDiag || !diagIds.has(q.id));
    if (pool.length < count) pool = fullPool; // final fallback
  }
  // Initial session: start with Silver (2), pad with Bronze (1) and Gold (3)
  const shuffle = arr => [...arr].sort(() => Math.random() - 0.5);
  const bronze = shuffle(pool.filter(q => q.difficulty === 1));
  const silver = shuffle(pool.filter(q => q.difficulty === 2));
  const gold   = shuffle(pool.filter(q => q.difficulty === 3));
  // Start at Silver, fill gaps with Bronze then Gold
  const ordered = [...silver.slice(0,3), ...bronze.slice(0,2), ...silver.slice(3,5), ...gold.slice(0,2), ...silver.slice(5), ...gold.slice(2)];
  const result = ordered.slice(0, count);
  return (result.length ? result : pool.slice(0, count)) || [];
}

// Called after each answer — returns next question difficulty to target
// adaptiveState: { currentLevel: 1|2|3, streak: number, streakDirection: 'correct'|'wrong' }
function getNextAdaptiveLevel(adaptiveState, wasCorrect) {
  const { currentLevel, streak, streakDirection } = adaptiveState;
  const newStreak = streakDirection === (wasCorrect ? 'correct' : 'wrong') ? streak + 1 : 1;
  const newDirection = wasCorrect ? 'correct' : 'wrong';
  let newLevel = currentLevel;
  // Promote: 2 consecutive correct → go up one level
  if (wasCorrect && newStreak >= 2 && currentLevel < 3) newLevel = currentLevel + 1;
  // Demote: 2 consecutive wrong → go down one level
  if (!wasCorrect && newStreak >= 2 && currentLevel > 1) newLevel = currentLevel - 1;
  return { currentLevel: newLevel, streak: newLevel !== currentLevel ? 1 : newStreak, streakDirection: newDirection };
}

// Get the next question from pool matching target difficulty (falls back to any available)
function getNextAdaptiveQuestion(pool, usedIds, targetLevel) {
  const available = pool.filter(q => !usedIds.has(q.id));
  if (!available.length) return null;
  const atLevel = available.filter(q => q.difficulty === targetLevel);
  if (atLevel.length) return atLevel[Math.floor(Math.random() * atLevel.length)];
  // Fallback: pick closest difficulty
  const sorted = available.sort((a, b) => Math.abs(a.difficulty - targetLevel) - Math.abs(b.difficulty - targetLevel));
  return sorted[0] || null;
}

// Topic areas → topics for one exam section, from that exam's own full pool. NAPLAN topics with fewer than 5
// questions are left out (too few for a session); the smaller exams list every topic that has 2 or more.
// (Non-NAPLAN exams used to list the NAPLAN inline bank's topics here, which belonged to a different test.)
function getTopicAreas(subject, yearId, examProg) {
  return groupTopics(buildPool(subject, yearId, null, examProg), examProg === "naplan" ? 5 : 2);
}

// ── QUICK Lumi QUESTIONS ───────────────────────────────────────
const QUICK_Qs = [
  "What is NAPLAN testing?","How do I improve my inference skills?",
  "What is the TEEL structure?","How do apostrophes work?",
  "What is place value?","How do I find the main idea of a text?",
  "What is the difference between affect and effect?","How do I solve algebra problems?",
  "What makes a good persuasive essay?","How do I calculate percentages?",
];


// ── XP SYSTEM ──────────────────────────────────────────────
function getXP() {
  try {
    const xp = JSON.parse(localStorage.getItem(profileKey("lumio_xp", getActiveProfile()?.id || "")) || "{}"); // per child, like Learn XP
    const month = new Date().toISOString().slice(0, 7);
    // Learn topics run in their own pages and never call trackSession(), so their XP (completed lessons and
    // mastered syllabus points) is added at display time - see src/lib/topicProgress.js.
    const lx = learnXP(getActiveProfile()?.id || ''); // the active child's Learn XP
    return { monthly: (xp[month] || 0) + lx.monthly, total: (xp.total || 0) + lx.total };
  } catch(e) { return { monthly: 0, total: 0 }; }
}

function getXPLevel(totalXP) {
  if (totalXP >= 10000) return { level: 5, name: "Legend", color: "#F59E0B", next: null };
  if (totalXP >= 5000)  return { level: 4, name: "Champion", color: "#8B5CF6", next: 10000 };
  if (totalXP >= 2000)  return { level: 3, name: "Scholar", color: "#0D9488", next: 5000 };
  if (totalXP >= 500)   return { level: 2, name: "Explorer", color: "#3B82F6", next: 2000 };
  return                       { level: 1, name: "Starter", color: "#10B981", next: 500 };
}

// ── WOW LEADERBOARD COMPONENT ──────────────────────────────
function Leaderboard({ onClose, activeProfile, examProgram }) {
  const { monthly, total } = getXP();
  const xpLevel = getXPLevel(total);

  // There is no real cross-family ranking data source yet (no backend
  // aggregates XP across users). Previously this screen filled the gap
  // with procedurally generated fake names and XP values, which is not
  // acceptable while real families are using the app. Show the user's
  // own real XP, and an honest "coming soon" state for the rankings
  // themselves rather than inventing other students.
  const userXP = monthly;
  const seed = examProgram?.charCodeAt(0) || 78;
  const month = new Date().toLocaleString("en-AU", { month: "long", year: "numeric" });

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:"#EEEDF3", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,#6C5CE7,#4A3EAE)", padding:"20px 20px 0", position:"relative", overflow:"hidden" }}>
        {/* Animated background stars */}
        <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
          {[...Array(20)].map((_,i) => (
            <div key={i} style={{
              position:"absolute",
              width: 2 + (i%3)*2,
              height: 2 + (i%3)*2,
              background:"white",
              borderRadius:"50%",
              opacity: 0.2 + (i%5)*0.1,
              left: `${(i*17+seed)%100}%`,
              top: `${(i*13+seed)%100}%`,
              animation:`lumiBob ${1.5+(i%3)*0.5}s ease-in-out ${i*0.2}s infinite`,
            }}/>
          ))}
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, position:"relative" }}>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:8, width:32, height:32, color:"white", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>←</button>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:18, fontWeight:800, color:"white", display:"flex", alignItems:"center", gap:8 }}><TrophyIcon size={16} />Live Rankings</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.75)" }}>Resets 1st of every month · {month}</div>
          </div>
        </div>

        {/* User XP card */}
        <div style={{ background:"rgba(255,255,255,0.12)", backdropFilter:"blur(10px)", borderRadius:"16px 16px 0 0", padding:"16px 20px", display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ width:52, height:52, borderRadius:"50%", background:"rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, flexShrink:0, border:"2px solid rgba(255,255,255,0.4)" }}>
            {activeProfile?.avatar || "⭐"}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:15, fontWeight:800, color:"white", marginBottom:2 }}>{activeProfile?.name || "You"} · {xpLevel.name}</div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ flex:1, height:6, background:"rgba(255,255,255,0.2)", borderRadius:99, overflow:"hidden" }}>
                <div style={{ height:"100%", background:"#FF7A59", borderRadius:99, width: xpLevel.next ? `${Math.min((total/xpLevel.next)*100,100)}%` : "100%", transition:"width 1s ease" }}/>
              </div>
              <span style={{ fontSize:11, color:"rgba(255,255,255,0.85)", fontWeight:600, flexShrink:0 }}>{total.toLocaleString()} XP</span>
            </div>
          </div>
          <div style={{ textAlign:"right", flexShrink:0 }}>
            <div style={{ fontSize:22, fontWeight:900, color:"white" }}>{userXP.toLocaleString()}</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.7)" }}>XP this month</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #ECEAF3", background:"#FFFFFF", flexShrink:0 }}>
        <div style={{ padding:"10px 16px 8px", borderBottom:"2px solid #6C5CE7" }}>
          <span style={{ fontSize:12, fontWeight:700, color:"#16162A", textTransform:"uppercase", letterSpacing:".06em" }}>National Rankings</span>
        </div>
      </div>

      {/* XP info bar */}
      <div style={{ padding:"10px 16px", background:"#6C5CE711", borderBottom:"1px solid #6C5CE722", display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
        <span style={{ fontSize:12, color:"#6B6A82", fontWeight:600 }}>This month: <span style={{ color:"#6C5CE7", fontWeight:700 }}>{userXP.toLocaleString()} XP</span></span>
        <span style={{ fontSize:12, color:"#8B87A8" }}>· Earn 10 XP per correct answer</span>
      </div>

      {/* Leaderboard entries */}
      <div style={{ flex:1, overflowY:"auto", padding:"12px 16px 16px" }}>

        {/* Honest empty state — no real cross-family ranking data exists yet */}
        <div style={{ textAlign:"center", padding:"36px 20px", background:"#FFFFFF", border:"1px solid #ECEAF3", borderRadius:22, boxShadow:"0 1px 2px rgba(30,22,70,.04), 0 12px 30px rgba(40,30,90,.06)", marginBottom:16 }}>
          <div style={{ fontSize:15, fontWeight:700, color:"#16162A", marginBottom:6 }}>Live rankings are coming soon</div>
          <div style={{ fontSize:13, color:"#6B6A82", lineHeight:1.6, maxWidth:320, margin:"0 auto" }}>
            We are building a real national leaderboard. Keep earning XP. Your {userXP.toLocaleString()} XP this month will count when it goes live.
          </div>
        </div>

        {/* How XP works */}
        <div style={{ marginTop:16, padding:"14px 16px", background:"#FFFFFF", border:"1px solid #ECEAF3", borderRadius:22, boxShadow:"0 1px 2px rgba(30,22,70,.04), 0 12px 30px rgba(40,30,90,.06)" }}>
          <p style={{ fontSize:12, fontWeight:700, color:"#16162A", margin:"0 0 8px" }}>How XP works</p>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            {[["Correct answer","10 XP"],["Question answered","2 XP"],["Lesson finished","100 XP"],["Syllabus point mastered","25 XP"]].map(([label,xp]) => (
              <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:11, color:"#6B6A82" }}>{label}</span>
                <span style={{ fontSize:11, fontWeight:700, color:"#6C5CE7" }}>{xp}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize:11, color:"#8B87A8", margin:"8px 0 0" }}>Practice XP is added when you finish a session. Monthly XP resets on the 1st. Everyone gets a fresh start.</p>
        </div>
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════
// CONCEPTS LIBRARY — Year 3-9 All Subjects
// Each concept: id, subject, years, title, icon, color,
//   explanation, keyFacts[], example, diagramType, practiceTopic
// ═══════════════════════════════════════════════════════════════

const CONCEPTS_LIBRARY = [

// ─── MATHEMATICS Y3-4 ───────────────────────────────────────────

{id:"m_placevalue",subject:"maths",years:["y3","y4"],title:"Place Value",icon:"🔢",color:"#3B82F6",
explanation:"Every digit in a number has a place — and that place tells you its value. In 3,472, the 3 means 3 thousands, the 4 means 4 hundreds, the 7 means 7 tens, and the 2 means 2 ones. Moving one place left makes a digit 10 times bigger.",
keyFacts:["Digits 0-9 each have a different value depending on their position","Moving left multiplies by 10; moving right divides by 10","A zero holds a place — 405 is very different from 45"],
example:"3,472 = 3,000 + 400 + 70 + 2\\nThe 3 is in the thousands place → worth 3,000\\nThe 4 is in the hundreds place → worth 400\\nThe 7 is in the tens place → worth 70\\nThe 2 is in the ones place → worth 2",
diagramType:"numberline",practiceTopic:"Place Value"},

{id:"m_addition_sub",subject:"maths",years:["y3","y4"],title:"Addition & Subtraction",icon:"➕",color:"#3B82F6",
explanation:"Addition combines numbers to find a total. Subtraction finds the difference. They are inverse operations — addition undoes subtraction and vice versa. Always line up digits by place value.",
keyFacts:["When a column adds to 10+, carry the extra to the next column","Subtraction can be checked: answer + smaller number = larger number","The order matters for subtraction (8−3 ≠ 3−8) but not addition"],
example:"247 + 185:\\nOnes: 7+5=12 → write 2, carry 1\\nTens: 4+8+1=13 → write 3, carry 1\\nHundreds: 2+1+1=4\\nAnswer: 432",
diagramType:"numberline",practiceTopic:"Addition & Subtraction"},

{id:"m_multiplication",subject:"maths",years:["y3","y4"],title:"Multiplication",icon:"✖️",color:"#3B82F6",
explanation:"Multiplication is repeated addition. 4 × 3 means four groups of three (3+3+3+3=12). Arrays and times tables help you see patterns. Knowing your tables to 12×12 makes everything in maths faster.",
keyFacts:["Any number × 0 = 0","Any number × 1 = itself","Order doesn't matter: 4×3 = 3×4 (commutative property)","Doubles, then add one group, helps with hard facts"],
example:"6 × 7:\\nThink: 6 × 6 = 36, then add one more 6\\n36 + 6 = 42\\nSo 6 × 7 = 42",
diagramType:"multiplication",practiceTopic:"Multiplication",
realWorld:"Every supermarket price, every recipe scaling, every sports score calculation uses multiplication. A chef scaling a recipe for 200 guests instead of 4 multiplies every ingredient by 50.",
thinkAbout:"Why does 7 × 8 give the same answer as 8 × 7? This isn't obvious — it's a property called commutativity. Can you draw a picture that proves it?"},

{id:"m_division",subject:"maths",years:["y3","y4"],title:"Division",icon:"➗",color:"#3B82F6",
explanation:"Division splits a number into equal groups. 20 ÷ 4 = 5 means 20 split into 4 equal groups gives 5 in each. Division is the inverse of multiplication — if you know 4×5=20, you know 20÷4=5.",
keyFacts:["You cannot divide by zero","If a number doesn't divide evenly, you get a remainder","Division fact families: 3×4=12, 4×3=12, 12÷3=4, 12÷4=3"],
example:"56 ÷ 8:\\nThink: what × 8 = 56?\\n8 × 7 = 56\\nSo 56 ÷ 8 = 7",
diagramType:"division",practiceTopic:"Division"},

{id:"m_fractions_basic",subject:"maths",years:["y3","y4"],title:"Fractions",icon:"🍕",color:"#3B82F6",
explanation:"A fraction shows part of a whole. The bottom number (denominator) tells you how many equal parts the whole is split into. The top number (numerator) tells you how many parts you have.",
keyFacts:["Numerator = top number (parts you have)","Denominator = bottom number (total equal parts)","Equivalent fractions have the same value: 1/2 = 2/4 = 4/8","A fraction where numerator = denominator equals 1 whole"],
example:"A pizza cut into 8 slices:\\nYou eat 3 slices → you ate 3/8 of the pizza\\nNumerator: 3 (slices eaten)\\nDenominator: 8 (total slices)",
diagramType:"fractions",practiceTopic:"Fractions",
realWorld:"Fractions are everywhere: a half-time score, splitting a bill at a restaurant, reading a ruler, understanding a sale ('¼ off'). Pharmacists use fractions for medication doses — accuracy is life-or-death.",
thinkAbout:"Is ½ always exactly half? What if you're sharing ½ of a small pizza vs ½ of a giant pizza? This shows that fractions describe relationships, not fixed amounts."},

{id:"m_time",subject:"maths",years:["y3","y4"],title:"Time",icon:"⏰",color:"#3B82F6",
explanation:"We measure time in seconds, minutes, hours, days, weeks, months and years. Clocks have a 12-hour cycle (AM for morning, PM for afternoon/night). To find duration, count from the start time to the end time.",
keyFacts:["60 seconds = 1 minute","60 minutes = 1 hour","24 hours = 1 day","Duration = end time − start time"],
example:"Bus leaves at 9:45am, arrives at 11:20am\\nFrom 9:45 → 10:00 = 15 minutes\\nFrom 10:00 → 11:00 = 60 minutes\\nFrom 11:00 → 11:20 = 20 minutes\\nTotal: 15 + 60 + 20 = 95 minutes = 1 hour 35 minutes",
diagramType:"numberline",practiceTopic:"Time"},

{id:"m_shapes2d",subject:"maths",years:["y3","y4"],title:"2D Shapes",icon:"🔷",color:"#3B82F6",
explanation:"2D shapes are flat — they have length and width but no depth. We classify them by their number of sides, angles and whether sides are equal. Polygons are closed shapes with straight sides.",
keyFacts:["Triangle: 3 sides, angles add to 180°","Quadrilateral: 4 sides, angles add to 360°","Regular shapes: all sides and angles equal","Irregular shapes: sides and/or angles differ"],
example:"Square: 4 equal sides, 4 right angles (90°)\\nRectangle: opposite sides equal, 4 right angles\\nRhombus: 4 equal sides, opposite angles equal\\nTrapezium: exactly 1 pair of parallel sides",
diagramType:"angles",practiceTopic:"2D Shapes"},

{id:"m_money",subject:"maths",years:["y3","y4"],title:"Money & Shopping",icon:"💰",color:"#3B82F6",
explanation:"Australian money uses dollars ($) and cents (¢). 100 cents = 1 dollar. When shopping, you need to calculate total cost and change. Always round prices to the nearest 5 cents when paying with coins.",
keyFacts:["$1 = 100 cents","Change = amount given − amount spent","Always check: does your answer seem reasonable?","Round to nearest 5c when using cash"],
example:"Buy items costing $3.45 and $1.85\\nTotal: $3.45 + $1.85 = $5.30\\nPay with $10 note\\nChange: $10 − $5.30 = $4.70",
diagramType:"bargraph",practiceTopic:"Money"},

// ─── MATHEMATICS Y5-6 ───────────────────────────────────────────

{id:"m_decimals",subject:"maths",years:["y5","y6"],title:"Decimals",icon:"🔟",color:"#3B82F6",
explanation:"Decimals extend our place value system past the ones column. The decimal point separates whole numbers from parts of a whole. Tenths are 10 times smaller than ones, hundredths are 100 times smaller.",
keyFacts:["Tenths: 1 decimal place (0.1 = 1/10)","Hundredths: 2 decimal places (0.01 = 1/100)","Adding zeros after the last decimal digit doesn't change the value","To compare decimals, align decimal points then compare digit by digit"],
example:"0.3 vs 0.27:\\nLine up decimals: 0.30 vs 0.27\\n3 tenths > 2 tenths\\nSo 0.3 > 0.27\\n(Many students get this wrong!)\\n",
diagramType:"numberline",practiceTopic:"Decimals"},

{id:"m_percentages",subject:"maths",years:["y5","y6"],title:"Percentages",icon:"%",color:"#3B82F6",
explanation:"Percent means 'out of 100.' 45% means 45 out of every 100. Percentages, fractions and decimals are all different ways of showing the same thing. They convert between each other easily.",
keyFacts:["50% = 1/2 = 0.5","25% = 1/4 = 0.25","10% = 1/10 = 0.1","To find 10%, divide by 10. To find 1%, divide by 100"],
example:"Find 35% of 200:\\nMethod: 10% of 200 = 20\\n30% = 3 × 20 = 60\\n5% = half of 10% = 10\\n35% = 60 + 10 = 70",
diagramType:"fractions",practiceTopic:"Percentages",
realWorld:"A 30% pay rise sounds great — but if your salary is $400/week, it's only $120 extra. Banks charge interest rates as percentages. Understanding this protects you from being misled by advertising.",
thinkAbout:"If a price goes up by 50% then down by 50%, do you end up where you started? Most people say yes — but try calculating it. The answer will surprise you."},

{id:"m_area",subject:"maths",years:["y5","y6"],title:"Area & Perimeter",icon:"📐",color:"#3B82F6",
explanation:"Perimeter is the distance around the outside of a shape. Area is the amount of space inside a shape. They measure different things and use different formulas.",
keyFacts:["Rectangle perimeter = 2 × (length + width)","Rectangle area = length × width","Area is measured in square units (cm², m²)","Perimeter is measured in linear units (cm, m)"],
example:"Rectangle: length=8cm, width=5cm\\nPerimeter: 2×(8+5) = 2×13 = 26cm\\nArea: 8×5 = 40cm²\\nNote: perimeter and area are completely independent!",
diagramType:"rectangle",practiceTopic:"Area & Perimeter"},

{id:"m_angles",subject:"maths",years:["y5","y6"],title:"Angles",icon:"📐",color:"#3B82F6",
explanation:"An angle measures the amount of turn between two lines meeting at a point. We measure angles in degrees (°). A full turn is 360°. Angles are everywhere — in shapes, buildings, and nature.",
keyFacts:["Acute: less than 90°","Right angle: exactly 90° (the square corner symbol)","Obtuse: between 90° and 180°","Straight angle: exactly 180°","Reflex: between 180° and 360°"],
example:"Angles in a triangle always add to 180°\\nIf two angles are 60° and 70°:\\nThird angle = 180° − 60° − 70° = 50°\\nAngles in a quadrilateral always add to 360°",
diagramType:"angles",practiceTopic:"Angles"},

{id:"m_probability",subject:"maths",years:["y5","y6"],title:"Probability",icon:"🎲",color:"#3B82F6",
explanation:"Probability measures how likely an event is to happen. It ranges from 0 (impossible) to 1 (certain). We can express it as a fraction, decimal or percentage. It predicts what will happen in the long run.",
keyFacts:["P(event) = favourable outcomes ÷ total outcomes","Probability of 0 = impossible","Probability of 1 = certain","P(event happening) + P(event not happening) = 1"],
example:"Rolling a standard die (1-6):\\nP(rolling a 4) = 1/6\\nP(rolling an even number) = 3/6 = 1/2\\nP(rolling less than 7) = 6/6 = 1 (certain)\\nP(rolling 8) = 0 (impossible)",
diagramType:"probability",practiceTopic:"Probability",
realWorld:"Insurance companies calculate risk using probability. Weather forecasters give percentage chances. Casino games are carefully designed so the probability slightly favours the house — that's how they profit.",
thinkAbout:"If you flip a fair coin 10 times and get heads every time, what's the probability of getting heads on the 11th flip? Most people say 'tails is due' — but the coin has no memory."},

{id:"m_data",subject:"maths",years:["y5","y6"],title:"Data & Statistics",icon:"📊",color:"#3B82F6",
explanation:"Statistics helps us make sense of data. We collect data, organise it into graphs and tables, then analyse it using measures like mean, median, mode and range to find patterns and draw conclusions.",
keyFacts:["Mean = sum of all values ÷ number of values","Median = middle value when sorted in order","Mode = most frequently occurring value","Range = largest value − smallest value"],
example:"Data: 5, 8, 3, 8, 6, 7, 8\\nSorted: 3, 5, 6, 7, 8, 8, 8\\nMean: (5+8+3+8+6+7+8)÷7 = 45÷7 ≈ 6.4\\nMedian: 7 (middle value)\\nMode: 8 (appears 3 times)\\nRange: 8−3 = 5",
diagramType:"bargraph",practiceTopic:"Data"},

{id:"m_algebra_intro",subject:"maths",years:["y5","y6"],title:"Introduction to Algebra",icon:"🔣",color:"#3B82F6",
explanation:"Algebra uses letters to represent unknown numbers. Instead of asking 'what plus 3 equals 7?', we write x + 3 = 7. Solving means finding the value of the letter that makes the equation true.",
keyFacts:["A variable (like x or n) represents an unknown number","Whatever you do to one side of = you must do to the other","Inverse operations help isolate the variable","Check your answer by substituting it back in"],
example:"Solve: x + 3 = 7\\nSubtract 3 from both sides: x = 7 − 3 = 4\\nCheck: 4 + 3 = 7 ✓\\n\\nSolve: 2n = 14\\nDivide both sides by 2: n = 7\\nCheck: 2 × 7 = 14 ✓",
diagramType:"lineargraph",practiceTopic:"Algebra",
realWorld:"Every app you use was built with algebra. When Netflix recommends a show, an algorithm with thousands of variables (unknown values) calculates what you'll probably like. Algebra is the language of computers.",
thinkAbout:"If x + 5 = 12, most people can solve this by inspection. But what if it were x² + 5x = 84? Algebra gives us systematic methods that work even when inspection fails."},

// ─── MATHEMATICS Y7-9 ───────────────────────────────────────────

{id:"m_pythagoras",subject:"maths",years:["y7","y8","y9"],title:"Pythagoras Theorem",icon:"📐",color:"#3B82F6",
explanation:"In any right-angled triangle, the square of the hypotenuse (longest side) equals the sum of the squares of the other two sides. Written as a²+b²=c², where c is always the hypotenuse (opposite the right angle).",
keyFacts:["Only works for right-angled triangles","The hypotenuse is ALWAYS opposite the right angle","To find hypotenuse: c = √(a²+b²)","To find a shorter side: a = √(c²−b²)","Famous triple: 3,4,5 (and multiples: 6,8,10)"],
example:"Find the hypotenuse: a=3, b=4\\na²+b² = 3²+4² = 9+16 = 25\\nc = √25 = 5\\n\\nFind missing side: c=13, b=5\\na² = c²−b² = 169−25 = 144\\na = √144 = 12",
diagramType:"pythagoras",practiceTopic:"Pythagoras",
realWorld:"Builders use 3-4-5 triangles (a Pythagorean triple) to check corners are perfectly square — no special equipment needed. GPS satellites use Pythagoras extended to 3D space to calculate your exact position.",
thinkAbout:"Pythagoras works for ALL right-angled triangles — but how did ancient mathematicians discover this before algebra existed? They did it with squares drawn on sand."},

{id:"m_linear_eq",subject:"maths",years:["y7","y8","y9"],title:"Linear Equations",icon:"📈",color:"#3B82F6",
explanation:"A linear equation creates a straight line when graphed. The gradient (slope) tells you how steep the line is. The y-intercept tells you where it crosses the y-axis. Every point on the line satisfies the equation.",
keyFacts:["Gradient = rise ÷ run = (y₂-y₁)÷(x₂-x₁)","Positive gradient: line goes up left to right","Negative gradient: line goes down left to right","y = mx + c: m is gradient, c is y-intercept"],
example:"y = 2x + 3\\nGradient = 2 (goes up 2 for every 1 across)\\ny-intercept = 3 (crosses y-axis at 3)\\nWhen x=0: y=3\\nWhen x=2: y=2(2)+3=7\\nPlot (0,3) and (2,7) and draw a straight line",
diagramType:"lineargraph",practiceTopic:"Linear Equations"},

{id:"m_indices",subject:"maths",years:["y7","y8","y9"],title:"Indices & Powers",icon:"⚡",color:"#3B82F6",
explanation:"An index (power) tells you how many times to multiply a number by itself. 2³ means 2×2×2=8. The base is the number being multiplied; the index is how many times. Index laws make calculations much faster.",
keyFacts:["aᵐ × aⁿ = aᵐ⁺ⁿ (multiply: add indices)","aᵐ ÷ aⁿ = aᵐ⁻ⁿ (divide: subtract indices)","(aᵐ)ⁿ = aᵐⁿ (power of power: multiply indices)","a⁰ = 1 (anything to power zero = 1)","a⁻¹ = 1/a (negative index = reciprocal)"],
example:"2³ × 2⁴ = 2³⁺⁴ = 2⁷ = 128\\n5⁶ ÷ 5² = 5⁶⁻² = 5⁴ = 625\\n(3²)³ = 3²ˣ³ = 3⁶ = 729\\n7⁰ = 1\\n2⁻³ = 1/2³ = 1/8",
diagramType:"numberline",practiceTopic:"Indices"},

{id:"m_geometry_circles",subject:"maths",years:["y7","y8","y9"],title:"Circle Geometry",icon:"⭕",color:"#3B82F6",
explanation:"Circles have special properties. The radius is the distance from centre to edge. The diameter is twice the radius. The circumference (perimeter) and area use π (pi ≈ 3.14159...). π is irrational — it never ends or repeats.",
keyFacts:["Diameter = 2 × radius","Circumference = π × diameter = 2πr","Area = π × r²","π ≈ 3.14 (or use the π button on calculator)","Semi-circle area = πr²/2"],
example:"Circle with radius = 5cm:\\nDiameter = 2×5 = 10cm\\nCircumference = 2×π×5 = 31.42cm\\nArea = π×5² = π×25 = 78.54cm²",
diagramType:"circle",practiceTopic:"Circle Geometry"},

{id:"m_statistics",subject:"maths",years:["y7","y8","y9"],title:"Statistics & Graphs",icon:"📊",color:"#3B82F6",
explanation:"Statistical graphs display data visually to reveal patterns. Different graph types suit different data. Knowing which graph to use and how to interpret it critically is a key skill in Year 7-9 and NAPLAN.",
keyFacts:["Histogram: continuous data in intervals (no gaps)","Box plot: shows median, quartiles and outliers","Scatter plot: shows relationship between two variables","Misleading graphs: watch for truncated axes or unequal intervals"],
example:"Box plot values for test scores:\\nMinimum: 45, Q1: 60, Median: 72\\nQ3: 85, Maximum: 98\\nInterquartile Range (IQR) = Q3−Q1 = 85−60 = 25\\nOutlier rule: below Q1−1.5×IQR or above Q3+1.5×IQR",
diagramType:"bargraph",practiceTopic:"Statistics"},

// ─── ENGLISH Y3-4 ───────────────────────────────────────────────

{id:"e_mainidea",subject:"english",years:["y3","y4"],title:"Finding the Main Idea",icon:"💡",color:"#8B5CF6",
explanation:"The main idea is what a text is mostly about. It is the central message the author wants you to understand. Supporting details are facts, examples or reasons that back up the main idea. Every paragraph usually has one main idea.",
keyFacts:["Main idea = the BIG point of the whole text or paragraph","Supporting details explain or prove the main idea","The main idea is often (not always) in the first or last sentence","Ask: what does every sentence have in common?"],
example:"Paragraph: 'Dogs make excellent pets. They are loyal and loving. Studies show dog owners have lower stress. Dogs also encourage exercise by needing daily walks.'\\n\\nMain idea: Dogs make excellent pets\\nSupporting details: loyal, reduce stress, encourage exercise",
diagramType:"narrative",practiceTopic:"Main Idea"},

{id:"e_inference",subject:"english",years:["y3","y4","y5","y6"],title:"Making Inferences",icon:"🔍",color:"#8B5CF6",
explanation:"An inference is a conclusion you reach using clues from the text plus your own knowledge. Authors don't always state everything directly — good readers 'read between the lines.' Evidence + Reasoning = Inference.",
keyFacts:["Inferences are not guesses — they must be supported by text evidence","Look for words that suggest emotion, relationships or setting","'Show don't tell' writing requires inference","Always ask: what clues does the text give me?"],
example:"Text: 'Sarah slammed her locker shut and walked past her friends without looking up.'\\n\\nInference: Sarah is upset or angry\\nEvidence: slammed locker (physical action), avoided eye contact with friends\\nReasoning: these are signs of someone who is troubled or angry",
diagramType:"narrative",practiceTopic:"Inference"},

{id:"e_grammar_basic",subject:"english",years:["y3","y4"],title:"Parts of Speech",icon:"📝",color:"#8B5CF6",
explanation:"Every word in a sentence plays a role — its part of speech. Understanding parts of speech helps you write clearly and correct grammatical errors. The main parts are nouns, verbs, adjectives, adverbs, pronouns and conjunctions.",
keyFacts:["Noun: person, place, thing or idea (dog, Sydney, happiness)","Verb: action or state of being (run, is, think)","Adjective: describes a noun (tall, blue, happy)","Adverb: describes a verb, adjective or other adverb (quickly, very, never)"],
example:"'The enormous dog barked loudly.'\\nThe → article (a type of adjective)\\nenormous → adjective (describes dog)\\ndog → noun (the thing)\\nbarked → verb (the action)\\nloudly → adverb (describes how it barked)",
diagramType:"narrative",practiceTopic:"Grammar"},

{id:"e_punctuation",subject:"english",years:["y3","y4"],title:"Punctuation",icon:"❗",color:"#8B5CF6",
explanation:"Punctuation marks are symbols that control the rhythm, pace and meaning of writing. Without punctuation, writing becomes confusing or ambiguous. Each mark has specific rules for when and how to use it.",
keyFacts:["Capital letters: start sentences, proper nouns, I","Full stop (.): ends a statement","Comma (,): separates items in a list, joins clauses, after introductory phrases","Apostrophe ('): shows possession (dog's bowl) or contraction (don't = do not)"],
example:"Without punctuation: lets eat grandma\nWith punctuation: Let us eat, Grandma.\n(Punctuation saves lives!)\n\nPossession: the dog\u2019s bowl (belongs to dog)\nContraction: do not = don\u2019t, it is = it\u2019s\nNote: its (no apostrophe) = belonging to it",
diagramType:"narrative",practiceTopic:"Punctuation"},

{id:"e_narrative",subject:"english",years:["y3","y4","y5","y6"],title:"Narrative Writing",icon:"📖",color:"#8B5CF6",
explanation:"Narratives tell stories. Effective narratives have a clear structure, engaging characters, vivid descriptions and a satisfying resolution. NAPLAN assesses narrative writing on criteria including ideas, structure, vocabulary and sentence fluency.",
keyFacts:["Structure: Orientation → Complication → Resolution","Show don't tell: 'His hands trembled' not 'He was scared'","Vary sentence length for rhythm and effect","Strong verbs create energy: sprinted, whispered, crumbled"],
example:"Weak: 'He was scared and ran away quickly.'\\n\\nStrong: 'His heart hammered against his ribs. He spun around and sprinted into the darkness, branches whipping at his face.'\\n\\nThe second version SHOWS the fear through action and sensation.",
diagramType:"narrative",practiceTopic:"Narrative Writing"},

// ─── ENGLISH Y5-6 ───────────────────────────────────────────────

{id:"e_figurative",subject:"english",years:["y5","y6"],title:"Figurative Language",icon:"🎨",color:"#8B5CF6",
explanation:"Figurative language uses words in non-literal ways to create vivid images, emotions and comparisons. It makes writing more interesting and memorable. Recognising these devices is essential for reading and writing tasks.",
keyFacts:["Simile: comparison using 'like' or 'as' (cold as ice)","Metaphor: direct comparison without 'like/as' (the world is a stage)","Personification: giving human qualities to non-human things (the wind whispered)","Alliteration: repeated consonant sounds (she sells seashells)","Onomatopoeia: words that sound like their meaning (crash, buzz, sizzle)"],
example:"'The wind howled through the trees like a wounded animal, its icy fingers reaching into every corner of the old house.'\\n\\nPersonification: wind howled, icy fingers\\nSimile: like a wounded animal\\nImplied metaphor: the wind has fingers",
diagramType:"narrative",practiceTopic:"Figurative Language",
realWorld:"Martin Luther King's 'I Have a Dream' speech is one of the most powerful in history — almost entirely built on extended metaphors. The 'cheque' metaphor, the 'mountain' metaphor. Figurative language isn't decoration — it's persuasion.",
thinkAbout:"When you say 'time flies', you're using a dead metaphor — one so common we don't notice it anymore. How many dead metaphors can you spot in a single newspaper paragraph? There are usually dozens."},

{id:"e_persuasive",subject:"english",years:["y5","y6","y7","y8","y9"],title:"Persuasive Writing",icon:"💬",color:"#8B5CF6",
explanation:"Persuasive writing aims to convince readers to adopt a point of view or take action. Effective persuasion combines logical arguments, emotional appeals and credible evidence. NAPLAN assesses this under the 'Persuasive' writing task.",
keyFacts:["State your position clearly in the introduction","Use evidence, statistics, expert quotes to support claims","Acknowledge and refute counterarguments to appear balanced","Strong conclusion restates position and calls for action","Rhetorical questions engage readers: 'Do we really want that?'"],
example:"Structure (TEEL):\\nTopic sentence: state the argument\\nEvidence: fact, statistic or example\\nExplanation: link evidence to argument\\nLink: transition to next paragraph\\n\\nPersuasive techniques: Rule of three, repetition, emotive language, inclusive 'we'",
diagramType:"narrative",practiceTopic:"Persuasive Writing"},

{id:"e_text_structure",subject:"english",years:["y5","y6"],title:"Text Structure & Features",icon:"📄",color:"#8B5CF6",
explanation:"Different text types have different structures and features. Recognising these helps you read more effectively and write more convincingly. Authors choose structure deliberately to achieve their purpose.",
keyFacts:["Newspaper: inverted pyramid (most important info first)","Explanation: general statement → sequence → conclusion","Discussion: both sides of an issue, then conclusion","Report: classification → description of each category"],
example:"Information report structure:\\n1. Opening: classification (what it is)\\n2. Body: appearance, habitat, diet, behaviour\\n3. Conclusion: significance or summary\\n\\nFeatures: headings, subheadings, diagrams, technical vocabulary",
diagramType:"narrative",practiceTopic:"Text Structure",
realWorld:"Newspapers deliberately put the most important information in the first paragraph (the 'inverted pyramid') — historically because telegraphs could be cut off mid-transmission. Structure shapes meaning.",
thinkAbout:"Why do most stories follow a similar arc — problem, complication, resolution? Is this because humans are naturally wired to think in narratives? Some researchers believe storytelling is how our brains store memories."},

// ─── ENGLISH Y7-9 ───────────────────────────────────────────────

{id:"e_critical_analysis",subject:"english",years:["y7","y8","y9"],title:"Critical Analysis",icon:"🧠",color:"#8B5CF6",
explanation:"Critical analysis means examining a text deeply — not just what it says but HOW and WHY it says it. You analyse the author's choices: language, structure, tone, perspective and purpose. This is essential for selective school exams.",
keyFacts:["Purpose: to inform, persuade, entertain, describe or argue","Audience: who the text is written for affects every choice","Tone: the author's attitude (formal, sarcastic, passionate, objective)","Bias: one-sided presentation of information"],
example:"Analysing a newspaper editorial:\\n'Purpose: persuade readers to support a policy\\nTone: urgent and concerned\\nEvidence of bias: only one perspective presented\\nPersuasive techniques: emotive language, rhetorical questions\\nStructure: problem → solution → call to action'",
diagramType:"narrative",practiceTopic:"Critical Analysis",
realWorld:"Every news article has an author with a perspective. Algorithms decide what news you see based on what you've clicked before. Critical reading — questioning sources and bias — is now a survival skill.",
thinkAbout:"If two newspapers report the same event completely differently, which one is telling the truth? Could both be factually accurate and still create completely different impressions? How?"},

{id:"e_advanced_grammar",subject:"english",years:["y7","y8","y9"],title:"Advanced Grammar",icon:"📝",color:"#8B5CF6",
explanation:"Advanced grammar includes understanding complex sentences, clauses, and how they combine. Knowing these structures helps you write with sophistication and variety — a key criterion in NAPLAN and selective exam writing.",
keyFacts:["Main clause: can stand alone as a sentence","Subordinate clause: adds information but can't stand alone","Relative clause: begins with who/which/that","Embedded clause: inserted within another clause (set off with commas)"],
example:"Simple: 'The scientist won the Nobel Prize.'\\nCompound: 'The scientist won the Nobel Prize and gave a speech.'\\nComplex: 'The scientist who discovered penicillin won the Nobel Prize.'\\nCompound-complex: 'Although the research took decades, the scientist won the Nobel Prize and changed medicine forever.'",
diagramType:"narrative",practiceTopic:"Advanced Grammar"},

// ─── SCIENCE Y3-4 ───────────────────────────────────────────────

{id:"s_living_things",subject:"science",years:["y3","y4"],title:"Living Things",icon:"🌿",color:"#10B981",
explanation:"All living things share seven characteristics: Movement, Respiration, Sensitivity, Growth, Reproduction, Excretion, Nutrition — remembered as MRSGREN. Scientists classify living things into groups based on shared characteristics.",
keyFacts:["MRSGREN: Movement, Respiration, Sensitivity, Growth, Reproduction, Excretion, Nutrition","All living things are made of cells","Vertebrates have a backbone; invertebrates do not","Plants make their own food (producers); animals must eat (consumers)"],
example:"Is a virus a living thing?\\nViruses cannot reproduce on their own (need a host cell)\\nThey don't have cells\\nThey fail several MRSGREN criteria\\nConclusion: viruses are considered non-living by most scientists — a great example of how classification can be complex!",
diagramType:"foodweb",practiceTopic:"Living Things"},

{id:"s_materials",subject:"science",years:["y3","y4"],title:"Materials & Properties",icon:"🧱",color:"#10B981",
explanation:"Materials have properties that make them suitable for different uses. Scientists choose materials based on properties like hardness, flexibility, conductivity, transparency and whether they dissolve in water.",
keyFacts:["Conductor: allows electricity or heat to pass through (metals)","Insulator: blocks electricity or heat (rubber, wood, plastic)","Soluble: dissolves in water (sugar, salt)","Insoluble: does not dissolve (sand, oil)","Properties determine the best use for a material"],
example:"Why is copper used for electrical wires?\\n→ Excellent electrical conductor\\n→ Flexible (can be bent without breaking)\\n→ Ductile (can be drawn into wire)\\n\\nWhy use rubber for the wire's coating?\\n→ Electrical insulator (prevents shocks)\\n→ Flexible and durable",
diagramType:"states",practiceTopic:"Materials"},

{id:"s_forces_basic",subject:"science",years:["y3","y4"],title:"Forces & Motion",icon:"🎯",color:"#10B981",
explanation:"A force is a push or pull. Forces can change the speed, direction or shape of an object. When forces are balanced, objects stay still or move at constant speed. When unbalanced, objects accelerate or decelerate.",
keyFacts:["Force is measured in Newtons (N)","Gravity pulls all objects toward Earth's centre","Friction opposes motion between surfaces","Balanced forces = no change in motion (still or constant speed)","Unbalanced forces = change in motion (acceleration)"],
example:"A book on a table:\\nGravity pulls DOWN (weight of book)\\nTable pushes UP (normal force)\\nForces are balanced → book stays still\\n\\nKick a football:\\nYour foot applies force\\nAir resistance and gravity act on it\\nForces unbalanced → it moves, curves, slows",
diagramType:"forces",practiceTopic:"Forces",
realWorld:"Skydivers reach a 'terminal velocity' of about 200 km/h when air resistance (drag) exactly balances gravity. At that point, net force = 0, so they stop accelerating — they fall at constant speed.",
thinkAbout:"If you drop a feather and a cannonball in a vacuum (no air), which hits the ground first? Galileo proved they land simultaneously — this shocked people who assumed heavier = faster."},

// ─── SCIENCE Y5-6 ───────────────────────────────────────────────

{id:"s_food_webs",subject:"science",years:["y5","y6"],title:"Food Webs & Ecosystems",icon:"🌐",color:"#10B981",
explanation:"A food web shows how energy flows through an ecosystem. Producers (plants) capture energy from the sun. Consumers eat other organisms. Decomposers break down dead matter. Everything is interconnected — remove one species and the whole web can change.",
keyFacts:["Producer: makes own food via photosynthesis","Primary consumer: eats producers (herbivore)","Secondary consumer: eats primary consumers","Tertiary/apex consumer: top of the food chain","Decomposer: breaks down dead organisms (fungi, bacteria)"],
example:"Simple food chain: Grass → Grasshopper → Frog → Snake → Eagle\\n\\nIf grasshoppers disappeared:\\n→ Frogs lose a food source (numbers decline)\\n→ Grass may overgrow\\n→ Snakes lose frogs as prey\\n→ Eagles must find other food\\nThis is a trophic cascade!",
diagramType:"foodweb",practiceTopic:"Food Webs",
realWorld:"When wolves were reintroduced to Yellowstone National Park in 1995, they changed river courses. They reduced deer, which let vegetation grow, which stabilised riverbanks. One species reshaped an entire ecosystem.",
thinkAbout:"If a new disease wiped out all bees tomorrow, how would that affect humans? Map out the chain reaction — it's longer and more serious than most people realise."},

{id:"s_water_cycle",subject:"science",years:["y5","y6"],title:"The Water Cycle",icon:"💧",color:"#10B981",
explanation:"The water cycle describes how water continuously moves through Earth's systems. The sun drives the cycle by providing energy for evaporation. Water changes state — liquid, gas, solid — as it moves through the cycle.",
keyFacts:["Evaporation: liquid water → water vapour (sun heats water)","Condensation: water vapour → liquid (forms clouds)","Precipitation: water falls as rain, snow, hail or sleet","Transpiration: water released by plants (part of evaporation)","Runoff: water flows across land into rivers and oceans"],
example:"Follow a water molecule:\\n1. Sits in the ocean\\n2. Sun heats it → evaporates (becomes water vapour)\\n3. Rises and cools → condenses into clouds\\n4. Clouds cool further → precipitates as rain\\n5. Flows into river → back to ocean\\n6. Cycle repeats!",
diagramType:"watercycle",practiceTopic:"Water Cycle"},

{id:"s_electricity",subject:"science",years:["y5","y6"],title:"Electricity & Circuits",icon:"⚡",color:"#10B981",
explanation:"Electricity is the flow of electric charge through a conductor. A complete circuit allows current to flow. If the circuit is broken anywhere, current stops. Understanding circuits is fundamental to all modern technology.",
keyFacts:["Series circuit: components in one path — break = all stop","Parallel circuit: multiple paths — break = others continue","Current flows from negative to positive terminal","Voltage = the 'push' given to electrons","Resistance = opposition to current flow"],
example:"Series circuit with 2 bulbs:\\nBoth bulbs connected in one line\\nBreak one bulb → both go out\\n(like old Christmas lights!)\\n\\nParallel circuit with 2 bulbs:\\nEach bulb has its own path\\nBreak one bulb → other stays on\\n(how house wiring works)",
diagramType:"circuit",practiceTopic:"Electricity",
realWorld:"The electricity in your home travels from power stations hundreds of kilometres away at 330,000 volts (stepped down to 230V at your house). The grid must balance supply and demand in real time — every second.",
thinkAbout:"Your phone charger converts AC (alternating current) to DC (direct current) and steps down voltage. Without understanding circuits, none of modern technology would exist."},

{id:"s_states_matter",subject:"science",years:["y5","y6"],title:"States of Matter",icon:"🧊",color:"#10B981",
explanation:"Matter exists in three main states: solid, liquid and gas. The state depends on temperature and pressure. Changes between states involve energy — heating adds energy to break bonds between particles; cooling removes energy to reform them.",
keyFacts:["Solid: fixed shape and volume, particles closely packed","Liquid: fixed volume, takes shape of container","Gas: no fixed shape or volume, particles spread out","Melting: solid → liquid (adding heat)","Evaporation/boiling: liquid → gas","Condensation: gas → liquid (removing heat)","Freezing: liquid → solid"],
example:"Water changes:\\n0°C: melting/freezing point\\nBelow 0°C: ice (solid)\\n0°C to 100°C: water (liquid)\\n100°C: boiling/condensation point\\nAbove 100°C: steam (gas)\\n\\nAt higher altitudes, lower pressure means water boils below 100°C — that's why cooking takes longer on mountains!",
diagramType:"states",practiceTopic:"States of Matter",
realWorld:"Dry ice (solid CO₂) skips the liquid phase entirely — going straight from solid to gas. This is called sublimation. It's used in fog machines, food preservation, and cleaning electronics without leaving moisture.",
thinkAbout:"Water is one of the only substances that expands when it freezes. Ice is less dense than liquid water — that's why it floats. If ice sank, all oceans would eventually freeze solid from the bottom up."},

// ─── SCIENCE Y7-9 ───────────────────────────────────────────────

{id:"s_cells",subject:"science",years:["y7","y8","y9"],title:"Cells",icon:"🔬",color:"#10B981",
explanation:"The cell is the basic unit of all living things. Robert Hooke first observed cells in 1665. Animal and plant cells share some structures but differ in key ways. Cells work together to form tissues, organs and organ systems.",
keyFacts:["Cell membrane: controls what enters/exits the cell","Nucleus: contains DNA, controls cell activities","Cytoplasm: jelly-like fluid where chemical reactions occur","Mitochondria: powerhouse — produces energy via respiration","Plant only: cell wall, chloroplasts, large vacuole"],
example:"Plant cell vs Animal cell:\\n\\nBoth have: nucleus, cell membrane, cytoplasm, mitochondria\\n\\nPlant cells also have:\\n→ Cell wall (rigid, made of cellulose)\\n→ Chloroplasts (photosynthesis)\\n→ Large central vacuole (water storage)\\n\\nThink: plants are stationary, so they need cell walls for support",
diagramType:"cell",practiceTopic:"Cells",
realWorld:"You have about 37 trillion cells in your body. If laid end to end, they would circle the Earth 40 times. Each cell is a complete living unit — dividing, generating energy, reading DNA instructions — simultaneously.",
thinkAbout:"A single fertilised egg cell becomes a full human being with 200+ different cell types. All those cells have identical DNA — so how do they 'know' to become a brain cell vs a liver cell?"},

{id:"s_chemistry",subject:"science",years:["y7","y8","y9"],title:"Chemical Reactions",icon:"🧪",color:"#10B981",
explanation:"A chemical reaction occurs when substances (reactants) change into new substances (products) with different properties. Evidence of a reaction includes colour change, gas production, temperature change or precipitate formation. Matter is conserved — atoms rearrange but are not created or destroyed.",
keyFacts:["Reactants → Products","Physical change: reversible, no new substance (melting ice)","Chemical change: usually irreversible, new substance formed (burning wood)","Law of Conservation of Mass: mass of reactants = mass of products","Catalyst: speeds up a reaction without being used up"],
example:"Signs of a chemical reaction:\\n1. Burning toast: colour change + smell + heat\\n2. Vinegar + baking soda: bubbles (CO₂ gas)\\n3. Rusting iron: colour change (red-brown), mass increases (iron gains oxygen)\\n4. Milk souring: smell change, texture change\\n\\nAll irreversible → chemical changes",
diagramType:"states",practiceTopic:"Chemical Reactions"},

{id:"s_genetics",subject:"science",years:["y8","y9"],title:"Genetics & Heredity",icon:"🧬",color:"#10B981",
explanation:"Genetics is the study of how traits pass from parents to offspring through genes. DNA contains the genetic code — a sequence of base pairs that determines everything from eye colour to disease risk. Gregor Mendel discovered the basic rules of inheritance using pea plants.",
keyFacts:["DNA: double helix molecule containing genetic information","Gene: a section of DNA that codes for a trait","Chromosome: structure that contains thousands of genes (humans have 46)","Dominant: expressed when one copy present (B)","Recessive: only expressed when two copies present (bb)"],
example:"Pea plant cross: Tall (Tt) × Tall (Tt)\\n\\nPossible offspring:\\nTT (tall) TT (tall)\\nTt (tall) tt (short)\\n\\nResult: 3 tall : 1 short ratio\\n\\nThe short trait didn't disappear — it was recessive, hiding in Tt plants. This is why grandparent traits can reappear in grandchildren!",
diagramType:"bargraph",practiceTopic:"Genetics"},

// ─── THINKING SKILLS Y3-4 ──────────────────────────────────────

{id:"t_patterns",subject:"thinking",years:["y3","y4"],title:"Number Patterns",icon:"🔄",color:"#F59E0B",
explanation:"Patterns repeat or change according to a rule. Finding the rule lets you predict what comes next. Number patterns can grow (add/multiply) or shrink (subtract/divide). Identifying patterns is a core thinking skill for selective school tests.",
keyFacts:["Look at the difference between consecutive terms","Check if the difference itself changes (second-order patterns)","Patterns can involve adding, subtracting, multiplying or dividing","Describe the rule precisely: 'Add 3 each time' or 'Multiply by 2'"],
example:"Pattern: 2, 5, 8, 11, 14, ?\\nDifference: +3 each time\\nNext: 14 + 3 = 17\\n\\nPattern: 3, 6, 12, 24, ?\\nRule: multiply by 2 each time\\nNext: 24 × 2 = 48\\n\\nPattern: 1, 1, 2, 3, 5, 8, ?\\nEach term = sum of previous two (Fibonacci!)\\nNext: 5 + 8 = 13",
diagramType:"numberline",practiceTopic:"Number Patterns"},

{id:"t_logic_basic",subject:"thinking",years:["y3","y4"],title:"Logic & Reasoning",icon:"🧩",color:"#F59E0B",
explanation:"Logic means using given information to reach valid conclusions. If the information (premises) is true and the reasoning is valid, the conclusion must be true. Logic puzzles train the systematic thinking needed for selective school exams.",
keyFacts:["Start with definite facts, not guesses","Use elimination: rule out what can't be true","Draw a table or diagram to organise information","Check your answer against all original clues"],
example:"3 friends: Alex, Ben, Cara. Pets: cat, dog, fish.\\nClue 1: Alex doesn't have the cat.\\nClue 2: Ben has the dog.\\nClue 3: Cara doesn't have the fish.\\n\\nSolution:\\nBen → dog (Clue 2)\\nCara → not fish, not dog → cat\\nAlex → fish (only option left)\\n\\nMethod: start with certainties, eliminate!",
diagramType:"venn",practiceTopic:"Logic"},

{id:"t_spatial",subject:"thinking",years:["y4","y5","y6"],title:"Spatial Reasoning",icon:"🎲",color:"#F59E0B",
explanation:"Spatial reasoning is the ability to mentally visualise and manipulate 2D and 3D shapes. It's heavily tested in selective school, OC and ACER exams. Practice rotating, reflecting and folding shapes in your mind.",
keyFacts:["Rotation: turning a shape around a fixed point","Reflection: flipping a shape across a mirror line","Translation: sliding a shape without rotating or flipping","3D nets: 2D shapes that fold into 3D solids"],
example:"Visualising a cube net:\\nA cube has 6 faces\\nThere are 11 different nets that fold into a cube\\nMost common mistake: trying to fold in your head without a system\\nTip: identify the 'bottom' face first, then fold the remaining faces around it",
diagramType:"angles",practiceTopic:"Spatial Reasoning"},

{id:"t_sequences",subject:"thinking",years:["y5","y6"],title:"Sequences & Series",icon:"📐",color:"#F59E0B",
explanation:"A sequence is an ordered list of numbers following a rule. An arithmetic sequence adds the same amount each time. A geometric sequence multiplies by the same amount. Finding the nth term lets you calculate any value without listing all terms.",
keyFacts:["Arithmetic: same difference between terms (d = common difference)","Geometric: same ratio between terms (r = common ratio)","nth term of arithmetic: a + (n-1)d","Sum of arithmetic series: n/2 × (first + last)"],
example:"Arithmetic: 4, 7, 10, 13...\\nd = 3, first term = 4\\n10th term = 4 + (10-1)×3 = 4+27 = 31\\n\\nGeometric: 2, 6, 18, 54...\\nr = 3, first term = 2\\n5th term = 2 × 3⁴ = 2 × 81 = 162",
diagramType:"lineargraph",practiceTopic:"Sequences"},

{id:"t_abstract",subject:"thinking",years:["y6","y7","y8","y9"],title:"Abstract Reasoning",icon:"🔷",color:"#F59E0B",
explanation:"Abstract reasoning identifies patterns in shapes, symbols and figures without relying on words or numbers. It measures pure logical thinking and is heavily tested in selective school, OC and ACER exams. Practice spotting rules about size, colour, position and number.",
keyFacts:["Look at: number of shapes, size, colour/shading, position, rotation","Changes can be additions, removals, alternations or cycles","Consider relationships between shapes within each panel","Eliminate answer options that break any rule you've found"],
example:"In an abstract sequence, each figure has one more side than the previous:\\nTriangle → Square → Pentagon → Hexagon → ?\\nAnswer: Heptagon (7 sides)\\n\\nAnother rule: shapes alternate between filled and outline\\nFilled triangle → Outline square → Filled pentagon → ?\\nAnswer: Outline hexagon",
diagramType:"bargraph",practiceTopic:"Abstract Reasoning"},

{id:"t_venn",subject:"thinking",years:["y6","y7","y8","y9"],title:"Venn Diagrams",icon:"🔵",color:"#F59E0B",
explanation:"Venn diagrams show relationships between groups (sets). The overlap (intersection) shows items that belong to both groups. Items outside all circles belong to neither group. They're powerful for visualising logic problems.",
keyFacts:["Circle = a set of items with a common property","Overlap = intersection: items in BOTH sets","Union = all items in either set (or both)","Complement = items NOT in the set"],
example:"30 students: 18 play sport, 15 play music, 7 play both.\\n\\nVenn diagram:\\nSport only: 18-7 = 11\\nBoth: 7\\nMusic only: 15-7 = 8\\nNeither: 30-(11+7+8) = 4\\n\\nAlways check: all groups should add to the total!",
diagramType:"venn",practiceTopic:"Venn Diagrams",
realWorld:"Database queries, search engines, and medical research all use set theory (Venn diagram logic) constantly. When you search 'Italian AND restaurants NOT pizza', you're performing a Venn diagram operation.",
thinkAbout:"If Set A has 50 members and Set B has 40 members and the union has 70 members, how big is the overlap? This kind of reasoning helps epidemiologists track disease — who got sick AND was vaccinated AND travelled recently?"},

// ─── MATHS Y3-4 ADDITIONAL ──────────────────────────────────────

{id:"m_3dshapes",subject:"maths",years:["y3","y4"],title:"3D Shapes",icon:"🎲",color:"#3B82F6",
explanation:"3D shapes have length, width AND depth. Unlike flat 2D shapes, you can hold them. We describe 3D shapes by their faces (flat surfaces), edges (lines where faces meet) and vertices (corner points).",
keyFacts:["Cube: 6 square faces, 12 edges, 8 vertices","Rectangular prism (cuboid): 6 rectangular faces","Sphere: 1 curved surface, no edges, no vertices","Cylinder: 2 circular faces, 1 curved surface","Pyramid: triangular faces meeting at an apex"],
example:"A cube:\nFaces: 6 (all squares)\nEdges: 12\nVertices (corners): 8\n\nA triangular prism (like a Toblerone box):\nFaces: 5 (2 triangles + 3 rectangles)\nEdges: 9\nVertices: 6\n\nMemory trick: Faces + Vertices - Edges = 2 (Euler's formula)",
diagramType:"rectangle",practiceTopic:"3D Shapes"},

{id:"m_measurement",subject:"maths",years:["y3","y4"],title:"Measurement",icon:"📏",color:"#3B82F6",
explanation:"Measurement tells us how much of something there is. We measure length, mass (weight), capacity (volume of liquid) and temperature. Choosing the right unit is just as important as getting the number right.",
keyFacts:["Length: mm, cm, m, km (10mm=1cm, 100cm=1m, 1000m=1km)","Mass: g, kg (1000g = 1kg)","Capacity: mL, L (1000mL = 1L)","Always estimate first, then measure","Use appropriate tools: ruler, scales, measuring jug"],
example:"Converting units:\n3.5 km = 3,500 m = 350,000 cm\n2.4 kg = 2,400 g\n1.75 L = 1,750 mL\n\nWhen to use which unit:\n- Width of a finger: mm\n- Height of a person: cm\n- Distance between cities: km\n- Weight of a feather: g\n- Weight of a person: kg",
diagramType:"numberline",practiceTopic:"Measurement"},

{id:"m_data_basic",subject:"maths",years:["y3","y4"],title:"Data & Graphs",icon:"📊",color:"#3B82F6",
explanation:"Data is information we collect. We display it in graphs and tables to spot patterns and draw conclusions. Different graphs suit different types of data. Always label axes and give your graph a title.",
keyFacts:["Picture graph (pictograph): uses symbols to represent data","Bar graph: uses bars to compare categories","Tally chart: counting in groups of 5","Column graph: like a bar graph but vertical","The scale tells you what each unit on the axis represents"],
example:"Favourite fruits of 20 students:\nApple: 8 students\nBanana: 5 students\nOrange: 4 students\nGrape: 3 students\n\nBar graph: draw bars up to the correct height\nMost popular: Apple (tallest bar)\nLeast popular: Grape (shortest bar)\nTotal: 8+5+4+3 = 20 ✓",
diagramType:"bargraph",practiceTopic:"Data"},

{id:"m_patterns_basic",subject:"maths",years:["y3","y4"],title:"Patterns & Sequences",icon:"🔄",color:"#3B82F6",
explanation:"Patterns are sequences that follow a rule. Recognising and extending patterns is a key thinking skill. Patterns can involve numbers, shapes, colours or objects. Finding the rule is the key step.",
keyFacts:["Look for what changes and what stays the same","Number patterns can grow (increase) or shrink (decrease)","Shape patterns repeat in a cycle","Describe the rule precisely before extending","Check your extension fits the rule"],
example:"Number pattern: 3, 6, 9, 12, ?\nRule: add 3 each time\nNext: 15\n\nShape pattern: circle, square, triangle, circle, square, ?\nRule: repeating cycle of 3\nNext: triangle\n\nGrowing pattern: 1, 3, 6, 10, ?\nDifference: +2, +3, +4... (increasing by 1 each time)\nNext difference: +5\nNext term: 15",
diagramType:"numberline",practiceTopic:"Patterns"},

// ─── MATHS Y5-6 ADDITIONAL ──────────────────────────────────────

{id:"m_fractions_ops",subject:"maths",years:["y5","y6"],title:"Fractions Operations",icon:"🍕",color:"#3B82F6",
explanation:"Adding and subtracting fractions requires the same denominator (common denominator). Multiplying fractions is simpler - multiply tops, multiply bottoms. Dividing by a fraction means multiplying by its reciprocal (flip it).",
keyFacts:["Add/subtract: must have same denominator first","Multiply fractions: multiply numerators, multiply denominators","Divide fractions: flip the second fraction then multiply","Mixed numbers: convert to improper fractions first","Always simplify your answer"],
example:"Add: 1/3 + 1/4\nCommon denominator = 12\n1/3 = 4/12, 1/4 = 3/12\n4/12 + 3/12 = 7/12\n\nMultiply: 2/3 x 3/4\n= (2x3)/(3x4) = 6/12 = 1/2\n\nDivide: 3/4 / 1/2\n= 3/4 x 2/1 = 6/4 = 1 and 1/2",
diagramType:"fractions",practiceTopic:"Fractions"},

{id:"m_ratios",subject:"maths",years:["y5","y6"],title:"Ratios & Rates",icon:"⚖️",color:"#3B82F6",
explanation:"A ratio compares two or more quantities. A rate compares two quantities with different units (like km per hour). Ratios and rates appear in cooking, maps, speed, and many real-world situations.",
keyFacts:["Ratio 3:2 means for every 3 of one thing, there are 2 of another","Ratios can be simplified like fractions (6:4 = 3:2)","Rate has units: km/h, $/kg, words/minute","Speed = distance / time","Unit rate: rate for 1 unit (cost per single item)"],
example:"Recipe: 2 cups flour to 1 cup sugar (ratio 2:1)\nFor 6 cups flour, you need 3 cups sugar\n\nSpeed: A car travels 240km in 3 hours\nSpeed = 240 / 3 = 80 km/h\nIn 5 hours at 80km/h: 80 x 5 = 400km\n\nValue: 500g coffee for $12\nRate = $12/500g = $0.024 per gram = $24/kg",
diagramType:"lineargraph",practiceTopic:"Ratios & Rates"},

{id:"m_volume",subject:"maths",years:["y5","y6"],title:"Volume & Capacity",icon:"📦",color:"#3B82F6",
explanation:"Volume is the amount of 3D space a shape takes up. Capacity is how much liquid a container can hold. They are related: 1 cm3 of water = 1 mL. Volume is measured in cubic units (cm3, m3).",
keyFacts:["Volume of rectangular prism = length x width x height","1 cm3 = 1 mL of water","1000 cm3 = 1 L","Volume of liquid = capacity of container when full","Count unit cubes to find volume of irregular shapes"],
example:"Box: length=5cm, width=4cm, height=3cm\nVolume = 5 x 4 x 3 = 60 cm3\n= 60 mL of water\n\nFish tank: 50cm x 30cm x 40cm\nVolume = 50 x 30 x 40 = 60,000 cm3\n= 60,000 mL = 60 L\n\nNeed 60 litres of water to fill it!",
diagramType:"rectangle",practiceTopic:"Volume"},

{id:"m_coordinates",subject:"maths",years:["y5","y6"],title:"Coordinates & Mapping",icon:"🗺️",color:"#3B82F6",
explanation:"Coordinates pinpoint exact locations on a grid. We write them as (x, y) - always across first (x-axis), then up (y-axis). Remember: along the corridor, then up the stairs. The origin (0,0) is where axes meet.",
keyFacts:["Always write coordinates as (x, y) - horizontal first, vertical second","The x-axis is horizontal, y-axis is vertical","Origin (0,0) is where axes cross","Negative x is left of origin, negative y is below","Quadrants: top-right is Q1, going anticlockwise"],
example:"Plot point (3, 4):\nStart at origin (0,0)\nMove 3 right (x=3)\nMove 4 up (y=4)\nMark the point\n\nFind coordinates of a point:\nCount across from 0 first\nThen count up from 0\nWrite as (across, up)\n\nCommon mistake: (y, x) instead of (x, y)!",
diagramType:"lineargraph",practiceTopic:"Coordinates"},

// ─── MATHS Y7-9 ADDITIONAL ──────────────────────────────────────

{id:"m_trigonometry",subject:"maths",years:["y8","y9"],title:"Trigonometry",icon:"📐",color:"#3B82F6",
explanation:"Trigonometry finds unknown sides and angles in right-angled triangles using three ratios: sine, cosine and tangent (SOH CAH TOA). It is essential for NSW Selective, VIC Selective, and Year 9 maths.",
keyFacts:["SOH: sin(angle) = Opposite / Hypotenuse","CAH: cos(angle) = Adjacent / Hypotenuse","TOA: tan(angle) = Opposite / Adjacent","Hypotenuse is always opposite the right angle","To find an angle: use inverse trig (sin-1, cos-1, tan-1)"],
example:"Right triangle: angle=30 degrees, hypotenuse=10cm\nFind the opposite side:\nsin(30) = Opposite / 10\n0.5 = Opposite / 10\nOpposite = 5cm\n\nFind angle when opposite=6, adjacent=8:\ntan(angle) = 6/8 = 0.75\nangle = tan-1(0.75) = 36.87 degrees\n\nRemember SOH CAH TOA!",
diagramType:"angles",practiceTopic:"Trigonometry"},

{id:"m_simultaneous",subject:"maths",years:["y8","y9"],title:"Simultaneous Equations",icon:"📈",color:"#3B82F6",
explanation:"Simultaneous equations are two equations with two unknowns that must both be true at the same time. We solve them to find the values of both unknowns. Methods include substitution, elimination, and graphing.",
keyFacts:["Substitution: rearrange one equation, substitute into other","Elimination: add or subtract equations to remove one variable","The solution is the point where two lines intersect on a graph","Always check your answer in BOTH original equations","If equations are parallel, there is no solution"],
example:"Solve: x + y = 7 and x - y = 3\n\nElimination method:\nAdd both equations:\n2x = 10\nx = 5\n\nSubstitute x=5 into x+y=7:\n5 + y = 7\ny = 2\n\nSolution: x=5, y=2\nCheck: 5+2=7 ✓ and 5-2=3 ✓",
diagramType:"lineargraph",practiceTopic:"Simultaneous Equations"},

{id:"m_financial",subject:"maths",years:["y7","y8","y9"],title:"Financial Mathematics",icon:"💰",color:"#3B82F6",
explanation:"Financial maths covers profit, loss, discount, GST, simple interest and compound interest. These are practical skills tested in NAPLAN Year 9 and relevant to real life. Understanding percentages is the foundation.",
keyFacts:["Profit = selling price - cost price","Discount = original price x discount rate","GST in Australia = 10% of price","Simple interest = Principal x Rate x Time","Compound interest grows faster because interest earns interest"],
example:"Buy for $200, sell for $270\nProfit = $270 - $200 = $70\nProfit % = 70/200 x 100 = 35%\n\n20% discount on $150 item:\nDiscount = 0.20 x $150 = $30\nSale price = $150 - $30 = $120\n\nSimple interest: $1000 at 5% for 3 years\nInterest = 1000 x 0.05 x 3 = $150\nTotal = $1,150",
diagramType:"bargraph",practiceTopic:"Financial Mathematics"},

{id:"m_surface_area",subject:"maths",years:["y8","y9"],title:"Surface Area & Volume",icon:"📦",color:"#3B82F6",
explanation:"Surface area is the total area of all outer faces of a 3D shape. Volume is the space inside. Both are crucial for real-world problems like packaging, construction and science. Each shape has its own formula.",
keyFacts:["Cube SA = 6 x side squared","Cylinder SA = 2 pi r squared + 2 pi r h","Sphere SA = 4 pi r squared","Cylinder Volume = pi r squared x h","Sphere Volume = (4/3) pi r cubed"],
example:"Cylinder: radius=3cm, height=10cm\nSA = 2 pi r squared + 2 pi r h\n= 2 x pi x 9 + 2 x pi x 3 x 10\n= 18 pi + 60 pi\n= 78 pi = 245.0 cm squared\n\nVolume = pi x 9 x 10\n= 90 pi = 282.7 cm cubed\n\nUse pi = 3.14159 or calculator",
diagramType:"circle",practiceTopic:"Surface Area & Volume"},

// ─── ENGLISH ADDITIONAL ──────────────────────────────────────────

{id:"e_vocabulary",subject:"english",years:["y3","y4","y5","y6"],title:"Vocabulary & Word Meanings",icon:"📚",color:"#8B5CF6",
explanation:"A strong vocabulary helps you understand what you read and express yourself precisely when you write. Words have denotations (dictionary meanings) and connotations (emotional associations). Context clues help you work out unfamiliar words.",
keyFacts:["Synonyms: words with similar meanings (happy/joyful/elated)","Antonyms: words with opposite meanings (hot/cold)","Connotation: emotional feel of a word (slim vs skinny vs scrawny)","Context clues: surrounding words that hint at meaning","Word parts: prefix + root + suffix help decode new words"],
example:"Working out meaning from context:\nSentence: The arid desert stretched endlessly, without a drop of water or blade of grass.\n\nContext clues: desert, no water, no grass\nMeaning of arid: extremely dry\n\nWord parts: un+believe+able = not + to believe + able to be\n= unbelievable (impossible to believe)",
diagramType:"narrative",practiceTopic:"Vocabulary"},

{id:"e_author_purpose",subject:"english",years:["y5","y6","y7","y8","y9"],title:"Author Purpose & Audience",icon:"🎯",color:"#8B5CF6",
explanation:"Every text is written for a reason (purpose) and for specific readers (audience). Understanding purpose and audience explains why authors make the choices they do - the words they use, the structure they choose, the tone they adopt.",
keyFacts:["To inform: presents facts objectively (reports, encyclopaedias)","To persuade: argues a position, uses evidence and rhetoric","To entertain: engages through story, humour, drama","To describe: creates vivid imagery of people, places or events","Audience determines: vocabulary level, tone, examples used"],
example:"Compare these openings about sharks:\n\nInforming: Sharks are cartilaginous fish of the subclass Elasmobranchii, comprising approximately 500 species.\n\nPersuading: Sharks are vital to our oceans - without them, entire ecosystems collapse.\n\nEntertaining: The great white moved silently beneath the kayak, its grey shadow invisible against the dark water...\n\nSame subject, completely different purpose, audience and language!",
diagramType:"narrative",practiceTopic:"Author Purpose"},

{id:"e_point_of_view",subject:"english",years:["y5","y6","y7","y8","y9"],title:"Point of View & Perspective",icon:"👁️",color:"#8B5CF6",
explanation:"Point of view is the position from which a story is told. Perspective is how a character sees the world based on their experiences and values. Recognising perspective is key for both reading comprehension and writing tasks.",
keyFacts:["First person: I/we - narrator is a character in the story","Second person: you - rare, reader becomes the character","Third person limited: he/she - one character's thoughts shown","Third person omniscient: all-knowing narrator sees all thoughts","Perspective shapes which events are emphasised or omitted"],
example:"The same event from two perspectives:\n\nChild: Dad said no to the puppy again. It's not fair - I promised I'd look after it!\n\nFather: I said no to the puppy again. It breaks my heart, but with my work hours, I know who would really end up caring for it.\n\nNeither is lying - both are sharing their genuine perspective.",
diagramType:"narrative",practiceTopic:"Point of View"},

{id:"e_sentence_structure",subject:"english",years:["y4","y5","y6"],title:"Sentence Structure",icon:"🔗",color:"#8B5CF6",
explanation:"Sentences come in different structures that create different effects. Varying your sentence structure is one of the key markers of sophisticated writing. Short sentences create impact. Long, complex sentences allow you to develop ideas fully.",
keyFacts:["Simple sentence: one main clause (The dog barked)","Compound sentence: two main clauses joined by and/but/or/so","Complex sentence: main clause + subordinate clause","Fragment: incomplete sentence - missing subject or verb","Run-on: two sentences incorrectly joined without punctuation"],
example:"Simple: The storm hit suddenly.\n\nCompound: The storm hit suddenly and the lights went out.\n\nComplex: Although warnings had been issued, nobody expected the storm to hit so suddenly.\n\nCompound-complex: Although warnings had been issued, nobody expected such ferocity, and the town was completely unprepared.\n\nEach adds information and sophistication!",
diagramType:"narrative",practiceTopic:"Sentence Structure"},

{id:"e_poetry",subject:"english",years:["y6","y7","y8","y9"],title:"Poetry Analysis",icon:"🌹",color:"#8B5CF6",
explanation:"Poetry uses language economically and deliberately. Every word, sound, line break and image is a choice. When analysing poetry, consider: what the poem says (content), how it says it (form and techniques), and what effect it creates (impact on reader).",
keyFacts:["Rhyme scheme: pattern of rhyming lines (ABAB, AABB)","Rhythm/metre: the beat or flow of lines","Stanza: a group of lines (like a paragraph in prose)","Enjambment: continuing a sentence past a line break","Imagery: vivid sensory language that creates pictures"],
example:"Analysing a technique:\n\nLine: The fog comes on little cat feet (Carl Sandburg)\n\nTechnique: Metaphor (fog compared to a cat)\nEffect: Both fog and cats move silently, stealthily\nThe comparison makes the fog feel alive, almost sinister\nShort lines mirror the quiet, creeping movement\n\nAlways: technique + quote + effect on reader",
diagramType:"narrative",practiceTopic:"Poetry"},

// ─── SCIENCE ADDITIONAL ──────────────────────────────────────────

{id:"s_plants",subject:"science",years:["y4","y5","y6"],title:"Plants & Photosynthesis",icon:"🌱",color:"#10B981",
explanation:"Plants are producers - they make their own food using sunlight, water and carbon dioxide through photosynthesis. This process produces glucose (food) and oxygen (which we breathe). Photosynthesis is the foundation of almost all food chains on Earth.",
keyFacts:["Photosynthesis equation: CO2 + water + light energy = glucose + oxygen","Chlorophyll in leaves absorbs sunlight (makes leaves green)","Respiration is the opposite of photosynthesis (releases energy from glucose)","Plants need: light, water, CO2, warmth, minerals","Stomata (tiny pores) on leaves allow gas exchange"],
example:"Why do plants need sunlight?\nSunlight provides the ENERGY to drive the chemical reaction that converts CO2 and water into glucose.\n\nWhy are leaves flat and wide?\nMaximum surface area to catch sunlight.\n\nWhy do leaves have veins?\nVeins carry water (xylem) and carry glucose away (phloem).\n\nWhy do plants respire AND photosynthesise?\nPhotosynthesis stores energy; respiration releases it for growth.",
diagramType:"photosynthesis",practiceTopic:"Photosynthesis"},

{id:"s_earth_space",subject:"science",years:["y5","y6","y7"],title:"Earth & Space",icon:"🌍",color:"#10B981",
explanation:"Earth is one of eight planets orbiting the Sun in our solar system. Earth rotates on its axis (causing day/night) and orbits the Sun (causing seasons). The Moon orbits Earth and its phases depend on its position relative to Earth and the Sun.",
keyFacts:["Earth rotates once every 24 hours (day/night cycle)","Earth orbits the Sun once every 365.25 days (1 year)","Seasons occur because Earth is tilted at 23.5 degrees","When Australia has summer, it is tilted toward the Sun","The Moon orbits Earth every 29.5 days (lunar month)"],
example:"Why does Australia have summer in December?\nEarth is tilted at 23.5 degrees\nIn December, the Southern Hemisphere tilts toward the Sun\nSun is higher in sky, days are longer, more energy received = summer\n\nWhy is it colder in winter?\nSun is lower in sky\nSunlight hits at an angle, spreading energy over larger area\nDays are shorter - less time for heating\nOpposite tilt = opposite seasons from Northern Hemisphere",
diagramType:"solarsystem",practiceTopic:"Earth & Space",
realWorld:"Light from the Sun takes 8 minutes to reach Earth. When you look at distant stars, you see them as they were thousands of years ago — some of the stars you see at night may not even exist anymore.",
thinkAbout:"Earth is 150 million km from the Sun. If Earth were just 5% closer, the oceans would evaporate. Just 5% further, they'd freeze permanently. We live in an incredibly precise habitable zone."},

{id:"s_light_sound",subject:"science",years:["y6","y7"],title:"Light & Sound",icon:"💡",color:"#10B981",
explanation:"Light and sound are both forms of energy that travel in waves, but they behave very differently. Light travels incredibly fast and can travel through a vacuum. Sound travels much slower and needs a medium (matter) to travel through.",
keyFacts:["Light travels at 300,000 km/s - nothing travels faster","Sound travels at 343 m/s in air (much slower than light)","Sound needs a medium: cannot travel through a vacuum (space is silent)","Reflection: light bouncing off a surface","Refraction: light bending as it moves between materials","Frequency of sound = pitch; amplitude = volume"],
example:"Why do you see lightning before you hear thunder?\nLight travels at 300,000 km/s\nSound travels at 343 m/s\nSame event, but light reaches you almost instantly\nSound takes about 3 seconds per km\n\nCount the seconds between lightning and thunder\nDivide by 3 = distance in km\n\nEchoes: sound reflecting off a surface\nNeeds to travel at least 17m away and back = 34m total",
diagramType:"numberline",practiceTopic:"Light & Sound"},

{id:"s_human_body",subject:"science",years:["y6","y7"],title:"Human Body Systems",icon:"🫀",color:"#10B981",
explanation:"The human body has specialised organ systems that work together. Each system has a specific function, but they are all interconnected. Understanding how systems work helps us understand health, disease and medicine.",
keyFacts:["Digestive: breaks food into nutrients (mouth to intestines)","Circulatory: heart pumps blood carrying oxygen and nutrients","Respiratory: lungs exchange oxygen and carbon dioxide","Nervous: brain and nerves control everything","Skeletal: bones provide structure and protect organs"],
example:"How systems work together during exercise:\n\nRespiratory: breathe faster to get more O2 in, more CO2 out\nCirculatory: heart beats faster to deliver O2 to muscles quickly\nMuscular: muscles contract using O2 and glucose, producing CO2 and heat\nNervous: brain detects need for more O2, signals heart and lungs to speed up\nAll four systems coordinating in real time!",
diagramType:"cell",practiceTopic:"Human Body"},

{id:"s_separating",subject:"science",years:["y5","y6"],title:"Separating Mixtures",icon:"🧪",color:"#10B981",
explanation:"Mixtures can be separated using physical methods that exploit differences in the properties of substances. The right method depends on the properties of the substances in the mixture - particle size, solubility, density, boiling point.",
keyFacts:["Filtration: separates insoluble solid from liquid (sand from water)","Evaporation: separates dissolved solid from liquid (salt from water)","Distillation: separates liquids with different boiling points","Sieving: separates particles of different sizes","Magnetic separation: separates magnetic materials from non-magnetic"],
example:"Salt + sand + iron filings mixture:\n\nStep 1: Magnetic separation - magnet removes iron filings\nStep 2: Add water, stir - salt dissolves, sand does not\nStep 3: Filter - sand (residue) stays in filter paper\nStep 4: Evaporate filtrate - water evaporates, salt crystals remain\n\nEach step uses a different property!",
diagramType:"states",practiceTopic:"Separating Mixtures"},

{id:"s_weather",subject:"science",years:["y3","y4"],title:"Weather & Climate",icon:"🌤️",color:"#10B981",
explanation:"Weather is the day-to-day state of the atmosphere (sunny, rainy, windy). Climate is the average weather pattern of a region over a long period. Australia has diverse climates from tropical north to temperate south.",
keyFacts:["Weather changes daily; climate describes long-term patterns","Sun drives weather by heating air and water unevenly","Evaporation, condensation and precipitation form the water cycle","Wind is caused by differences in air pressure","Temperature, precipitation, humidity and wind speed describe weather"],
example:"Why is northern Australia hot and wet in summer?\n\nNorthern Australia is close to the equator\nEquator receives direct sunlight (high energy) all year\nHot air rises, cools, condenses = heavy rain (monsoon)\nThis is a tropical climate\n\nSouthern Australia (Melbourne):\nFurther from equator\nMore angled sunlight = less intense\nFour distinct seasons\nThis is a temperate climate",
diagramType:"bargraph",practiceTopic:"Weather"},

// ─── THINKING SKILLS ADDITIONAL ──────────────────────────────────

{id:"t_analogies",subject:"thinking",years:["y4","y5","y6","y7","y8","y9"],title:"Analogies",icon:"🔗",color:"#F59E0B",
explanation:"An analogy is a comparison that shows a relationship between two pairs of things. Analogies appear in virtually every selective school, OC and ACER exam. The key is identifying the TYPE of relationship in the first pair, then applying it to the second.",
keyFacts:["Type 1: Part to whole (wheel : car)","Type 2: Function (pen : write)","Type 3: Synonym/antonym (hot : cold)","Type 4: Category (rose : flower)","Type 5: Degree (warm : boiling)","Type 6: Cause/effect (rain : flood)"],
example:"Dog : puppy = cat : ?\nRelationship: adult animal to its young\nAnswer: kitten\n\nLibrary : books = museum : ?\nRelationship: place to thing stored there\nAnswer: exhibits/artefacts\n\nHappy : elated = cold : ?\nRelationship: mild to extreme degree\nAnswer: freezing\n\nMethod: always identify the relationship type first!",
diagramType:"narrative",practiceTopic:"Analogies"},

{id:"t_matrix",subject:"thinking",years:["y5","y6","y7","y8","y9"],title:"Matrix Reasoning",icon:"🔲",color:"#F59E0B",
explanation:"Matrix reasoning presents a grid of shapes with a missing piece. You must identify the rules governing rows and columns, then select the answer that completes the pattern. This is a core test of abstract logical thinking.",
keyFacts:["Look for rules in ROWS first, then COLUMNS","Rules can involve: size, shape, colour, number, position, rotation","Each row and column usually has its own consistent rule","Elimination works well: cross out options that break any rule","Check your answer satisfies ALL rules simultaneously"],
example:"3x3 grid pattern rules:\nRow rule: each row contains a circle, square, triangle\nColumn rule: each column shading gets darker (white, grey, black)\nMissing piece (bottom right): triangle with black shading\n\nApproach:\n1. Identify row rule (shapes)\n2. Identify column rule (shading)\n3. Apply both rules to find missing piece\n4. Check answer against all rows and columns",
diagramType:"bargraph",practiceTopic:"Matrix Reasoning"},

{id:"t_deductive",subject:"thinking",years:["y5","y6","y7","y8","y9"],title:"Deductive Reasoning",icon:"🕵️",color:"#F59E0B",
explanation:"Deductive reasoning draws specific conclusions from general rules. If the premises are true and the logic is valid, the conclusion must be true. This is the basis of mathematics, law and science. Recognising valid vs invalid arguments is key.",
keyFacts:["Valid deduction: if premises are true, conclusion must be true","Syllogism: two premises + one conclusion","All A are B. X is A. Therefore X is B.","Invalid: conclusion does not follow from premises","Watch for false premises - argument can be valid but still wrong"],
example:"Valid syllogism:\nAll mammals are warm-blooded.\nDolphins are mammals.\nTherefore, dolphins are warm-blooded. (TRUE)\n\nInvalid argument:\nAll cats have four legs.\nThat animal has four legs.\nTherefore, it is a cat. (INVALID - dogs also have 4 legs!)\n\nThe conclusion does not follow - having 4 legs does not make something a cat.",
diagramType:"venn",practiceTopic:"Deductive Reasoning"},

{id:"t_word_problems",subject:"thinking",years:["y3","y4","y5","y6","y7","y8","y9"],title:"Mathematical Word Problems",icon:"🧮",color:"#F59E0B",
explanation:"Word problems require you to translate everyday language into mathematical operations. The skill is identifying WHAT to calculate and WHICH operation to use - not just doing the arithmetic. Multi-step problems require careful planning.",
keyFacts:["Read the problem twice before attempting","Identify: what is given, what is asked","Key words: total/sum (+), difference/less (-), product/times (x), share/each (/)","Draw a diagram or table for complex problems","Check: does the answer make sense?"],
example:"Problem: A shop sells apples for $0.45 each and oranges for $0.60 each. Mia buys 4 apples and 3 oranges and pays with a $5 note. How much change does she receive?\n\nStep 1: Cost of apples = 4 x $0.45 = $1.80\nStep 2: Cost of oranges = 3 x $0.60 = $1.80\nStep 3: Total = $1.80 + $1.80 = $3.60\nStep 4: Change = $5.00 - $3.60 = $1.40",
diagramType:"numberline",practiceTopic:"Word Problems"},

{id:"t_classification",subject:"thinking",years:["y3","y4","y5","y6"],title:"Classification & Odd One Out",icon:"🔍",color:"#F59E0B",
explanation:"Classification groups items by shared properties. Odd one out identifies which item does not belong to the group. Success depends on identifying the property that most of the items share - then finding the one that does not.",
keyFacts:["Look for the most specific category that groups the majority","Multiple answers might seem possible - find the BEST one","Properties to check: size, colour, category, function, number of sides","In tests, one answer will be clearly better than others","Always state the rule: the others are all... because..."],
example:"Odd one out: dog, cat, whale, bird, fish\n\nPossible groupings:\n- All animals (whale fits - wrong approach, too broad)\n- All pets (whale doesn't fit - but neither does fish usually)\n- Mammals (bird and fish excluded - but that gives 2 odd ones)\n- Animals that live on land: whale and fish are odd ones out\n\nBest answer: whale - because all others are commonly kept as pets OR all others can live on land entirely.",
diagramType:"venn",practiceTopic:"Classification"},

];

// Pre-computed total content count:
// General lessons (public/lessons/index.json): 1,192
// HSC lessons (public/lessons/hsc/index.json): 87
// VCE lessons (public/lessons/vce/index.json): 154
// CONCEPTS_LIBRARY interactive concepts: 74
const TOTAL_CONTENT_COUNT = 1192 + 87 + 154 + CONCEPTS_LIBRARY.length;

// ── CONCEPTS LIBRARY COMPONENT ─────────────────────────────────
// A WOW-factor interactive learning hub — click topics, learn, ask Lumi

// ═══════════════════════════════════════════════════════════════
// LumiLessonPlayer — Atomi-style interactive video lesson
// Claude generates script + embedded questions → ElevenLabs narrates
// Narration pauses mid-lesson → student answers inline → continues
// ═══════════════════════════════════════════════════════════════
function LumiLessonPlayer({ concept, yearId, onClose, serverUrl }) {
  const RAIL = serverUrl || 'https://sparky-app-production.up.railway.app';

  // ── State ────────────────────────────────────────────────────
  const [phase, setPhase]       = React.useState('loading'); // loading|playing|question|complete
  const [lesson, setLesson]     = React.useState(null);       // { segments, question }
  const [segIdx, setSegIdx]     = React.useState(0);          // current narration segment
  const [audioEl, setAudioEl]   = React.useState(null);
  const [chosen, setChosen]     = React.useState(null);       // chosen answer index
  const [submitted, setSubmitted] = React.useState(false);
  const [transcript, setTranscript] = React.useState('');     // live transcript text
  const [error, setError]       = React.useState(null);
  const [progress, setProgress] = React.useState(0);          // 0-100

  const rafRef   = React.useRef(null);
  const startRef = React.useRef(0);
  const durRef   = React.useRef(0); // estimated duration for current segment

  const accentColor = concept.color || '#0D9488';
  const T_local = T; // use app theme

  // ── Build lesson on mount ────────────────────────────────────
  React.useEffect(() => {
    buildLesson();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  async function buildLesson() {
    setPhase('loading');
    try {
      // ── Step 1: Claude writes the lesson script ──────────────
      const prompt = `You are Lumi, a warm Australian AI tutor explaining "${concept.title}" to a Year ${yearId?.replace('y','')} student.

Write a short interactive lesson in JSON (no markdown, no backticks):
{
  "intro": "1-2 friendly sentences to hook the student. Make it relatable and surprising.",
  "teach1": "2-3 sentences explaining the core concept clearly. Use simple language.",
  "teach2": "2-3 sentences going deeper with an example or analogy. Use everyday Australian context.",
  "question": {
    "stem": "A question testing understanding of what was just taught — not trivial, makes them think.",
    "options": ["Option A", "Option B", "Option C", "Option D"],
    "answer": 0,
    "explanation": "1-2 sentences explaining why the answer is correct and what's wrong with the others."
  },
  "wrap": "1-2 encouraging sentences that summarise the key takeaway and connect to real life."
}

Context about the concept:
- Explanation: ${concept.explanation}
- Key facts: ${(concept.keyFacts || []).join(' | ')}
- Example: ${concept.example || 'N/A'}

Rules:
- Year ${yearId?.replace('y','')} language level — clear, warm, Lumi's voice
- question.answer is the 0-based index of the correct option
- Each segment should feel conversational, like a tutor talking to a friend
- The question should feel like a natural pause: "Before I continue, let me check..."`;

      let conceptAuthHeaders = {};
      try {
        const { data: { session: conceptSession } } = await supabase.auth.getSession();
        if (conceptSession?.access_token) conceptAuthHeaders = { 'Authorization': `Bearer ${conceptSession.access_token}` };
      } catch { /* proceed without auth — server handles gracefully */ }

      const res = await fetch(`${RAIL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...conceptAuthHeaders },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 1200,
          messages: [{ role: 'user', content: prompt }]
        })
      });
      if (!res.ok) throw new Error(`Claude error ${res.status}`);
      const data = await res.json();
      const raw = (data.content || []).map(b => b.text || '').join('').trim();
      let parsed;
      try { parsed = JSON.parse(raw.replace(/^```json?\s*/,'').replace(/\s*```$/,'')); }
      catch(_) { const m = raw.match(/\{[\s\S]*\}/); if (!m) throw new Error('Bad JSON'); parsed = JSON.parse(m[0]); }

      // ── Step 2: ElevenLabs voices all segments ───────────────
      // We voice intro+teach1+teach2+wrap in one go, question separately
      const narrationScript = `${parsed.intro} ${parsed.teach1} ${parsed.teach2}`;
      const wrapScript = parsed.wrap;

      let narrationUrl = null;
      let wrapUrl = null;

      const ttsAuth = await authHeaders();
      const [tts1, tts2] = await Promise.allSettled([
        fetch(`${RAIL}/api/tts`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...ttsAuth },
          body: JSON.stringify({ text: narrationScript })
        }),
        fetch(`${RAIL}/api/tts`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...ttsAuth },
          body: JSON.stringify({ text: wrapScript })
        })
      ]);

      if (tts1.status === 'fulfilled' && tts1.value.ok) {
        narrationUrl = URL.createObjectURL(await tts1.value.blob());
      }
      if (tts2.status === 'fulfilled' && tts2.value.ok) {
        wrapUrl = URL.createObjectURL(await tts2.value.blob());
      }

      // ── Step 3: Build lesson object ──────────────────────────
      const lessonObj = {
        segments: [
          { text: parsed.intro,   part: 'intro' },
          { text: parsed.teach1,  part: 'teach1' },
          { text: parsed.teach2,  part: 'teach2' },
        ],
        question: parsed.question,
        wrap: { text: parsed.wrap, part: 'wrap' },
        narrationUrl,
        wrapUrl,
      };

      setLesson(lessonObj);
      setPhase('playing');
      startNarration(lessonObj, narrationUrl);

    } catch(e) {
      console.error('Lesson build failed:', e);
      setError(e.message);
      setPhase('error');
    }
  }

  // ── Narration playback ────────────────────────────────────────
  function startNarration(lessonObj, url) {
    if (!url) {
      // No audio — use estimated timing based on word count
      playSegmentsSilently(lessonObj);
      return;
    }
    const audio = new Audio(url);
    setAudioEl(audio);

    // Estimate segment timing from word counts
    const segs = lessonObj.segments;
    const totalWords = segs.reduce((a, s) => a + s.text.split(/\s+/).length, 0);
    const wordTimes = segs.map(s => s.text.split(/\s+/).length);

    audio.addEventListener('loadedmetadata', () => {
      const totalDur = audio.duration;
      let cumulativeTime = 0;
      const segmentTimings = wordTimes.map(wc => {
        const segDur = (wc / totalWords) * totalDur;
        const start = cumulativeTime;
        cumulativeTime += segDur;
        return { start, end: cumulativeTime };
      });

      // Pause point: after teach2 (before question)
      const pauseAt = segmentTimings[segmentTimings.length - 1].end;
      durRef.current = totalDur;

      audio.play().catch(() => {});

      // Track progress and transcript
      const tick = () => {
        if (!audio.paused) {
          const t = audio.currentTime;
          const pct = Math.min((t / pauseAt) * 60, 60); // first 60% = narration
          setProgress(pct);

          // Update segment index
          let curSeg = 0;
          for (let i = 0; i < segmentTimings.length; i++) {
            if (t >= segmentTimings[i].start) curSeg = i;
          }
          setSegIdx(curSeg);
          setTranscript(segs[curSeg]?.text || '');

          if (t >= pauseAt - 0.1) {
            audio.pause();
            setPhase('question');
            setProgress(60);
            return;
          }
        }
        if (!audio.ended) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    });

    audio.addEventListener('error', () => {
      // Fallback to silent timing
      playSegmentsSilently(lessonObj);
    });
  }

  function playSegmentsSilently(lessonObj) {
    // Estimate ~0.4s per word
    const segs = lessonObj.segments;
    let delay = 800;
    segs.forEach((seg, i) => {
      const dur = Math.max(2500, seg.text.split(/\s+/).length * 420);
      setTimeout(() => {
        setSegIdx(i);
        setTranscript(seg.text);
        setProgress(((i+1) / segs.length) * 60);
      }, delay);
      delay += dur;
    });
    setTimeout(() => {
      setPhase('question');
      setProgress(60);
    }, delay);
  }

  function handleAnswer(i) {
    if (submitted) return;
    setChosen(i);
  }

  function handleSubmit() {
    if (chosen === null || submitted) return;
    setSubmitted(true);
    setProgress(80);
  }

  function handleContinue() {
    setPhase('wrap');
    setProgress(90);
    if (lesson?.wrapUrl) {
      const audio = new Audio(lesson.wrapUrl);
      audio.play().catch(() => {});
      setTranscript(lesson.wrap.text);
      audio.addEventListener('ended', () => {
        setPhase('complete');
        setProgress(100);
      });
    } else {
      setTranscript(lesson.wrap.text);
      setTimeout(() => {
        setPhase('complete');
        setProgress(100);
      }, lesson?.wrap.text.split(/\s+/).length * 420 + 500);
    }
  }

  // ── RENDER ───────────────────────────────────────────────────
  const isCorrect = submitted && chosen === lesson?.question?.answer;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: T_local.bg, fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>

      {/* ── Header ── */}
      <div style={{
        padding: '14px 20px', borderBottom: `1px solid ${T_local.border}`,
        background: T_local.surface, display: 'flex', alignItems: 'center', gap: 12,
        flexShrink: 0,
      }}>
        <button onClick={onClose} style={{
          background: T_local.card, border: `1px solid ${T_local.border}`,
          borderRadius: 8, width: 32, height: 32, color: T_local.textMid,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>←</button>
        <div style={{ fontSize: 20 }}>{concept.icon}</div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: T_local.text, margin: 0 }}>{concept.title}</p>
          <p style={{ fontSize: 11, color: T_local.textMid, margin: 0 }}>
            {phase === 'loading' ? 'Preparing lesson…' :
             phase === 'playing' ? '▶ Lumi is explaining…' :
             phase === 'question' ? '💬 Quick check-in' :
             phase === 'wrap'     ? '✅ Almost done…' :
             phase === 'complete' ? '🎉 Lesson complete!' : 'Lesson'}
          </p>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Badge label={`Year ${yearId?.replace('y','')}`} color={accentColor} />
        </div>
      </div>

      {/* ── Progress bar ── */}
      <div style={{ height: 3, background: T_local.border, flexShrink: 0 }}>
        <div style={{
          height: '100%', width: `${progress}%`,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}bb)`,
          transition: 'width 0.5s ease',
        }} />
      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px 40px' }}>

        {/* LOADING */}
        {phase === 'loading' && (
          <div style={{ textAlign: 'center', paddingTop: 60 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: `linear-gradient(135deg, ${accentColor}, ${accentColor}bb)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, margin: '0 auto 20px', animation: 'lumiBob 1.5s ease-in-out infinite',
            }}>✨</div>
            <p style={{ fontSize: 16, fontWeight: 700, color: T_local.text, marginBottom: 8 }}>
              Lumi is preparing your lesson
            </p>
            <p style={{ fontSize: 13, color: T_local.textMid }}>
              Writing script · Generating voice · Building lesson
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 20 }}>
              {['Writing script…', 'Recording voice…', 'Building lesson…'].map((s, i) => (
                <div key={i} style={{
                  fontSize: 11, color: T_local.textDim, background: T_local.card,
                  border: `1px solid ${T_local.border}`, borderRadius: 99,
                  padding: '4px 10px', animation: `pulse ${1 + i * 0.3}s ease-in-out infinite`,
                }}>{s}</div>
              ))}
            </div>
          </div>
        )}

        {/* ERROR */}
        {phase === 'error' && (
          <div style={{ textAlign: 'center', paddingTop: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>😔</div>
            <p style={{ fontSize: 15, fontWeight: 700, color: T_local.text, marginBottom: 8 }}>
              Lesson failed to load
            </p>
            <p style={{ fontSize: 13, color: T_local.textMid, marginBottom: 24 }}>{error}</p>
            <button onClick={buildLesson} style={{
              background: accentColor, border: 'none', borderRadius: 12,
              color: 'white', padding: '12px 24px', fontWeight: 700, fontSize: 14,
              cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>Try Again</button>
          </div>
        )}

        {/* PLAYING / WRAP */}
        {(phase === 'playing' || phase === 'wrap') && lesson && (
          <div style={{ maxWidth: 600, margin: '0 auto' }}>

            {/* Lumi avatar + live transcript */}
            <div style={{
              background: T_local.card,
              border: `1.5px solid ${accentColor}33`,
              borderRadius: '4px 20px 20px 20px',
              padding: '20px 22px', marginBottom: 20,
              animation: 'fadeUp .3s ease',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${accentColor}, ${accentColor}bb)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, flexShrink: 0,
                  boxShadow: `0 0 0 4px ${accentColor}22`,
                  animation: 'lumiBob 2s ease-in-out infinite',
                }}>✨</div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: accentColor, margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    Lumi
                  </p>
                  <p style={{ fontSize: 15, color: T_local.text, lineHeight: 1.7, margin: 0 }}>
                    {transcript || lesson.segments[0]?.text}
                  </p>
                </div>
              </div>
            </div>

            {/* Previous segments (breadcrumb) */}
            {segIdx > 0 && lesson.segments.slice(0, segIdx).map((seg, i) => (
              <div key={i} style={{
                background: T_local.surface, borderRadius: 12,
                padding: '12px 16px', marginBottom: 10,
                border: `1px solid ${T_local.border}`,
                opacity: 0.6,
              }}>
                <p style={{ fontSize: 13, color: T_local.textMid, margin: 0, lineHeight: 1.6 }}>{seg.text}</p>
              </div>
            ))}

            {/* Sound wave indicator */}
            <div style={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center', height: 24, marginTop: 8 }}>
              {[...Array(7)].map((_, i) => (
                <div key={i} style={{
                  width: 3, borderRadius: 99,
                  background: accentColor,
                  height: `${Math.random() * 16 + 8}px`,
                  animation: `pulse ${0.6 + i * 0.1}s ease-in-out infinite`,
                }} />
              ))}
              <span style={{ fontSize: 11, color: T_local.textMid, marginLeft: 8 }}>Lumi is speaking…</span>
            </div>
          </div>
        )}

        {/* QUESTION */}
        {phase === 'question' && lesson?.question && (
          <div style={{ maxWidth: 600, margin: '0 auto', animation: 'fadeUp .35s ease' }}>

            {/* Question header */}
            <div style={{
              background: `${accentColor}12`, border: `1.5px solid ${accentColor}33`,
              borderRadius: 16, padding: '18px 20px', marginBottom: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${accentColor}, ${accentColor}bb)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                }}>✨</div>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: accentColor, margin: 0, textTransform: 'uppercase', letterSpacing: '.06em' }}>Quick check-in</p>
                  <p style={{ fontSize: 12, color: T_local.textMid, margin: 0 }}>Answer to continue the lesson</p>
                </div>
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, color: T_local.text, lineHeight: 1.6, margin: 0 }}>
                {lesson.question.stem}
              </p>
            </div>

            {/* Answer options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {lesson.question.options.map((opt, i) => {
                let bg = T_local.card;
                let border = T_local.border;
                let color = T_local.text;
                let icon = null;
                if (submitted) {
                  if (i === lesson.question.answer) { bg = `${T_local.green}18`; border = T_local.green; color = T_local.green; icon = '✓'; }
                  else if (i === chosen) { bg = `${T_local.red}12`; border = T_local.red; color = T_local.red; icon = '✗'; }
                } else if (chosen === i) {
                  bg = `${accentColor}18`; border = accentColor; color = accentColor;
                }
                return (
                  <button key={i} onClick={() => handleAnswer(i)}
                    disabled={submitted}
                    style={{
                      background: bg, border: `1.5px solid ${border}`,
                      borderRadius: 12, padding: '14px 18px',
                      display: 'flex', alignItems: 'center', gap: 12,
                      cursor: submitted ? 'default' : 'pointer',
                      textAlign: 'left', transition: 'all .15s',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                      background: submitted && i === lesson.question.answer ? `${T_local.green}33` :
                                  submitted && i === chosen ? `${T_local.red}22` :
                                  chosen === i ? `${accentColor}22` : T_local.surface,
                      border: `1px solid ${border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, color,
                    }}>
                      {icon || ['A','B','C','D'][i]}
                    </span>
                    <span style={{ fontSize: 14, color, fontWeight: chosen === i || (submitted && i === lesson.question.answer) ? 600 : 400 }}>
                      {opt}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Submit / explanation */}
            {!submitted ? (
              <button onClick={handleSubmit} disabled={chosen === null}
                style={{
                  width: '100%', padding: 14,
                  background: chosen !== null ? accentColor : T_local.border,
                  border: 'none', borderRadius: 12, color: 'white',
                  fontWeight: 700, fontSize: 15, cursor: chosen !== null ? 'pointer' : 'not-allowed',
                  fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'background .2s',
                }}>
                Check Answer →
              </button>
            ) : (
              <div style={{ animation: 'fadeUp .3s ease' }}>
                <div style={{
                  background: isCorrect ? `${T_local.green}12` : `${T_local.red}10`,
                  border: `1px solid ${isCorrect ? T_local.green : T_local.red}33`,
                  borderRadius: 12, padding: '14px 16px', marginBottom: 16,
                }}>
                  <p style={{ fontWeight: 800, color: isCorrect ? T_local.green : T_local.red, fontSize: 15, margin: '0 0 6px' }}>
                    {isCorrect ? '✅ Correct' : '❌ Not quite'}
                  </p>
                  <p style={{ fontSize: 13, color: T_local.textMid, margin: 0, lineHeight: 1.6 }}>
                    {lesson.question.explanation}
                  </p>
                </div>
                <button onClick={handleContinue} style={{
                  width: '100%', padding: 14,
                  background: `linear-gradient(135deg, ${accentColor}, ${accentColor}bb)`,
                  border: 'none', borderRadius: 12, color: 'white',
                  fontWeight: 700, fontSize: 15, cursor: 'pointer',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}>
                  Continue lesson →
                </button>
              </div>
            )}
          </div>
        )}

        {/* COMPLETE */}
        {phase === 'complete' && lesson && (
          <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', animation: 'fadeUp .35s ease' }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: `linear-gradient(135deg, ${accentColor}, ${accentColor}bb)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 32, margin: '0 auto 20px',
              boxShadow: `0 8px 24px ${accentColor}44`,
            }}>🎉</div>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif", fontSize: 24,
              color: T_local.text, margin: '0 0 8px',
            }}>Lesson complete!</h2>
            <p style={{ fontSize: 14, color: T_local.textMid, lineHeight: 1.7, marginBottom: 24, maxWidth: 420, margin: '0 auto 24px' }}>
              {lesson.wrap.text}
            </p>

            {/* Result badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: isCorrect ? `${T_local.green}15` : `${accentColor}15`,
              border: `1px solid ${isCorrect ? T_local.green : accentColor}33`,
              borderRadius: 99, padding: '8px 18px', marginBottom: 24,
            }}>
              <span style={{ fontSize: 16 }}>{isCorrect ? '✅' : '💪'}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: isCorrect ? T_local.green : accentColor }}>
                {isCorrect ? 'Got the check-in question right!' : 'Keep practising — you\'ve got this!'}
              </span>
            </div>

            {/* Key facts recap */}
            {concept.keyFacts && (
              <div style={{
                background: T_local.card, border: `1px solid ${T_local.border}`,
                borderRadius: 14, padding: '16px 18px', textAlign: 'left', marginBottom: 24,
              }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: accentColor, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                  Key takeaways
                </p>
                {concept.keyFacts.map((fact, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 5,
                      background: accentColor, color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 1,
                    }}>{i+1}</div>
                    <p style={{ fontSize: 13, color: T_local.textMid, margin: 0, lineHeight: 1.6 }}>{fact}</p>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={buildLesson} style={{
                flex: 1, padding: '12px', background: T_local.card,
                border: `1px solid ${T_local.border}`, borderRadius: 12,
                color: T_local.text, fontWeight: 600, fontSize: 14,
                cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>↺ Replay lesson</button>
              <button onClick={onClose} style={{
                flex: 1, padding: '12px',
                background: `linear-gradient(135deg, ${accentColor}, ${accentColor}bb)`,
                border: 'none', borderRadius: 12, color: 'white',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>Done ✓</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}


// ── GLOBAL STYLES ──────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,600;0,9..144,700;0,9..144,900;1,9..144,300;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Instrument+Serif:ital@0;1&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${T.bg}; color: ${T.text}; font-family: 'Plus Jakarta Sans', sans-serif; -webkit-font-smoothing: antialiased; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${T.accentMid}; border-radius: 99px; }
  @keyframes lumiBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
    @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  
  /* ── Responsive grid helpers ─────────────────── */
  .lumio-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .lumio-grid-3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; }
  .lumio-grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
  @media (max-width: 768px) {
    .lumio-grid-2, .lumio-grid-3, .lumio-grid-4 { grid-template-columns: 1fr; }
  }
  @media (max-width: 1024px) {
    .lumio-grid-4 { grid-template-columns: 1fr 1fr; }
  }
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  @keyframes scaleIn { from { opacity:0; transform:scale(.94); } to { opacity:1; transform:scale(1); } }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
  @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  @keyframes confetti-fall { 0%{transform:translateY(-20px) rotate(0deg);opacity:1} 100%{transform:translateY(110vh) rotate(720deg);opacity:0} }
  @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-5px)} 80%{transform:translateX(5px)} }
  @keyframes timerPulse { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.4)} 50%{box-shadow:0 0 0 8px rgba(239,68,68,0)} }

  /* ── Navigation responsive ─────────────────────────────── */
  .lumio-nav-pills  { display: flex !important; }
  .lumio-nav-mobile { display: none !important; }
  .lumio-bottom-tabs { display: none !important; }
  /* tablet portrait: the bar must fit inside the screen, or a touch browser widens the whole page and cuts every right edge */
  @media (max-width: 900px) {
    .lumio-topbar-side { min-width: 0 !important; }
    .lumio-me-text { display: none !important; }
    .lumio-nav-pills button { padding: 0 14px !important; }
  }
  @media (max-width: 680px) {
    .lumio-nav-pills  { display: none !important; }
    .lumio-nav-mobile { display: flex !important; }
    .lumio-bottom-tabs { display: flex !important; }
    .lumio-scroll { padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px)); } /* room for the bottom tabs, which only show here */
  }
`;

// ── EXAM RESULTS ───────────────────────────────────────────────
// Score and proficiency for a finished exam. A multistage exam is judged on the level reached and the score there,
// as NAPLAN does, not the raw percentage.
function examOutcome(questions, answers, mode, route = [], testletSize = TESTLET_SIZE) {
  const isRight = (q, i) => isCorrectResponse(q, answers[i]);
  const total = questions.length;
  const correct = questions.filter(isRight).length;
  const pct = total ? Math.round((correct / total) * 100) : 0;
  const staged = mode === "exam" && route.length > 1;
  const lastStart = (route.length - 1) * testletSize;
  const lastGot = staged ? questions.slice(lastStart).filter((q, k) => isRight(q, lastStart + k)).length : 0;
  const proficiency = staged ? routeProficiency(route, lastGot, Math.max(1, total - lastStart))
    : (pct >= 80 ? "Exceeding" : pct >= 60 ? "Strong" : pct >= 40 ? "Developing" : "Needs Support");
  return { correct, total, pct, staged, proficiency };
}

function ExamResults({ questions, answers, subject, year, mode, route = [], testletSize = TESTLET_SIZE, onRetry, onHome, onMorePractice }) {
  const subj = SUBJECTS.find(s => s.id === subject);
  const isRight = (q, i) => isCorrectResponse(q, answers[i]);
  const correct = questions.filter(isRight).length;
  const pct = Math.round((correct / questions.length) * 100);
  // a multistage exam is judged on the level reached and the score there, as NAPLAN does, not the raw percentage
  const staged = mode === "exam" && route.length > 1;
  const lastStart = (route.length - 1) * testletSize;
  const lastGot = staged ? questions.slice(lastStart).filter((q, k) => isRight(q, lastStart + k)).length : 0;
  const proficiency = staged ? routeProficiency(route, lastGot, Math.max(1, questions.length - lastStart))
    : pct !== null ? (pct >= 80 ? "Exceeding" : pct >= 60 ? "Strong" : pct >= 40 ? "Developing" : "Needs Support") : "In Progress";
  const profColor = { Exceeding: T.green, Strong: T.accent, Developing: T.gold, "Needs Support": T.red }[proficiency] || T.red;

  const topicStats = {};
  questions.forEach((q, i) => {
    if (!topicStats[q.topic]) topicStats[q.topic] = { correct: 0, total: 0 };
    topicStats[q.topic].total++;
    if (isRight(q, i)) topicStats[q.topic].correct++;
  });

  const radarData = Object.entries(topicStats)
    .filter(([, s]) => s.total > 0)  // exclude topics with 0 attempts (avoids NaN)
    .map(([label, s]) => ({
      // Use first 2 words max to keep labels short but distinct
      label: label.split(" ").slice(0, 2).join(" "),
      value: Math.round((s.correct / s.total) * 100) || 0,
    }));

  return (
    <div style={{ padding: "32px 40px 80px", animation: "fadeUp .4s ease", maxWidth: 900, margin: "0 auto", width: "100%" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ fontSize: 52, marginBottom: 12 }}>
          {pct >= 80 ? "🏆" : pct >= 60 ? "🎯" : pct >= 40 ? "📈" : "💪"}
        </div>
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 30, color: T.text, marginBottom: 4 }}>
          {mode === "exam" ? "Exam Complete" : "Practice Complete"}
        </h2>
        <Badge label={proficiency} color={profColor} />
        {staged && (
          <p style={{ fontSize: 13, color: T.textMid, marginTop: 10 }}>
            Your path: {route.map((l) => LEVEL_NAME[l]).join(" → ")}. Like NAPLAN, each testlet's score chose the next one.
          </p>
        )}
      </div>

      {/* Score card */}
      <Card style={{ marginBottom: 20, textAlign: "center", padding: 28 }} glow={subj?.color}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24, marginBottom: 16 }}>
          <ProgressRing value={correct} max={questions.length} size={80} color={profColor} label={`${pct}%`} />
          <div style={{ textAlign: "left" }}>
            <p style={{ fontSize: 36, fontWeight: 700, color: T.text, fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 }}>{correct}/{questions.length}</p>
            <p style={{ color: T.textMid, fontSize: 14, marginTop: 4 }}>questions correct</p>
            <p style={{ color: profColor, fontSize: 13, fontWeight: 600, marginTop: 4 }}>{proficiency} standard</p>
          </div>
        </div>
        <ProgressBar value={correct} max={questions.length} color={profColor} height={8} />
      </Card>

      {/* Radar chart */}
      {radarData.length >= 3 && (
        <Card style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: T.textMid, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Topic Performance</p>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <RadarChart data={radarData} size={200} />
          </div>
        </Card>
      )}

      {/* Per-question breakdown */}
      <Card style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: T.textMid, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 14 }}>Question Breakdown</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {questions.map((q, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: isRight(q, i) ? T.greenLo : T.redLo, border: `1px solid ${isRight(q, i) ? T.green + "44" : T.red + "44"}`, borderRadius: 10 }}>
              <span style={{ fontSize: 14, color: isRight(q, i) ? T.green : T.red, flexShrink: 0, marginTop: 2 }}>{isRight(q, i) ? "✓" : "✗"}</span>
              <div>
                <p style={{ fontSize: 13, color: T.text, fontWeight: 500, marginBottom: 2 }}>Q{i+1}: {q.q.substring(0, 60)}…</p>
                {!isRight(q, i) && responseText(q, answers[i]) && <p style={{ fontSize: 12, color: T.textMid, marginBottom: 2 }}>{`Your answer: ${responseText(q, answers[i])}`}</p>}
                <p style={{ fontSize: 12, color: T.textMid }}>{!isRight(q, i) ? `Answer: ${answerText(q)}` : "Correct"}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
        <Btn variant="ghost" onClick={onRetry}>Retry →</Btn>
        <Btn variant="ghost" onClick={onMorePractice}>More Practice</Btn>
        <Btn onClick={onHome}>Home</Btn>
      </div>
    </div>
  );
}

// ── PARENT DASHBOARD ───────────────────────────────────────────
// ── WRITING PRACTICE SCREEN ────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════
// SPARKY INTERACTIVE VIDEO ENGINE
// Adaptive concept explainer — replaces the human tutor
// Works for all 134 NAPLAN concepts × 4 year levels × unlimited attempts
// Each attempt is MORE creative than the last, never repeating
// ═══════════════════════════════════════════════════════════════════

// ── CONCEPT REGISTRY ───────────────────────────────────────────────
// Maps any concept to visual type + year-appropriate defaults
const CONCEPT_REGISTRY = {
  // NUMERACY — Number
  place_value:               { visual:"blocks",   emoji:"🔢", label:"Place Value" },
  fractions:                 { visual:"pie",       emoji:"🍕", label:"Fractions" },
  decimals:                  { visual:"numberline",emoji:"🔟", label:"Decimals" },
  percentages:               { visual:"bar100",    emoji:"%",  label:"Percentages" },
  integers:                  { visual:"numberline",emoji:"➖", label:"Integers" },
  addition:                  { visual:"blocks",    emoji:"➕", label:"Addition" },
  subtraction:               { visual:"blocks",    emoji:"➖", label:"Subtraction" },
  multiplication:            { visual:"grid",      emoji:"✖️", label:"Multiplication" },
  division:                  { visual:"groups",    emoji:"➗", label:"Division" },
  order_of_operations:       { visual:"equation",  emoji:"🔢", label:"Order of Operations" },
  factors_multiples:         { visual:"grid",      emoji:"🔢", label:"Factors & Multiples" },
  prime_numbers:             { visual:"sieve",     emoji:"⭐", label:"Prime Numbers" },
  squares_roots:             { visual:"square",    emoji:"²",  label:"Squares & Roots" },
  powers_indices:            { visual:"equation",  emoji:"^",  label:"Powers & Indices" },
  scientific_notation:       { visual:"equation",  emoji:"🔬", label:"Scientific Notation" },
  fractions_decimals_percentages: { visual:"triple", emoji:"🔄", label:"FDP Conversions" },
  ratio_proportion:          { visual:"bar",       emoji:"⚖️", label:"Ratio & Proportion" },
  rates:                     { visual:"graph",     emoji:"📈", label:"Rates" },
  algebra_expressions:       { visual:"balance",   emoji:"🔤", label:"Algebra Expressions" },
  algebra_equations:         { visual:"balance",   emoji:"⚖️", label:"Solving Equations" },
  linear_equations:          { visual:"balance",   emoji:"📐", label:"Linear Equations" },
  simultaneous_equations:    { visual:"graph",     emoji:"📊", label:"Simultaneous Equations" },
  patterns_sequences:        { visual:"sequence",  emoji:"🔢", label:"Patterns & Sequences" },
  nth_term:                  { visual:"sequence",  emoji:"n",  label:"The nth Term" },
  financial_maths:           { visual:"money",     emoji:"💰", label:"Financial Maths" },
  simple_interest:           { visual:"money",     emoji:"💵", label:"Simple Interest" },
  compound_interest:         { visual:"growth",    emoji:"📈", label:"Compound Interest" },
  // NUMERACY — Measurement & Geometry
  perimeter:                 { visual:"shape",     emoji:"📏", label:"Perimeter" },
  area_rectangle:            { visual:"grid",      emoji:"⬛", label:"Area of Rectangle" },
  area_triangle:             { visual:"triangle",  emoji:"🔺", label:"Area of Triangle" },
  area_circle:               { visual:"circle",    emoji:"⭕", label:"Area of Circle" },
  area_composite:            { visual:"shape",     emoji:"🔷", label:"Composite Area" },
  volume_prism:              { visual:"3d",        emoji:"📦", label:"Volume of Prism" },
  volume_cylinder:           { visual:"3d",        emoji:"🥫", label:"Volume of Cylinder" },
  pythagoras:                { visual:"triangle",  emoji:"📐", label:"Pythagoras' Theorem" },
  angles:                    { visual:"angles",    emoji:"📐", label:"Angles" },
  triangles:                 { visual:"triangle",  emoji:"🔺", label:"Triangles" },
  circles_parts:             { visual:"circle",    emoji:"⭕", label:"Parts of a Circle" },
  coordinate_plane:          { visual:"grid",      emoji:"📍", label:"Coordinate Plane" },
  linear_graphs:             { visual:"graph",     emoji:"📈", label:"Linear Graphs" },
  gradient:                  { visual:"graph",     emoji:"📐", label:"Gradient" },
  // NUMERACY — Statistics
  mean_median_mode:          { visual:"data",      emoji:"📊", label:"Mean, Median, Mode" },
  range:                     { visual:"data",      emoji:"↔️", label:"Range" },
  probability_basic:         { visual:"spinner",   emoji:"🎲", label:"Probability" },
  probability_compound:      { visual:"tree",      emoji:"🌳", label:"Compound Probability" },
  // READING
  literal_comprehension:     { visual:"text",      emoji:"📖", label:"Literal Comprehension" },
  inference:                 { visual:"detective", emoji:"🔍", label:"Inference" },
  authors_purpose:           { visual:"thought",   emoji:"💭", label:"Author's Purpose" },
  text_structure:            { visual:"diagram",   emoji:"🏗️", label:"Text Structure" },
  main_idea:                 { visual:"text",      emoji:"💡", label:"Main Idea" },
  fact_vs_opinion:           { visual:"scales",    emoji:"⚖️", label:"Fact vs Opinion" },
  figurative_language:       { visual:"text",      emoji:"🎨", label:"Figurative Language" },
  point_of_view:             { visual:"thought",   emoji:"👁️", label:"Point of View" },
  vocabulary_in_context:     { visual:"text",      emoji:"📚", label:"Vocabulary in Context" },
  // LANGUAGE
  nouns:                     { visual:"wordbox",   emoji:"🏷️", label:"Nouns" },
  verbs:                     { visual:"wordbox",   emoji:"⚡", label:"Verbs" },
  adjectives:                { visual:"wordbox",   emoji:"🎨", label:"Adjectives" },
  adverbs:                   { visual:"wordbox",   emoji:"💨", label:"Adverbs" },
  apostrophe_possession:     { visual:"text",      emoji:"'",  label:"Apostrophe: Possession" },
  apostrophe_contraction:    { visual:"text",      emoji:"'",  label:"Apostrophe: Contraction" },
  subject_verb_agreement:    { visual:"sentence",  emoji:"✅", label:"Subject-Verb Agreement" },
  tense:                     { visual:"timeline",  emoji:"⏰", label:"Verb Tense" },
  synonyms_antonyms:         { visual:"wordbox",   emoji:"↔️", label:"Synonyms & Antonyms" },
  homophones:                { visual:"wordbox",   emoji:"👂", label:"Homophones" },
  spelling_rules:            { visual:"wordbox",   emoji:"✏️", label:"Spelling Rules" },
  // WRITING
  narrative_structure:       { visual:"diagram",   emoji:"📖", label:"Narrative Structure" },
  persuasive_structure:      { visual:"diagram",   emoji:"💬", label:"Persuasive Structure" },
  teel_paragraph:            { visual:"diagram",   emoji:"📝", label:"TEEL Paragraph" },
  show_dont_tell:            { visual:"text",      emoji:"✍️", label:"Show, Don't Tell" },
  rhetorical_devices:        { visual:"text",      emoji:"🎭", label:"Rhetorical Devices" },
  argument_evidence:         { visual:"diagram",   emoji:"⚖️", label:"Argument & Evidence" },
};

// ── EXPLANATION STYLE TEMPLATES ────────────────────────────────────
// Different approaches for each attempt — never repeating
const EXPLANATION_STYLES = [
  {
    id: "visual_story",
    name: "Story & Visuals",
    prompt: "Explain using a relatable real-world story with vivid imagery. Build step by step. Use 'Imagine...' to start."
  },
  {
    id: "detective",
    name: "Detective Method",
    prompt: "Explain as if we're solving a mystery together. Use clues, evidence, and a 'reveal'. Start with 'Here's a mystery for you...'"
  },
  {
    id: "analogy",
    name: "Everyday Analogy",
    prompt: "Explain using a completely different everyday analogy (food, sports, games, animals). Make the abstract concrete."
  },
  {
    id: "build_up",
    name: "Build From Scratch",
    prompt: "Start from the absolute basics, building up brick by brick. Assume zero prior knowledge. Use numbered steps."
  },
  {
    id: "common_mistakes",
    name: "Common Mistakes",
    prompt: "Show the most common mistakes students make, explain WHY they're wrong, then show the right way. Use 'Here's a trap...' language."
  },
];

// ── VISUAL RENDERERS ───────────────────────────────────────────────
// Each visual type renders differently based on concept + step

function VisualPie({ step, concept, data }) {
  const total = data.total || 4;
  const highlight = data.highlight || 1;
  const slices = Array.from({ length: total }, (_, i) => {
    const startAngle = (i / total) * 2 * Math.PI - Math.PI / 2;
    const endAngle = ((i + 1) / total) * 2 * Math.PI - Math.PI / 2;
    const r = 70;
    const cx = 90, cy = 90;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const colors = ["#3B82F6","#8B5CF6","#10B981","#F59E0B","#EF4444","#EC4899","#06B6D4","#84CC16"];
    const isHighlighted = step >= 2 && i < highlight;
    return { x1, y1, x2, y2, cx, cy, color: colors[i % colors.length], isHighlighted, i };
  });

  return (
    <svg width="180" height="180" viewBox="0 0 180 180">
      {step >= 1 && slices.map(s => (
        <path key={s.i}
          d={`M${s.cx} ${s.cy} L${s.x1} ${s.y1} A70 70 0 ${1/total > 0.5 ? 1 : 0} 1 ${s.x2} ${s.y2}Z`}
          fill={s.isHighlighted ? "#F59E0B" : s.color}
          stroke="#0A0C10" strokeWidth="2"
          style={{ transform: s.isHighlighted ? `scale(1.06)` : "scale(1)", transformOrigin:"90px 90px", transition:"all 0.4s ease" }}
        />
      ))}
      <circle cx="90" cy="90" r="5" fill="#0A0C10" />
      {step >= 3 && (
        <text x="90" y="20" textAnchor="middle" fill="#F59E0B" fontSize="14" fontWeight="700">
          {highlight}/{total}
        </text>
      )}
    </svg>
  );
}

function VisualBlocks({ step, data }) {
  const rows = data.rows || [[3,2],[2,3]];
  return (
    <svg width="200" height="140" viewBox="0 0 200 140">
      {rows.map((row, ri) =>
        row.map((count, ci) =>
          Array.from({ length: count }, (_, bi) => (
            <rect key={`${ri}-${ci}-${bi}`}
              x={10 + ci * 70 + bi * 22}
              y={20 + ri * 50}
              width={18} height={18} rx={3}
              fill={step >= ri + 1 ? ["#3B82F6","#F59E0B","#10B981"][ci % 3] : "#1E2433"}
              stroke="#0A0C10" strokeWidth="1"
              style={{ transition:`all 0.3s ease ${(ri*10+ci*5+bi)*0.05}s` }}
            />
          ))
        )
      )}
    </svg>
  );
}

function VisualNumberLine({ step, data }) {
  const min = data.min ?? -5;
  const max = data.max ?? 10;
  const highlight = data.highlight ?? [0];
  const range = max - min;
  const w = 280, pad = 20;
  const toX = v => pad + ((v - min) / range) * (w - pad * 2);

  return (
    <svg width={w} height="80" viewBox={`0 0 ${w} 80`}>
      {step >= 1 && <line x1={pad} y1={40} x2={w-pad} y2={40} stroke="#3B82F6" strokeWidth="2"/>}
      {step >= 1 && Array.from({ length: range + 1 }, (_, i) => {
        const v = min + i;
        const x = toX(v);
        return (
          <g key={v}>
            <line x1={x} y1={34} x2={x} y2={46} stroke="#3B82F6" strokeWidth="1.5"/>
            <text x={x} y={60} textAnchor="middle" fill={v === 0 ? "#F59E0B" : "#8892A4"} fontSize="11">{v}</text>
          </g>
        );
      })}
      {step >= 2 && highlight.map((v, i) => (
        <circle key={i} cx={toX(v)} cy={40} r={8} fill="#F59E0B"
          style={{ animation:"pulse 1s ease-in-out infinite" }}/>
      ))}
    </svg>
  );
}

function VisualGrid({ step, data }) {
  const rows = data.rows || 3;
  const cols = data.cols || 4;
  const highlightCells = data.highlight || [];
  return (
    <svg width="200" height="160" viewBox="0 0 200 160">
      {step >= 1 && Array.from({ length: rows }, (_, r) =>
        Array.from({ length: cols }, (_, c) => {
          const isHighlighted = step >= 2 && highlightCells.some(([hr,hc]) => hr === r && hc === c);
          return (
            <rect key={`${r}-${c}`}
              x={10 + c * 44} y={10 + r * 44} width={38} height={38} rx={4}
              fill={isHighlighted ? "#F59E0B" : "#1E2433"}
              stroke={isHighlighted ? "#F59E0B" : "#2A3245"} strokeWidth="1.5"
              style={{ transition:`all 0.3s ease ${(r*cols+c)*0.04}s` }}
            />
          );
        })
      )}
      {step >= 3 && (
        <text x="100" y="155" textAnchor="middle" fill="#F59E0B" fontSize="13" fontWeight="700">
          {rows} × {cols} = {rows * cols}
        </text>
      )}
    </svg>
  );
}

function VisualBalance({ step, data }) {
  const left = data.left || "2x + 3";
  const right = data.right || "11";
  const steps = data.steps || ["2x + 3 = 11", "2x = 8", "x = 4"];
  return (
    <div style={{ textAlign:"center" }}>
      <svg width="200" height="120" viewBox="0 0 200 120">
        {/* Fulcrum */}
        <polygon points="100,90 90,110 110,110" fill="#F59E0B"/>
        <line x1="100" y1="30" x2="100" y2="90" stroke="#F59E0B" strokeWidth="3"/>
        {/* Beam */}
        <line x1="20" y1="30" x2="180" y2="30" stroke={step >= 1 ? "#3B82F6" : "#1E2433"} strokeWidth="3"
          style={{ transition:"all 0.5s ease" }}/>
        {/* Left pan */}
        <rect x="10" y="38" width="60" height="28" rx="4" fill="#1E2433" stroke="#3B82F6" strokeWidth="1.5"/>
        <text x="40" y="57" textAnchor="middle" fill="#F59E0B" fontSize="13" fontWeight="700">{left}</text>
        {/* Right pan */}
        <rect x="130" y="38" width="60" height="28" rx="4" fill="#1E2433" stroke="#3B82F6" strokeWidth="1.5"/>
        <text x="160" y="57" textAnchor="middle" fill="#10B981" fontSize="13" fontWeight="700">{right}</text>
        {step >= 2 && <text x="100" y="20" textAnchor="middle" fill="#8892A4" fontSize="11">= balanced</text>}
      </svg>
      {steps.slice(0, step).map((s, i) => (
        <div key={i} style={{ fontSize:14, color: i === step-1 ? "#F59E0B" : "#8892A4",
          fontWeight: i === step-1 ? 700 : 400, transition:"all 0.3s ease", marginBottom:4 }}>
          {s}
        </div>
      ))}
    </div>
  );
}

function VisualDetective({ step, clues, reveal }) {
  return (
    <div style={{ padding:"0 8px" }}>
      {clues.slice(0, step).map((clue, i) => (
        <div key={i} style={{
          background:"#111318", border:"1px solid #1E2433",
          borderRadius:8, padding:"8px 12px", marginBottom:8,
          opacity: step >= i+1 ? 1 : 0,
          transform: step >= i+1 ? "translateX(0)" : "translateX(-12px)",
          transition:`all 0.4s ease ${i*0.15}s`
        }}>
          <span style={{ marginRight:8 }}>🔍</span>
          <span style={{ fontSize:13, color:"#CBD5E1" }}>{clue}</span>
        </div>
      ))}
      {step >= clues.length + 1 && (
        <div style={{ background:"#1E1040", border:"1px solid #8B5CF6",
          borderRadius:10, padding:"10px 14px", textAlign:"center",
          animation:"fadeUp 0.5s ease" }}>
          <div style={{ fontSize:16, fontWeight:700, color:"#F59E0B" }}>💡 {reveal}</div>
        </div>
      )}
    </div>
  );
}

function VisualDiagram({ step, boxes }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6, alignItems:"center" }}>
      {boxes.slice(0, step).map((box, i) => (
        <div key={i} style={{
          background: box.color ? `${box.color}22` : "#111318",
          border: `2px solid ${box.color || "#1E2433"}`,
          borderRadius:10, padding:"8px 16px", width:"100%", maxWidth:260,
          opacity: step >= i+1 ? 1 : 0,
          transform: step >= i+1 ? "translateY(0)" : "translateY(10px)",
          transition:`all 0.4s ease ${i*0.12}s`
        }}>
          <div style={{ fontSize:12, fontWeight:700, color: box.color || "#F59E0B", marginBottom:2 }}>{box.title}</div>
          {box.desc && <div style={{ fontSize:12, color:"#94A3B8", lineHeight:1.5 }}>{box.desc}</div>}
        </div>
      ))}
    </div>
  );
}

function VisualWordBox({ step, words, highlight, label }) {
  return (
    <div style={{ textAlign:"center" }}>
      <div style={{ marginBottom:12, fontSize:13, color:"#F59E0B", fontWeight:700 }}>{label}</div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center" }}>
        {words.slice(0, Math.ceil(words.length * Math.min(step/3, 1))).map((w, i) => (
          <div key={i} style={{
            background: highlight.includes(w) ? "#F59E0B22" : "#111318",
            border: `2px solid ${highlight.includes(w) ? "#F59E0B" : "#1E2433"}`,
            borderRadius:8, padding:"6px 12px",
            fontSize:14, fontWeight:700,
            color: highlight.includes(w) ? "#F59E0B" : "#CBD5E1",
            transition:`all 0.3s ease ${i*0.08}s`,
            animation: highlight.includes(w) && step >= 2 ? "pulse 1.5s ease-in-out infinite" : "none"
          }}>{w}</div>
        ))}
      </div>
    </div>
  );
}

function VisualTriangle({ step, data }) {
  const { a, b, c, formula, highlight } = data;
  return (
    <svg width="200" height="160" viewBox="0 0 200 160">
      {step >= 1 && (
        <polygon points="100,15 15,145 185,145"
          fill="#3B82F622" stroke="#3B82F6" strokeWidth="2.5" strokeLinejoin="round"/>
      )}
      {step >= 2 && a && <text x="52" y="88" fill="#F59E0B" fontSize="13" fontWeight="700">{a}</text>}
      {step >= 2 && b && <text x="144" y="88" fill="#10B981" fontSize="13" fontWeight="700">{b}</text>}
      {step >= 2 && c && <text x="95" y="158" fill="#8B5CF6" fontSize="13" fontWeight="700">{c}</text>}
      {step >= 3 && highlight && (
        <text x="100" y="195" textAnchor="middle" fill="#F59E0B" fontSize="12" fontWeight="700">{formula}</text>
      )}
      {step >= 3 && <rect x="15" y="125" width="20" height="20" fill="none" stroke="#F59E0B" strokeWidth="2"/>}
    </svg>
  );
}

function VisualTimeline({ step, events }) {
  return (
    <div style={{ position:"relative", paddingLeft:20 }}>
      <div style={{ position:"absolute", left:8, top:0, bottom:0, width:2, background:"#1E2433" }}/>
      {events.slice(0, step).map((ev, i) => (
        <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:12,
          opacity: step >= i+1 ? 1 : 0, transform: step >= i+1 ? "translateX(0)" : "translateX(-8px)",
          transition:`all 0.4s ease ${i*0.12}s` }}>
          <div style={{ width:16, height:16, borderRadius:"50%", background:ev.color || "#F59E0B",
            flexShrink:0, marginTop:2, zIndex:1 }}/>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:ev.color || "#F59E0B" }}>{ev.label}</div>
            <div style={{ fontSize:12, color:"#94A3B8", lineHeight:1.4 }}>{ev.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── 1. DIAGNOSTIC ENGINE ──────────────────────────────────────────
// ── 2. SESSION PLANNER ────────────────────────────────────────────
// After every session, analyses performance and recommends what to study next
// Stored in progress object, shown on subject home screen

function SessionPlanner({ progress, yearId, onStart }) {
  const [plan, setPlan]       = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const totalQ = Object.values(progress).reduce((a,b) => a + (b.total||0), 0);
    if (totalQ >= 5) generatePlan();
  }, [progress]);

  async function generatePlan() {
    setLoading(true);

    // Build performance summary
    const summary = Object.entries(progress).map(([subj, p]) => {
      const pct = p.total > 0 ? Math.round((p.correct/p.total)*100) : null;
      const topicBreakdown = Object.entries(p.topics||{}).map(([topic, tp]) => {
        const tpct = tp.total > 0 ? Math.round((tp.correct/tp.total)*100) : 0;
        return `${topic}: ${tpct}% (${tp.total} questions)`;
      }).join(', ');
      return `${subj}: ${pct !== null ? pct+'%' : 'not started'} overall${topicBreakdown ? '. Topics: '+topicBreakdown : ''}`;
    }).join('\n');

    const system = `You are Lumi, a personalised NAPLAN study planner.
Based on a student's performance data, recommend a focused study session.
Return ONLY valid JSON — no markdown.

{
  "headline": "Short motivating headline (e.g. 'Time to tackle fractions!')",
  "focus_subject": "reading|writing|language|numeracy",
  "focus_topic": "specific topic name matching their weakest area",
  "reason": "1 sentence explaining why this topic (specific, not generic)",
  "session_goal": "What the student will achieve in this session (1 sentence)",
  "encouragement": "1 short encouraging message referencing their actual strengths",
  "estimated_minutes": 10
}`;

    const user = `Year level: ${yearId?.replace('y','')}
Performance data:\n${summary}

Recommend the single most important topic to study next. Pick the weakest area with enough data to act on.`;

    try {
      const res = await fetch(`${SPARKY_SERVER_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          model: 'claude-haiku-5-5',
          max_tokens: 400,
          system,
          messages: [{ role: 'user', content: user }],
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || '';
      const clean = text.replace(/```json|```/g, '').trim();
      setPlan(JSON.parse(clean));
    } catch(e) {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }

  const subjectColors = { reading:'#3B82F6', writing:'#8B5CF6', language:'#10B981', numeracy:'#F59E0B' };
  const totalQ = Object.values(progress).reduce((a,b) => a+(b.total||0), 0);

  if (totalQ < 5) return null;
  if (loading) return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#FBBF24,#F59E0B)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>⚡</div>
      <div>
        <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 2px' }}>Lumi is building your study plan...</p>
        <p style={{ fontSize: 12, color: T.textMid, margin: 0 }}>Analysing your performance</p>
      </div>
    </div>
  );
  if (!plan) return null;

  const col = subjectColors[plan.focus_subject] || T.accent;

  return (
    <div style={{ background: `linear-gradient(135deg, ${col}18, ${col}08)`, border: `1px solid ${col}44`, borderRadius: 14, padding: '16px', marginBottom: 16, animation: 'fadeUp 0.4s ease' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#FBBF24,#F59E0B)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>⚡</div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>Lumi's Study Plan</p>
          <p style={{ fontSize: 11, color: col, margin: 0, fontWeight: 600 }}>Personalised for you</p>
        </div>
      </div>

      {/* Headline */}
      <p style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: '0 0 6px', fontFamily: "'Space Grotesk',sans-serif" }}>{plan.headline}</p>

      {/* Reason */}
      <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.55, margin: '0 0 10px' }}>{plan.reason}</p>

      {/* Goal */}
      <div style={{ background: `${col}15`, borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
        <p style={{ fontSize: 12, color: col, fontWeight: 700, margin: '0 0 2px' }}>🎯 Today's goal</p>
        <p style={{ fontSize: 13, color: T.text, margin: 0 }}>{plan.session_goal}</p>
      </div>

      {/* Encouragement */}
      <p style={{ fontSize: 12, color: T.textMid, fontStyle: 'italic', margin: '0 0 12px', lineHeight: 1.5 }}>"{plan.encouragement}"</p>

      {/* Start button */}
      <button
        onClick={() => onStart(plan.focus_subject, plan.focus_topic)}
        style={{ width: '100%', padding: '11px', background: `linear-gradient(135deg,#FBBF24,#F59E0B)`, border: 'none', borderRadius: 10, color: '#0A0C10', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        Start {plan.estimated_minutes}-min session →
      </button>
    </div>
  );
}

// ── EXAM TIMER ─────────────────────────────────────────────────
// Shows countdown during exam mode — auto-navigates to results on expiry
function ExamTimer({ totalQuestions, onTimeUp, minutes }) {
  // an official paper's time when given; otherwise ~2 min per question, min 10 min, max 45 min
  const totalSeconds = React.useRef(minutes ? minutes * 60 : Math.max(600, Math.min(2700, totalQuestions * 120)));
  // counted against the wall clock, not by ticks: browsers slow timers in background tabs and on sleeping tablets,
  // so a tick counter drifts and quietly gives extra time
  const endsAt = React.useRef(Date.now() + totalSeconds.current * 1000);
  const [secondsLeft, setSecondsLeft] = React.useState(totalSeconds.current);
  const fired = React.useRef(false);
  React.useEffect(() => {
    const tick = () => {
      const left = Math.max(0, Math.round((endsAt.current - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0 && !fired.current) { fired.current = true; clearInterval(id); onTimeUp && onTimeUp(); }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pct = secondsLeft / totalSeconds.current;
  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, '0');
  const color = pct > 0.5 ? T.green : pct > 0.2 ? T.gold : '#EF4444';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
      <div style={{ width:36, height:36, borderRadius:'50%', border:`2.5px solid ${color}`, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', background:`${color}12` }}>
        <span style={{ fontSize:9, fontWeight:800, color, lineHeight:1 }}>{mins}</span>
        <span style={{ fontSize:7, color, lineHeight:1 }}>{secs}</span>
      </div>
    </div>
  );
}

// ── MAIN APP ───────────────────────────────────────────────────
function getProfiles() {
  try {
    return JSON.parse(localStorage.getItem("lumio_profiles") || "[]");
  } catch(e) { return []; }
}

function getActiveProfile() {
  try {
    const profiles = getProfiles();
    const activeId = localStorage.getItem("lumio_active_profile");
    return profiles.find(p => p.id === activeId) || profiles[0] || null;
  } catch(e) { return null; }
}

function saveProfiles(profiles) {
  localStorage.setItem("lumio_profiles", JSON.stringify(profiles));
}

// maxProfiles: 3 on Family, otherwise 1. "Add child" only shows while the plan has room, so a single-child plan just
// names its one child and then hides this card. Children beyond the plan's limit stay saved but are not offered.
function ProfileSwitcher({ onSwitch, currentProfileId, maxProfiles = 3 }) {
  const [profiles, setProfiles] = useState(getProfiles());
  const [showAdd, setShowAdd]   = useState(false);
  const [newName, setNewName]   = useState("");
  const [newYear, setNewYear]   = useState("y5");

  function addProfile() {
    if (!newName.trim()) return;
    const newProfile = {
      // The first child named takes over the progress already on this account (stored under the bare keys, which
      // profileKey uses for an empty id); every later child starts their own.
      id: profiles.length === 0 ? '' : `profile_${Date.now()}`,
      name: newName.trim(),
      yearId: newYear,
      avatar: ["🐨","🦘","🐸","🦜","🐬"][profiles.length % 5],
      created: Date.now(),
    };
    const updated = [...profiles, newProfile];
    saveProfiles(updated);
    setProfiles(updated);
    localStorage.setItem("lumio_active_profile", newProfile.id);
    setNewName("");
    setShowAdd(false);
    onSwitch(newProfile);
  }

  function switchProfile(profile) {
    localStorage.setItem("lumio_active_profile", profile.id);
    onSwitch(profile);
  }

  const shown = profiles.slice(0, Math.max(1, maxProfiles));
  const canAdd = profiles.length < maxProfiles;
  if (!canAdd && shown.length <= 1) return null;

  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "16px 18px", marginBottom: 16 }}>
      <p style={{ fontSize: 12, fontWeight: 800, color: T.textMid, textTransform: "uppercase", letterSpacing: ".08em", margin: "0 0 12px" }}>Who's practising?</p>

      {profiles.length === 0 && (
        <p style={{ fontSize: 13, color: T.textMid, margin: "0 0 12px", lineHeight: 1.55 }}>
          Add your child's name. Their progress so far stays with them.{maxProfiles > 1 ? " Add a brother or sister to give each child their own progress and weekly report." : ""}
        </p>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        {shown.map(profile => (
          <button key={profile.id || 'first-child'}
            onClick={() => switchProfile(profile)}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              padding: "10px 14px", borderRadius: 12,
              background: profile.id === currentProfileId ? `${T.accent}12` : T.surface,
              border: `2px solid ${profile.id === currentProfileId ? T.accent : T.border}`,
              cursor: "pointer", transition: "all .15s",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
            <span style={{ fontSize: 24 }}>{profile.avatar}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: profile.id === currentProfileId ? T.accent : T.text }}>{profile.name}</span>
            <span style={{ fontSize: 10, color: T.textDim }}>{YEARS.find(y => y.id === profile.yearId)?.label || profile.yearId}</span>
          </button>
        ))}

        {canAdd && (
          <button onClick={() => setShowAdd(true)}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "10px 14px", borderRadius: 12, background: T.surface, border: `2px dashed ${T.border}`, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <span style={{ fontSize: 24, color: T.textDim }}>+</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.textDim }}>{profiles.length ? "Add child" : "Add your child"}</span>
          </button>
        )}
      </div>

      {showAdd && (
        <div style={{ background: T.surface, borderRadius: 12, padding: 14, border: `1px solid ${T.border}` }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: "0 0 10px" }}>Add a child</p>
          <input
            value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="Child's name"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.card, color: T.text, fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 8, boxSizing: "border-box", outline: "none" }}
          />
          <select value={newYear} onChange={e => setNewYear(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1.5px solid ${T.border}`, background: T.card, color: T.text, fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 10, boxSizing: "border-box", outline: "none" }}>
            {YEARS.map(y => <option key={y.id} value={y.id}>{y.label} ({y.age})</option>)}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={addProfile}
              style={{ flex: 1, padding: 10, background: T.accent, border: "none", borderRadius: 8, color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Add →
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: "10px 14px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMid, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── SHAREABLE PROGRESS REPORT ─────────────────────────────────
function ShareableReport({ progress, profileName, yearId, activeProfile, examProgram, onClose }) {
  const [generating, setGenerating] = useState(false);
  const [insight, setInsight]       = useState(null);

  const totalQ = Object.values(progress).reduce((a, b) => a + (b.total || 0), 0);
  const totalC = Object.values(progress).reduce((a, b) => a + (b.correct || 0), 0);
  const pct    = totalQ > 0 ? Math.round((totalC / totalQ) * 100) : 0;
  const stats  = getActivityStats(activeProfile?.id);
  const year   = YEARS.find(y => y.id === yearId);

  // Build a deterministic fallback insight from actual data — always safe to render
  function buildFallbackInsight() {
    const entries = Object.entries(progress).filter(([,v]) => v && v.total > 0);
    if (totalQ === 0 || entries.length === 0) {
      return `**Getting started.** Complete a few practice questions and Lumi will summarise ${profileName || "your"} progress here.`;
    }
    const ranked = entries.map(([s,v]) => ({ s, pct: Math.round((v.correct/v.total)*100), total: v.total }))
      .sort((a,b) => b.pct - a.pct);
    const best = ranked[0];
    const weak = ranked[ranked.length - 1];
    const acc = Math.round((totalC / totalQ) * 100);
    const streakLine = stats.streak > 0 ? ` ${profileName || "Your child"} is on a ${stats.streak}-day streak — keep the momentum!` : "";
    let tip;
    if (weak && weak.pct < 60 && weak.s !== best.s) {
      tip = `Focus on **${subjectDisplayName(weak.s)}** next — a 10-minute targeted session can lift this quickly.`;
    } else if (best && best.pct >= 80) {
      tip = `Stretch into harder questions in **${subjectDisplayName(best.s)}** to keep pushing the ceiling.`;
    } else {
      tip = `Consistent daily practice (10–15 minutes) is the best predictor of exam readiness.`;
    }
    return `**${profileName || "Your child"} has completed ${totalQ} questions with ${acc}% accuracy.** Strongest area: **${subjectDisplayName(best.s)}** (${best.pct}%).${streakLine}\n\n${tip}`;
  }

  useEffect(() => {
    async function fetchInsight() {
      // Always seed with a data-driven fallback so the Assessment panel is never blank
      const fallback = buildFallbackInsight();
      if (totalQ < 5) { setInsight(fallback); return; }
      setGenerating(true);
      try {
        const prompt = `You are Lumi, a friendly AI tutor for Empirio, an Australian exam prep app.

Write a SHORT encouraging progress summary (2-3 sentences) for a parent to share.
Student: ${profileName || "Your child"}
Year level: ${year?.label || "Unknown"}
Total questions: ${totalQ}
Correct: ${totalC}/${totalQ}
Streak: ${stats.streak} days
Subject scores: ${JSON.stringify(Object.entries(progress).map(([s,v]) => ({subject:s, pct: v.total>0?Math.round(v.correct/v.total*100):0})))}

Be warm, specific, and encouraging. Mention the strongest subject. End with one tip.`;

        const res = await fetch(SPARKY_SERVER_URL + "/api/chat", {
          method: "POST", headers: { "Content-Type": "application/json", ...(await authHeaders()) },
          body: JSON.stringify({ model: "claude-haiku-5-5", max_tokens: 200, messages: [{ role: "user", content: prompt }] }),
        });
        const data = await res.json();
        const text = data?.content?.[0]?.text;
        setInsight(text && text.trim() ? text : fallback);
      } catch(e) {
        setInsight(fallback);
      }
      setGenerating(false);
    }
    fetchInsight();
  }, []);

  function copyReport() {
    const subjectLines = Object.entries(progress)
      .filter(([,v]) => v.total > 0)
      .map(([s,v]) => `${subjectDisplayName(s)}: ${Math.round(v.correct/v.total*100)}%`)
      .join(" | ");

    const text = `📊 ${profileName || "Empirio"} Progress Report
${year?.label || ""} · ${new Date().toLocaleDateString("en-AU")}

✅ ${totalQ} questions completed
🎯 ${totalC}/${totalQ} correct
🔥 ${stats.streak}-day streak

${subjectLines}

${insight ? `✨ ${insight}` : ""}

Prepared with Empirio — Australia's AI exam prep app
empirio.com.au`;

    navigator.clipboard?.writeText(text).then(() => {
      alert("Report copied! Paste it into a message to share with your child's teacher or family.");
    }).catch(() => {
      alert("Copy this text:\n\n" + text);
    });
  }

  return (
    <div style={{ padding: "24px 20px 80px", maxWidth: 600, margin: "0 auto", width: "100%", animation: "fadeUp .3s ease" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onClose} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, width: 32, height: 32, color: T.textMid, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>←</button>
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, color: T.text, margin: 0 }}>Progress Report</h2>
      </div>

      {/* Report card */}
      <div style={{ background: "linear-gradient(135deg, #0D9488, #0F766E)", borderRadius: 20, padding: "24px", marginBottom: 16, color: "white", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
        <div style={{ position: "absolute", bottom: -30, left: -10, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, position: "relative" }}>
          <div>
            <p style={{ fontSize: 12, opacity: 0.8, margin: "0 0 4px", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em" }}>Empirio Progress Report</p>
            <h3 style={{ fontSize: 22, fontFamily: "'Space Grotesk', sans-serif", margin: "0 0 2px" }}>{profileName || "Student"}</h3>
            <p style={{ fontSize: 13, opacity: 0.8, margin: 0 }}>{year?.label} · {new Date().toLocaleDateString("en-AU")}</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontSize: 28, fontFamily: "'Space Grotesk', sans-serif", margin: 0, lineHeight: 1 }}>{totalC}<span style={{fontSize:16}}>&nbsp;/ {totalQ}</span></p>
            <p style={{ fontSize: 11, opacity: 0.8 }}>correct answers</p>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, position: "relative" }}>
          {[
            { label: "Questions", value: totalQ, icon: "📝" },
            { label: "Correct", value: totalC, icon: "✅" },
            { label: "Streak", value: `${stats.streak}d`, icon: "🔥" },
          ].map(stat => (
            <div key={stat.label} style={{ background: "rgba(255,255,255,0.12)", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontSize: 18, marginBottom: 4 }}>{stat.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{stat.value}</div>
              <div style={{ fontSize: 10, opacity: 0.8 }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Subject breakdown */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 14 }}>
        <p style={{ fontSize: 13, fontWeight: 800, color: T.text, margin: "0 0 12px" }}>Subject Performance</p>
        {Object.entries(progress).filter(([,v]) => v.total > 0).map(([subj, v]) => {
          const p = v.total > 0 ? Math.round((v.correct / v.total) * 100) : null;
          const color = p !== null && p >= 70 ? T.green : p !== null && p >= 50 ? T.gold : T.textMid;
          const subjIcon = { reading:"📖", writing:"✍️", language:"🔤", numeracy:"🔢", thinking:"🧠" }[subj] || "📚";
          return (
            <div key={subj} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{subjIcon} {subjectDisplayName(subj)}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color }}>{p !== null ? `${p}%` : `${v.correct}/${v.total}`}</span>
              </div>
              <div style={{ height: 6, background: T.surface, borderRadius: 99, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${p ?? 0}%`, background: color, borderRadius: 99, transition: "width .6s ease" }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Lumi insight — always show when there's activity; fallback guarantees non-empty body */}
      {totalQ > 0 && (
        <div style={{ background: `${T.accent}10`, border: `1.5px solid ${T.accent}33`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 16 }}>✨</span>
            <p style={{ fontSize: 13, fontWeight: 800, color: T.accent, margin: 0 }}>Lumi's Assessment</p>
          </div>
          {generating && !insight
            ? <p style={{ fontSize: 13, color: T.textMid }}>Generating insight...</p>
            : <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, margin: 0 }}><LumiMarkdown>{insight || buildFallbackInsight()}</LumiMarkdown></div>
          }
        </div>
      )}

      {/* Share buttons */}
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={copyReport}
          style={{ flex: 1, padding: 14, background: T.accent, border: "none", borderRadius: 12, color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          📋 Copy Report
        </button>
        <button onClick={() => {
          const url = encodeURIComponent("https://empirio.com.au");
          const text = encodeURIComponent(`${profileName || "My child"} just completed ${totalQ} questions on Empirio with ${totalC}/${totalQ} correct! Great NAPLAN prep app 📚`);
          window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
        }}
          style={{ padding: "14px 16px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, color: T.text, fontSize: 14, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          𝕏 Share
        </button>
      </div>

      {totalQ < 5 && (
        <p style={{ textAlign: "center", fontSize: 13, color: T.textDim, marginTop: 12 }}>Complete at least 10 questions to unlock the full report.</p>
      )}
    </div>
  );
}


// ── YEAR-ROUND SKILL BUILDER ──────────────────────────────────
// Keeps students engaged between exam cycles
// Daily micro-challenges, skill streaks, and curriculum-aligned practice

const SKILL_AREAS = [
  { id: "vocab",     label: "Vocabulary",     icon: "📖", color: "#8B5CF6", desc: "Build word power daily",      subject: "reading"  },
  { id: "grammar",   label: "Grammar",        icon: "✏️",  color: "#0D9488", desc: "Master language rules",      subject: "language" },
  { id: "mental",    label: "Mental Maths",   icon: "🔢", color: "#F59E0B", desc: "Speed and accuracy",          subject: "numeracy" },
  { id: "reasoning", label: "Reasoning",      icon: "🧠", color: "#EF4444", desc: "Critical thinking skills",    subject: "thinking" },
  { id: "spelling",  label: "Spelling",       icon: "🔤", color: "#10B981", desc: "Never misspell again",        subject: "language" },
  { id: "reading",   label: "Comprehension",  icon: "📚", color: "#3B82F6", desc: "Read faster, understand more", subject: "reading" },

// ─── HASS (ACARA-Aligned) ────────────────────────────────────────

{id:"h_history_y3",subject:"hass",years:["y3","y4"],title:"Our Community History",icon:"🏛️",color:"#EF4444",
explanation:"History helps us understand how communities have changed and stayed the same over time. Every community has a unique story shaped by the people who lived there. Aboriginal and Torres Strait Islander peoples have the world's oldest continuous cultures — over 60,000 years old.",
keyFacts:["Primary sources: letters, photos, objects FROM the time period","Secondary sources: books, documentaries ABOUT the time period","Chronology means arranging events in time order","Communities change through: migration, technology, laws and cultural exchange","Aboriginal and Torres Strait Islander peoples have been custodians of Country for 60,000+ years"],
example:"Investigating local history:\n1. What was here before our town existed?\n2. Who were the first peoples of this Country?\n3. How did the community grow and change?\n4. What buildings or places are heritage-listed?\n\nPrimary source: 1920s photograph of main street\nSecondary source: local council history book\nBoth give different perspectives on the SAME place",
diagramType:"timeline",practiceTopic:"History"},

{id:"h_geography_y3",subject:"hass",years:["y3","y4"],title:"Places & Their Features",icon:"🗺️",color:"#EF4444",
explanation:"Geography studies the features of places and how people interact with their environment. Every place on Earth has both natural features (mountains, rivers, climate) and human features (cities, roads, farms) that make it unique.",
keyFacts:["Natural features: landforms, waterways, vegetation, climate","Human features: settlements, infrastructure, land use","Location can be described absolutely (coordinates) or relatively (near/far)","Climate affects how people live, what they grow, and how they build","Australia's diverse geography includes tropical, desert, temperate and alpine regions"],
example:"Comparing two Australian places:\n\nSydney (coastal, temperate):\n→ Harbour, beaches, mild climate\n→ Dense urban population, tourism, finance hub\n→ Buildings designed for rain, not extreme heat\n\nAlice Springs (central desert):\n→ Red Rock, dry riverbeds, extreme heat\n→ Small population, tourism, cattle stations\n→ Buildings designed for heat management",
diagramType:"bargraph",practiceTopic:"Geography"},

{id:"h_civics_y5",subject:"hass",years:["y5","y6"],title:"Australian Democracy",icon:"🏛️",color:"#EF4444",
explanation:"Australia is a constitutional monarchy and parliamentary democracy. Citizens vote to elect representatives who make laws on their behalf. Understanding how our government works helps us participate as informed citizens.",
keyFacts:["Australia has three levels of government: Federal, State/Territory, Local","Parliament makes laws; the Executive (Government) puts them into action; Courts interpret them","Citizens 18+ must vote in Federal and State elections (compulsory voting)","The Constitution is the highest law — it cannot be changed without a referendum","Australia became a federation on 1 January 1901"],
example:"Three levels of government responsibilities:\n\nFederal (Canberra):\nDefence, immigration, Medicare, social security, foreign affairs\n\nState (e.g. NSW Parliament):\nSchools, hospitals, police, roads, public transport\n\nLocal (Your council):\nParks, libraries, rubbish collection, local roads, development approvals\n\nWhy three levels? Different issues need different scales of decision-making!",
diagramType:"bargraph",practiceTopic:"Civics"},

{id:"h_economics_y5",subject:"hass",years:["y5","y6"],title:"Economics & Business",icon:"💼",color:"#EF4444",
explanation:"Economics studies how people make decisions about scarce resources. Every choice has an opportunity cost — what you give up. Understanding supply, demand, and how businesses work helps you make smarter decisions and understand the world.",
keyFacts:["Scarcity: resources are limited but wants are unlimited","Opportunity cost: the next best alternative you give up with a choice","Supply and demand: price rises when demand exceeds supply","Consumer: person who buys goods/services; Producer: person who makes them","Australia exports iron ore, coal, natural gas, beef, wheat and education"],
example:"Supply and demand in action:\n\nScenario: A new popular video game releases\nDemand: Very high (everyone wants it)\nSupply: Limited (not enough made yet)\nResult: Price is high\n\nSix months later:\nDemand: Lower (fewer new buyers)\nSupply: Much higher (more produced)\nResult: Price drops\n\nThis happens with houses, concert tickets, and fuel too!",
diagramType:"supplydemand",practiceTopic:"Economics"},

{id:"h_history_y7",subject:"hass",years:["y7","y8"],title:"Ancient Civilisations",icon:"🏺",color:"#EF4444",
explanation:"Ancient civilisations — Egypt, Greece, Rome, China, Mesopotamia — shaped the modern world. They developed writing, mathematics, architecture, philosophy and governance systems we still use today. Studying them helps us understand where our culture, laws and ideas come from.",
keyFacts:["Civilisation requires: surplus food, specialised jobs, cities, writing, government","Mesopotamia (modern Iraq) had the world's first writing system (cuneiform) ~3200 BCE","Ancient Greece gave us democracy, the Olympic Games, philosophy and theatre","Ancient Rome gave us legal systems, engineering (roads, aqueducts), and Latin (root of many languages)","Historical evidence: artefacts, written records, oral histories, archaeological sites"],
example:"Legacy of Ancient Rome in Australia today:\n\nLaw: Innocent until proven guilty comes from Roman legal principles\nLanguage: English has thousands of Latin-derived words (e.g. video=I see)\nCalendar: Our months named after Roman gods (January=Janus, March=Mars)\nArchitecture: Columns, arches and domes in our parliament buildings\nGovernance: Senate, republic, constitution all Roman concepts\n\nRome fell 1,500 years ago — yet its influence surrounds us",
diagramType:"timeline",practiceTopic:"Ancient History"},

{id:"h_geography_y7",subject:"hass",years:["y7","y8","y9"],title:"Biomes & Environmental Change",icon:"🌿",color:"#EF4444",
explanation:"A biome is a large area with a characteristic climate, plants and animals. Human activity is altering biomes at unprecedented rates through deforestation, urbanisation and climate change. Understanding biomes helps us make informed decisions about environmental sustainability.",
keyFacts:["Major biomes: tropical rainforest, desert, grassland, temperate forest, tundra, ocean","Biomes are defined by climate (temperature + rainfall) not by location","Australia has unique biomes due to its isolation — 80% of species found nowhere else","Deforestation releases stored carbon, reducing biodiversity and disrupting water cycles","Sustainable development: meeting current needs without compromising future generations"],
example:"Why is the Amazon rainforest critically important?\n\n1. Biodiversity: Home to 10% of all Earth's species\n2. Climate: Absorbs 2 billion tonnes of CO2 per year\n3. Water: Releases 20 billion tonnes of water vapour daily (creates its own rain)\n4. Medicine: 25% of all medicines come from rainforest plants\n\nDeforestation rate: An area the size of a football field cleared every minute\nAt this rate: irreversible tipping point within decades\n\nThis is why it matters to everyone, not just Brazil",
diagramType:"foodweb",practiceTopic:"Geography"},

{id:"h_civics_y9",subject:"hass",years:["y9"],title:"Global Citizenship",icon:"🌐",color:"#EF4444",
explanation:"In an interconnected world, actions in one country affect people everywhere. Global citizenship means understanding your rights and responsibilities not just as an Australian, but as a member of humanity. Issues like climate change, human rights and global trade require international cooperation.",
keyFacts:["United Nations has 193 member states and upholds international law","Universal Declaration of Human Rights (1948): 30 fundamental rights for all humans","Globalisation: increased connection of economies, cultures and information worldwide","Australia is connected globally through trade (China is our largest partner), immigration and aid","Active citizenship: voting, volunteering, advocacy, informed consumption"],
example:"How global events affect everyday Australians:\n\nCOVID-19 pandemic:\nVirus originated overseas → spread globally in weeks\nSupply chains disrupted → supermarket shortages in Australia\nInternational travel stopped → tourism industry collapsed\nVaccines developed globally → Australia's rollout depended on international supply\n\nConclusion: No country is an island anymore (not even Australia!)",
diagramType:"bargraph",practiceTopic:"Civics"},


// ════════════════════════════════════════════════════════════════
// ACARA-VERIFIED ADDITIONS — Based on official ACARA PDFs Y3-Y9
// ════════════════════════════════════════════════════════════════

// ─── MATHS: NEW ACARA-VERIFIED CONCEPTS ─────────────────────────

{id:"m_odd_even",subject:"maths",years:["y3","y4"],title:"Odd & Even Numbers",icon:"🔢",color:"#3B82F6",
explanation:"Every whole number is either odd or even. Even numbers can be split into two equal groups with nothing left over. Odd numbers always have one left over. This matters in patterns, multiplication and algebra.",
keyFacts:["Even numbers end in 0, 2, 4, 6 or 8","Odd numbers end in 1, 3, 5, 7 or 9","Even + Even = Even. Odd + Odd = Even. Even + Odd = Odd","Even numbers are divisible by 2 with no remainder"],
example:"Is 348 odd or even?\nLook only at the last digit: 8\n8 is even → 348 is even\n\nIs 7,531 odd or even?\nLast digit: 1\n1 is odd → 7,531 is odd\n\nPattern: Even × Even = Even (4×6=24)\nEven × Odd = Even (4×3=12)\nOdd × Odd = Odd (3×5=15)",
diagramType:"numberline",practiceTopic:"Odd & Even Numbers",
realWorld:"Architects use even numbers to balance buildings symmetrically. Barcode scanners use odd/even patterns to detect errors. Your phone uses parity bits — based on odd/even — to check data isn't corrupted.",
thinkAbout:"If you add any two odd numbers together, is the result always odd or always even? Can you figure out WHY without just testing examples?"},

{id:"m_symmetry",subject:"maths",years:["y3","y4"],title:"Symmetry",icon:"🦋",color:"#3B82F6",
explanation:"A shape has line symmetry when one half is a mirror image of the other. The line of symmetry (mirror line) divides the shape into two identical parts. Some shapes have many lines of symmetry, others have none.",
keyFacts:["Line of symmetry: the mirror line that creates two identical halves","A square has 4 lines of symmetry","A rectangle has 2 lines of symmetry","A circle has infinitely many lines of symmetry","Letters like A, M, T, U, V, W, Y have vertical symmetry"],
example:"Lines of symmetry in regular shapes:\nEquilateral triangle: 3 lines\nSquare: 4 lines\nRegular pentagon: 5 lines\nRegular hexagon: 6 lines\nRule: A regular polygon with n sides has n lines of symmetry\n\nReal world: butterflies, leaves, faces and many logos use symmetry — our brains find it beautiful!",
diagramType:"angles",practiceTopic:"Symmetry"},

{id:"m_decimals_intro",subject:"maths",years:["y4","y5"],title:"Introducing Decimals",icon:"🔟",color:"#3B82F6",
explanation:"Decimals extend our place value system beyond the ones. The decimal point separates whole numbers from parts. Tenths (0.1) are one tenth of a whole. Hundredths (0.01) are one hundredth. This connects directly to fractions.",
keyFacts:["0.1 = 1/10 (one tenth)","0.01 = 1/100 (one hundredth)","0.5 = 5/10 = 1/2 (half)","0.25 = 25/100 = 1/4 (quarter)","Adding zeros after a decimal point does not change the value (0.5 = 0.50)"],
example:"Representing on a number line:\n0 ──── 0.1 ── 0.2 ── 0.3 ── ... ── 0.9 ──── 1\n\nConverting: 3/10 = 0.3, 7/10 = 0.7\n4/100 = 0.04, 37/100 = 0.37\n\nComparing: 0.6 vs 0.59\n0.60 vs 0.59 (add zero to compare)\n6 tenths > 5 tenths → 0.6 > 0.59\n(Many students think 0.59 is bigger because 59 > 6 — this is a very common mistake!)",
diagramType:"numberline",practiceTopic:"Decimals"},

{id:"m_factors",subject:"maths",years:["y5","y6"],title:"Factors & Multiples",icon:"🔢",color:"#3B82F6",
explanation:"A factor of a number divides it exactly with no remainder. A multiple is what you get when you multiply a number by a whole number. Finding factors and multiples is essential for working with fractions and algebra.",
keyFacts:["Factors of 12: 1, 2, 3, 4, 6, 12 (all divide into 12 exactly)","Multiples of 4: 4, 8, 12, 16, 20... (the 4 times table)","Every number has 1 and itself as factors","A prime number has exactly two factors: 1 and itself","HCF: Highest Common Factor, LCM: Lowest Common Multiple"],
example:"Factors of 24:\n1 × 24 = 24\n2 × 12 = 24\n3 × 8 = 24\n4 × 6 = 24\nFactors: 1, 2, 3, 4, 6, 8, 12, 24\n\nHCF of 24 and 36:\nFactors of 24: 1,2,3,4,6,8,12,24\nFactors of 36: 1,2,3,4,6,9,12,18,36\nCommon: 1,2,3,4,6,12 → HCF = 12",
diagramType:"multiplication",practiceTopic:"Factors & Multiples"},

{id:"m_prime_composite",subject:"maths",years:["y6","y7"],title:"Prime & Composite Numbers",icon:"🔢",color:"#3B82F6",
explanation:"A prime number has exactly two factors: 1 and itself. A composite number has more than two factors. 1 is neither prime nor composite. Prime numbers are the building blocks of all other numbers.",
keyFacts:["Primes: 2, 3, 5, 7, 11, 13, 17, 19, 23, 29...","2 is the only even prime number","Composite: 4, 6, 8, 9, 10, 12... (have more than 2 factors)","Every composite number can be written as a product of primes (prime factorisation)","Square numbers: 1, 4, 9, 16, 25, 36... (n × n)"],
example:"Is 37 prime?\nTest primes up to √37 ≈ 6.1\nDivide by 2: 37/2 = 18.5 (not exact)\nDivide by 3: 37/3 = 12.3 (not exact)\nDivide by 5: 37/5 = 7.4 (not exact)\nNo factors found → 37 is PRIME\n\nPrime factorisation of 60:\n60 = 2 × 30 = 2 × 2 × 15 = 2 × 2 × 3 × 5 = 2² × 3 × 5",
diagramType:"bargraph",practiceTopic:"Prime Numbers",
realWorld:"Internet security — every time you see a padlock in your browser — relies on the fact that multiplying two huge prime numbers is easy, but working backwards (factoring) takes millions of years even for supercomputers.",
thinkAbout:"There are infinitely many prime numbers. Euclid proved this over 2,000 years ago with a brilliantly simple argument. Can you imagine what the proof might look like?"},

{id:"m_bodmas",subject:"maths",years:["y6","y7"],title:"Order of Operations (BODMAS)",icon:"🧮",color:"#3B82F6",
explanation:"When a calculation has multiple operations, we must follow a specific order. BODMAS: Brackets, Orders (powers/roots), Division, Multiplication, Addition, Subtraction. Without this rule, everyone would get different answers.",
keyFacts:["B — Brackets first: (3+2) × 4 = 5 × 4 = 20","O — Orders (powers/roots): 2³ = 8","DM — Division and Multiplication: left to right","AS — Addition and Subtraction: left to right","When operations have equal priority: work left to right"],
example:"Without BODMAS: 3 + 4 × 2\nWrong: (3+4) × 2 = 14\nRight: 3 + (4×2) = 3 + 8 = 11\n\nComplex: 5 + 3² × (8-2) ÷ 9\nStep 1: Brackets → (8-2) = 6\nStep 2: Orders → 3² = 9\nStep 3: × and ÷ → 9 × 6 ÷ 9 = 6\nStep 4: + → 5 + 6 = 11",
diagramType:"numberline",practiceTopic:"Order of Operations"},

{id:"m_integers",subject:"maths",years:["y6","y7"],title:"Integers & Negative Numbers",icon:"🌡️",color:"#3B82F6",
explanation:"Integers include all whole numbers and their negatives: ...−3, −2, −1, 0, 1, 2, 3... Negative numbers represent quantities below zero — temperature, debt, altitude below sea level. They extend our number line in both directions.",
keyFacts:["Integers: all whole numbers including negatives and zero","On a number line, negative numbers are to the left of zero","Adding a negative is the same as subtracting","Subtracting a negative is the same as adding","Multiplying/dividing two negatives gives a positive"],
example:"Temperature: −5°C + 8°C = 3°C\n(Started at minus 5, warmed up 8 degrees)\n\nRules for signs:\n3 + (−5) = 3 − 5 = −2\n3 − (−5) = 3 + 5 = 8\n(−4) × (−3) = 12 (negative × negative = positive)\n(−4) × 3 = −12 (negative × positive = negative)\n\nOrder: −7 < −3 < 0 < 4 < 9",
diagramType:"numberline",practiceTopic:"Integers"},

{id:"m_fractions_decimals_pct",subject:"maths",years:["y6","y7"],title:"Fractions, Decimals & Percentages",icon:"🔄",color:"#3B82F6",
explanation:"Fractions, decimals and percentages are three different ways to express the same value. Being able to convert fluently between them is a fundamental skill for all of mathematics and everyday life.",
keyFacts:["Fraction to decimal: divide numerator by denominator","Decimal to percentage: multiply by 100","Percentage to decimal: divide by 100","Common conversions: 1/2=0.5=50%, 1/4=0.25=25%, 1/5=0.2=20%","3/8 = 3÷8 = 0.375 = 37.5%"],
example:"Convert 3/8 to a decimal and percentage:\n3 ÷ 8 = 0.375\n0.375 × 100 = 37.5%\n\nConvert 65% to a fraction and decimal:\n65% = 65/100 = 13/20 (simplified)\n65 ÷ 100 = 0.65\n\nWhich is bigger: 3/5 or 62%?\n3/5 = 0.6 = 60%\n60% < 62% → 62% is bigger",
diagramType:"fractions",practiceTopic:"Fractions Decimals Percentages"},

{id:"m_square_roots",subject:"maths",years:["y7","y8"],title:"Square Roots & Surds",icon:"√",color:"#3B82F6",
explanation:"The square root of a number is what you multiply by itself to get that number. √25 = 5 because 5×5=25. Perfect squares have whole number square roots. Other square roots are surds — irrational numbers that go on forever without repeating.",
keyFacts:["√n means: what number times itself equals n?","Perfect squares: 1, 4, 9, 16, 25, 36, 49, 64, 81, 100","√2 ≈ 1.414... (irrational — never ends or repeats)","Square and square root are inverse operations","√(a×b) = √a × √b"],
example:"Perfect square roots to memorise:\n√1=1, √4=2, √9=3, √16=4, √25=5\n√36=6, √49=7, √64=8, √81=9, √100=10\n\nEstimating √50:\n√49=7, √64=8\nSo √50 is between 7 and 8, closer to 7\n√50 ≈ 7.07\n\nPythagoras use: a²+b²=c²\n3²+4²=c² → 9+16=25 → c=√25=5",
diagramType:"pythagoras",practiceTopic:"Square Roots"},

{id:"m_expanding_algebra",subject:"maths",years:["y8","y9"],title:"Expanding & Factorising",icon:"📐",color:"#3B82F6",
explanation:"Expanding means removing brackets by multiplying. Factorising is the reverse — putting brackets back in by finding common factors. These are core algebraic skills for solving equations and simplifying expressions.",
keyFacts:["Expanding: a(b+c) = ab + ac","Expanding double brackets: (a+b)(c+d) = ac+ad+bc+bd","FOIL: First, Outer, Inner, Last for double brackets","Factorising: find the HCF and put it outside brackets","Difference of squares: a²−b² = (a+b)(a−b)"],
example:"Expand: 3(2x+5)\n= 3×2x + 3×5\n= 6x + 15\n\nExpand: (x+3)(x+2)\nF: x×x = x²\nO: x×2 = 2x\nI: 3×x = 3x\nL: 3×2 = 6\n= x² + 5x + 6\n\nFactorise: 6x + 15\nHCF of 6 and 15 = 3\n= 3(2x + 5)",
diagramType:"lineargraph",practiceTopic:"Expanding & Factorising"},

{id:"m_scientific_notation",subject:"maths",years:["y9"],title:"Scientific Notation",icon:"🔭",color:"#3B82F6",
explanation:"Scientific notation writes very large or very small numbers as a number between 1 and 10 multiplied by a power of 10. Scientists, astronomers and engineers use it constantly to avoid writing endless zeros.",
keyFacts:["Form: a × 10ⁿ where 1 ≤ a < 10","Large numbers: positive power (Earth mass = 5.97 × 10²⁴ kg)","Small numbers: negative power (virus size = 1.0 × 10⁻⁷ m)","To convert: count how many places the decimal moves","Multiplying: multiply the a values, add the powers"],
example:"Convert to scientific notation:\n3,500,000 = 3.5 × 10⁶ (moved decimal 6 places left)\n0.000047 = 4.7 × 10⁻⁵ (moved decimal 5 places right)\n\nDistance from Earth to Sun: 150,000,000 km\n= 1.5 × 10⁸ km\n\nSize of a hydrogen atom: 0.000000000053 m\n= 5.3 × 10⁻¹¹ m\n\nMultiply: (3 × 10⁴) × (2 × 10³) = 6 × 10⁷",
diagramType:"numberline",practiceTopic:"Scientific Notation"},

// ─── ENGLISH: NEW ACARA-VERIFIED CONCEPTS ───────────────────────

{id:"e_verbs_tense",subject:"english",years:["y3","y4"],title:"Verbs & Tense",icon:"⏱️",color:"#8B5CF6",
explanation:"Verbs are action or state words. Tense tells us WHEN the action happens: past, present or future. Choosing the right tense and keeping it consistent is one of the most important grammar skills in writing.",
keyFacts:["Past tense: describes what already happened (ran, wrote, ate)","Present tense: describes what is happening now (runs, writes, eats)","Future tense: describes what will happen (will run, will write)","Regular past tense adds -ed (walk → walked)","Irregular verbs change completely (go → went, see → saw)"],
example:"Regular verbs:\nwalk → walked → will walk\njump → jumped → will jump\n\nIrregular verbs (must memorise):\ngo → went → will go\nsee → saw → will see\nbring → brought → will bring\nhave → had → will have\n\nConsistency: Once you choose a tense, stick to it!\nWrong: She walked to school and sees her friend.\nRight: She walked to school and saw her friend.",
diagramType:"narrative",practiceTopic:"Verbs & Tense"},

{id:"e_speech_dialogue",subject:"english",years:["y4","y5"],title:"Direct & Reported Speech",icon:"💬",color:"#8B5CF6",
explanation:"Direct speech uses the exact words someone said, placed in quotation marks. Reported (indirect) speech retells what was said without quotation marks. Both are essential for narrative writing and comprehension.",
keyFacts:["Direct: exactly what was said, inside quotation marks","Reported: paraphrased, no quotation marks, pronoun shifts","Direct: She said, 'I am tired.'","Reported: She said that she was tired.","Commas separate dialogue from dialogue tags (he said, she replied)"],
example:"Direct speech rules:\nNew speaker = new line\nPunctuation inside quotation marks\n'Come here now,' said Mum.\n'Why?' asked Jake.\n'Because I said so,' she replied.\n\nConverting to reported speech:\nDirect: Tom said, 'I will finish tomorrow.'\nReported: Tom said that he would finish the next day.\nNote: 'will' → 'would', 'tomorrow' → 'the next day', 'I' → 'he'",
diagramType:"narrative",practiceTopic:"Direct Speech"},

{id:"e_modality",subject:"english",years:["y6","y7"],title:"Modality & Modal Verbs",icon:"🎭",color:"#8B5CF6",
explanation:"Modality expresses degrees of certainty, possibility, obligation or ability. Modal verbs like must, might, could, should, will express HOW CERTAIN or OBLIGATORY something is. Choosing the right modal completely changes the meaning and effect of a sentence.",
keyFacts:["High modality (certainty): must, will, shall, is/are","Medium modality (probability): should, would, ought to","Low modality (possibility): might, may, could, can","Modality in persuasive writing signals confidence","Modality in arguments affects how forceful claims appear"],
example:"Compare these sentences — same idea, different modality:\nHigh: You MUST eat vegetables. (obligation, no choice)\nMedium: You SHOULD eat vegetables. (recommended)\nLow: You COULD eat vegetables. (just a possibility)\n\nIn writing analysis:\n'Scientists believe the asteroid MIGHT impact Earth' (low certainty)\n'Scientists confirm the asteroid WILL impact Earth' (high certainty)\nThe choice of modal tells us how confident the writer is.",
diagramType:"narrative",practiceTopic:"Modality"},

{id:"e_rhetorical",subject:"english",years:["y7","y8","y9"],title:"Rhetorical Devices",icon:"🎤",color:"#8B5CF6",
explanation:"Rhetorical devices are techniques writers and speakers use to persuade, engage and influence their audience. Identifying these devices — and explaining their effect — is essential for text analysis tasks in selective school exams.",
keyFacts:["Rhetorical question: question asked for effect, not answer ('Who would disagree?')","Rule of three: three items for emphasis and rhythm ('life, liberty and happiness')","Repetition: repeating key words for impact ('never, never, never give up')","Anaphora: repeating at the start of sentences ('We shall fight... We shall defend...')","Emotive language: words that trigger emotional responses ('innocent victims')"],
example:"Analyse this sentence:\n'How can we, as a nation, stand by and watch innocent children suffer?'\n\nRhetorical question: 'How can we...?' — implies we cannot/should not\nEmotive language: 'innocent children', 'suffer' — creates sympathy\nInclusive pronoun: 'we' — makes reader feel responsible\nEffect: Creates urgency, guilt and a call to action\n\nThis is exactly the type of analysis selective school exams ask for.",
diagramType:"narrative",practiceTopic:"Rhetorical Devices",
realWorld:"Political speeches, advertising, court arguments, and TED talks all deliberately use rhetorical devices. Once you learn to recognise them, you'll never hear a speech the same way again.",
thinkAbout:"Why are three examples more persuasive than two or four? The 'rule of three' works across every language and culture — suggesting it's connected to how human working memory processes information."},

{id:"e_characterisation",subject:"english",years:["y7","y8"],title:"Characterisation",icon:"🎭",color:"#8B5CF6",
explanation:"Characterisation is how authors create and develop characters. Strong characterisation makes characters feel real and complex. Authors reveal character through what they say, do, think, look like, and how others react to them — known as STEAL.",
keyFacts:["STEAL: Speech, Thoughts, Effect on others, Actions, Looks","Direct characterisation: author tells you directly ('She was selfish')","Indirect: reader must infer from evidence (she grabbed the last piece)","Round characters: complex, change through the story","Flat characters: simple, unchanging, one-dimensional"],
example:"STEAL analysis of a character:\n\nSpeech: 'I deserve this more than anyone.' (arrogant)\nThoughts: She wondered if anyone would notice her kindness. (insecure)\nEffect on others: Students moved away when she sat down. (disliked)\nActions: She helped a younger student find their class. (kind)\nLooks: Her worn uniform suggested a difficult home life. (struggling)\n\nConclusion: A complex character — outwardly arrogant but inwardly insecure and genuinely kind. The contradiction makes her interesting.",
diagramType:"narrative",practiceTopic:"Characterisation"},

// ─── SCIENCE: NEW ACARA-VERIFIED CONCEPTS ───────────────────────

{id:"s_earth_rotation",subject:"science",years:["y3","y4"],title:"Earth's Rotation & Day/Night",icon:"🌍",color:"#10B981",
explanation:"Earth spins on its axis once every 24 hours, causing day and night. The side facing the Sun has day; the side facing away has night. Earth also orbits the Sun, which is why we have seasons and years.",
keyFacts:["Earth rotates west to east (which is why the Sun appears to rise in the east)","One full rotation = 24 hours = 1 day","Earth's axis is tilted at 23.5 degrees","The Moon orbits Earth, not the Sun","Time zones exist because different parts of Earth face the Sun at different times"],
example:"Imagine Earth as a spinning ball under a lamp (the Sun):\nThe lit side = day\nThe dark side = night\n\nAs Earth spins:\nAustralia faces the Sun → Australian daytime\nAustralia spins away → Australian night, while USA has day\n\nWhy does the Sun appear to move across the sky?\nEarth is rotating — the Sun is staying still!\nAustralia gets daylight about 10-12 hours depending on the season.",
diagramType:"solarsystem",practiceTopic:"Earth & Space"},

{id:"s_life_cycles",subject:"science",years:["y4","y5"],title:"Life Cycles",icon:"🦋",color:"#10B981",
explanation:"All living things go through a life cycle — birth, growth, reproduction and death. Different organisms have very different life cycles. Understanding life cycles helps us understand ecosystems, agriculture and conservation.",
keyFacts:["Complete metamorphosis: egg → larva → pupa → adult (butterfly, beetle)","Incomplete metamorphosis: egg → nymph → adult (grasshopper, cockroach)","Plants: seed → germination → growth → flowering → pollination → seed","Life cycles can take days (housefly) to thousands of years (bristlecone pine)","Reproduction ensures species survival"],
example:"Butterfly life cycle (complete metamorphosis):\n1. Egg: laid on a leaf (1-2 weeks)\n2. Larva (caterpillar): eats constantly to grow (2-4 weeks)\n3. Pupa (chrysalis): transformation occurs inside (1-2 weeks)\n4. Adult (butterfly): emerges, mates, lays eggs (2-4 weeks)\n\nFrog (also complete metamorphosis):\nEgg → Tadpole (has gills, lives in water) → Froglet → Adult frog (has lungs, lives on land)\n\nThe change in body form and habitat is remarkable!",
diagramType:"lifecycle",practiceTopic:"Life Cycles",
realWorld:"A caterpillar inside a chrysalis essentially dissolves into soup — its body breaks down almost entirely — and reforms as a butterfly. Researchers have found that some memories survive this transformation.",
thinkAbout:"Pacific salmon swim hundreds of kilometres upstream, spawn, and die — their bodies fertilising the rivers that feed the trees that shade the water that cools the eggs. Can you see how one life cycle feeds another?"},

{id:"s_adaptations",subject:"science",years:["y5","y6"],title:"Adaptations",icon:"🦘",color:"#10B981",
explanation:"An adaptation is a feature that helps an organism survive in its environment. Adaptations can be structural (physical features), behavioural (things they do) or physiological (internal processes). Australian wildlife has extraordinary adaptations.",
keyFacts:["Structural: camouflage, thick fur, long beak, large ears","Behavioural: migration, hibernation, nocturnal activity","Physiological: producing antifreeze, storing water","Animals adapted to their environment survive longer and reproduce more (natural selection)","Introduced species often lack local adaptations and can become invasive"],
example:"Adaptations of the red kangaroo (Australian desert):\nStructural:\n→ Large back feet for efficient hopping (uses less energy than walking)\n→ Light-coloured fur reflects sunlight, reduces heat\n→ Large ears radiate heat to cool blood\n\nBehavioural:\n→ Rests in shade during hottest part of day\n→ Most active at dawn and dusk\n\nPhysiological:\n→ Concentrates urine to conserve water\n→ Can slow digestion to survive drought\n\nResult: Perfectly suited for hot, dry conditions",
diagramType:"adaptations",practiceTopic:"Adaptations",
realWorld:"The thorny devil lizard channels dew along microscopic grooves on its scales directly to its mouth — it drinks through its skin. Engineers are studying this to design water collectors for drought-affected regions.",
thinkAbout:"Humans have almost no fur, can sweat, and can throw — three adaptations that made us extraordinary long-distance hunters. We didn't need to be fast. We could run prey to exhaustion over hours."},

{id:"s_classification",subject:"science",years:["y7","y8"],title:"Classification of Organisms",icon:"🔬",color:"#10B981",
explanation:"Scientists classify organisms into groups based on shared characteristics. The modern classification system (taxonomy) was developed by Carl Linnaeus. All life is organised into a hierarchy from broad (Kingdom) to specific (Species).",
keyFacts:["Hierarchy: Kingdom → Phylum → Class → Order → Family → Genus → Species","Mnemonic: King Philip Came Over For Good Soup","All species have a two-part Latin name (binomial nomenclature)","Humans are Homo sapiens (genus=Homo, species=sapiens)","5 kingdoms: Animals, Plants, Fungi, Protists, Bacteria"],
example:"Classification of a domestic cat:\nKingdom: Animalia (animal)\nPhylum: Chordata (has a backbone)\nClass: Mammalia (warm-blooded, feeds young with milk)\nOrder: Carnivora (meat eater)\nFamily: Felidae (cats)\nGenus: Felis\nSpecies: catus\nFull name: Felis catus\n\nA lion is Panthera leo — same family (Felidae) but different genus, showing they are related but distinct species.",
diagramType:"classification",practiceTopic:"Classification",
realWorld:"Scientists are still discovering new species at a remarkable rate — about 15,000 per year. In 2023, researchers found a new species of whale. Classification is ongoing, not finished.",
thinkAbout:"A bat and a bird both have wings and fly — but bats are mammals and birds are their own class. Classification ignores surface similarities and looks at deeper shared ancestry. What does that tell us about evolution?"},

{id:"s_energy",subject:"science",years:["y8","y9"],title:"Energy Forms & Transfer",icon:"⚡",color:"#10B981",
explanation:"Energy cannot be created or destroyed — it can only change form. This is the Law of Conservation of Energy. Understanding energy forms and transfers is fundamental to all of physics and explains how everything from toasters to nuclear reactors work.",
keyFacts:["Kinetic energy: energy of motion (a moving ball, flowing water)","Potential energy: stored energy (a raised object, a stretched spring, chemical bonds)","Thermal energy: heat (faster particle motion)","Light, sound and electrical energy are also common forms","Energy transfer: moving energy from one object to another","Energy transformation: changing energy from one form to another"],
example:"Energy transformations in a power station:\nChemical energy (coal)\n→ Thermal energy (burning)\n→ Kinetic energy (steam turning turbine)\n→ Electrical energy (generator)\n→ Light/heat/kinetic at your home\n\nIn a car:\nChemical (petrol) → Thermal (combustion) → Kinetic (movement)\nPlus wasted energy as heat, sound, vibration\n\nKey point: Total energy is always conserved — some just becomes unusable heat.",
diagramType:"energy",practiceTopic:"Energy",
realWorld:"Your body converts chemical energy (food) into heat so efficiently that you warm the air around you. A room full of people can heat a building. Some schools in Sweden are heated partly by body heat from students.",
thinkAbout:"The Sun releases energy through nuclear fusion — combining hydrogen atoms into helium. This has been happening for 4.6 billion years. What does that tell us about the scale of energy locked inside atoms?"},

{id:"s_rocks",subject:"science",years:["y8","y9"],title:"Rocks & Geological Processes",icon:"🪨",color:"#10B981",
explanation:"Rocks form through different processes and can be transformed from one type to another over millions of years. The rock cycle shows how igneous, sedimentary and metamorphic rocks are all connected through heat, pressure and erosion.",
keyFacts:["Igneous: formed from cooled magma/lava (granite, basalt)","Sedimentary: formed from compressed sediments (sandstone, limestone, coal)","Metamorphic: formed by heat and pressure transforming existing rock (marble, slate)","The rock cycle shows rocks constantly transforming over millions of years","Fossils are found in sedimentary rock — organisms trapped in sediment"],
example:"How sandstone forms (sedimentary):\n1. Mountains erode over millions of years\n2. Sand and silt carried by rivers to the sea\n3. Sediment settles on the seafloor in layers\n4. Upper layers compress lower layers\n5. Minerals cement particles together\n6. Sandstone forms (takes millions of years)\n\nHow marble forms (metamorphic):\nLimestone + intense heat and pressure deep in Earth → marble\nThis is why marble has a crystalline texture — the original shells and coral in limestone recrystallise!",
diagramType:"rocks",practiceTopic:"Rocks"},

// ─── HASS: NEW ACARA-VERIFIED CONCEPTS ──────────────────────────

{id:"h_first_fleet",subject:"hass",years:["y4","y5"],title:"First Fleet & Colonisation",icon:"⛵",color:"#EF4444",
explanation:"In 1788, the First Fleet of 11 ships arrived in Sydney Cove, beginning British colonisation of Australia. This event transformed the continent forever — for Aboriginal and Torres Strait Islander peoples, it was the beginning of dispossession and enormous loss.",
keyFacts:["First Fleet arrived 26 January 1788 (now Australia Day)","778 convicts transported from Britain","Captain Arthur Phillip was the first Governor of NSW","Aboriginal peoples had lived on the continent for over 60,000 years before 1788","Colonisation led to loss of land, culture and life for First Nations peoples"],
example:"Two perspectives on 1788:\n\nBritish perspective:\n→ Claimed land for the Crown\n→ Solution to overcrowded prisons\n→ Saw land as uninhabited (terra nullius — now legally rejected)\n→ Establishment of a colony\n\nAboriginal perspective:\n→ Invasion of sovereign Country\n→ Dispossession from ancestral lands\n→ Introduction of disease (smallpox killed thousands)\n→ Destruction of culture and communities\n\nHistorians always consider multiple perspectives on past events.",
diagramType:"timeline",practiceTopic:"Australian History",
realWorld:"The Eora people had lived in Sydney Harbour for over 60,000 years when the First Fleet arrived. They had detailed knowledge of tides, seasons, food sources, and medicine that Europeans had to learn from scratch.",
thinkAbout:"The colonists claimed 'terra nullius' (empty land) — but 750,000 Aboriginal people already lived in Australia. How do the words we use to describe historical events shape how we understand them?"},

{id:"h_federation",subject:"hass",years:["y6","y7"],title:"Australian Federation",icon:"🇦🇺",color:"#EF4444",
explanation:"On 1 January 1901, six separate British colonies became one nation — the Commonwealth of Australia. Federation created our federal system of government with a Constitution. It was a remarkable achievement but excluded Aboriginal Australians and restricted Asian immigration.",
keyFacts:["Federation date: 1 January 1901","Edmund Barton became Australia's first Prime Minister","The Constitution divides powers between federal and state governments","The White Australia Policy was introduced at federation","Aboriginal Australians were excluded from the census until 1967"],
example:"Why did the colonies federate?\n\n1. Defence: One army stronger than six separate ones\n2. Trade: Free movement of goods between colonies\n3. Immigration: Common national border control\n4. Infrastructure: National rail network needed\n\nHow it happened:\n→ Colonial representatives wrote a Constitution (1890s)\n→ Citizens voted in referendums to accept it\n→ Britain's Parliament passed the Commonwealth of Australia Constitution Act\n→ Queen Victoria signed it on 9 July 1900\n→ Effective 1 January 1901",
diagramType:"timeline",practiceTopic:"Australian History",
realWorld:"Australia's Constitution was written in the 1890s — before women could vote, before Aboriginal Australians were counted in the census, before aeroplanes existed. We still live under that document today.",
thinkAbout:"Federation gave Australia one army, one currency, and one set of trade rules. But it also introduced the White Australia Policy. Can an event be both a nation-building achievement and a moral failure at the same time?"},

{id:"h_water_world",subject:"hass",years:["y7","y8"],title:"Water in the World",icon:"💧",color:"#EF4444",
explanation:"Water is Earth's most vital resource. Freshwater is scarce — only 3% of Earth's water is fresh, and most of that is frozen. Australia is the driest inhabited continent. Understanding water distribution, scarcity and management is critical for our future.",
keyFacts:["97% of Earth's water is saltwater (oceans)","2% is frozen in ice caps and glaciers","Only 1% is accessible freshwater (rivers, lakes, groundwater)","Australia's rainfall is highly variable and unreliable","The Murray-Darling Basin provides water for 40% of Australian agriculture"],
example:"Water scarcity case study — Cape Town, South Africa (2018):\nCape Town came within days of running out of water ('Day Zero')\nCauses: drought, population growth, inefficient use\nResponse: Level 6B water restrictions\n→ 50 litres per person per day (Australians use 340L on average)\n→ Agriculture cut back\n→ Desalination plants built\nResult: Day Zero was narrowly avoided\n\nLesson: Water scarcity can happen anywhere — planning, conservation and infrastructure are essential.",
diagramType:"bargraph",practiceTopic:"Geography",
realWorld:"By 2050, an estimated 5 billion people will face water scarcity for at least one month per year. Australia is already the driest inhabited continent. Water is increasingly called 'the oil of the 21st century'.",
thinkAbout:"Water falls as rain for free, yet people in some cities pay for it and others don't. Who should own water — governments, private companies, or is it a human right? This debate is shaping global politics right now."},

{id:"h_medieval",subject:"hass",years:["y8","y9"],title:"Medieval World",icon:"🏰",color:"#EF4444",
explanation:"The medieval period (roughly 500-1500 CE) shaped the modern world. Feudalism organised society, the Catholic Church held enormous power, the Black Death transformed Europe, and the Renaissance began. Trade routes connected Europe, Asia and Africa.",
keyFacts:["Feudalism: King → Lords → Knights → Serfs (peasants)","Catholic Church was the most powerful institution in medieval Europe","Black Death (1347-1351) killed 30-60% of Europe's population","Trade routes (Silk Road) connected Europe with China and India","The Renaissance (1300s-1600s) began in Italy — a rebirth of art and learning"],
example:"The Black Death in perspective:\n→ Caused by Yersinia pestis bacteria (spread by fleas on rats)\n→ Reached Europe from Asia via trade ships (1347)\n→ Killed 75-200 million people worldwide\n→ 30-60% of Europe's population dead within 5 years\n\nUnexpected consequences:\n→ Labour shortage → serfs gained bargaining power → feudalism weakened\n→ Church's authority questioned (prayers didn't stop it)\n→ Scientific thinking about disease began\n→ The crisis helped trigger the Renaissance\n\nA catastrophe that accidentally accelerated human progress.",
diagramType:"timeline",practiceTopic:"Medieval History"},

];

// Daily challenge questions — 5 questions per day per skill
const DAILY_CHALLENGES = {
  vocab: [
    { q: "What does 'benevolent' mean?", options: ["Kind and generous","Strict and harsh","Quiet and shy","Loud and angry"], answer: 0, explanation: "'Benevolent' comes from Latin 'bene' (good) + 'volent' (wishing). A benevolent person wishes good for others and is kind and generous." },
    { q: "Which word means the opposite of 'transparent'?", options: ["Clear","Obvious","Opaque","Shiny"], answer: 2, explanation: "'Opaque' means not allowing light through, not transparent. If something is opaque, you cannot see through it." },
    { q: "What does 'meticulous' mean?", options: ["Careless","Very careful about detail","Quick to finish","Loud and boisterous"], answer: 1, explanation: "A meticulous person pays great attention to detail and is very careful and precise in everything they do." },
    { q: "Choose the correct meaning of 'persevere':", options: ["Give up easily","Continue despite difficulty","Move very quickly","Speak very loudly"], answer: 1, explanation: "To persevere means to continue doing something despite difficulty or delay in achieving success. It is a quality of determination and resilience." },
    { q: "What does 'eloquent' mean?", options: ["Poorly spoken","Fluent and persuasive in speech","Extremely quiet","Difficult to understand"], answer: 1, explanation: "An eloquent person speaks or writes in a fluent and persuasive way, expressing ideas clearly and effectively." },
  ],
  mental: [
    { q: "What is 17 × 8?", options: ["126","136","144","132"], answer: 1, explanation: "17 × 8: think 20 × 8 = 160, minus 3 × 8 = 24. So 160 - 24 = 136." },
    { q: "What is 25% of 180?", options: ["40","45","50","35"], answer: 1, explanation: "25% = ¼. 180 ÷ 4 = 45." },
    { q: "144 ÷ 12 = ?", options: ["10","11","12","14"], answer: 2, explanation: "144 ÷ 12 = 12. Remember: 12 × 12 = 144." },
    { q: "What is 0.75 as a fraction?", options: ["3/5","2/3","3/4","4/5"], answer: 2, explanation: "0.75 = 75/100 = 3/4. To convert: 75 ÷ 25 = 3, 100 ÷ 25 = 4." },
    { q: "What is 15% of 60?", options: ["6","8","9","12"], answer: 2, explanation: "10% of 60 = 6. 5% of 60 = 3. 15% = 6 + 3 = 9." },
  ],
  grammar: [
    { q: "Which sentence is correct?", options: ["Me and Jake went shopping","Jake and I went shopping","Jake and me went shopping","I and Jake went shopping"], answer: 1, explanation: "Use 'I' as the subject of a sentence. Remove 'Jake and' — you'd say 'I went shopping', not 'Me went shopping'. So: 'Jake and I went shopping'." },
    { q: "Choose the correct word: 'There are ___ students in the class.'", options: ["less","fewer","more less","lesser"], answer: 1, explanation: "Use 'fewer' for things you can count (students, apples). Use 'less' for things you cannot count (water, time). Students can be counted, so use 'fewer'." },
    { q: "Which is the correct plural of 'criterion'?", options: ["criterions","criterias","criteria","criterium"], answer: 2, explanation: "'Criterion' is from Greek and has an irregular plural: 'criteria'. Many academic/scientific words from Greek/Latin follow this pattern." },
    { q: "Identify the adverb: 'She spoke softly to the child.'", options: ["She","spoke","softly","child"], answer: 2, explanation: "'Softly' is an adverb — it modifies the verb 'spoke' by describing HOW she spoke. Adverbs often (but not always) end in '-ly'." },
    { q: "Which sentence uses the apostrophe correctly?", options: ["The dog's are barking","The dogs bone is here","The dog's bone is here","The dogs' is barking"], answer: 2, explanation: "'The dog's bone' — the apostrophe shows possession (the bone belongs to the dog). No apostrophe for plurals: 'The dogs are barking'." },
  ],
  reasoning: [
    { q: "If all birds can fly, and a penguin is a bird, what must be true?", options: ["Penguins can fly","This argument has a false premise","Penguins are not birds","Birds cannot fly"], answer: 1, explanation: "The premise 'all birds can fly' is false — penguins are birds but cannot fly. This is a logical fallacy based on a false universal statement." },
    { q: "What comes next: 1, 4, 9, 16, 25, ___?", options: ["30","35","36","49"], answer: 2, explanation: "These are perfect squares: 1²=1, 2²=4, 3²=9, 4²=16, 5²=25, 6²=36." },
    { q: "If A > B and B > C, which must be true?", options: ["C > A","A > C","B > A","C = A"], answer: 1, explanation: "If A is greater than B, and B is greater than C, then by transitivity, A must be greater than C. This is a basic logical deduction." },
    { q: "Tom is older than Sam. Sam is older than Jess. Who is youngest?", options: ["Tom","Sam","Jess","Cannot tell"], answer: 2, explanation: "Tom > Sam > Jess. Following the chain, Jess is younger than Sam, who is younger than Tom. Jess is youngest." },
    { q: "A bag has 3 red and 2 blue balls. What is the probability of picking red?", options: ["2/5","3/5","1/2","3/2"], answer: 1, explanation: "Probability = favourable outcomes ÷ total outcomes. Red balls = 3, total = 5. P(red) = 3/5." },
  ],
};

function SkillBuilder({ yearId, onClose }) {
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [qIdx, setQIdx]                   = useState(0);
  const [chosen, setChosen]               = useState(null);
  const [sessionScore, setSessionScore]   = useState(0);
  const [done, setDone]                   = useState(false);

  // Load today's streak per skill
  const today = new Date().toDateString();
  const skillStreaks = JSON.parse(localStorage.getItem("lumio_skill_streaks") || "{}");

  function startSkill(skill) {
    setSelectedSkill(skill);
    setQIdx(0);
    setChosen(null);
    setSessionScore(0);
    setDone(false);
  }

  const questions = selectedSkill ? (DAILY_CHALLENGES[selectedSkill.id] || []).filter(isMcqItem) : [];

  function handleAnswer(i) {
    if (chosen !== null) return;
    setChosen(i);
    if (i === questions[qIdx].answer) setSessionScore(s => s + 1);
  }

  function nextQ() {
    if (qIdx < questions.length - 1) {
      setQIdx(q => q + 1);
      setChosen(null);
    } else {
      // Save streak
      const updated = { ...skillStreaks, [selectedSkill.id]: { date: today, streak: (skillStreaks[selectedSkill.id]?.streak || 0) + 1 } };
      localStorage.setItem("lumio_skill_streaks", JSON.stringify(updated));
      celebrate({ intensity: 'normal' });
      setDone(true);
    }
  }

  // Done screen
  if (done && selectedSkill) return (
    <div style={{ padding: "40px 24px", textAlign: "center", animation: "fadeUp .3s ease" }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
      <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 28, color: T.text, marginBottom: 8 }}>
        {sessionScore === questions.length ? "Perfect Score!" : "Well done!"}
      </h2>
      <p style={{ fontSize: 16, color: T.textMid, marginBottom: 8 }}>{sessionScore}/{questions.length} correct</p>
      <p style={{ fontSize: 14, color: T.accent, fontWeight: 700, marginBottom: 28 }}>
        🔥 {selectedSkill.label} streak: {(skillStreaks[selectedSkill.id]?.streak || 1)} day{(skillStreaks[selectedSkill.id]?.streak || 1) !== 1 ? "s" : ""}
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button onClick={() => setSelectedSkill(null)} style={{ padding: "12px 24px", background: T.accent, border: "none", borderRadius: 12, color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Try Another Skill
        </button>
        <button onClick={onClose} style={{ padding: "12px 24px", background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, color: T.text, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Back to Home
        </button>
      </div>
    </div>
  );

  // Question screen
  if (selectedSkill && questions.length > 0) {
    const q = questions[qIdx];
    return (
      <div style={{ padding: "24px 20px 80px", maxWidth: 700, margin: "0 auto", width: "100%", animation: "fadeUp .3s ease" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <button onClick={() => setSelectedSkill(null)} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, width: 32, height: 32, color: T.textMid, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>←</button>
          <div>
            <p style={{ fontSize: 13, fontWeight: 800, color: selectedSkill.color, margin: 0 }}>{selectedSkill.icon} {selectedSkill.label}</p>
            <p style={{ fontSize: 11, color: T.textDim, margin: 0 }}>Q{qIdx+1} of {questions.length}</p>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
            {questions.map((_, i) => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: i < qIdx ? T.green : i === qIdx ? selectedSkill.color : T.border }} />)}
          </div>
        </div>

        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "20px", marginBottom: 16 }}>
          <p style={{ fontSize: 16, color: T.text, fontWeight: 600, lineHeight: 1.7, margin: 0 }}>{q.q}</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
          {q.options.map((opt, i) => {
            let bg = T.card, border = T.border, color = T.text;
            if (chosen !== null) {
              if (i === q.answer) { bg = `${T.green}12`; border = T.green; color = T.green; }
              else if (i === chosen) { bg = `${T.red}10`; border = T.red; color = T.red; }
            } else if (chosen === null) {
              bg = T.card; border = T.border;
            }
            return (
              <div key={i} onClick={() => handleAnswer(i)} style={{ background: bg, border: `2px solid ${border}`, borderRadius: 12, padding: "13px 16px", display: "flex", alignItems: "center", gap: 12, cursor: chosen === null ? "pointer" : "default", transition: "all .15s" }}>
                <span style={{ width: 28, height: 28, borderRadius: 8, background: chosen !== null && i === q.answer ? T.green : chosen !== null && i === chosen ? T.red : T.surface, color: chosen !== null ? "white" : T.textMid, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                  {String.fromCharCode(65+i)}
                </span>
                <span style={{ fontSize: 14, color, fontWeight: chosen !== null && i === q.answer ? 700 : 500 }}>{opt}</span>
                {chosen !== null && i === q.answer && <span style={{ marginLeft: "auto" }}>✅</span>}
                {chosen !== null && i === chosen && i !== q.answer && <span style={{ marginLeft: "auto" }}>❌</span>}
              </div>
            );
          })}
        </div>

        {chosen !== null && (
          <div>
            <div style={{ background: T.surface, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
              <MultilineText text={q.explanation} style={{ fontSize:13, color:T.textMid, lineHeight:1.6 }} />
            </div>
            <button onClick={nextQ} style={{ width: "100%", padding: 14, background: T.accent, border: "none", borderRadius: 12, color: "white", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {qIdx < questions.length - 1 ? "Next →" : "See Results →"}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Skill selection
  return (
    <div style={{ padding: "24px 20px 80px", maxWidth: 800, margin: "0 auto", width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onClose} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, width: 32, height: 32, color: T.textMid, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>←</button>
        <div>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, color: T.text, margin: 0 }}>Daily Skill Builder</h2>
          <p style={{ fontSize: 12, color: T.textMid, margin: 0 }}>5 questions · builds year-round mastery</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
        {SKILL_AREAS.filter(s => s.id && s.label && s.desc).slice(0,6).map(skill => {
          const skillData = skillStreaks[skill.id];
          const doneToday = skillData?.date === today;
          const streak = skillData?.streak || 0;
          return (
            <button key={skill.id}
              onClick={() => startSkill(skill)}
              disabled={doneToday}
              style={{
                background: doneToday ? `${T.green}10` : T.card,
                border: `2px solid ${doneToday ? T.green : T.border}`,
                borderRadius: 14, padding: "18px 16px",
                cursor: doneToday ? "default" : "pointer",
                transition: "all .2s", position: "relative",
                textAlign: "left", width: "100%",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
              onMouseOver={e => { if (!doneToday) { e.currentTarget.style.borderColor = skill.color; e.currentTarget.style.transform = "translateY(-2px)"; }}}
              onMouseOut={e => { e.currentTarget.style.borderColor = doneToday ? T.green : T.border; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              {doneToday && <div style={{ position: "absolute", top: 8, right: 8, fontSize: 16 }}>✅</div>}
              <div style={{ fontSize: 28, marginBottom: 8 }}>{skill.icon}</div>
              <p style={{ fontSize: 14, fontWeight: 800, color: T.text, margin: "0 0 4px" }}>{skill.label}</p>
              <p style={{ fontSize: 12, color: T.textMid, margin: "0 0 10px", lineHeight: 1.4 }}>{skill.desc}</p>
              {streak > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 12 }}>🔥</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.gold }}>{streak} day streak</span>
                </div>
              )}
              {!doneToday && (
                <div style={{ marginTop: 10, padding: "6px 10px", background: `${skill.color}15`, borderRadius: 8, textAlign: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: skill.color }}>Start →</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ── AI BUG PIPELINE ───────────────────────────────────────────
// Smart bug collection → AI diagnosis → auto-fix queue

async function diagnoseBugWithAI(report, serverUrl) {
  try {
    const prompt = `You are a code quality analyst for Empirio, an Australian exam prep app.

A user has reported a bug:
- Question ID: ${report.id || "unknown"}
- Question text: "${report.question || "unknown"}"
- Reason: ${report.reason}
- Notes: ${report.note || "none"}
- Year level: ${report.yearLevel}
- Topic: ${report.topic}
- Timestamp: ${report.at || report.receivedAt}

Diagnose this bug and respond ONLY with JSON (no markdown):
{
  "category": "wrong_answer|typo|unclear_question|missing_explanation|difficulty_mismatch|other",
  "severity": "critical|high|medium|low",
  "diagnosis": "one sentence explaining the likely cause",
  "suggested_fix": "one sentence describing how to fix it",
  "confidence": "high|medium|low",
  "auto_fixable": true or false
}`;

    const res = await fetch(`${serverUrl}/api/chat`, {
      method: "POST", headers: { "Content-Type": "application/json", ...(await authHeaders()) },
      body: JSON.stringify({ model: "claude-haiku-5-5", max_tokens: 300, messages: [{ role: "user", content: prompt }] }),
    });
    const data = await res.json();
    const text = data.content?.[0]?.text || "{}";
    return JSON.parse(text.replace(/```json|```/g, "").trim());
  } catch (e) {
    return { category: "other", severity: "low", diagnosis: "Could not auto-diagnose", confidence: "low", auto_fixable: false };
  }
}

function BugPipelineDashboard({ serverUrl, onClose }) {
  const [reports, setReports]     = useState([]);
  const [diagnoses, setDiagnoses] = useState({});
  const [loading, setLoading]     = useState(true);
  const [diagnosing, setDiagnosing] = useState(false);
  // The admin key is typed in here (kept for this browser tab only), never built into the app: a key in the bundle
  // lets anyone read every submitted report. It must match ADMIN_KEY set on the Railway server.
  const [adminKey, setAdminKey]   = useState(() => { try { return sessionStorage.getItem('lumio_admin_key') || ''; } catch { return ''; } });
  const [keyDraft, setKeyDraft]   = useState('');
  const [keyError, setKeyError]   = useState('');

  useEffect(() => {
    if (adminKey) loadReports(); else setLoading(false);
  }, [adminKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadReports() {
    setLoading(true);
    try {
      // Load from Railway
      const res = await fetch(`${serverUrl}/api/reports?key=${encodeURIComponent(adminKey)}`, {
        headers: { Accept: "application/json" },
      });
      if (res.status === 401 || res.status === 503) {
        setKeyError(res.status === 401 ? 'That key was not accepted.' : 'ADMIN_KEY is not set on the server yet.');
        try { sessionStorage.removeItem('lumio_admin_key'); } catch {}
        setAdminKey(''); setLoading(false); return;
      }
      const data = await res.json();
      const reportsArr = Array.isArray(data) ? data : (data.reports || []);
      setReports([...reportsArr].reverse());
    } catch(e) {
      // Fall back to localStorage
      let local; try { const _l = JSON.parse(localStorage.getItem("lumio_reports") || "[]"); local = Array.isArray(_l) ? _l : []; } catch { local = []; }
      setReports([...local].reverse());
    }
    setLoading(false);
  }

  async function diagnoseAll() {
    setDiagnosing(true);
    const newDiagnoses = { ...diagnoses };
    for (const report of reports.slice(0, 10)) {
      const key = report.id + (report.at || report.receivedAt || "");
      if (!newDiagnoses[key]) {
        newDiagnoses[key] = await diagnoseBugWithAI(report, serverUrl);
        setDiagnoses({ ...newDiagnoses });
      }
    }
    setDiagnosing(false);
  }

  const severityColor = { critical: T.red, high: "#F97316", medium: T.gold, low: T.green };
  const categoryIcon  = { wrong_answer: "❌", typo: "✏️", unclear_question: "❓", missing_explanation: "📝", difficulty_mismatch: "⚖️", other: "🔧" };

  return (
    <div style={{ padding: "20px", minHeight: "100%", background: T.bg }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onClose} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, width: 32, height: 32, color: T.textMid, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>←</button>
        <div>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, color: T.text, margin: 0 }}>🔧 Bug Pipeline</h2>
          <p style={{ fontSize: 12, color: T.textMid, margin: 0 }}>AI-powered bug diagnosis</p>
        </div>
        {!adminKey && (
          <form onSubmit={e => { e.preventDefault(); const k = keyDraft.trim(); if (!k) return; setKeyError(''); try { sessionStorage.setItem('lumio_admin_key', k); } catch {} setAdminKey(k); }} style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <input type="password" aria-label="Admin key" placeholder="Admin key" value={keyDraft} onChange={e => setKeyDraft(e.target.value)} style={{ padding:"7px 10px", border:`1px solid ${T.border}`, borderRadius:8, fontSize:13 }} />
            <button type="submit" style={{ padding:"7px 12px", background:T.accent, color:"white", border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>Load reports</button>
            {keyError && <span style={{ fontSize:12, color:T.red }}>{keyError}</span>}
          </form>
        )}
        <button onClick={diagnoseAll} disabled={diagnosing || reports.length === 0}
          style={{ marginLeft: "auto", padding: "8px 14px", background: diagnosing ? T.border : T.accent, border: "none", borderRadius: 8, color: diagnosing ? T.textDim : "white", fontSize: 12, fontWeight: 700, cursor: diagnosing ? "not-allowed" : "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {diagnosing ? "Analysing..." : "✨ Diagnose All"}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: T.textMid }}>Loading reports...</div>
      ) : reports.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
          <p style={{ color: T.textMid, fontSize: 14 }}>No bug reports yet</p>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: 13, color: T.textMid, marginBottom: 14 }}>{reports.length} report{reports.length !== 1 ? "s" : ""} total</p>
          {reports.map((report, i) => {
            const key = report.id + (report.at || report.receivedAt || "");
            const dx = diagnoses[key];
            return (
              <div key={i} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: dx ? 10 : 0 }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>🚩</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                      {report.yearLevel && <span style={{ fontSize: 11, fontWeight: 700, background: T.accentLo, color: T.accent, padding: "2px 7px", borderRadius: 99 }}>{report.yearLevel}</span>}
                      {report.topic && <span style={{ fontSize: 11, fontWeight: 700, background: T.surface, color: T.textMid, padding: "2px 7px", borderRadius: 99 }}>{report.topic}</span>}
                    </div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: "0 0 3px" }}>{report.reason || "No reason given"}</p>
                    {report.question && <p style={{ fontSize: 12, color: T.textMid, margin: "0 0 3px", fontStyle: "italic" }}>"{report.question.slice(0, 80)}..."</p>}
                    {report.note && <p style={{ fontSize: 12, color: T.textMid, margin: 0 }}>💬 {report.note}</p>}
                    <p style={{ fontSize: 11, color: T.textDim, margin: "4px 0 0" }}>{report.at || report.receivedAt ? new Date(report.at || report.receivedAt).toLocaleString("en-AU") : ""}</p>
                  </div>
                </div>

                {/* AI Diagnosis */}
                {dx && (
                  <div style={{ background: `${severityColor[dx.severity] || T.gold}10`, border: `1px solid ${severityColor[dx.severity] || T.gold}33`, borderRadius: 8, padding: "10px 12px", marginTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 14 }}>{categoryIcon[dx.category] || "🔧"}</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: severityColor[dx.severity] || T.gold, textTransform: "uppercase" }}>{dx.severity} · {dx.category?.replace(/_/g, " ")}</span>
                      <span style={{ marginLeft: "auto", fontSize: 11, color: T.textDim }}>Confidence: {dx.confidence}</span>
                    </div>
                    <p style={{ fontSize: 12, color: T.text, margin: "0 0 4px", fontWeight: 600 }}>Diagnosis: {dx.diagnosis}</p>
                    <p style={{ fontSize: 12, color: T.textMid, margin: 0 }}>Fix: {dx.suggested_fix}</p>
                    {dx.auto_fixable && (
                      <div style={{ marginTop: 8, background: `${T.green}15`, borderRadius: 6, padding: "4px 8px", display: "inline-block" }}>
                        <span style={{ fontSize: 11, color: T.green, fontWeight: 700 }}>✓ Auto-fixable</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// LUMI PARENT ALERT AGENT
// ═══════════════════════════════════════════════════════════════

function trackSession(subjectId, correct, total, profileId, mins) {
  const today = new Date().toDateString();
  const key = profileKey("lumio_activity", profileId);
  let history; try { const _p = JSON.parse(localStorage.getItem(key) || "[]"); history = Array.isArray(_p) ? _p : []; } catch { history = []; }
  // mins: how long the session took (for the parent's weekly report); absent on older entries
  history.push({ date: today, subject: subjectId, correct, total, ts: Date.now(), ...(Number.isFinite(mins) ? { mins } : {}) });
  const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
  localStorage.setItem(key, JSON.stringify(history.filter(h => h.ts > cutoff)));

  // XP for Live Rank, per child (the first child keeps the plain key, so existing XP stays with them)
  const xpKey = profileKey("lumio_xp", profileId);
  const xp = JSON.parse(localStorage.getItem(xpKey) || "{}");
  const month = new Date().toISOString().slice(0, 7); // "2025-01"
  xp[month] = (xp[month] || 0) + correct * 10 + total * 2;
  xp.total = (xp.total || 0) + correct * 10 + total * 2;
  localStorage.setItem(xpKey, JSON.stringify(xp));
}

// ── Exam progress tracking ──────────────────────────────────
function trackExamProgress(examProgram, questionsAnswered, profileId) {
  if (!examProgram) return;
  const key = profileKey("lumio_exam_progress", profileId);
  const progress = JSON.parse(localStorage.getItem(key) || "{}");
  progress[examProgram] = (progress[examProgram] || 0) + questionsAnswered;
  localStorage.setItem(key, JSON.stringify(progress));
}

function getExamProgress(examProgram, profileId) {
  const key = profileKey("lumio_exam_progress", profileId);
  const progress = JSON.parse(localStorage.getItem(key) || "{}");
  return progress[examProgram] || 0;
}

function getActivityStats(profileId) {
  const key = profileKey("lumio_activity", profileId);
  const history = JSON.parse(localStorage.getItem(key) || "[]");
  const today = new Date();
  let streak = 0;
  // newest first by actual date (sorting the toDateString() text orders by weekday name, which broke streaks)
  const dates = [...new Set(history.map(h => h.date))].sort((a, b) => new Date(b) - new Date(a));
  for (let i = 0; i < dates.length; i++) {
    const d = new Date(dates[i]);
    const expected = new Date(today - i * 86400000).toDateString();
    if (d.toDateString() === expected) streak++;
    else break;
  }
  const weekAgo = Date.now() - 7 * 86400000;
  const weekHistory = history.filter(h => h.ts > weekAgo);
  const subjectStats = {};
  history.filter(h => h.ts > Date.now() - 30*86400000).forEach(h => {
    if (!subjectStats[h.subject]) subjectStats[h.subject] = { correct: 0, total: 0 };
    subjectStats[h.subject].correct += h.correct;
    subjectStats[h.subject].total += h.total;
  });
  let weakestSubject = null, lowestPct = 100;
  Object.entries(subjectStats).forEach(([subj, stats]) => {
    if (stats.total >= 5) {
      const pct = (stats.correct / stats.total) * 100;
      if (pct < lowestPct) { lowestPct = pct; weakestSubject = subj; }
    }
  });
  const lastSession = history.length > 0 ? history[history.length - 1] : null;
  const lastDate = lastSession ? new Date(lastSession.date) : null;
  const daysSince = lastDate ? Math.floor((today - lastDate) / 86400000) : null;
  return { daysSince, streak, weekSessions: weekHistory.length, weekCorrect: weekHistory.reduce((a,b)=>a+b.correct,0), weekTotal: weekHistory.reduce((a,b)=>a+b.total,0), subjectStats, weakestSubject, lowestPct, lastSession, history };
}

async function generateLumiInsight(stats, serverUrl) {
  const { daysSince, streak, weakestSubject, lowestPct } = stats;
  if (daysSince === null) return null; // first visit: Practice already shows what to do, so no banner
  if (daysSince >= 7) return { type:"urgent", title:"It's been a while!", message:`No practice for ${daysSince} days. Just 10 minutes today makes a difference.`, action:"Resume Practice →" };
  if (daysSince >= 3) return { type:"warning", title:"Keep the momentum going", message:`Last session was ${daysSince} days ago. Ready for a quick session?`, action:"Practice Now →" };
  if (streak >= 7) return { type:"success", title:`🔥 ${streak}-day streak!`, message:`Incredible consistency! ${streak} days in a row.`, action:null };
  if (weakestSubject && lowestPct < 50) {
    const programSubjects = EXAM_SUBJECTS[stats.examProgram] || [];
    if (!stats.examProgram || programSubjects.includes(weakestSubject)) {
      return { type:"info", title:"Lumi spotted a focus area", message:`${weakestSubject} is scoring ${Math.round(lowestPct)}% recently. A few targeted sessions here could make a big difference.`, action:`Practice ${weakestSubject} →` };
    }
  }

  return null;
}

function LumiAlertBanner({ alert, onDismiss, onAction }) {
  if (!alert) return null;
  const colors = {
    warning: { bg:`${T.gold}15`, border:`${T.gold}44`, icon:"⚠️", color:T.gold },
    success: { bg:`${T.green}12`, border:`${T.green}33`, icon:"🎉", color:T.green },
    info:    { bg:`${T.accent}10`, border:`${T.accent}33`, icon:"✨", color:T.accent },
    urgent:  { bg:"#EF444410", border:"#EF444433", icon:"🚨", color:"#EF4444" },
  };
  const c = colors[alert.type] || colors.info;
  return (
    <div style={{ margin:"12px 16px 0", background:c.bg, border:`1.5px solid ${c.border}`, borderRadius:14, padding:"12px 14px", animation:"fadeUp .3s ease", position:"relative" }}>
      <button onClick={onDismiss} style={{ position:"absolute", top:8, right:10, background:"none", border:"none", fontSize:16, color:T.textDim, cursor:"pointer", padding:"2px 4px" }}>×</button>
      <div style={{ display:"flex", gap:10, alignItems:"flex-start", paddingRight:20 }}>
        <span style={{ fontSize:20, flexShrink:0 }}>{c.icon}</span>
        <div style={{ flex:1 }}>
          <p style={{ fontSize:13, fontWeight:800, color:c.color, margin:"0 0 3px" }}>{alert.title}</p>
          <p style={{ fontSize:12, color:T.textMid, margin:"0 0 8px", lineHeight:1.5 }}>{alert.message}</p>
          {alert.action && <button onClick={onAction} style={{ background:c.color, border:"none", borderRadius:8, padding:"6px 14px", color:"white", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{alert.action}</button>}
        </div>
      </div>
    </div>
  );
}

function LumiInsightsTab({ progress, serverUrl, activeProfile, examProgram }) {
  const [insight, setInsight] = useState(null);
  const stats = getActivityStats(activeProfile?.id);
  const { daysSince, streak, weekSessions, weekCorrect, weekTotal, subjectStats } = stats;
  const weekPct = weekTotal > 0 ? Math.round((weekCorrect / weekTotal) * 100) : 0;
  useEffect(() => {
    generateLumiInsight({ ...stats, examProgram }, serverUrl)
      .then(a => setInsight(a))
      .catch(() => setInsight("Keep practising — Lumi will have insights for you soon! 🌟"));
  }, []);
  return (
    <div style={{ animation:"fadeUp .3s ease" }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        {[
          { label:"Day Streak", value:streak||0, icon:"🔥", color:T.gold },
          { label:"This Week", value:`${weekSessions} session${weekSessions === 1 ? '' : 's'}`, icon:"📅", color:T.accent },
          { label:"Week Accuracy", value:weekTotal>0?`${weekPct}%`:"—", icon:"🎯", color:T.green },
          { label:"Last Practice", value:daysSince===null?"Never":daysSince===0?"Today":`${daysSince}d ago`, icon:"⏰", color:daysSince>=3?"#EF4444":T.green },
        ].map(stat => (
          <div key={stat.label} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:"14px" }}>
            <div style={{ fontSize:20, marginBottom:6 }}>{stat.icon}</div>
            <div style={{ fontSize:11, color:T.textMid, fontWeight:600, marginBottom:2 }}>{stat.label}</div>
            <div style={{ fontSize:18, fontWeight:800, color:stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>
      {insight && (
        <div style={{ background:`${T.accent}10`, border:`1.5px solid ${T.accent}33`, borderRadius:12, padding:14, marginBottom:14 }}>
          <div style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:24, flexShrink:0 }}>✨</span>
            <div>
              <p style={{ fontSize:13, fontWeight:800, color:T.accent, margin:"0 0 4px" }}>{insight.title}</p>
              <p style={{ fontSize:12, color:T.textMid, lineHeight:1.5, margin:0 }}>{insight.message}</p>
            </div>
          </div>
        </div>
      )}
      {Object.keys(subjectStats).length === 0 && (
        <div style={{ textAlign:"center", padding:"32px 16px", color:T.textMid }}>
          <div style={{ fontSize:40, marginBottom:12 }}>✨</div>
          <p style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:6 }}>No activity yet</p>
          <p style={{ fontSize:13 }}>Complete some practice sessions and Lumi will generate personalised insights here.</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════

// ── Trial limits ──────────────────────────────────────────────
const TRIAL_QUESTION_LIMIT = 10;

function getTrialQuestionsUsed(subjectId) {
  try { const d = JSON.parse(localStorage.getItem('lumio_trial_usage') || '{}'); return d[subjectId] || 0; }
  catch(e) { return 0; }
}

function recordTrialQuestion(subjectId) {
  try { const d = JSON.parse(localStorage.getItem('lumio_trial_usage') || '{}'); d[subjectId] = (d[subjectId] || 0) + 1; localStorage.setItem('lumio_trial_usage', JSON.stringify(d)); }
  catch(e) {}
}

function isTrial() {
  try { const s = localStorage.getItem('lumio_access'); if (!s) return true; const { plan } = JSON.parse(s); return !plan || plan === 'trial'; }
  catch(e) { return true; }
}

function hasTrialQuestionsLeft(subjectId) {
  if (!isTrial()) return true;
  return getTrialQuestionsUsed(subjectId) < TRIAL_QUESTION_LIMIT;
}


// ═══════════════════════════════════════════════════════════════
// EXAM COUNTDOWN WIDGET
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// DIAGNOSTIC TEST
// 20 questions across subjects → personalised Lumi report
// ═══════════════════════════════════════════════════════════════

function DiagnosticTest({ examProgram, yearId, onComplete, onClose }) {
  const [banksIn, setBanksIn] = useState(banksLoaded()); // the diagnostic draws on the banks, which arrive after sign-in
  useEffect(() => { if (!banksIn) loadBanks().then(() => setBanksIn(true)).catch(() => {}); }, [banksIn]);
  const DIAG_SUBJECTS = (SUBJECTS || []).filter(s =>
    ["reading","numeracy","thinking","language"].includes(s.id)
  );
  const QS_PER_SUBJECT = 5;

  // Build 20 questions — 5 from each subject
  const diagQuestions = useMemo(() => {
    const qs = [];
    try {
      DIAG_SUBJECTS.forEach(s => {
        const pool = (s && s.id) ? (getAdaptiveQuestions(s.id, yearId, null, QS_PER_SUBJECT, examProgram, { mcqOnly: true }) || []) : [];
        qs.push(...pool.slice(0, QS_PER_SUBJECT));
      });
    } catch(e) { console.error('DiagnosticTest pool error:', e); }
    return qs;
  }, [examProgram, yearId, banksIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const [qIdx, setQIdx]     = useState(0);
  const [answers, setAnswers] = useState({});
  const [chosen, setChosen]   = useState(null);
  const [phase, setPhase]     = useState("intro"); // intro|test|report
  const [report, setReport]   = useState(null);
  const [generating, setGenerating] = useState(false);
  const [qNum, setQNum]       = useState(1);
  const [diagAnswers, setDiagAnswers] = useState({});
  // Sample diagnostic questions across subjects
  const questions = React.useMemo(() => {
    const pool = [...(QB.reading||[]), ...(QB.numeracy||[]), ...(QB.writing||[]), ...(QB.language||[])]
      .filter(q => q.year && q.year.includes(yearId) && isMcqItem(q))
      .sort(() => Math.random() - 0.5)
      .slice(0, 10);
    return pool.length > 0 ? pool : (QB.numeracy||[]).filter(q => q.year?.includes('y5') && isMcqItem(q)).slice(0,10);
  }, [yearId]);

  async function runDiagnostic(answersObj) {
    setPhase("generating");
    setGenerating(true);

    // Calculate scores per subject from answersObj
    const subjectScores = {};
    const subjectIds = [...new Set(Object.values(answersObj).map(a => a.subject))];
    subjectIds.forEach(subjectId => {
      const subj = SUBJECTS.find(s => s.id === subjectId);
      const subjAnswers = Object.values(answersObj).filter(a => a.subject === subjectId);
      const correct = subjAnswers.filter(a => a.correct).length;
      const total = subjAnswers.length;
      subjectScores[subjectId] = { correct, total, pct: total > 0 ? Math.round((correct / total) * 100) : 0, label: subj?.label || subjectId };
    });

    const sorted = Object.entries(subjectScores).sort((a, b) => b[1].pct - a[1].pct);
    const strongest = sorted[0];
    const weakest = sorted[sorted.length - 1];
    const overall = sorted.length > 0 ? Math.round(Object.values(subjectScores).reduce((a, b) => a + b.pct, 0) / sorted.length) : 0;

    try {
      const scoreText = Object.entries(subjectScores)
        .map(([, v]) => `${v.label}: ${v.correct}/${v.total} (${v.pct}%)`)
        .join(", ");
      const res = await fetch(SPARKY_SERVER_URL + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeaders()) },
        body: JSON.stringify({
          model: "claude-haiku-5-5",
          max_tokens: 300,
          system: `You are Lumi, a friendly Australian exam prep tutor. Generate a warm, encouraging 3-sentence diagnostic summary for a student. Mention their strongest area, their area to improve, and one specific actionable tip. Keep it under 80 words. No emojis in text.`,
          messages: [{ role: "user", content: `Student scores for ${examProgram} prep (${yearId}): ${scoreText}. Overall: ${overall}%.` }],
        }),
      });
      const data = await res.json();
      const insight = data?.content?.[0]?.text || `You scored ${overall}% overall. Focus on ${weakest?.[1]?.label} to improve your results.`;
      setReport({ subjectScores, strongest, weakest, overall, insight });
    } catch(e) {
      setReport({ subjectScores, strongest, weakest, overall, insight: `You scored ${overall}% overall.${strongest ? ` Your strongest area is ${strongest[1].label}.` : ''}${weakest ? ` Focus on ${weakest[1].label} to improve.` : ''}` });
    }
    setGenerating(false);
    setPhase("report");
    try {
      const usedIds = (diagQuestions || []).map(q => q && q.id).filter(Boolean);
      localStorage.setItem('lumio_diagnostic_used', JSON.stringify({ ids: usedIds, ts: Date.now() }));
    } catch(_) {}
    localStorage.setItem("lumio_diagnostic", JSON.stringify({
      date: new Date().toISOString(),
      examProgram, yearId,
      scores: subjectScores,
      overall,
    }));
    if (onComplete) onComplete(subjectScores);
  }

  function handleDiagAnswer(answerIdx) {
    const q = questions[qNum-1];
    const newAnswers = { ...diagAnswers, [qNum-1]: { answer: answerIdx, correct: answerIdx === q.answer, topic: q.topic, subject: q.subject||'reading' } };
    setDiagAnswers(newAnswers);
    if (qNum < questions.length) {
      setQNum(q => q + 1);
    } else {
      // All answered — generate report
      runDiagnostic(newAnswers);
    }
  }

  const q = diagQuestions[qIdx];
  const total = diagQuestions.length;
  const progress = Math.round((qIdx / total) * 100);

  function handleAnswer(i) {
    if (chosen !== null) return;
    setChosen(i);
    setAnswers(a => ({ ...a, [qIdx]: i }));
  }

  function next() {
    if (qIdx < total - 1) {
      setQIdx(i => i + 1);
      setChosen(answers[qIdx + 1] !== undefined ? answers[qIdx + 1] : null);
    } else {
      generateReport();
    }
  }

  async function generateReport() {
    setPhase("generating");
    setGenerating(true);

    // Calculate scores per subject
    const subjectScores = {};
    DIAG_SUBJECTS.forEach(s => {
      const subjectQs = diagQuestions.filter(dq => dq.subject === s.id);
      const correct = subjectQs.filter((dq, i) => {
        const globalIdx = diagQuestions.indexOf(dq);
        return answers[globalIdx] === dq.answer;
      }).length;
      subjectScores[s.id] = { correct, total: subjectQs.length, pct: Math.round((correct / subjectQs.length) * 100), label: s.label };
    });

    // Find strongest and weakest
    const sorted = Object.entries(subjectScores).sort((a, b) => b[1].pct - a[1].pct);
    const strongest = sorted[0];
    const weakest = sorted[sorted.length - 1];
    const overall = Math.round(Object.values(subjectScores).reduce((a, b) => a + b.pct, 0) / DIAG_SUBJECTS.length);

    // Generate AI insights
    try {
      const scoreText = Object.entries(subjectScores)
        .map(([, v]) => `${v.label}: ${v.correct}/${v.total} (${v.pct}%)`)
        .join(", ");

      const res = await fetch(SPARKY_SERVER_URL + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeaders()) },
        body: JSON.stringify({
          model: "claude-haiku-5-5",
          max_tokens: 300,
          system: `You are Lumi, a friendly Australian exam prep tutor. Generate a warm, encouraging 3-sentence diagnostic summary for a student. Mention their strongest area, their area to improve, and one specific actionable tip. Keep it under 80 words. No emojis in text.`,
          messages: [{ role: "user", content: `Student scores for ${examProgram} prep (${yearId}): ${scoreText}. Overall: ${overall}%.` }],
        }),
      });
      const data = await res.json();
      const insight = data?.content?.[0]?.text || `You scored ${overall}% overall. Focus on ${weakest[1].label} to improve your results.`;

      setReport({ subjectScores, strongest, weakest, overall, insight });
    } catch(e) {
      setReport({ subjectScores, strongest, weakest, overall, insight: `You scored ${overall}% overall. Your strongest area is ${strongest[1].label} and your biggest opportunity is ${weakest[1].label}.` });
    }
    setGenerating(false);
    setPhase("report");

    // Save used question IDs so future practice sessions don't repeat them
    try {
      const usedIds = (diagQuestions || []).map(q => q && q.id).filter(Boolean);
      localStorage.setItem('lumio_diagnostic_used', JSON.stringify({ ids: usedIds, ts: Date.now() }));
    } catch(_) {}
    // Save diagnostic to localStorage
    localStorage.setItem("lumio_diagnostic", JSON.stringify({
      date: new Date().toISOString(),
      examProgram, yearId,
      scores: subjectScores,
      overall,
    }));
    if (onComplete) onComplete(subjectScores);
  }

  // ── INTRO ────────────────────────────────────────────────
  if (phase === "intro") return (
    <div style={{ padding:"32px 24px", maxWidth:500, margin:"0 auto", textAlign:"center" }}>
      <div style={{ fontSize:56, marginBottom:16 }}>🎯</div>
      <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:28, color:T.text, marginBottom:8 }}>Quick Diagnostic Test</h2>
      <p style={{ fontSize:14, color:T.textMid, lineHeight:1.7, marginBottom:24 }}>
        Answer {questions.length} questions across all subjects.<br/>
        Takes about <strong style={{ color:T.text }}>5 minutes</strong>.<br/>
        Lumi will generate a personalised report showing your strengths and where to focus.
      </p>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:24 }}>
        {DIAG_SUBJECTS.map(s => (
          <div key={s.id} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:"12px", textAlign:"center" }}>
            <div style={{ fontSize:22, marginBottom:4 }}>{s.icon}</div>
            <p style={{ fontSize:12, fontWeight:700, color:T.text, margin:"0 0 2px" }}>{s.label}</p>
            <p style={{ fontSize:11, color:T.textMid, margin:0 }}>mixed questions</p>
          </div>
        ))}
      </div>
      <button onClick={() => setPhase("test")} style={{ width:"100%", padding:14, background:T.accent, border:"none", borderRadius:12, color:"white", fontWeight:800, fontSize:16, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", marginBottom:10 }}>
        Start Diagnostic →
      </button>
      <button onClick={onClose} style={{ background:"none", border:"none", color:T.textMid, fontSize:13, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Maybe later</button>
    </div>
  );

  // ── GENERATING ───────────────────────────────────────────
  if (phase === "test") return (
    <div style={{ padding:"16px 20px 80px", maxWidth:600, margin:"0 auto" }}>
      <div style={{ marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Question {qNum} of {questions.length}</span>
          <span style={{ fontSize:12, color:T.textMid }}>{examProgram?.toUpperCase()} Diagnostic</span>
        </div>
        <div style={{ height:4, background:T.border, borderRadius:99 }}>
          <div style={{ height:"100%", width:`${(qNum/questions.length)*100}%`, background:T.accent, borderRadius:99, transition:"width .3s" }} />
        </div>
      </div>
      {questions[qNum-1] && (
        <div style={{ animation:"fadeUp .3s ease" }}>
          <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:"20px 22px", marginBottom:16 }}>
            <p style={{ fontSize:12, fontWeight:700, color:T.accent, margin:"0 0 10px", textTransform:"uppercase", letterSpacing:".06em" }}>{questions[qNum-1].topic}</p>
            <p style={{ fontSize:16, color:T.text, lineHeight:1.7, margin:0 }}>{questions[qNum-1].q}</p>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {(questions[qNum-1].options||[]).map((opt,i) => (
              <button key={i} onClick={() => handleDiagAnswer(i)}
                style={{ background:T.card, border:`1.5px solid ${T.border}`, borderRadius:12, padding:"14px 18px",
                  display:"flex", alignItems:"center", gap:12, cursor:"pointer", textAlign:"left",
                  fontFamily:"'Plus Jakarta Sans',sans-serif", transition:"all .15s" }}
                onMouseOver={e=>e.currentTarget.style.borderColor=T.accent}
                onMouseOut={e=>e.currentTarget.style.borderColor=T.border}>
                <span style={{ width:28, height:28, borderRadius:8, background:T.surface, border:`1px solid ${T.border}`,
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:T.textMid, flexShrink:0 }}>
                  {["A","B","C","D"][i]}
                </span>
                <span style={{ fontSize:15, color:T.text }}>{String(opt)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (phase === "generating") return (
    <div style={{ padding:48, textAlign:"center" }}>
      <div style={{ fontSize:48, marginBottom:16, animation:"spin 2s linear infinite", display:"inline-block" }}>✨</div>
      <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:24, color:T.text, marginBottom:8 }}>Lumi is analysing your results...</h2>
      <p style={{ fontSize:14, color:T.textMid }}>Generating your personalised report</p>
    </div>
  );

  // ── REPORT ───────────────────────────────────────────────
  if (phase === "report" && report) return (
    <div style={{ padding:"24px", maxWidth:540, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:24 }}>
        <div style={{ fontSize:48, marginBottom:12 }}>{report.overall >= 80 ? "🏆" : report.overall >= 60 ? "📈" : "💪"}</div>
        <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:28, color:T.text, marginBottom:4 }}>Your Diagnostic Report</h2>
        <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:`${T.accent}15`, border:`1px solid ${T.accent}33`, borderRadius:99, padding:"6px 16px" }}>
          <span style={{ fontSize:20, fontWeight:800, color:T.accent }}>{report.overall}%</span>
          <span style={{ fontSize:13, color:T.textMid }}>overall score</span>
        </div>
      </div>

      {/* Subject breakdown */}
      <div style={{ marginBottom:20 }}>
        {Object.entries(report.subjectScores).map(([id, s]) => {
          const subj = SUBJECTS.find(sub => sub.id === id);
          const color = s.pct >= 80 ? T.green : s.pct >= 60 ? T.gold : "#EF4444";
          return (
            <div key={id} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ fontSize:13, fontWeight:700, color:T.text }}>{subj?.icon} {s.label}</span>
                <span style={{ fontSize:13, fontWeight:800, color }}>{s.correct}/{s.total} · {s.pct}%</span>
              </div>
              <div style={{ height:8, background:T.border, borderRadius:99, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${s.pct}%`, background:color, borderRadius:99, transition:"width 1s ease" }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Lumi insight */}
      <div style={{ background:`${T.accent}10`, border:`1.5px solid ${T.accent}33`, borderRadius:14, padding:"16px", marginBottom:20 }}>
        <div style={{ display:"flex", gap:10 }}>
          <span style={{ fontSize:28, flexShrink:0 }}>✨</span>
          <div>
            <p style={{ fontSize:13, fontWeight:800, color:T.accent, margin:"0 0 6px" }}>Lumi's Assessment</p>
            <div style={{ fontSize:13, color:T.textMid, lineHeight:1.6, margin:0 }}><LumiMarkdown>{report.insight}</LumiMarkdown></div>
          </div>
        </div>
      </div>

      {/* Recommended focus */}
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:14, padding:16, marginBottom:20 }}>
        <p style={{ fontSize:12, fontWeight:700, color:T.textMid, textTransform:"uppercase", letterSpacing:".08em", margin:"0 0 10px" }}>Recommended Focus</p>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:40, height:40, borderRadius:10, background:"#EF444415", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>
            {SUBJECTS.find(s => s.id === report.weakest[0])?.icon}
          </div>
          <div>
            <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:"0 0 2px" }}>{report.weakest[1].label}</p>
            <p style={{ fontSize:12, color:T.textMid, margin:0 }}>Practice 15 min/day for best results</p>
          </div>
          <span style={{ marginLeft:"auto", fontSize:20, fontWeight:800, color:"#EF4444" }}>{report.weakest[1].pct}%</span>
        </div>
      </div>

      <button onClick={onClose} style={{ width:"100%", padding:13, background:T.accent, border:"none", borderRadius:12, color:"white", fontWeight:800, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        Start Practising →
      </button>
    </div>
  );

  // ── TEST ─────────────────────────────────────────────────
  if (!q) return null;
  const subj = SUBJECTS.find(s => s.id === q.subject);
  const isCorrect = chosen !== null && chosen === q.answer;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>
      {/* Header */}
      <div style={{ padding:"12px 20px", borderBottom:`1px solid ${T.border}`, background:T.surface, flexShrink:0 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:16 }}>{subj?.icon}</span>
            <span style={{ fontSize:13, fontWeight:700, color:T.text }}>{subj?.label}</span>
            <Badge label="Diagnostic" color={T.accent} />
          </div>
          <span style={{ fontSize:13, fontWeight:700, color:T.textMid }}>{qIdx+1}/{total}</span>
        </div>
        <div style={{ height:6, background:T.border, borderRadius:99 }}>
          <div style={{ height:"100%", width:`${((qIdx+1)/total)*100}%`, background:T.accent, borderRadius:99, transition:"width .3s ease" }} />
        </div>
      </div>

      {/* Question */}
      <div style={{ flex:1, overflowY:"auto", padding:"24px 20px" }}>
        {q.passage && (
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, padding:"14px 16px", marginBottom:16, fontSize:13, color:T.textMid, lineHeight:1.7, whiteSpace:"pre-line" }}>
            {q.passage}
          </div>
        )}
        <p style={{ fontSize:15, fontWeight:700, color:T.text, lineHeight:1.6, marginBottom:20 }}>{q.q}</p>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {q.options.map((opt, i) => {
            const isChosen = chosen === i;
            const isAns = q.answer === i;
            let bg = T.card; let border = T.border; let color = T.text;
            if (chosen !== null) {
              if (isAns) { bg = `${T.green}20`; border = T.green; color = T.green; }
              else if (isChosen) { bg = "#EF444420"; border = "#EF4444"; color = "#EF4444"; }
            } else if (isChosen) { bg = `${T.accent}15`; border = T.accent; color = T.accent; }
            return (
              <button key={i} onClick={() => handleAnswer(i)} disabled={chosen !== null}
                style={{ padding:"12px 16px", background:bg, border:`1.5px solid ${border}`, borderRadius:12, color, textAlign:"left", fontSize:14, cursor:chosen===null?"pointer":"default", fontFamily:"'Plus Jakarta Sans',sans-serif", transition:"all .2s", fontWeight:isChosen||isAns?700:400 }}>
                <span style={{ fontWeight:800, marginRight:8 }}>{["A","B","C","D","E"][i]}.</span>{opt}
              </button>
            );
          })}
        </div>
        {chosen !== null && (
          <div style={{ marginTop:14, padding:"12px 14px", background:isCorrect?`${T.green}15`:"#EF444415", border:`1px solid ${isCorrect?T.green:"#EF4444"}33`, borderRadius:10 }}>
            <p style={{ fontSize:13, fontWeight:700, color:isCorrect?T.green:"#EF4444", margin:"0 0 4px" }}>{isCorrect?"✅ Correct":"❌ Not quite"}</p>
            <MultilineText text={q.explanation} style={{ fontSize:12, color:T.textMid, lineHeight:1.5 }} />
          </div>
        )}
      </div>

      {/* Next button */}
      {chosen !== null && (
        <div style={{ padding:"12px 20px 20px", borderTop:`1px solid ${T.border}`, flexShrink:0 }}>
          <button onClick={next} style={{ width:"100%", padding:13, background:T.accent, border:"none", borderRadius:12, color:"white", fontWeight:800, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {qIdx < total - 1 ? "Next Question →" : "See My Results →"}
          </button>
        </div>
      )}
    </div>
  );
}

function ClassLeaderboard({ onClose }) {
  const [groupCode, setGroupCode]   = useState(() => localStorage.getItem("lumio_group_code") || "");
  const [groupName, setGroupName]   = useState(() => localStorage.getItem("lumio_group_name") || "");
  const [myName, setMyName]         = useState(() => {
    const access = JSON.parse(localStorage.getItem("lumio_access") || "{}");
    return access.childName || access.name || "";
  });
  const [phase, setPhase]           = useState(groupCode ? "board" : "join");
  const [creating, setCreating]     = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [members, setMembers]       = useState([]);
  const [loading, setLoading]       = useState(false);

  const stats = getActivityStats(getActiveProfile()?.id);
  const myScore = {
    name: myName,
    questions: stats.history?.length * 8 || 0,
    streak: stats.streak || 0,
    accuracy: stats.weekTotal > 0 ? Math.round((stats.weekCorrect / stats.weekTotal) * 100) : 0,
    points: ((stats.history?.length || 0) * 10) + ((stats.streak || 0) * 25) + (stats.weekTotal > 0 ? Math.round((stats.weekCorrect / stats.weekTotal) * 50) : 0),
  };

  function createGroup() {
    if (!newGroupName.trim() || !myName.trim()) return;
    const code = "LG-" + Math.random().toString(36).substring(2, 6).toUpperCase();
    localStorage.setItem("lumio_group_code", code);
    localStorage.setItem("lumio_group_name", newGroupName.trim());
    setGroupCode(code);
    setGroupName(newGroupName.trim());
    setPhase("board");
  }

  function joinGroup() {
    if (!groupCode.trim() || !myName.trim()) return;
    localStorage.setItem("lumio_group_code", groupCode.trim().toUpperCase());
    setGroupCode(groupCode.trim().toUpperCase());
    setPhase("board");
  }

  function leaveGroup() {
    localStorage.removeItem("lumio_group_code");
    localStorage.removeItem("lumio_group_name");
    setGroupCode("");
    setGroupName("");
    setPhase("join");
  }

  // Mock leaderboard with real local data + demo entries for first time
  const demoMembers = [
    { name: myName || "You", questions: myScore.questions, streak: myScore.streak, accuracy: myScore.accuracy, points: myScore.points, isMe: true },
    { name: "Sophie K.", questions: 145, streak: 8, accuracy: 78, points: 1840, isMe: false },
    { name: "Liam T.", questions: 120, streak: 5, accuracy: 72, points: 1470, isMe: false },
    { name: "Mia C.", questions: 98, streak: 3, accuracy: 85, points: 1430, isMe: false },
    { name: "Ethan W.", questions: 76, streak: 2, accuracy: 68, points: 890, isMe: false },
  ].sort((a, b) => b.points - a.points).map((m, i) => ({ ...m, rank: i + 1 }));

  const MEDALS = ["🥇","🥈","🥉"];

  // ── JOIN / CREATE ─────────────────────────────────────────
  if (phase === "join") return (
    <div style={{ padding:"32px 24px", maxWidth:500, margin:"0 auto" }}>
      <div style={{ textAlign:"center", marginBottom:28 }}>
        <div style={{ fontSize:52, marginBottom:12 }}>🏆</div>
        <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:28, color:T.text, marginBottom:6 }}>Class Leaderboard</h2>
        <p style={{ fontSize:14, color:T.textMid, lineHeight:1.6 }}>Compete with classmates. The more you practise, the higher you climb.</p>
      </div>

      <label style={{ fontSize:13, fontWeight:700, color:T.text, display:"block", marginBottom:6 }}>Your name (shown on leaderboard)</label>
      <input value={myName} onChange={e => setMyName(e.target.value)} placeholder="e.g. Emma" style={{ width:"100%", padding:"12px 16px", background:T.surface, border:`1.5px solid ${T.border}`, borderRadius:12, color:T.text, fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:"none", boxSizing:"border-box", marginBottom:20 }} />

      {/* Create group */}
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:14, padding:"18px", marginBottom:12 }}>
        <p style={{ fontSize:14, fontWeight:800, color:T.text, margin:"0 0 12px" }}>Create a group</p>
        <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="e.g. Year 6 Chatswood" style={{ width:"100%", padding:"11px 14px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:"none", boxSizing:"border-box", marginBottom:10 }} />
        <button onClick={createGroup} disabled={!newGroupName.trim() || !myName.trim()} style={{ width:"100%", padding:11, background:T.accent, border:"none", borderRadius:10, color:"white", fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", opacity:!newGroupName.trim()||!myName.trim()?0.5:1 }}>
          Create Group →
        </button>
      </div>

      {/* Join group */}
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:14, padding:"18px" }}>
        <p style={{ fontSize:14, fontWeight:800, color:T.text, margin:"0 0 12px" }}>Join with a code</p>
        <input value={groupCode} onChange={e => setGroupCode(e.target.value.toUpperCase())} placeholder="e.g. LG-K7N2" style={{ width:"100%", padding:"11px 14px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:"none", boxSizing:"border-box", marginBottom:10, letterSpacing:".08em", fontWeight:700 }} />
        <button onClick={joinGroup} disabled={!groupCode.trim() || !myName.trim()} style={{ width:"100%", padding:11, background:T.surface, border:`1.5px solid ${T.border}`, borderRadius:10, color:T.text, fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", opacity:!groupCode.trim()||!myName.trim()?0.5:1 }}>
          Join Group
        </button>
      </div>
    </div>
  );

  // ── BOARD ─────────────────────────────────────────────────
  return (
    <div style={{ padding:"0 0 80px" }}>
      {/* Group header */}
      <div style={{ padding:"14px 20px", background:T.surface, borderBottom:`1px solid ${T.border}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <p style={{ fontSize:16, fontWeight:800, color:T.text, margin:"0 0 2px" }}>{groupName || "My Group"}</p>
            <p style={{ fontSize:12, color:T.textMid, margin:0, fontFamily:"monospace", letterSpacing:".06em" }}>Code: {groupCode}</p>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => navigator.clipboard?.writeText(groupCode)} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"6px 12px", color:T.text, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              📋 Share Code
            </button>
            <button onClick={leaveGroup} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:8, padding:"6px 10px", color:T.textMid, fontSize:12, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Leave</button>
          </div>
        </div>
      </div>

      {/* Score columns */}
      <div style={{ padding:"16px 16px 8px", display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr", gap:4 }}>
        {["Student","❓ Q's","🔥 Streak","🎯 Acc.","⭐ Pts"].map(h => (
          <p key={h} style={{ fontSize:10, fontWeight:700, color:T.textDim, margin:0, textAlign:h==="Student"?"left":"center", textTransform:"uppercase", letterSpacing:".06em" }}>{h}</p>
        ))}
      </div>

      {/* Members */}
      <div style={{ padding:"0 12px" }}>
        {demoMembers.map((m, i) => (
          <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr", gap:4, alignItems:"center", padding:"12px 10px", marginBottom:6, background:m.isMe?`${T.accent}15`:T.card, border:`1px solid ${m.isMe?T.accent:T.border}`, borderRadius:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:18, flexShrink:0 }}>{MEDALS[i] || `${i+1}.`}</span>
              <span style={{ fontSize:13, fontWeight:m.isMe?800:600, color:m.isMe?T.accent:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {m.name}{m.isMe ? " (you)" : ""}
              </span>
            </div>
            {[m.questions, `${m.streak}d`, `${m.accuracy}%`, m.points].map((v, vi) => (
              <p key={vi} style={{ fontSize:13, fontWeight:700, color:m.isMe?T.accent:T.text, margin:0, textAlign:"center" }}>{v}</p>
            ))}
          </div>
        ))}
      </div>

      {/* How points work */}
      <div style={{ margin:"16px 12px 0", padding:"14px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:12 }}>
        <p style={{ fontSize:12, fontWeight:700, color:T.textMid, margin:"0 0 8px", textTransform:"uppercase", letterSpacing:".06em" }}>How points work</p>
        {[["10 pts","per question answered"],["25 pts","per day in your streak"],["50 pts","for 80%+ accuracy this week"]].map(([pts, desc]) => (
          <div key={pts} style={{ display:"flex", gap:10, marginBottom:4 }}>
            <span style={{ fontSize:12, fontWeight:800, color:T.accent, width:40, flexShrink:0 }}>{pts}</span>
            <span style={{ fontSize:12, color:T.textMid }}>{desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExamCountdown({ examProgram, yearId }) {
  const [examDate, setExamDate] = useState(() => {
    return localStorage.getItem(`lumio_exam_date_${examProgram}`) || "";
  });
  const [editing, setEditing] = useState(false);
  const [tempDate, setTempDate] = useState(examDate);

  // Known upcoming exam dates (local AU time)
  const KNOWN_DATES = {
    selective_nsw: "2026-05-07",
    oc:            "2026-05-08",
    naplan:        "2027-03-10",
    vic_selective: "2026-06-13",
  };

  const effectiveDate = examDate || KNOWN_DATES[examProgram];

  const rawDaysLeft = effectiveDate ? Math.ceil(
    (new Date(effectiveDate) - new Date()) / (1000 * 60 * 60 * 24)
  ) : null;
  // If exam has passed, show 0 and "Completed" instead of negative days
  const daysLeft = rawDaysLeft !== null ? Math.max(0, rawDaysLeft) : null;
  const examPassed = rawDaysLeft !== null && rawDaysLeft < 0;

  const examLabels = {
    naplan: "NAPLAN", selective_nsw: "NSW Selective",
    oc: "OC Test", acer: "ACER", icas: "ICAS", vic_selective: "VIC Selective",
  };

  if (daysLeft === null) return null;
  // If exam date is in the past, show an informational banner instead of a countdown
  if (examPassed) {
    const yearAfter = effectiveDate ? new Date(effectiveDate).getFullYear() + 1 : new Date().getFullYear() + 1;
    const passedLabel = (examLabels[examProgram] || "Exam") + " " + yearAfter + " registrations open soon";
    return (
      <div style={{ marginBottom:16, background:T.card, border:`1.5px solid #94A3B844`, borderRadius:16, padding:"14px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:22 }}>📅</span>
          <div>
            <p style={{ fontSize:11, color:T.textMid, margin:"0 0 2px", fontWeight:700, textTransform:"uppercase", letterSpacing:".06em" }}>Next exam cycle</p>
            <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:0 }}>{passedLabel}</p>
          </div>
        </div>
      </div>
    );
  }

  const urgency = daysLeft <= 14 ? "#EF4444" : daysLeft <= 30 ? "#F59E0B" : "#0D9488";
  const pct = Math.max(0, Math.min(100, Math.round((1 - daysLeft / 180) * 100)));

  function saveDate() {
    localStorage.setItem(`lumio_exam_date_${examProgram}`, tempDate);
    setExamDate(tempDate);
    setEditing(false);
  }

  return (
    <div style={{ marginBottom:16, background:T.card, border:`1.5px solid ${urgency}44`, borderRadius:16, padding:"14px 16px", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:T.border }}>
        <div style={{ height:"100%", width:`${pct}%`, background:urgency, borderRadius:99, transition:"width 1s ease" }} />
      </div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:4 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:24 }}>📅</span>
          <div>
            <p style={{ fontSize:12, color:T.textMid, margin:"0 0 2px", fontWeight:600, textTransform:"uppercase", letterSpacing:".06em" }}>{examLabels[examProgram] || "Exam"} countdown</p>
            <p style={{ fontSize:22, fontWeight:800, color:urgency, margin:0, fontFamily:"'Space Grotesk',sans-serif" }}>
              {examPassed ? "Passed" : daysLeft === 0 ? "Today! 🎉" : `${daysLeft} days to go`}
            </p>
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <p style={{ fontSize:11, color:T.textMid, margin:"0 0 4px" }}>Preparation</p>
          <p style={{ fontSize:18, fontWeight:800, color:urgency, margin:0 }}>{pct}%</p>
          {!editing && (
            <button onClick={() => { setEditing(true); setTempDate(examDate); }} style={{ background:"none", border:"none", color:T.textDim, fontSize:11, cursor:"pointer", padding:0, fontFamily:"'Plus Jakarta Sans',sans-serif", textDecoration:"underline", marginTop:2 }}>
              {examDate ? "Change date" : "Set date"}
            </button>
          )}
        </div>
      </div>
      {editing && (
        <div style={{ marginTop:12, display:"flex", gap:8, alignItems:"center" }}>
          <input type="date" value={tempDate} onChange={e => setTempDate(e.target.value)}
            style={{ flex:1, padding:"8px 12px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:"none" }} />
          <button onClick={saveDate} style={{ background:T.accent, border:"none", borderRadius:8, padding:"8px 14px", color:"white", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Save</button>
          <button onClick={() => setEditing(false)} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:8, padding:"8px 10px", color:T.textMid, fontSize:13, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>×</button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PRICING SCREEN
// ═══════════════════════════════════════════════════════════════
// The plans (owner decision 2026-10-22). Prices in AUD; yearly is shown per week so families see the real cost.
// The server maps each plan + period to a Stripe price (server.js PRICE_IDS); a payment is recorded by the webhook.
const PLAN_OPTIONS = [
  { id: 'exam', name: 'Exam Only', monthly: 14.99, yearly: 119, color: '#0D9488', tag: null,
    features: ['Every exam: NAPLAN, NSW Selective, OC, VIC Selective and more', 'Practice, section tests and full mock exams', 'AI-marked writing tests', 'Glow Up mistakes review and weekly parent reports'] },
  { id: 'premium', name: 'Premium', monthly: 19.99, yearly: 149, color: '#6C5CE7', tag: 'Most popular',
    features: ['Everything in Exam Only', 'Every interactive lesson, Years 6 to 12', 'Every subject, with NSW HSC and VCE courses in Years 11 and 12'] },
  { id: 'family', name: 'Family', monthly: 29.99, yearly: 229, color: '#F59E0B', tag: 'Best for siblings',
    features: ['Everything in Premium', 'Up to 3 children, each with their own progress', 'A weekly report for every child'] },
];
const perWeek = (yearly) => (yearly / 52).toFixed(2);

function PricingScreen({ currentPlan, trial = null }) {
  const [loading, setLoading] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [period, setPeriod] = React.useState('yearly');
  const current = { essential: 'exam', plus: 'exam', selective: 'exam', allaccess: 'premium' }[currentPlan] || currentPlan;

  const paid = ['exam', 'premium', 'family'].includes(current);
  async function manage() {
    setLoading('manage'); setError(null);
    const msg = await openBillingPortal(); // leaves the page when it opens
    if (msg) { setError(msg); setLoading(null); }
  }

  async function handleSelect(plan) {
    if (current === plan.id) return;
    // an account that already pays switches plan in the billing portal; a second checkout would charge it twice
    if (paid) { await manage(); return; }
    setLoading(plan.id);
    setError(null);
    try {
      const access = JSON.parse(localStorage.getItem('lumio_access') || '{}');
      // checkout is tied to the signed-in account, so the plan can be recorded against it on the server
      const token = supabase ? (await supabase.auth.getSession())?.data?.session?.access_token : null;
      if (!token) { setError('Please sign in to your Empirio account before choosing a plan.'); setLoading(null); return; }
      const res = await fetch(SPARKY_SERVER_URL + '/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ planId: plan.id, period, name: access.name || '' }),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; return; } // Stripe Checkout
      if (data.error === 'already-subscribed') { await manage(); return; }
      setError('Could not start checkout. Please try again.');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(null);
  }

  // Back from Stripe: only re-read the account's plan (the webhook records it); the address itself grants nothing.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success' || params.get('plan')) {
      window.history.replaceState({}, '', window.location.pathname);
      window.dispatchEvent(new Event('lumio:refresh-plan'));
    }
  }, []);

  const toggle = (id, label) => (
    <button key={id} onClick={() => setPeriod(id)} aria-pressed={period === id}
      style={{ padding:'8px 16px', borderRadius:99, border:'none', cursor:'pointer', fontWeight:800, fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif",
        background: period === id ? T.text : 'transparent', color: period === id ? T.bg : T.textMid }}>{label}</button>
  );
  return (
    <div style={{ padding:'24px 20px 80px', maxWidth:640, margin:'0 auto' }}>
      <div style={{ textAlign:'center', marginBottom:20 }}>
        <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:28, color:T.text, marginBottom:6 }}>Choose a plan</h2>
        <p style={{ fontSize:14, color:T.textMid, margin:0 }}>Cancel any time · No lock-in · Prices in AUD</p>
        {trial && trial.active && <p style={{ fontSize:13, color:T.gold, fontWeight:700, margin:'8px 0 0' }}>Your free trial has {trial.daysLeft} day{trial.daysLeft === 1 ? '' : 's'} left.</p>}
        {trial && trial.known && !trial.active && <p style={{ fontSize:13, color:T.accent, fontWeight:700, margin:'8px 0 0' }}>Your free trial has ended. Everything your child has done is saved.</p>}
      </div>
      <div style={{ display:'flex', justifyContent:'center', marginBottom:22 }}>
        <div style={{ display:'inline-flex', gap:4, padding:4, borderRadius:99, background:T.surface, border:`1px solid ${T.border}` }}>
          {toggle('yearly', 'Yearly · best value')}{toggle('monthly', 'Monthly')}
        </div>
      </div>
      {error && (
        <div style={{ background:'#FEF2F2', border:'1px solid #FCA5A5', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#DC2626' }}>{error}</div>
      )}
      {paid && (
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:'16px 20px', marginBottom:18, display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'space-between', gap:12 }}>
          <div style={{ minWidth:0 }}>
            <p style={{ fontSize:15, fontWeight:800, color:T.text, margin:'0 0 2px' }}>Manage your plan</p>
            <p style={{ fontSize:13, color:T.textMid, margin:0 }}>Switch plans, update your card, see invoices or cancel. If you cancel, your plan keeps working until the end of the period you have paid for.</p>
          </div>
          <button onClick={manage} disabled={loading !== null}
            style={{ padding:'10px 18px', borderRadius:12, border:`1.5px solid ${T.border}`, background:T.surface, color:T.text, fontWeight:800, fontSize:14, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            {loading === 'manage' ? 'Opening billing…' : 'Manage or cancel'}
          </button>
        </div>
      )}
      {PLAN_OPTIONS.map(plan => {
        const active = current === plan.id;
        return (
          <div key={plan.id} style={{ background:T.card, border:`2px solid ${active || plan.tag === 'Most popular' ? plan.color : T.border}`, borderRadius:16, padding:'20px', marginBottom:14, position:'relative' }}>
            {plan.tag && <div style={{ position:'absolute', top:-10, right:16, background:plan.color, color:'white', fontSize:11, fontWeight:800, padding:'3px 10px', borderRadius:99 }}>{plan.tag}</div>}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:12 }}>
              <div>
                <p style={{ fontSize:16, fontWeight:800, color:T.text, margin:'0 0 4px' }}>{plan.name}</p>
                {period === 'yearly' ? (<>
                  <p style={{ fontSize:24, fontWeight:800, color:plan.color, margin:0 }}>${perWeek(plan.yearly)}<span style={{ fontSize:13, color:T.textMid, fontWeight:500 }}> / week</span></p>
                  <p style={{ fontSize:12, color:T.textMid, margin:'2px 0 0' }}>${plan.yearly} billed yearly · save {Math.round((1 - plan.yearly / (plan.monthly * 12)) * 100)}%</p>
                </>) : (
                  <p style={{ fontSize:24, fontWeight:800, color:plan.color, margin:0 }}>${plan.monthly}<span style={{ fontSize:13, color:T.textMid, fontWeight:500 }}> / month</span></p>
                )}
              </div>
              {active && <div style={{ background:`${plan.color}20`, border:`1px solid ${plan.color}`, borderRadius:8, padding:'4px 10px', fontSize:12, fontWeight:700, color:plan.color }}>✓ Your plan</div>}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:14 }}>
              {plan.features.map(f => <p key={f} style={{ fontSize:13, color:T.textMid, margin:0 }}>✅ {f}</p>)}
            </div>
            <button onClick={() => handleSelect(plan)} disabled={active || loading !== null}
              style={{ width:'100%', padding:'12px', borderRadius:12, border:'none', cursor: active ? 'default' : 'pointer', fontWeight:800, fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif",
                background: active ? T.surface : plan.color, color: active ? T.textMid : 'white', opacity: loading && loading !== plan.id ? 0.6 : 1 }}>
              {active ? 'Current plan' : loading === plan.id ? 'Opening checkout…' : loading === 'manage' ? 'Opening billing…' : paid ? `Switch to ${plan.name}` : `Choose ${plan.name}`}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function HomeHeroLumi() {
  const [heroState, setHeroState] = React.useState(LUMI_STATES.IDLE);
  const handleClick = () => {
    setHeroState(LUMI_STATES.CELEBRATING);
    setTimeout(() => setHeroState(LUMI_STATES.IDLE), 2000);
  };
  return (
    <div onClick={handleClick} style={{ flexShrink:0, cursor:"pointer" }} title="Click me!">
      <Lumi state={heroState} size={80} />
    </div>
  );
}

// ── Flight Deck home dashboard ─────────────────────────────────
// Visual spec ported from design-refs/Lumio Flight Deck standalone.html.
// Progress is computed from the real catalog of the child's Learn year (Year 6–12; public/curriculum/yearN/...)
// and the progress each topic page saves under lumio-pkg:<unit id> (a lesson is done once its step reaches 7).
function FlightDeckHome({ onLearn, onPractice, onExam, onGlowUp, glowUp, daily, profileId = "" }) {
  const [state, setState] = useState({ loaded:false, done:0, total:0, subjects:[], subjectsStarted:0, unitsComplete:0, learnYear:"year6" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const EXCLUDED_UNIT_IDS = new Set(["english_year3_home"]);

      const fetchJson = async (path) => {
        const res = await fetch(`/curriculum/${path}`);
        if (!res.ok) throw new Error(`${path}: ${res.status}`);
        return res.json();
      };


      // Learn starts at Year 6 (Years 3–5 were removed from Learn on 2026-11-03). Show the year the child last opened
      // in Learn, or Year 6 until they pick one. Subjects come straight from that year's catalog.
      const LEARN_YEARS = ["year6","year7","year8","year9","year10","year11","year12"];
      let learnYear = "year6";
      try { const y = localStorage.getItem("lumio_learn_year"); if (LEARN_YEARS.includes(y)) learnYear = y; } catch (e) {}
      const STYLE = {
        mathematics: { name:"Maths",        color:"#6C5CE7", tint:"#EDE8FF", icon:"maths" },
        science:     { name:"Science",      color:"#16A36B", tint:"#DEF3EA", icon:"science" },
        english:     { name:"English",      color:"#FF7A59", tint:"#FCE7DF", icon:"english" },
        hass:        { name:"HASS",         color:"#3B82F6", tint:"#E1ECFE", icon:"hass" },
        history:     { name:"History",      color:"#B45309", tint:"#FCEFD9", icon:"hass" },
        geography:   { name:"Geography",    color:"#0E9F9A", tint:"#DCF3F2", icon:"hass" },
        civics:      { name:"Civics",       color:"#3B82F6", tint:"#E1ECFE", icon:"hass" },
        economics:   { name:"Economics",    color:"#7C5CC4", tint:"#EEE8FA", icon:"hass" },
        hpe:         { name:"Health & PE",  color:"#E0457B", tint:"#FBE4EC", icon:"science" },
        digitaltech: { name:"Digital Tech", color:"#C2551F", tint:"#FBE7DC", icon:"tech" },
        legal:       { name:"Legal Studies", color:"#B91C1C", tint:"#FDE4E4", icon:"hass" },
        business:    { name:"Business Studies", color:"#B45309", tint:"#FCEFD9", icon:"hass" },
        religion:    { name:"Studies of Religion", color:"#6D28D9", tint:"#EDE8FF", icon:"hass" },
        society:     { name:"Society & Culture", color:"#0369A1", tint:"#E0F0FB", icon:"hass" },
        pdhpe:       { name:"PDHPE",        color:"#E0457B", tint:"#FBE4EC", icon:"science" },
        technologies:{ name:"Technologies", color:"#C2551F", tint:"#FBE7DC", icon:"tech" },
        vce:         { name:"VCE",          color:"#4F46E5", tint:"#E8E7FD", icon:"learn" },
      };
      // A topic page keeps its own progress under lumio-pkg:<unit id>; a lesson is done once its step reaches 7.
      const topicDone = (u) => {
        try {
          // this child's progress (the first child keeps the plain key, like profileKey)
          const st = JSON.parse(localStorage.getItem(profileId ? `lumio-pkg:${u.unit_id}_${profileId}` : `lumio-pkg:${u.unit_id}`) || "null");
          const n = st && st.lessons ? Object.values(st.lessons).filter((l) => l && l.reached >= 7).length : 0;
          return Math.min(n, u.lesson_count || 0);
        } catch (e) { return 0; }
      };

      try {
        const yearIndex = await fetchJson(`${learnYear}/index.json`);
        let totalDone = 0, totalLessons = 0, subjectsStarted = 0, unitsComplete = 0;
        const subjects = [];
        for (const entry of yearIndex.subjects || []) {
          const st = STYLE[entry.subject_id] || { name: entry.title, color:"#6C5CE7", tint:"#EDE8FF", icon:"hass" };
          const d = { key: entry.subject_id, ...st };
          const subjectIndex = await fetchJson(`${learnYear}/${entry.file}`);
          let units = subjectIndex.units || [];
          if (!units.length && Array.isArray(subjectIndex.subjects)) {
            // grouped subjects (e.g. Science → Biology, Chemistry, Physics): gather every group's units
            const base = entry.file.replace(/index\.json$/, "");
            for (const g of subjectIndex.subjects) {
              try { units = units.concat((await fetchJson(`${learnYear}/${base}${g.file}`)).units || []); } catch (e) {}
            }
          }
          units = units.filter((u) => !String(u.unit_id || "").startsWith("hub-") && !EXCLUDED_UNIT_IDS.has(u.unit_id));
          if (!units.length) { subjects.push({ ...d, has:false, unitsLabel:"Coming soon", pct:0, label:"Soon" }); continue; }
          let sDone = 0, sTotal = 0, started = false;
          for (const u of units) {
            const n = u.lesson_count || 0, dn = topicDone(u);
            sTotal += n; sDone += dn;
            if (dn > 0) started = true;
            if (n > 0 && dn >= n) unitsComplete++;
          }
          totalDone += sDone; totalLessons += sTotal;
          if (started) subjectsStarted++;
          const pctNum = sTotal > 0 ? Math.round(sDone / sTotal * 100) : 0;
          subjects.push({ ...d, has:true, unitsLabel: units.length + (units.length === 1 ? " unit" : " units"), pct: pctNum, label: sDone > 0 ? `${pctNum}%` : "Start" });
        }
        if (!cancelled) setState({ loaded:true, done:totalDone, total:totalLessons, subjects, subjectsStarted, unitsComplete, learnYear });
      } catch (e) {
        if (!cancelled) setState(s => ({ ...s, loaded:true }));
      }
    })();
    return () => { cancelled = true; };
  }, [profileId]); // recompute when the active child changes

  const { loaded, done, total, subjects, subjectsStarted, unitsComplete, learnYear } = state;
  const yearLabel = `Year ${String(learnYear || "year6").replace("year", "")}`;
  const started = done > 0;
  const pct = total > 0 ? Math.round(done / total * 100) : 0;
  const ringOff = 201 - (201 * pct / 100);
  const bigRingOff = 364 - (364 * pct / 100);

  const modes = [
    { key:"learn",    name:"Learn",    desc:"Interactive lessons",              tint:"#EDE8FF", color:"#6C5CE7", onClick:onLearn },
    { key:"practice", name:"Practice", desc:"Pick a topic, Lumi explains every answer", tint:"#F1EFF6", color:"#8B5CF6", onClick:onPractice },
    { key:"exam",     name:"Exam",     desc:"Timed practice with a performance report", tint:"#F1EFF6", color:"#F59E0B", onClick:onExam },
    ...(onGlowUp ? [{ key:"glowup", name:"Glow Up", tint:"#FEF7E0", color:"#D4A020", onClick:onGlowUp, badge: glowUp?.ready || 0,
      desc: !glowUp?.total ? "Mistakes you fix turn into skills" : glowUp.ready ? `${glowUp.ready} ready to fix · ${glowUp.shining} Shining` : `All caught up · ${glowUp.shining} Shining` }] : []),
  ];

  const subjectIcon = (icon, color) => {
    switch (icon) {
      case "maths":   return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M5 4h14v4H5zM5 11h5v9H5zM14 11h5v9h-5z" stroke={color} strokeWidth="1.8" fill="none" strokeLinejoin="round"/></svg>;
      case "science": return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3" stroke={color} strokeWidth="1.8" fill="none" strokeLinejoin="round"/></svg>;
      case "english": return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M5 5h11a3 3 0 0 1 3 3v11a3 3 0 0 0-3-3H5z" stroke={color} strokeWidth="1.8" fill="none" strokeLinejoin="round"/></svg>;
      case "hass":    return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.8"/><path d="M3.5 12h17M12 3.5c2.5 2.4 2.5 14.6 0 17M12 3.5c-2.5 2.4-2.5 14.6 0 17" stroke={color} strokeWidth="1.4" fill="none"/></svg>;
      case "tech":    return <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="4" y="5" width="16" height="11" rx="1.6" stroke={color} strokeWidth="1.8" fill="none"/><path d="M9 20h6M12 16v4" stroke={color} strokeWidth="1.8" strokeLinecap="round"/></svg>;
      default: return null;
    }
  };

  const modeIcon = (key, color) => {
    switch (key) {
      case "learn":    return <svg width="19" height="19" viewBox="0 0 24 24" fill="none"><path d="M4 6h7a2 2 0 0 1 2 2v11a2 2 0 0 0-2-2H4zM20 6h-7a2 2 0 0 0-2 2v11a2 2 0 0 1 2-2h7z" stroke={color} strokeWidth="1.7" fill="none"/></svg>;
      case "practice": return <svg width="19" height="19" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1.7"/><circle cx="12" cy="12" r="3.4" stroke={color} strokeWidth="1.7"/></svg>;
      case "exam":     return <svg width="19" height="19" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="13" r="8" stroke={color} strokeWidth="1.7"/><path d="M12 13V8.5M12 13l3.5 2M9.5 3h5" stroke={color} strokeWidth="1.8" strokeLinecap="round"/></svg>;
      default: return null;
    }
  };

  return (
    <div style={{ position:"relative", width:"100%", minHeight:"100vh", overflow:"hidden", background:"radial-gradient(1100px 620px at 84% -8%,#E7E1FB 0%,rgba(238,237,243,0) 60%),radial-gradient(820px 520px at 2% 106%,#FBE6DE 0%,rgba(238,237,243,0) 55%),#EEEDF3", fontFamily:"'Hanken Grotesk',sans-serif" }}>
      <style>{`
        @keyframes fdFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        @keyframes fdBlink{0%,44%,52%,100%{transform:scaleY(1)}48%{transform:scaleY(.12)}}
        @keyframes fdShimmer{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}
        @keyframes fdRise{0%{opacity:0;transform:translateY(16px)}100%{opacity:1;transform:translateY(0)}}
        @keyframes fdRingDraw{0%{stroke-dashoffset:var(--fd-circ)}100%{stroke-dashoffset:var(--fd-off)}}
        @keyframes fdSheen{0%{transform:translateX(-140%)}55%,100%{transform:translateX(260%)}}
        .fd-reveal{opacity:0;animation:fdRise .7s cubic-bezier(.2,.75,.2,1) forwards}
        .fd-card{background:#FFFFFF;border:1px solid #ECEAF3;border-radius:22px;box-shadow:0 1px 2px rgba(30,22,70,.04),0 12px 30px rgba(40,30,90,.06)}
        .fd-subj{transition:transform .22s cubic-bezier(.2,.75,.2,1),box-shadow .22s ease}
        .fd-subj:hover{transform:translateY(-4px);box-shadow:0 2px 4px rgba(30,22,70,.05),0 20px 44px rgba(40,30,90,.12)}
        .fd-sheenbtn{position:relative;overflow:hidden}
        .fd-sheenbtn::after{content:'';position:absolute;top:0;left:0;width:38%;height:100%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.5),transparent);animation:fdSheen 4.4s ease-in-out infinite}
        @media (max-width:900px){
          .fd-main-grid{grid-template-columns:1fr!important}
          .fd-subjects-grid{grid-template-columns:repeat(2,1fr)!important}
          .fd-topbar-links{display:none!important}
        }
        @media (max-width:540px){
          .fd-subjects-grid{grid-template-columns:1fr!important}
          .fd-counts-grid{grid-template-columns:1fr!important}
        }
      `}</style>

      {/* top bar */}
      <div style={{ position:"relative", maxWidth:1180, margin:"0 auto", padding:"24px 34px", display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ position:"relative", width:32, height:32, borderRadius:10, background:"radial-gradient(circle at 34% 28%,#B9A9FF,#6C5CE7 60%,#4A38B8)", boxShadow:"0 5px 14px rgba(108,92,231,.34)" }}>
            <div style={{ position:"absolute", left:"26%", top:"22%", width:9, height:6, borderRadius:"50%", background:"rgba(255,255,255,.85)", filter:"blur(.5px)" }}/>
          </div>
          <span style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:21, letterSpacing:"-.01em", color:"#16162A" }}>Empirio</span>
        </div>
        <div className="fd-topbar-links" style={{ flex:1, display:"flex", justifyContent:"center", gap:4 }}>
          <span style={{ fontSize:13, fontWeight:700, color:"#3A2E8C", background:"#EDE8FF", padding:"9px 17px", borderRadius:99 }}>Dashboard</span>
          <span onClick={onLearn} style={{ fontSize:13, fontWeight:600, color:"#6B6A82", padding:"9px 17px", borderRadius:99, cursor:"pointer" }}>Subjects</span>
        </div>
        <span style={{ fontSize:12.5, fontWeight:700, color:"#C2551F", background:"#FCE9E0", border:"1px solid #F6D3C4", padding:"8px 15px", borderRadius:99 }}>{yearLabel}</span>
      </div>

      {/* hero row */}
      <div className="fd-reveal" style={{ position:"relative", maxWidth:1180, margin:"0 auto", padding:"8px 34px 0", display:"flex", alignItems:"flex-end", justifyContent:"space-between", gap:20, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontFamily:"'Space Mono'", fontSize:11.5, fontWeight:700, letterSpacing:".14em", color:"#8B87A8" }}>WELCOME TO LUMIO</div>
          <h1 style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:37, lineHeight:1.05, letterSpacing:"-.02em", marginTop:9, color:"#16162A" }}>
            {!loaded ? " " : started ? "Welcome back." : "Let us begin."}
          </h1>
          <p style={{ fontSize:15, color:"#6B6A82", marginTop:8, maxWidth:460 }}>
            {!loaded ? "Loading your progress..." : started ? "Pick up where you left off, or explore a new subject below." : "Choose a subject to start your first lesson. Your progress will show up here as you go."}
          </p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
        {daily && (
          // today's goal: 10 questions in Practice or Exam; the streak counts days the goal was met
          <button onClick={onPractice} aria-label={`Daily goal: ${Math.min(daily.today, daily.goal)} of ${daily.goal} questions today. ${daily.streak}-day streak. Start practising.`}
            style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 16px", borderRadius:18, background:daily.met ? "#FFF6E0" : "rgba(255,255,255,.7)", border:`1px solid ${daily.met ? "#F3D58A" : "#ECEAF3"}`, cursor:"pointer", textAlign:"left", fontFamily:"inherit" }}>
            <div style={{ position:"relative", width:46, height:46 }}>
              <svg width="46" height="46" viewBox="0 0 46 46" style={{ transform:"rotate(-90deg)" }}>
                <circle cx="23" cy="23" r="19" fill="none" stroke="#ECEAF3" strokeWidth="5"/>
                <circle cx="23" cy="23" r="19" fill="none" stroke={daily.met ? "#E8A317" : "#6C5CE7"} strokeWidth="5" strokeLinecap="round"
                  strokeDasharray="119.4" strokeDashoffset={119.4 * (1 - Math.min(1, daily.today / daily.goal))}/>
              </svg>
              <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{daily.met ? "🔥" : "🎯"}</div>
            </div>
            <div>
              <div style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:14, color:"#16162A" }}>
                {daily.met ? "Goal done today!" : `${Math.min(daily.today, daily.goal)} / ${daily.goal} questions today`}
              </div>
              <div style={{ fontSize:11.5, color:"#8B87A8" }}>
                {daily.streak > 0 ? `🔥 ${daily.streak}-day streak${daily.met ? "" : " · keep it going"}` : "Answer 10 to start a streak"}
              </div>
            </div>
          </button>
        )}
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 18px", borderRadius:18, background:"rgba(255,255,255,.7)", border:"1px solid #ECEAF3" }}>
          <div style={{ position:"relative", width:46, height:46, animation:"fdFloat 5s ease-in-out infinite" }}>
            <div style={{ position:"absolute", inset:0, borderRadius:"50%", background:"radial-gradient(circle at 34% 28%,#C9BCFF,#6C5CE7 60%,#4A38B8)", boxShadow:"0 8px 20px rgba(108,92,231,.4)" }}/>
            <div style={{ position:"absolute", inset:5, borderRadius:"50%", background:"conic-gradient(from 0deg,transparent,rgba(255,255,255,.4),transparent 42%)", animation:"fdShimmer 7s linear infinite", mixBlendMode:"screen", opacity:.7 }}/>
            <div style={{ position:"absolute", left:0, top:"44%", width:"100%", display:"flex", justifyContent:"center", gap:7 }}>
              <div style={{ width:5, height:11, borderRadius:"50%", background:"#fff", boxShadow:"0 0 7px rgba(255,255,255,.8)", animation:"fdBlink 4.8s ease-in-out infinite" }}/>
              <div style={{ width:5, height:11, borderRadius:"50%", background:"#fff", boxShadow:"0 0 7px rgba(255,255,255,.8)", animation:"fdBlink 4.8s ease-in-out infinite" }}/>
            </div>
          </div>
          <div>
            <div style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:14, color:"#16162A" }}>Lumi</div>
            <div style={{ fontSize:11.5, color:"#8B87A8" }}>your guide</div>
          </div>
        </div>
        </div>
      </div>

      {/* main grid */}
      <div className="fd-main-grid" style={{ position:"relative", maxWidth:1180, margin:"0 auto", padding:"22px 34px 34px", display:"grid", gridTemplateColumns:"1.5fr .82fr", gap:20, alignItems:"start" }}>

        {/* LEFT column */}
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

          {/* feature card */}
          <div className="fd-reveal fd-card" style={{ position:"relative", overflow:"hidden", borderRadius:24, padding:"26px 28px", animationDelay:".06s", background:"linear-gradient(135deg,#2E2470,#4A38B8 62%,#6C5CE7)" }}>
            <div style={{ position:"absolute", right:-40, top:-40, width:220, height:220, borderRadius:"50%", background:"radial-gradient(circle at 40% 40%,rgba(255,122,89,.4),transparent 62%)" }}/>
            <div style={{ position:"relative" }}>
              <div style={{ display:"inline-flex", alignItems:"center", gap:7, background:"rgba(255,255,255,.14)", border:"1px solid rgba(255,255,255,.22)", borderRadius:99, padding:"5px 12px" }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:"#FF9A80" }}/>
                <span style={{ fontFamily:"'Space Mono'", fontSize:9.5, fontWeight:700, letterSpacing:".08em", color:"#FFD9CE" }}>{started ? "KEEP GOING" : "START HERE"}</span>
              </div>
              <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", gap:20, marginTop:16, flexWrap:"wrap" }}>
                <div style={{ minWidth:240 }}>
                  <div style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:26, color:"#fff", lineHeight:1.12, margin:"5px 0 6px" }}>{started ? "Continue learning" : "Start your first lesson"}</div>
                  <div style={{ fontSize:13.5, color:"#CFC9F5" }}>{started ? `Jump back into your ${yearLabel} subjects.` : `Browse the ${yearLabel} subjects and pick a lesson to begin.`}</div>
                </div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ position:"relative", width:74, height:74 }}>
                    <svg width="74" height="74" viewBox="0 0 74 74" style={{ transform:"rotate(-90deg)" }}>
                      <circle cx="37" cy="37" r="32" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="7"/>
                      <circle cx="37" cy="37" r="32" fill="none" stroke="#FFB59E" strokeWidth="7" strokeLinecap="round" strokeDasharray="201"
                        style={{ "--fd-circ":201, "--fd-off":ringOff, animation:"fdRingDraw 1.4s ease .3s both" }}/>
                    </svg>
                    <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column" }}>
                      <span style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:17, color:"#fff" }}>{pct}%</span>
                      <span style={{ fontFamily:"'Space Mono'", fontSize:8, color:"#C6BEFF" }}>{done} / {total}</span>
                    </div>
                  </div>
                </div>
              </div>
              <button onClick={onLearn} className="fd-sheenbtn" style={{ marginTop:20, border:"none", borderRadius:13, padding:"14px 24px", background:"#fff", color:"#3A2E8C", fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:15, cursor:"pointer", boxShadow:"0 10px 24px rgba(0,0,0,.18)" }}>
                <span style={{ position:"relative", zIndex:1 }}>Browse lessons →</span>
              </button>
            </div>
          </div>

          {/* subjects */}
          <div className="fd-reveal" style={{ animationDelay:".12s" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:13 }}>
              <span style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:17, color:"#16162A" }}>Your subjects</span>
              <span style={{ fontSize:12.5, fontWeight:600, color:"#8B87A8" }}>{yearLabel}</span>
            </div>
            <div className="fd-subjects-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
              {(loaded ? subjects : []).map(s => (
                <div key={s.key} onClick={s.has ? onLearn : undefined}
                  className={s.has ? "fd-card fd-subj" : "fd-card"}
                  style={{ padding:"17px 17px 15px", cursor:s.has ? "pointer" : "default", position:"relative" }}>
                  {s.key === "technologies" && (
                    <span style={{ position:"absolute", top:13, right:13, fontFamily:"'Space Mono'", fontSize:8, fontWeight:700, letterSpacing:".05em", color:"#fff", background:s.color, padding:"3px 8px", borderRadius:99 }}>NEW</span>
                  )}
                  <div style={{ width:42, height:42, borderRadius:13, background:s.tint, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:34 }}>
                    {subjectIcon(s.icon, s.color)}
                  </div>
                  <div style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:15.5, color:"#16162A" }}>{s.name}</div>
                  <div style={{ fontSize:11.5, color:"#8B87A8", marginBottom:11 }}>{s.unitsLabel}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                    <div style={{ flex:1, height:6, borderRadius:99, background:"#EFEDF5", overflow:"hidden" }}>
                      <div style={{ width:`${s.pct || 0}%`, height:"100%", borderRadius:99, background:s.color }}/>
                    </div>
                    <span style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:11.5, color:s.pct > 0 ? s.color : "#B4AEC8" }}>{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT rail */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {/* overall progress */}
          <div className="fd-reveal fd-card" style={{ borderRadius:22, padding:22, animationDelay:".16s", textAlign:"center" }}>
            <div style={{ fontFamily:"'Space Mono'", fontSize:10, fontWeight:700, letterSpacing:".1em", color:"#8B87A8", textAlign:"left" }}>YOUR PROGRESS</div>
            <div style={{ position:"relative", width:132, height:132, margin:"14px auto 6px" }}>
              <svg width="132" height="132" viewBox="0 0 132 132" style={{ transform:"rotate(-90deg)" }}>
                <circle cx="66" cy="66" r="58" fill="none" stroke="#EFEDF5" strokeWidth="12"/>
                <circle cx="66" cy="66" r="58" fill="none" stroke="url(#fd-ring-gradient)" strokeWidth="12" strokeLinecap="round" strokeDasharray="364"
                  style={{ "--fd-circ":364, "--fd-off":bigRingOff, animation:"fdRingDraw 1.5s ease .3s both" }}/>
                <defs>
                  <linearGradient id="fd-ring-gradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#6C5CE7"/>
                    <stop offset="1" stopColor="#FF7A59"/>
                  </linearGradient>
                </defs>
              </svg>
              <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                <span style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:34, color:"#16162A", lineHeight:1 }}>{done}</span>
                <span style={{ fontFamily:"'Space Mono'", fontSize:9, letterSpacing:".08em", color:"#8B87A8" }}>LESSONS DONE</span>
              </div>
            </div>
            <p style={{ fontSize:12.5, color:"#6B6A82", lineHeight:1.45 }}>
              {!loaded ? "Loading..." : started ? "Great start. Keep the momentum going." : "No lessons yet. Start one and it counts here."}
            </p>
          </div>

          {/* counts */}
          <div className="fd-reveal fd-counts-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, animationDelay:".2s" }}>
            <div className="fd-card" style={{ borderRadius:18, padding:16, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
              <div style={{ width:38, height:38, borderRadius:12, background:"#EDE8FF", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none"><path d="M4 6h7a2 2 0 0 1 2 2v11a2 2 0 0 0-2-2H4zM20 6h-7a2 2 0 0 0-2 2v11a2 2 0 0 1 2-2h7z" stroke="#6C5CE7" strokeWidth="1.7" fill="none"/></svg>
              </div>
              <div style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:21, color:"#16162A", marginTop:9 }}>{subjectsStarted}</div>
              <div style={{ fontFamily:"'Space Mono'", fontSize:8.5, fontWeight:700, letterSpacing:".05em", color:"#8B87A8" }}>SUBJECTS STARTED</div>
            </div>
            <div className="fd-card" style={{ borderRadius:18, padding:16, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
              <div style={{ width:38, height:38, borderRadius:12, background:"#DEF3EA", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#16A36B" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:21, color:"#16162A", marginTop:9 }}>{unitsComplete}</div>
              <div style={{ fontFamily:"'Space Mono'", fontSize:8.5, fontWeight:700, letterSpacing:".05em", color:"#8B87A8" }}>UNITS FINISHED</div>
            </div>
          </div>

          {/* three modes */}
          <div className="fd-reveal" style={{ display:"flex", flexDirection:"column", gap:10, animationDelay:".26s" }}>
            {/* Glow Up jumps to the top while mistakes are waiting to be fixed */}
            {[...modes].sort((a, b) => (b.badge > 0) - (a.badge > 0)).map(m => (
              <div key={m.key} onClick={m.onClick} className="fd-card fd-subj" style={{ borderRadius:16, padding:"14px 16px", display:"flex", alignItems:"center", gap:13, cursor:"pointer" }}>
                <div style={{ width:38, height:38, borderRadius:11, background:m.tint, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {m.key === "glowup" ? <span style={{ fontSize:18 }}>✨</span> : modeIcon(m.key, m.color)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontFamily:"'Space Grotesk'", fontWeight:700, fontSize:14.5, color:"#16162A" }}>{m.name}</div>
                  <div style={{ fontSize:11.5, color:"#8B87A8" }}>{m.desc}</div>
                </div>
                {m.badge > 0 && <span style={{ background:m.color, color:"#fff", borderRadius:99, padding:"2px 9px", fontSize:12, fontWeight:800 }}>{m.badge}</span>}
                <span style={{ color:"#C4C0D6", fontSize:18 }}>→</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EmpirioApp() {
  const [hasAccess, setHasAccess]       = useState(false);
  // ── Supabase session + admin bypass (env-gated) ─────────────────
  const [authSession, setAuthSession]   = useState(null);
  const [banksReady, setBanksReady]     = useState(banksLoaded()); // the question banks arrive after sign-in
  useEffect(() => {
    if (!authSession) return undefined;
    let off = false;
    syncSessionCookie(authSession); // the gate reads this cookie before it hands over a bank
    loadBanks().then(() => { if (!off) setBanksReady(true); }).catch(() => { /* the gate said no (trial over) or offline: the hubs show no questions */ });
    return () => { off = true; };
  }, [authSession]);
  const [recovering, setRecovering]     = useState(false); // opened a password-reset link: ask for the new password
  const [authChecked, setAuthChecked]   = useState(false);
  const [isAdmin, setIsAdmin]           = useState(false);
  // Trial-limit modal state — when set, blocks the underlying action.
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [pwaPrompt, setPwaPrompt]       = useState(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [showPwaInstall, setShowPwaInstall] = useState(false);
  const [showStudyPlanner, setShowStudyPlanner] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(() => {
    try { return localStorage.getItem("lumio_view") === "leaderboard"; } catch(e) { return false; }
  });
  const [showLearn, setShowLearn] = useState(() => {
    try { return localStorage.getItem("lumio_view") === "learn"; } catch(e) { return false; }
  });
  const [homeMode, setHomeMode] = useState(null);
  // student-first navigation (2026-11-02): with more than one child the app opens on "Who's learning?" once per
  // browser session
  const [pickLearner, setPickLearner] = useState(() => { try { return getProfiles().length > 1 && !sessionStorage.getItem("lumio_learner_picked"); } catch (e) { return false; } });

  // Remember which top-level overlay (Learn / Live Rank) is open so a
  // refresh doesn't drop the user back to Home.
  useEffect(() => {
    try {
      if (showLearn) localStorage.setItem("lumio_view", "learn");
      else if (showLeaderboard) localStorage.setItem("lumio_view", "leaderboard");
      else localStorage.removeItem("lumio_view");
    } catch(e) {}
  }, [showLearn, showLeaderboard]);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPwaPrompt(e); setShowPwaInstall(true); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // ── Auth: Supabase session ───────
  // Order of checks:
  //   1. Supabase session                → user mode
  //   2. None                            → show LoginScreen
  // No client-side admin shortcuts: the ?admin= URL, the LMIO-BOSS code and the sessionStorage/localStorage
  // owner flags were all removed because anything shipped to or stored in the browser can be read or set by anyone.
  // The session check is async. We always set authChecked=true
  // when finished so the gate logic can render without flashing the
  // LoginScreen during the async session restore.
  useEffect(() => {
    let unsubscribe = null;
    try {
      // no client-side admin or owner shortcuts (see above)
    } catch (_) { /* swallow — fall through to Supabase */ }

    if (!supabaseEnabled) {
      // Supabase not configured: mark auth-checked so the renderer can fall through.
      setAuthChecked(true);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setAuthSession(data?.session || null);
      setAuthChecked(true);
      if (data?.session) setHasAccess(true);
    }).catch(() => setAuthChecked(true));

    const sub = supabase.auth.onAuthStateChange((evt, session) => {
      if (evt === 'PASSWORD_RECOVERY') setRecovering(true);
      setAuthSession(session || null);
      if (session) setHasAccess(true);
    });
    unsubscribe = sub?.data?.subscription?.unsubscribe?.bind(sub.data.subscription);
    return () => { try { unsubscribe?.(); } catch (_) {} };
  }, []);

  async function installPWA() {
    if (!pwaPrompt) return;
    pwaPrompt.prompt();
    const { outcome } = await pwaPrompt.userChoice;
    if (outcome === "accepted") setShowPwaInstall(false);
    setPwaPrompt(null);
  }
  // Returning users (anyone with a prior lumio_session) skip the branded
  // splash entirely on refresh — it used to flash for a beat before the
  // restore-on-refresh effect below could correct it. First-time visitors
  // (no saved session yet) still see it.
  const [screen, setScreen]             = useState(() => {
    try { return localStorage.getItem("lumio_session") ? "loading" : "splash"; }
    catch(e) { return "splash"; }
  });
  const [examProgram, setExamProgram]   = useState("naplan");
  const [, setExamExtraLoads] = useState(0); // re-render (topic lists, counts) when an exam expansion file arrives
  const [currentPlan, setCurrentPlan]   = useState(() => localStorage.getItem("lumio_plan") || "trial");
  const sessionStartRef = useRef(null); // when the current practice/exam session began (time spent, for parent reports)
  const [goalJustMet, setGoalJustMet] = useState(null); // the streak, when this session is the one that met today's goal
  const [activeProfile, setActiveProfile] = useState(() => {
    try {
      const profiles = JSON.parse(localStorage.getItem("lumio_profiles") || "[]");
      const activeId = localStorage.getItem("lumio_active_profile");
      return profiles.find(p => p.id === activeId) || profiles[0] || null;
    } catch(e) { return null; }
  });
  const [showPricing, setShowPricing]       = useState(false);
  const [showSkills, setShowSkills]         = useState(false);
  const [showReport, setShowReport]         = useState(false);
  const [showBugPipeline, setShowBugPipeline] = useState(false);
  const [lumiAlert, setLumiAlert]       = useState(null);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [, setPrevScreen]               = useState(null);
  const [yearId, setYearId]             = useState(null);
  const [subjectId, setSubjectId]       = useState(null);
  const [mode, setMode]                 = useState("practice");
  const [topicFilter, setTopicFilter]   = useState(null);
  // Section tests and mock-exam papers run at the real test's length and time (src/lib/examStructure.js);
  // examTestletSize: questions per NAPLAN testlet, examMinutes: time allowed (null = the old ~2 min a question)
  const [examTestletSize, setExamTestletSize] = useState(TESTLET_SIZE);
  const [examMinutes, setExamMinutes]   = useState(null);
  const [mock, setMock]                 = useState(null); // full mock exam in progress: { program, yearId, papers, idx, results }
  const [questions, setQuestions]       = useState([]);
  const [qIndex, setQIndex]             = useState(0);
  const [adaptiveState, setAdaptiveState] = useState({ currentLevel:2, streak:0, streakDirection:'correct' });
  const [adaptivePool, setAdaptivePool]   = useState([]);
  const [adaptiveUsed, setAdaptiveUsed]   = useState(new Set());
  const [examRoute, setExamRoute]         = useState([]); // multistage exam: testlet levels taken so far
  const [answers, setAnswers]           = useState({});
  const [chosen, setChosen]             = useState(null);
  // Practice/exam progress is kept per child profile (profileKey: the bare key when there is no profile, which is
  // every family today, so nothing moves; when sibling profiles are switched on, each child gets their own record).
  const [progress, setProgress]         = useState(() => {
    try { return JSON.parse(localStorage.getItem(profileKey('lumio_progress_v2', getActiveProfile()?.id)) || '{}'); }
    catch(_) { return {}; }
  });
  const [showChat, setShowChat]         = useState(false);
  const [confetti, setConfetti]         = useState(false);
  const [lumiMood, setLumiMood]         = useState("idle");
  const [showWriting, setShowWriting]   = useState(false);
  const [showInteractive, setShowInteractive] = useState(false);
  const [showScratch, setShowScratch] = useState(false); // the working-out pad beside a question
  const [chatContext, setChatContext]   = useState({ subject:"", year:"", question:"", studentAnswer:"", correctAnswer:"", context:"" });
  // Fire once per app load — see src/utils/telemetry.js
  React.useEffect(() => {
    trackEvent('app_opened', { referrer: typeof document !== 'undefined' ? document.referrer : '' });
  }, []);

  // Progress follows the account: while a parent is signed in, the browser's progress keys sync to Supabase
  // (src/lib/accountSync.js). If a pull brings in newer progress from another device, re-read it into the app.
  const syncUserId = authSession?.user?.id || null;
  // What this family can use: 7-day trial from account creation, then Exam Only / Premium / Family (src/lib/entitlements.js)
  // Owner access comes only from the server (/api/me/role checks the signed-in account's verified email against
  // OWNER_EMAILS on Railway). The answer lives in memory, never in browser storage, so nothing stored can grant it.
  const [serverOwner, setServerOwner] = useState(false);
  React.useEffect(() => {
    if (!syncUserId || !supabase) { setServerOwner(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const token = (await supabase.auth.getSession())?.data?.session?.access_token;
        if (!token) return;
        const r = await fetch(`${SPARKY_SERVER_URL}/api/me/role`, { headers: { Authorization: `Bearer ${token}` } });
        const j = r.ok ? await r.json() : null;
        if (!cancelled) setServerOwner(!!(j && j.owner));
        // the welcome email, the first time this account is seen signed in (the server sends it once per account)
        const welcomeKey = `empirio_welcome_${syncUserId}`;
        if (!sessionStorage.getItem(welcomeKey)) {
          sessionStorage.setItem(welcomeKey, '1');
          fetch(`${SPARKY_SERVER_URL}/api/welcome`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        }
      } catch (_) { if (!cancelled) setServerOwner(false); }
    })();
    return () => { cancelled = true; };
  }, [syncUserId]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { setIsAdmin(serverOwner); }, [serverOwner]);
  const ownerAccess = serverOwner;
  const ent = entitlements({ plan: currentPlan, createdAt: authSession?.user?.created_at, owner: ownerAccess });
  // the children this plan covers (Family: up to 3, otherwise the first); any others stay saved for a later Family plan
  const learners = getProfiles().slice(0, Math.max(1, ent.profiles));
  const learnerIds = learners.map((p) => p.id).join("|");
  useEffect(() => {
    if (activeProfile && learners.length && !learners.some((p) => p.id === activeProfile.id)) setActiveProfile(learners[0]);
  }, [learnerIds, activeProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!syncUserId) return undefined;
    return startAccountSync(syncUserId, () => {
      try { setProgress(JSON.parse(localStorage.getItem(profileKey('lumio_progress_v2', getActiveProfile()?.id)) || '{}')); } catch (_) {}
    });
  }, [syncUserId]);

  // The account's plan comes from the server (public.subscriptions, written only by the Stripe webhook). Read it on
  // sign-in and after a Stripe payment (retrying briefly while the webhook lands). A signed-in parent without a paid plan gets the free trial (they used to get no stored access
  // at all, which could send them to the pricing page instead of the trial).
  React.useEffect(() => {
    if (!syncUserId || !supabase) return undefined;
    let cancelled = false;
    const readAccess = () => { try { return JSON.parse(localStorage.getItem('lumio_access') || '{}') || {}; } catch (_) { return {}; } };
    const apply = (plan) => {
      const access = readAccess();
      localStorage.setItem('lumio_access', JSON.stringify({ ...access, plan, granted: access.granted || Date.now() }));
      localStorage.setItem('lumio_plan', plan);
      setCurrentPlan(plan);
    };
    const refresh = async (tries = 1) => {
      for (let i = 0; i < tries && !cancelled; i++) {
        try {
          const { data, error } = await supabase.from('subscriptions').select('plan,status').eq('user_id', syncUserId).maybeSingle();
          if (!error) {
            const paid = data && ['active', 'trialing', 'past_due'].includes(data.status) && data.plan !== 'trial';
            apply(paid ? data.plan : 'trial');
            if (paid || i === tries - 1) return;
          }
        } catch (_) { /* offline: keep the cached plan */ }
        await new Promise((r) => setTimeout(r, 3000));
      }
    };
    // back from Stripe (checkout or the billing portal): keep re-reading while the webhook records the change
    const back = new URLSearchParams(window.location.search).get('payment');
    if (back === 'success' || back === 'portal') { window.history.replaceState({}, '', window.location.pathname); refresh(8); } else refresh(1);
    const onPaid = () => refresh(8); // up to ~24 s for the webhook
    window.addEventListener('lumio:refresh-plan', onPaid);
    return () => { cancelled = true; window.removeEventListener('lumio:refresh-plan', onPaid); };
  }, [syncUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist progress to localStorage whenever it changes
  React.useEffect(() => {
    if (Object.keys(progress).length > 0) {
      try { localStorage.setItem(profileKey('lumio_progress_v2', activeProfile?.id), JSON.stringify(progress)); }
      catch(_) {}
    }
  }, [progress]);

  // Load Lumi alert on start
  useEffect(() => {
    async function loadAlert() {
      const stats = getActivityStats(activeProfile?.id);
      const alert = await generateLumiInsight(stats, SPARKY_SERVER_URL);
      if (alert) setLumiAlert(alert);
    }
    loadAlert();
  }, []);

  // Clear stale legacy plan values on load
  useEffect(() => {
    const plan = localStorage.getItem("lumio_plan");
    if (plan === "plus") {
      localStorage.removeItem("lumio_plan");
      setCurrentPlan("trial");
    }
  }, []);

  // Preload the NAPLAN expansion files for the chosen year so a session starts without waiting
  useEffect(() => {
    if (examProgram === "naplan" && ["y3", "y5", "y7", "y9"].includes(yearId)) NAPLAN_EXTRA_SUBJECTS.forEach((sbj) => loadNaplanExtra(yearId, sbj));
    else if (examProgram && examProgram !== "naplan" && yearId) {
      loadExamManifest().then(() => setExamExtraLoads((n) => n + 1));
      (EXAM_SUBJECTS[examProgram] || []).forEach((sbj) => loadExamExtra(examProgram, yearId, sbj).then((qs) => { if (qs.length) setExamExtraLoads((n) => n + 1); }));
    }
  }, [examProgram, yearId]);

  // Restore session on refresh
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("lumio_session") || "{}");
      const safeScreens = ["yearSelect","subjectHome","dashboard","glowUp"];
      if (saved.screen && safeScreens.includes(saved.screen)) {
        if (saved.examProgram) setExamProgram(saved.examProgram);
        if (saved.yearId) setYearId(saved.yearId);
        if (saved.subjectId) setSubjectId(saved.subjectId);
        setScreen(saved.screen);
      }

      // Restore mid-exam session if it exists and is recent (< 2 hours old)
      const examSaved = JSON.parse(localStorage.getItem("lumio_exam_session") || "{}");
      const TWO_HOURS = 2 * 60 * 60 * 1000;
      if (
        examSaved.questions?.length > 0 &&
        examSaved.savedAt &&
        Date.now() - examSaved.savedAt < TWO_HOURS
      ) {
        // Rebuild question objects from IDs. Expansion questions arrive in a separate chunk: if any saved id is
        // not found yet, wait for that chunk and restore then, so a resumed exam never silently loses questions.
        const restoreExam = () => {
        const allQ = [
          ...Object.values(QB).flat(),
          ...ALL_QUESTIONS,
          ...EXAM_QUESTIONS,
          ...NAPLAN_Y3_QUESTIONS,
          ...NAPLAN_Y5_QUESTIONS,
          ...NAPLAN_Y7_QUESTIONS,
          ...NAPLAN_Y9_QUESTIONS,
          ...allLoadedNaplanExtra(),
          ...allLoadedExamExtra(),
        ];
        const idMap = {};
        allQ.forEach(q => { if (q?.id) idMap[q.id] = q; });
        const restored = examSaved.questions.map(id => idMap[id]).filter(Boolean);
        if (restored.length > 0) {
          setExamProgram(examSaved.examProgram || "naplan");
          setYearId(examSaved.yearId || null);
          setSubjectId(examSaved.subjectId || null);
          setMode(examSaved.mode || "practice");
          setTopicFilter(examSaved.topicFilter || null);
          setQuestions(restored);
          setQIndex(examSaved.qIndex || 0);
          setAnswers(examSaved.answers || {});
          setAdaptiveState(examSaved.adaptiveState || { currentLevel:2, streak:0, streakDirection:"correct" });
          setExamRoute(Array.isArray(examSaved.examRoute) ? examSaved.examRoute : []);
          setExamTestletSize(examSaved.testletSize || TESTLET_SIZE);
          setExamMinutes(examSaved.minutes || null);
          const savedChosen = (examSaved.answers || {})[examSaved.qIndex];
          setChosen(savedChosen !== undefined ? savedChosen : null);
          setScreen("question");
          return true;
        }
        return false;
        };
        const savedProg = examSaved.examProgram || "naplan";
        const needNaplanExtra = examSaved.yearId && examSaved.subjectId && !naplanExtraReady(examSaved.yearId, examSaved.subjectId)
            && examSaved.questions.some(id => /^naplan_y\d_[a-z]+_x/.test(String(id)));
        const needExamExtra = savedProg !== "naplan" && examSaved.yearId && examSaved.subjectId && !examExtraReady(savedProg, examSaved.yearId, examSaved.subjectId)
            && examSaved.questions.some(id => /^[a-z_]+_y\d+_[a-z]+_x/.test(String(id)));
        if (!banksLoaded() || needNaplanExtra || needExamExtra) {
          loadBanks()
            .then(() => (needNaplanExtra ? loadNaplanExtra(examSaved.yearId, examSaved.subjectId) : needExamExtra ? loadExamExtra(savedProg, examSaved.yearId, examSaved.subjectId) : null))
            .then(() => { try { restoreExam(); } catch (_) {} })
            .catch(() => {});
          return; // the deferred restore sets the screen
        }
        if (restoreExam()) return; // Don't override with screen restore
      }

      // Restore in-flight Practice/Exam picker selection (homeMode), if any.
      // Only reachable when no exam session was restored above, so a
      // mid-question resume always takes priority over the picker state.
      const homeModeSaved = JSON.parse(localStorage.getItem("lumio_home_mode") || "{}");
      const HOME_MODE_MAX_AGE = 24 * 60 * 60 * 1000;
      if (
        homeModeSaved.homeMode &&
        homeModeSaved.savedAt &&
        Date.now() - homeModeSaved.savedAt < HOME_MODE_MAX_AGE
      ) {
        if (homeModeSaved.examProgram) setExamProgram(homeModeSaved.examProgram);
        if (homeModeSaved.yearId) setYearId(homeModeSaved.yearId);
        setHomeMode(homeModeSaved.homeMode);
      }
    } catch(e) {}
    // Fallback: if the initial-state check above guessed "loading" but
    // nothing here actually matched (stale/malformed saved data), fall
    // through to the normal home screen instead of hanging on a blank page.
    setScreen(s => s === "loading" ? "yearSelect" : s);
  // eslint-disable-next-line
  }, []);

  // Splash auto-advance — only fires if nothing else (session/exam/homeMode
  // restore) has already moved the screen away from splash.
  useEffect(() => {
    const t = setTimeout(() => setScreen(s => s === "splash" ? "yearSelect" : s), 400);
    return () => clearTimeout(t);
  }, []);

  // Persist the Practice/Exam picker's in-flight selection so a refresh
  // doesn't drop the user back to Home mid-flow (mirrors lumio_learn_nav).
  // Skips its first invocation so it can't clear lumio_home_mode using the
  // pre-restore (homeMode===null) closure before the restore-on-refresh
  // effect's setHomeMode has landed — a real race under StrictMode's
  // dev-only double effect invocation.
  const skipHomeModePersist = useRef(true);
  useEffect(() => {
    if (skipHomeModePersist.current) { skipHomeModePersist.current = false; return; }
    try {
      if (homeMode) {
        localStorage.setItem("lumio_home_mode", JSON.stringify({ homeMode, examProgram, yearId, savedAt: Date.now() }));
      } else {
        localStorage.removeItem("lumio_home_mode");
      }
    } catch(e) {}
  }, [homeMode, examProgram, yearId]);

  // ── Official papers: section tests and the full mock exam ─────────────
  // A paper runs at its official length only when the bank has enough questions for it; otherwise the section test
  // falls back to the short default exam, and the mock exam is not offered for that exam and year.
  function availablePaper(program, year, subject) {
    const paper = getPaper(program, year, subject);
    if (!paper) return null;
    // timed writing: NAPLAN's 10 criteria, or an exam with its own prompts and marking (EXAM_WRITING)
    if (paper.writing) return program === "naplan" || EXAM_WRITING[program] ? paper : null;
    return !banksReady || buildPool(subject, year, null, program).length >= (paper.questions || 10) ? paper : null;
  }
  function mockPapers(program, year) {
    const papers = hasMock(program) ? getPapers(program, year) : [];
    return papers.length >= 2 && papers.every(p => availablePaper(program, year, p.subject)) ? papers : [];
  }
  function startMock() {
    if (!ent.exams) { setShowPricing(true); return; } // the trial has ended without a plan
    setMock({ program: examProgram, yearId, papers: mockPapers(examProgram, yearId), idx: 0, results: [] });
    navigate("mock");
  }
  function runMockPaper(i) {
    const paper = mock?.papers?.[i];
    if (!paper) return;
    setMock(m => ({ ...m, idx: i }));
    if (paper.writing) { setSubjectId(paper.subject); setShowWriting("mock"); }
    else startSession(paper.subject, mock.yearId, "exam", null, { paper });
  }
  function recordMockResult(r) {
    setMock(m => (m && m.results.length === m.idx ? { ...m, results: [...m.results, r] } : m));
  }
  function continueMock() {
    if (!mock) return;
    const next = mock.idx + 1;
    if (next < mock.papers.length) runMockPaper(next);
    else { setMock(m => ({ ...m, idx: m.papers.length })); navigate("mock"); }
  }
  // a mock paper that just finished (answered through, or the timer ran out) is recorded once on its results screen
  useEffect(() => {
    if (screen !== "results" || !mock || mode !== "exam" || mock.idx >= mock.papers.length || mock.results.length !== mock.idx || !questions.length) return;
    const o = examOutcome(questions, answers, mode, examRoute, examTestletSize);
    // if time ran out before the later testlets were served, those questions still count, as unanswered
    const full = mock.papers[mock.idx].questions || 0;
    recordMockResult({ name: mock.papers[mock.idx].name, subject: subjectId, ...o, total: Math.max(o.total, full) });
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  function navigate(s) {
    setPrevScreen(screen); setScreen(s);
    try { window.scrollTo(0, 0); } catch (_) {}
    if (s !== "yearSelect") setHomeMode(null);
    localStorage.setItem("lumio_session", JSON.stringify({ screen:s, examProgram, yearId, subjectId }));
    // Clear mid-exam state when leaving the question screen
    if (s !== "question") {
      localStorage.removeItem("lumio_exam_session");
    }
  }

  // Save mid-exam state after every answer
  function saveExamSession(newAnswers, newQIndex, questionsNow, routeNow) {
    if (screen !== "question" && questions.length === 0) return;
    try {
      localStorage.setItem("lumio_exam_session", JSON.stringify({
        questions: (questionsNow || questions).map(q => q.id), // save IDs only
        examRoute: routeNow || examRoute,
        qIndex: newQIndex !== undefined ? newQIndex : qIndex,
        answers: newAnswers || answers,
        subjectId, yearId, mode, examProgram, topicFilter,
        adaptiveState, testletSize: examTestletSize, minutes: examMinutes,
        savedAt: Date.now(),
      }));
    } catch(e) {}
  }

  // every paid plan includes every exam; the trial does too, until its 7 days end
  function canAccessExam(examId) { return ent.exams; }

  function isOnTrial() { return ent.tier === "trial"; }

  function isOwner() {
    const stored = localStorage.getItem("lumio_access");
    if (!stored) return false;
    return false; // a stored "owner" plan is no longer trusted (anyone could set it in the browser)
  }

  function getTrialQuestionsUsed(subjectId) {
    const usage = JSON.parse(localStorage.getItem("lumio_trial_usage") || "{}");
    return usage[subjectId] || 0;
  }

  function incrementTrialUsage(subjectId, count) {
    try {
      const usage = JSON.parse(localStorage.getItem("lumio_trial_usage") || "{}");
      const current = usage[subjectId] || 0;
      usage[subjectId] = Math.min(10, current + count);
      localStorage.setItem("lumio_trial_usage", JSON.stringify(usage));
    } catch(_) {}
  }

  function getPlanLabel() {
    if (ent.tier === "trial") return "Free Trial";
    if (ent.tier === "expired") return "Trial ended";
    if (ent.tier === "owner") return "Owner";
    return PLAN_OPTIONS.find(p => p.id === ent.tier)?.name || "Free Trial";
  }

  // opts.paper: an official paper { minutes, questions } — a section test or one paper of a full mock exam
  function startSession(subj, year, m, topic = null, opts = {}) {
    if (!banksLoaded()) { loadBanks().then(() => startSession(subj, year, m, topic, opts)).catch(() => {}); return true; } // fetch, then start
    // NAPLAN expansion questions for this year+subject must be loaded first: fetch, then start (usually preloaded)
    if (examProgram === "naplan" && NAPLAN_EXTRA_SUBJECTS.includes(subj) && !naplanExtraReady(year, subj)) {
      loadNaplanExtra(year, subj).then(() => startSession(subj, year, m, topic, opts));
      return true;
    }
    // same for the other exams' expansion banks
    if (examProgram !== "naplan" && year && subj !== "all" && !examExtraReady(examProgram, year, subj)) {
      loadExamExtra(examProgram, year, subj).then(() => startSession(subj, year, m, topic, opts));
      return true;
    }
    if (!canAccessExam(examProgram)) { setShowPricing(true); return false; }
    // (the 7-day trial has no question limits; canAccessExam above locks everything once it ends)
    setSubjectId(subj); setYearId(year); setMode(m); setTopicFilter(topic);
    // NAPLAN exams are multistage: start with one middle-difficulty testlet; the rest are added as the student answers
    const multistage = m === "exam" && examProgram === "naplan";
    const paper = m === "exam" ? opts.paper : null;
    // an official paper's question count is split over the testlets; otherwise the short default exam
    const size = multistage && paper?.questions ? Math.max(3, Math.ceil(paper.questions / EXAM_TESTLETS)) : TESTLET_SIZE;
    setExamTestletSize(size);
    setExamMinutes(paper?.minutes || null);
    // reading passages come as whole units, their questions together and in order
    const unitPool = buildPool(subj, year, topic, examProgram);
    const want = m === "exam" ? (paper?.questions || 10) : 8;
    const qs = multistage
      ? groupByPassage(buildTestlet(unitPool, new Set(), 2, size), unitPool, size, { exact: true })
      : groupByPassage(getAdaptiveQuestions(subj, year, topic, want, examProgram), unitPool, want, { exact: m === "exam" });
    setExamRoute(multistage ? [2] : []);
    console.log('[Practice] startSession', { subj, year, m, examProgram, poolSize: qs?.length ?? 0 });
    if (!qs || qs.length === 0) { console.warn('[Practice] empty pool — not navigating', { subj, year, examProgram }); return false; }
    trackEvent('practice_started', { subject: subj, yearId: year, mode: m, examProgram, topic: topic || null, poolSize: qs.length });
    // Mark these questions as seen so next session gets fresh ones (practice mode only)
    if (m === "practice") markIdsSeen(qs.map(q => q.id), subj, year, examProgram);
    // Build and store full pool for adaptive mid-session question selection
    const fullPool = buildPool(subj, year, topic, examProgram);
    const initUsed = new Set(qs.map(q => q.id));
    setAdaptivePool(fullPool);
    setAdaptiveUsed(initUsed);
    setAdaptiveState({ currentLevel:2, streak:0, streakDirection:'correct' });
    setQuestions(qs); setQIndex(0); setAnswers({});
    sessionStartRef.current = Date.now();
    setGoalJustMet(null);
    setChosen(null); // always null — fresh session, no pre-answered questions
    navigate("question");
    return true;
  }

  function handleAnswer(i) {
    if (chosen !== null) return;
    try {
      setChosen(i);
      const newAnswers = { ...answers, [qIndex]: i };
      setAnswers(() => newAnswers);
      const q = questions[qIndex];
      if (!q) return;
      const correct = isCorrectResponse(q, i);
      trackEvent('question_answered', {
        questionId: q.id, subject: q.subject, topic: q.topic, yearId: q.year?.[0],
        difficulty: q.difficulty, correct, examProgram, mode,
      });
      // Persist mid-exam progress immediately
      saveExamSession(newAnswers, qIndex);
      // Every wrong answer goes to this child's Glow Up, to be fixed and proved later
      if (!correct) { try { recordMistake(activeProfile?.id, q, { subject: subjectId, year: yearId, examProgram }); } catch (_) {} }

      // ── Multistage exam: at the end of each testlet, add the next one at the routed level ──
      if (mode === "exam" && examRoute.length > 0 && examRoute.length < EXAM_TESTLETS && qIndex + 1 === examRoute.length * examTestletSize) {
        const start = qIndex + 1 - examTestletSize;
        let got = 0;
        for (let k = start; k <= qIndex; k++) if (isCorrectResponse(questions[k], newAnswers[k])) got++;
        const level = routeNextLevel(examRoute[examRoute.length - 1], got, examTestletSize);
        const used = new Set(questions.map(qq => qq.id));
        const tPool = adaptivePool.length ? adaptivePool : buildPool(subjectId, yearId, topicFilter, examProgram);
        const next = groupByPassage(buildTestlet(tPool, used, level, examTestletSize), tPool, examTestletSize, { exact: true, exclude: used });
        if (next.length) {
          const qsNow = [...questions, ...next], routeNow = [...examRoute, level];
          setQuestions(qsNow); setExamRoute(routeNow);
          saveExamSession(newAnswers, qIndex, qsNow, routeNow);
        }
      }
      // ── Adaptive difficulty update ──────────────────
      if (mode === "practice") {
        const newAdaptiveState = getNextAdaptiveLevel(adaptiveState, correct);
        setAdaptiveState(newAdaptiveState);
        // If we're not on the last question, replace the NEXT question
        // with one at the new target difficulty (if a better one exists)
        // (never inside a reading unit: a passage's questions stay together, so no swap into or out of one)
        if (qIndex < questions.length - 2 && adaptivePool.length > 0 && !questions[qIndex + 1]?.passage) {
          const nextQ = getNextAdaptiveQuestion(adaptivePool.filter(x => !x.passage), adaptiveUsed, newAdaptiveState.currentLevel);
          if (nextQ && nextQ.id !== questions[qIndex + 1]?.id) {
            setAdaptiveUsed(u => { const n = new Set(u); n.add(nextQ.id); return n; });
            setQuestions(prev => {
              const updated = [...prev];
              updated[qIndex + 1] = nextQ;
              return updated;
            });
          }
        }
      }
      // ── Progress tracking ───────────────────────────
      setProgress(p => {
        const progKey = examProgram + '-' + (yearId||'y5') + '-' + subjectId; // scoped by exam+year+subject
        const sp = p[progKey] || p[subjectId] || { correct:0, total:0, topics:{} }; // fallback for legacy data
        const topic = q.topic || 'General';
        const tp = (sp.topics || {})[topic] || { correct:0, total:0 };
        if (correct) { setConfetti(true); setLumiMood("correct"); celebrate({ intensity: 'subtle' }); setTimeout(() => { setConfetti(false); setLumiMood("idle"); }, 2500); }
        else { setLumiMood("wrong"); setTimeout(() => setLumiMood("idle"), 2500); }
        return { ...p, [progKey]: { ...sp, correct:sp.correct+(correct?1:0), total:sp.total+1, topics:{ ...(sp.topics||{}), [topic]:{ correct:tp.correct+(correct?1:0), total:tp.total+1 } } } };
      });
    } catch(e) {
      console.error('handleAnswer error:', e, 'question:', questions[qIndex]?.id);
    }
  }

  function nextQuestion() {
    if (qIndex < questions.length - 1) {
      setQIndex(i => i + 1);
      setChosen(answers[qIndex + 1] !== undefined ? answers[qIndex + 1] : null);
    } else {
      const correct = questions.filter((qq, i) => isCorrectResponse(qq, answers[i])).length;
      const mins = sessionStartRef.current ? Math.max(1, Math.min(120, Math.round((Date.now() - sessionStartRef.current) / 60000))) : undefined;
      const readActivity = () => { try { const a = JSON.parse(localStorage.getItem(profileKey("lumio_activity", activeProfile?.id)) || "[]"); return Array.isArray(a) ? a : []; } catch { return []; } };
      const wasMet = dailyGoal(readActivity()).met;
      trackSession(subjectId, correct, questions.length, activeProfile?.id, mins);
      const goalNow = dailyGoal(readActivity());
      setGoalJustMet(!wasMet && goalNow.met ? goalNow.streak : null);
      trackExamProgress(examProgram, questions.length, activeProfile?.id);
      navigate("results");
      window.scrollTo(0, 0); // results open at the top, where the goal and Glow Up banners are, not at the last question's scroll

    }
  }

  function prevQuestion() {
    if (qIndex > 0) { setQIndex(i => i - 1); setChosen(answers[qIndex-1] !== undefined ? answers[qIndex-1] : null); }
  }

  const subj = SUBJECTS.find(s => s.id === subjectId);
  const year = ALL_YEARS.find(y => y.id === yearId);

  // ── Nav action handler ───────────────────────────────────────
  function handleNavAction(id) {
    setShowLearn(false);
    setShowLeaderboard(false);
    setShowPricing(false);
    if (id === "home" || id === "today") navigate("yearSelect");
    else if (id === "learn")       setShowLearn(true);
    else if (id === "practice")    { navigate("yearSelect"); setHomeMode("hub"); }
    else if (id === "leaderboard") setShowLeaderboard(true);
    else if (id === "progress" || id === "me") navigate("me");   // the child's own progress; the parent's is in the parent area
    else if (id === "parent")      navigate("dashboard");
    else if (id === "account")     { sessionStorage.setItem("lumio_dashboard_tab","settings"); navigate("dashboard"); }
  }
  // which of the four tabs is showing
  const activeTab = showLearn ? "learn" : screen === "me" ? "me"
    : screen === "yearSelect" ? (homeMode ? "practice" : "today")
    : ["subjectHome", "topicSelect", "question", "results", "glowUp", "mock"].includes(screen) ? "practice" : null;
  const pickChild = (p) => {
    setActiveProfile(p);
    try { localStorage.setItem("lumio_active_profile", p?.id || ""); sessionStorage.setItem("lumio_learner_picked", "1"); } catch (_) {}
    try { setProgress(JSON.parse(localStorage.getItem(profileKey('lumio_progress_v2', p?.id)) || '{}')); } catch (_) { setProgress({}); }
    setPickLearner(false); setShowLearn(false); navigate("yearSelect");
  };
  // Today and Practice open a test straight at the child's year: practice goes to the subject list, a mock exam to
  // the exam's sections
  // every test opens on one practice page (subjectHome; HSC/VCE pick a subject first). Mock exam starts the full mock
  // when the test has one, otherwise it opens the same page, whose section tests are the timed option.
  const openTest = (pid, yid) => { setExamProgram(pid); setYearId(yid); if (pid === "hsc" || pid === "vce") { navigate("yearSelect"); setHomeMode("exam-y12-subject"); } else navigate("subjectHome"); };
  const practiseTest = openTest;
  const hasFullMock = (pid, yid) => mockPapers(pid, yid).length > 0;
  const mockTest = (pid, yid) => {
    if (!hasFullMock(pid, yid)) return openTest(pid, yid);
    if (!ent.exams) { setShowPricing(true); return; }
    setExamProgram(pid); setYearId(yid);
    setMock({ program: pid, yearId: yid, papers: mockPapers(pid, yid), idx: 0, results: [] });
    navigate("mock");
  };
  // a test with one year level skips the year step
  const pickProgram = (pid) => { const ys = EXAM_YEARS[pid] || []; if (ys.length === 1) openTest(pid, ys[0]); else { setExamProgram(pid); setYearId(null); setHomeMode("practice-year"); } };

  // Props passed to every AppShell
  const shellProps = {
    screen, onAction: handleNavAction, tab: activeTab,
    activeProfile, currentPlan, planTier: ent.tier,
    navigate,
    noNav: ["question","results"].includes(screen),
  };

  // ── OVERLAYS ──────────────────────────────────────────────
  if (showChat) return (
    <AppShell {...shellProps}>
      <ErrorBoundary fallback={<div style={{padding:24,textAlign:"center",color:T.text}}><div style={{margin:"0 auto 12px",width:64,height:64}}><Lumi state={LUMI_STATES.STUCK} size={64}/></div><p style={{fontWeight:700,marginBottom:12}}>Lumi is taking a break</p><button onClick={()=>setShowChat(false)} style={{background:T.accent,border:"none",borderRadius:10,color:"white",padding:"10px 20px",fontWeight:700,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Go Back</button></div>}>
        <LumiTutor onClose={() => setShowChat(false)} subject={chatContext?.subject||""} yearLevel={chatContext?.year||""} lastQuestion={(() => {
          const q = chatContext?.question || "";
          const sa = chatContext?.studentAnswer || "";
          const ca = chatContext?.correctAnswer || "";
          const ctx = chatContext?.context || "";
          let composed = q;
          if (ca) composed += `\n\n[Correct answer: "${ca}"]`;
          if (ctx) composed += `\n\n${ctx}`;
          return composed;
        })()} lastAnswer={chatContext?.studentAnswer || ""} wasCorrect={chatContext?.studentAnswer ? false : null} theme={T} />
      </ErrorBoundary>
    </AppShell>
  );




  if (showInteractive) return <AppShell {...shellProps}><ErrorBoundary fallback={(err) => <div style={{padding:24,textAlign:"center"}}><p style={{color:T.text,fontWeight:700,marginBottom:8}}>Interactive mode failed to load</p>{err && <p style={{color:"#DC2626",fontSize:12,marginBottom:8,maxWidth:300,margin:"0 auto 8px",wordBreak:"break-all"}}>{err.message || String(err)}</p>}<button onClick={()=>setShowInteractive(false)} style={{background:T.accent,border:"none",borderRadius:10,color:"white",padding:"10px 20px",fontWeight:700,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}> Back</button></div>}><ExamInteractive yearId={yearId} examProgram={examProgram} subject={subjectId} onClose={() => setShowInteractive(false)} /></ErrorBoundary></AppShell>;
  // showWriting: true = essay practice with prompt choice and tips; "exam" = timed NAPLAN writing test
  if (showWriting === "mock") {
    const paperDone = mock && mock.results.length > mock.idx;
    const nextPaper = mock?.papers?.[mock.idx + 1];
    return <AppShell {...shellProps} noNav><WritingPractice yearId={yearId} exam serverUrl={SPARKY_SERVER_URL} examProgram={examProgram}
      paper={mock?.papers?.[mock.idx]}
      onResult={(r) => recordMockResult({ name: mock.papers[mock.idx].name, subject: "writing", writing: true, ...r })}
      doneLabel={nextPaper ? `Continue to ${nextPaper.name} →` : "See mock exam report →"}
      onClose={() => { setShowWriting(false); if (paperDone || (mock && mock.results.length > mock.idx)) continueMock(); else navigate("mock"); }} /></AppShell>;
  }
  // writing screens hide the tabs like a question does: the back arrow asks before work is thrown away
  if (showWriting) return <AppShell {...shellProps} noNav><WritingPractice yearId={yearId} examProgram={examProgram} exam={showWriting === "exam"} paper={showWriting === "exam" ? getPaper(examProgram, yearId, "writing") : null} onClose={() => setShowWriting(false)} serverUrl={SPARKY_SERVER_URL} /></AppShell>;

  // ── Trial-limit helpers ─────────────────────────────────────────
  // Pragmatic wiring: we check + increment at *session start*, not
  // per-question. Per-question would require changes inside the
  // ExamInteractive widget which we explicitly mustn't touch. With a
  // 10-session budget per (year, subject, mode), beta families get a
  // genuine taste of each subject and the upgrade gate fires after.
  // When ExamInteractive grows a per-question callback, switch this to
  // an onQuestionRendered increment instead.
  const userId = authSession?.user?.id || null;
  const isTrialAdmin = isAdmin; // Convenience alias for clarity at call sites.

  // Try to start an interactive (practice or exam) session. Returns
  // true iff the call site should proceed; false means the upgrade
  // modal has been shown and the action should be cancelled.
  // Interactive mode is part of every exam plan and the trial; once the trial has ended without a plan it locks.
  async function tryStartInteractive(targetYearId, targetSubjectId, mode = 'exam') {
    if (!ent.exams) { setShowPricing(true); return false; }
    return true;
  }


  // Try to open a Learn topic. Returns true iff the call site should
  // proceed. Records the topic in topics_opened[] on first open.
  async function tryOpenTopic(targetYearId, targetSubjectId, topicId) {
    if (isTrialAdmin || !supabaseEnabled || !userId) return true;
    const { allowed, alreadyOpened } = await canOpenTopic({
      userId, yearId: targetYearId, subject: targetSubjectId, topicId, isAdmin: isTrialAdmin,
    });
    if (!allowed) {
      setUpgradeModal({
        reason: 'learn',
        yearLabel: ALL_YEARS.find(y => y.id === targetYearId)?.label || targetYearId,
        subjectLabel: SUBJECTS.find(s => s.id === targetSubjectId)?.label || targetSubjectId,
      });
      return false;
    }
    if (!alreadyOpened) recordTopicOpened(userId, targetYearId, targetSubjectId, topicId);
    return true;
  }

  // ── Gate render order ────────────────────────────────────────────
  // 1. Don't flash the gate until the auth check finishes.
  // 2. Otherwise → LoginScreen (always, regardless of Supabase config). There is no code or admin shortcut.
  if (!authChecked) return <div style={{ minHeight: '100vh', background: T.bg }} />;
  if (recovering && supabase) return <SetNewPassword email={authSession?.user?.email} onDone={() => setRecovering(false)} />;
  if (!isAdmin && !authSession && !hasAccess) {
    return <LoginScreen
      onAuthed={(s) => { setAuthSession(s); setHasAccess(true); }}
    />;
  }

  if (pickLearner) {
    const ps = learners;
    if (ps.length > 1) return <LearnerPicker profiles={ps} onPick={pickChild} onParent={() => { setPickLearner(false); navigate("dashboard"); }} />;
  }
  // free week over and no plan: one calm page for the child (plans and the parent area stay reachable)
  if (ent.tier === "expired" && screen !== "dashboard" && !showPricing) return (
    <TrialEnded profile={activeProfile} email={authSession?.user?.email}
      onRemind={async () => {
        const r = await fetch(`${SPARKY_SERVER_URL}/api/remind-parent`, { method: "POST",
          headers: { "Content-Type": "application/json", ...(await authHeaders()) }, body: JSON.stringify({ childName: activeProfile?.name || "" }) });
        if (r.status === 429) return "already";
        return r.ok ? "sent" : "failed";
      }}
      onPlans={() => setShowPricing(true)} onParent={() => navigate("dashboard")}
      onSwitch={learners.length > 1 ? () => { try { sessionStorage.removeItem("lumio_learner_picked"); } catch (_) {} setPickLearner(true); } : null} />
  );

  // ── Global overlays portal (visible across every in-app screen) ──
  // Rendered into document.body so the position:fixed overlays don't
  // get clipped by any of the many `return <AppShell>` branches.
  // We render this as a side-effect component invoked for its
  // returned portal node — see <GlobalOverlays /> below.
  const renderGlobalOverlays = () => {
    if (typeof document === 'undefined') return null;
    return ReactDOM.createPortal(
      <>
        {authSession?.user && <EmailVerifyBanner user={authSession.user} />}
        {upgradeModal && (
          <UpgradeModal
            {...upgradeModal}
            userEmail={authSession?.user?.email || ''}
            userId={userId}
            onDismiss={() => setUpgradeModal(null)}
          />
        )}
      </>,
      document.body,
    );
  };
  // Feature screens
  if (showLearn) return (
    <>
    {renderGlobalOverlays()}
    <AppShell {...shellProps}>
      <LearnSection key={activeProfile?.id ?? 'default'} onClose={() => setShowLearn(false)} onNavigateProgress={() => handleNavAction("me")}
        learnAccess={ent.learn} onUpgrade={() => { setShowLearn(false); setShowPricing(true); }} defaultYear={learnYearOf(activeProfile?.yearId)} />
    </AppShell>
    </>
  );

  if (showLeaderboard) return (
    <AppShell {...shellProps}>
      <Leaderboard
        onClose={() => setShowLeaderboard(false)}
        activeProfile={activeProfile}
        examProgram={examProgram}
      />
    </AppShell>
  );

  if (showPricing) return (
    <AppShell {...shellProps}>
      <div style={{ overflowY:"auto", flex:1 }}>
        <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={() => setShowPricing(false)} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>←</button>
          <span style={{ fontSize:14, fontWeight:700, color:T.text }}>Choose a Plan</span>
        </div>
        <PricingScreen currentPlan={currentPlan} trial={ent.tier === "owner" ? null : ent.trial} />
      </div>
    </AppShell>
  );

  if (showLeaderboard) return (
    <AppShell {...shellProps}>
      <div style={{ overflowY:"auto", flex:1 }}>
        <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={() => setShowLeaderboard(false)} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>←</button>
          <span style={{ fontSize:14, fontWeight:700, color:T.text }}>Class Leaderboard</span>
        </div>
        <ClassLeaderboard onClose={() => setShowLeaderboard(false)} />
      </div>
    </AppShell>
  );
  if (showStudyPlanner) return (
    <AppShell {...shellProps}>
      <div style={{ overflowY:"auto", flex:1 }}>
        <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={() => setShowStudyPlanner(false)} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>←</button>
          <span style={{ fontSize:14, fontWeight:700, color:T.text }}>Study Planner</span>
        </div>
        <StudyPlanner examProgram={examProgram} yearId={yearId||"y5"} onClose={() => setShowStudyPlanner(false)} onStartPractice={(subject, yr) => { setShowStudyPlanner(false); startSession(subject, yr||yearId, "practice"); }} />
      </div>
    </AppShell>
  );
  if (showReport) return <AppShell {...shellProps}><div style={{ overflowY:"auto", flex:1 }}><ShareableReport progress={progress} profileName={activeProfile?.name||"Student"} yearId={yearId||activeProfile?.yearId||"y5"} activeProfile={activeProfile} examProgram={examProgram} onClose={() => setShowReport(false)} /></div></AppShell>;
  if (showBugPipeline) return <AppShell {...shellProps}><div style={{ overflowY:"auto", flex:1 }}><BugPipelineDashboard serverUrl={SPARKY_SERVER_URL} onClose={() => setShowBugPipeline(false)} /></div></AppShell>;

  // Loading — brief placeholder for returning users while the restore-on-
  // refresh effect figures out which real screen to land on. Deliberately
  // blank (no branding) so it never reads as its own screen.
  if (screen === "loading") return <div style={{ minHeight: "100vh", background: T.bg }} />;

  // Splash
  if (screen === "splash") return (
    <AppShell>
      <div onClick={() => setScreen("yearSelect")} role="button" tabIndex={0} onKeyDown={e => (e.key === "Enter" || e.key === " ") && setScreen("yearSelect")} style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:32, background:T.bg, position:"relative", overflow:"hidden", cursor:"default" }}>
        {/* Background blobs */}
        <div style={{ position:"absolute", top:-200, right:-100, width:500, height:500, borderRadius:"50%", background:T.accentLo, filter:"blur(80px)", opacity:0.5, pointerEvents:"none" }}/>
        <div style={{ position:"absolute", bottom:-150, left:-80, width:400, height:400, borderRadius:"50%", background:"#FEF7E0", filter:"blur(80px)", opacity:0.6, pointerEvents:"none" }}/>
        <div style={{ textAlign:"center", animation:"scaleIn .8s ease", position:"relative", zIndex:1 }}>
          {/* Logo mark */}
          <div style={{ width:80, height:80, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 24px" }}><Lumi state={LUMI_STATES.IDLE} size={80} /></div>
          <h1 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:"clamp(36px,8vw,58px)", color:T.text, marginBottom:8, lineHeight:1.05, fontWeight:900, letterSpacing:"-0.02em" }}>
            Empirio
          </h1>
          <p style={{ color:T.textMid, fontSize:16, marginBottom:8, fontWeight:500 }}>Hands-on learning for Years 6 to 12</p>
          <p style={{ color:T.textDim, fontSize:13 }}>Loading your learning experience...</p>
          {/* Loading bar */}
          <div style={{ width:160, height:3, background:T.border, borderRadius:99, margin:"24px auto 0", overflow:"hidden" }}>
            <div style={{ height:"100%", background:`linear-gradient(90deg, ${T.accent}, ${T.accentMid})`, borderRadius:99, animation:"shimmer 1.5s ease infinite", backgroundSize:"200% 100%" }}/>
          </div>
        </div>
      </div>
    </AppShell>
  );

  // Year/Exam Select
  if (screen === "yearSelect") return (
    <>
    {renderGlobalOverlays()}
    <AppShell {...shellProps}>
      <div style={{ minHeight:"100vh", background:T.bg, animation:"fadeUp .35s ease" }}>

        {/* ── Trial strip (and the banner once it has ended) ── */}
        {false && ent.tier === "expired" && (
          <div role="status" style={{ background:T.accentLo, borderBottom:`1px solid ${T.accent}33`, padding:"12px 24px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <span style={{ fontSize:14, color:T.text, flex:1, minWidth:220 }}><b>Your 7-day free trial has ended.</b> Choose a plan to keep practising and learning — everything you've done is saved.</span>
            <button onClick={() => setShowPricing(true)} style={{ padding:"8px 16px", background:T.accent, border:"none", borderRadius:8, color:"white", fontWeight:800, fontSize:13, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>See plans</button>
          </div>
        )}
        {false && isOnTrial() && (
          <div style={{ background:`${T.gold}12`, borderBottom:`1px solid ${T.gold}22`, padding:"8px 24px", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:12, color:T.gold, fontWeight:600, flex:1 }}>🎯 Free trial · {ent.trial.daysLeft} day{ent.trial.daysLeft === 1 ? "" : "s"} left · every exam included</span>
            <button onClick={() => setShowPricing(true)} style={{ padding:"4px 12px", background:T.gold, border:"none", borderRadius:6, color:"white", fontWeight:700, fontSize:11, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Upgrade</button>
          </div>
        )}

        {/* ── Today (the child's home) and the Practice tab ── */}
        {!homeMode && (
          <TodayHome profile={activeProfile} programs={EXAM_PROGRAMS} learnAccess={ent.learn}
            glowUp={getGlowUpCounts(activeProfile?.id)}
            daily={(() => { try { const a = JSON.parse(localStorage.getItem(profileKey("lumio_activity", activeProfile?.id)) || "[]"); return dailyGoal(Array.isArray(a) ? a : []); } catch { return dailyGoal([]); } })()}
            onContinue={(nav) => { resumeLearnAt(nav); setShowLearn(true); }}
            onLearn={() => setShowLearn(true)} onPractise={practiseTest} onGlowUp={() => navigate("glowUp")} />
        )}
        {homeMode === "hub" && (
          <PracticeHub profile={activeProfile} programs={EXAM_PROGRAMS} examProgram={examProgram} glowUp={getGlowUpCounts(activeProfile?.id)}
            onPractise={practiseTest} onMock={mockTest} hasMock={hasFullMock} onGlowUp={() => navigate("glowUp")} onMore={() => setHomeMode("practice")} />
        )}

        <div style={{ padding: homeMode && homeMode !== "hub" ? "24px 24px 0" : 0, maxWidth: homeMode && homeMode !== "hub" ? 1000 : undefined, margin: "0 auto" }}>

          {/* ── Practice: program picker ── */}
          {homeMode === "practice" && (
            <div style={{ animation:"fadeUp .3s ease", marginBottom:32 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                <button onClick={() => setHomeMode("hub")} style={{ width:44, height:44, borderRadius:8, background:T.bg, border:`1px solid ${T.border}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:T.textMid }}>←</button>
                <h2 style={{ fontSize:16, fontWeight:800, color:T.text, margin:0 }}>📝 Practice — Choose program</h2>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 280px), 1fr))", gap:10 }}>
                {EXAM_PROGRAMS.filter(prog => prog.available !== false).map(prog => {
                  const hasAcc = canAccessExam(prog.id);
                  return (
                    <button key={prog.id}
                      onClick={() => { if (!hasAcc) { setShowPricing(true); return; } pickProgram(prog.id); }}
                      style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:14, border:`1.5px solid ${T.border}`, background:T.surface, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", textAlign:"left", transition:"all .18s", opacity: hasAcc?1:0.55 }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor=prog.color; e.currentTarget.style.background=`${prog.color}08`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor=T.border; e.currentTarget.style.background=T.surface; }}>
                      <div style={{ width:40, height:40, borderRadius:12, background:`${prog.color}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{prog.icon}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{prog.label}</div>
                        <div style={{ fontSize:11, color:T.textMid, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{prog.desc}</div>
                      </div>
                      {!hasAcc && <span style={{ fontSize:11, color:T.textDim, flexShrink:0 }}>🔒</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Practice mode: year selector ── */}
          {homeMode === "practice-year" && (
            <div style={{ animation:"fadeUp .3s ease", marginBottom:32 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                <button onClick={() => setHomeMode("practice")} style={{ width:28, height:28, borderRadius:8, background:T.bg, border:`1px solid ${T.border}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:T.textMid }}>←</button>
                <h2 style={{ fontSize:16, fontWeight:800, color:T.text, margin:0 }}>
                  {EXAM_PROGRAMS.find(p=>p.id===examProgram)?.icon} {EXAM_PROGRAMS.find(p=>p.id===examProgram)?.label} — Select Year Level
                </h2>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 280px), 1fr))", gap:10 }}>
                {(EXAM_YEARS[examProgram]||["y5"]).map(yid => {
                  const y = ALL_YEARS.find(y=>y.id===yid);
                  if (!y) return null;
                  return (
                    <button key={y.id}
                      onClick={() => openTest(examProgram, y.id)}
                      style={{ display:"flex", alignItems:"center", gap:14, padding:"16px 18px", borderRadius:14, border:`1.5px solid ${T.border}`, background:T.surface, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", textAlign:"left", transition:"all .18s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor=y.color; e.currentTarget.style.background=`${y.color}08`; e.currentTarget.style.transform="translateY(-1px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor=T.border; e.currentTarget.style.background=T.surface; e.currentTarget.style.transform=""; }}>
                      <div style={{ width:44, height:44, borderRadius:14, background:`${y.color}18`, border:`1.5px solid ${y.color}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <span style={{ fontSize:11, fontWeight:800, color:y.color }}>{y.label.toUpperCase()}</span>
                      </div>
                      <div>
                        <div style={{ fontSize:15, fontWeight:800, color:T.text, fontFamily:"'Space Grotesk',sans-serif" }}>{y.label}</div>
                        <div style={{ fontSize:12, color:T.textMid }}>{y.age}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Practice: subject picker ── */}
          {homeMode === "practice-subject" && (
            <div style={{ animation:"fadeUp .3s ease", marginBottom:32 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                <button onClick={() => setHomeMode("practice-year")} style={{ width:44, height:44, borderRadius:8, background:T.bg, border:`1px solid ${T.border}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:T.textMid }}>←</button>
                <h2 style={{ fontSize:16, fontWeight:800, color:T.text, margin:0 }}>
                  {EXAM_PROGRAMS.find(p=>p.id===examProgram)?.icon} {ALL_YEARS.find(y=>y.id===yearId)?.label} — Choose subject
                </h2>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {(EXAM_SUBJECTS[examProgram] ? SUBJECTS.filter(s => (EXAM_SUBJECTS[examProgram]||[]).includes(s.id)) : SUBJECTS).map(s => {
                  const hasQs = !banksReady || buildPool(s.id, yearId, null, examProgram).length > 0;
                  const isWriting = isWritingSubject(s.id);
                  const naplanWriting = isWriting && examProgram === "naplan" && s.id === "writing";
                  return (
                    <div key={s.id} style={{ background:T.surface, border:`1.5px solid ${T.border}`, borderRadius:14, padding:"14px 16px", display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:40, height:40, borderRadius:12, background:s.lo, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{s.icon}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:14, fontWeight:700, color:T.text, margin:"0 0 2px" }}>{s.label}</p>
                        <p style={{ fontSize:11, color:T.textMid, margin:0 }}>{naplanWriting ? "Writing skills, essays and a timed test" : isWriting ? "Written response only" : !hasQs ? "Questions being added" : s.desc}</p>
                      </div>
                      {naplanWriting ? (
                        <div style={{ display:"flex", gap:6, flexShrink:0, flexWrap:"wrap", justifyContent:"flex-end" }}>
                          {<button onClick={() => { const ok = startSession(s.id, yearId, "practice"); if (ok) setHomeMode(null); }} style={{ padding:"9px 14px", background:s.color, border:"none", borderRadius:99, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:"nowrap" }}>✏️ Skills</button>}
                          <button onClick={() => { setSubjectId(s.id); setShowWriting(true); }} style={{ padding:"9px 14px", background:T.surface, border:`1.5px solid ${s.color}`, borderRadius:99, color:s.color, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:"nowrap" }}>📝 Essay</button>
                        </div>
                      ) : isWriting || !hasQs ? (
                        <span style={{ fontSize:11, fontWeight:600, color:T.textDim, background:T.border, padding:"4px 10px", borderRadius:99, whiteSpace:"nowrap", flexShrink:0 }}>Coming soon</span>
                      ) : (
                        <button
                          onClick={() => { const ok = startSession(s.id, yearId, "practice"); if (ok) setHomeMode(null); }}
                          style={{ padding:"9px 20px", background:s.color, border:"none", borderRadius:99, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:"nowrap", flexShrink:0 }}
                          onMouseOver={e => { e.currentTarget.style.filter="brightness(1.12)"; }}
                          onMouseOut={e => { e.currentTarget.style.filter=""; }}>
                          ✏️ Practice
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Exam mode: program selector ── */}
          {homeMode === "exam" && (
            <div style={{ animation:"fadeUp .3s ease", marginBottom:32 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                <button onClick={() => setHomeMode("hub")} style={{ width:28, height:28, borderRadius:8, background:T.bg, border:`1px solid ${T.border}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:T.textMid }}>←</button>
                <h2 style={{ fontSize:16, fontWeight:800, color:T.text, margin:0 }}>🎯 Exam Preparation — Choose program</h2>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 280px), 1fr))", gap:10 }}>
                {EXAM_PROGRAMS.filter(prog => prog.available !== false).map(prog => {
                  const hasAcc = canAccessExam(prog.id);
                  return (
                    <button key={prog.id}
                      onClick={() => { if (!hasAcc) { setShowPricing(true); return; } pickProgram(prog.id); }}
                      style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:14, border:`1.5px solid ${T.border}`, background:T.surface, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", textAlign:"left", transition:"all .18s", opacity: hasAcc?1:0.55 }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor=prog.color; e.currentTarget.style.background=`${prog.color}08`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor=T.border; e.currentTarget.style.background=T.surface; }}>
                      <div style={{ width:40, height:40, borderRadius:12, background:`${prog.color}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{prog.icon}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{prog.label}</div>
                        <div style={{ fontSize:11, color:T.textMid }}>{prog.format}</div>
                      </div>
                      {!hasAcc && <span style={{ fontSize:11, color:T.textDim, flexShrink:0 }}>🔒</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Exam mode: year selector ── */}
          {homeMode === "exam-year" && (
            <div style={{ animation:"fadeUp .3s ease", marginBottom:32 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                <button onClick={() => setHomeMode("exam")} style={{ width:28, height:28, borderRadius:8, background:T.bg, border:`1px solid ${T.border}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:T.textMid }}>←</button>
                <h2 style={{ fontSize:16, fontWeight:800, color:T.text, margin:0 }}>
                  {EXAM_PROGRAMS.find(p=>p.id===examProgram)?.icon} {EXAM_PROGRAMS.find(p=>p.id===examProgram)?.label} — Select Year Level
                </h2>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 280px), 1fr))", gap:10 }}>
                {(EXAM_YEARS[examProgram]||["y5"]).map(yid => {
                  const y = ALL_YEARS.find(y=>y.id===yid);
                  if (!y) return null;
                  return (
                    <button key={y.id}
                      onClick={() => openTest(examProgram, y.id)}
                      style={{ display:"flex", alignItems:"center", gap:14, padding:"16px 18px", borderRadius:14, border:`1.5px solid #F59E0B33`, background:"#F59E0B08", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", textAlign:"left", transition:"all .18s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor="#F59E0B88"; e.currentTarget.style.transform="translateY(-1px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor="#F59E0B33"; e.currentTarget.style.transform=""; }}>
                      <div style={{ width:44, height:44, borderRadius:14, background:"#F59E0B18", border:"1.5px solid #F59E0B33", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <span style={{ fontSize:11, fontWeight:800, color:"#F59E0B" }}>{y.label.toUpperCase()}</span>
                      </div>
                      <div>
                        <div style={{ fontSize:15, fontWeight:800, color:T.text, fontFamily:"'Space Grotesk',sans-serif" }}>{y.label}</div>
                        <div style={{ fontSize:12, color:T.textMid }}>{y.age}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Exam mode: Y12 subject picker (HSC / VCE) ── */}
          {homeMode === "exam-y12-subject" && (
            <div style={{ animation:"fadeUp .3s ease", marginBottom:32 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                <button onClick={() => setHomeMode("hub")} aria-label="Back to Practice" style={{ width:28, height:28, borderRadius:8, background:T.bg, border:`1px solid ${T.border}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:T.textMid }}>←</button>
                <h2 style={{ fontSize:16, fontWeight:800, color:T.text, margin:0 }}>
                  {EXAM_PROGRAMS.find(p=>p.id===examProgram)?.icon} {EXAM_PROGRAMS.find(p=>p.id===examProgram)?.label} — Choose Subject
                </h2>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {(examProgram === 'hsc' ? HSC_SUBJECTS : VCE_SUBJECTS).map((s, idx) => {
                  const prog = EXAM_PROGRAMS.find(p => p.id === examProgram);
                  const accentColor = prog?.color || T.accent;
                  return (
                    <button key={s.id}
                      onClick={async () => {
                        setSubjectId(s.id); setYearId('y12'); setHomeMode(null);
                        const ok = await tryStartInteractive('y12', s.id, 'exam');
                        if (ok) setShowInteractive(true);
                      }}
                      style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:14, border:`1.5px solid ${T.border}`, background:T.surface, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", textAlign:"left", transition:"all .18s", animation:`fadeUp ${.1+idx*.06}s ease` }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor=accentColor; e.currentTarget.style.background=`${accentColor}08`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor=T.border; e.currentTarget.style.background=T.surface; }}>
                      <div style={{ width:40, height:40, borderRadius:12, background:`${s.color}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{s.icon}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{s.label}</div>
                        <div style={{ fontSize:11, color:T.textMid, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.desc}</div>
                      </div>
                      <span style={{ fontSize:12, color:accentColor, fontWeight:700, flexShrink:0 }}>Practice →</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Quick actions: replaced by the four tabs and the parent area ── */}
          {false && !homeMode && (
            <div style={{ marginBottom:32, animation:"fadeUp .4s ease .1s both" }}>
              <p style={{ fontSize:11, fontWeight:700, color:T.textMid, textTransform:"uppercase", letterSpacing:".08em", marginBottom:12 }}>Quick access</p>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
                {[
                  { key:"progress", label:"My Progress",  action:() => navigate("dashboard"),
                    icon:(<path d="M4 20h16M7 20v-6M12 20v-9M17 20v-4" stroke="#6C5CE7" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>) },
                  { key:"planner", label:"Study Planner", action:() => setShowStudyPlanner(true),
                    icon:(<>
                      <rect x="4" y="5" width="16" height="15" rx="2" stroke="#6C5CE7" strokeWidth="1.8" fill="none"/>
                      <path d="M4 9h16M8 3v4M16 3v4" stroke="#6C5CE7" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
                    </>) },
                  { key:"report", label:"Report", action:() => setShowReport(true),
                    icon:(<>
                      <path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="#6C5CE7" strokeWidth="1.8" fill="none" strokeLinejoin="round"/>
                      <path d="M14 3v4h4M9 12h6M9 16h6" stroke="#6C5CE7" strokeWidth="1.6" fill="none" strokeLinecap="round"/>
                    </>) },
                  { key:"settings", label:"Settings", action:() => { sessionStorage.setItem("lumio_dashboard_tab","settings"); navigate("dashboard"); },
                    icon:(<>
                      <circle cx="12" cy="12" r="3" stroke="#6C5CE7" strokeWidth="1.8" fill="none"/>
                      <path d="M12 3v2.5M12 18.5V21M21 12h-2.5M5.5 12H3M18.36 5.64l-1.77 1.77M7.41 16.59l-1.77 1.77M18.36 18.36l-1.77-1.77M7.41 7.41 5.64 5.64" stroke="#6C5CE7" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
                    </>) },
                ].map(item => (
                  <button key={item.key} onClick={item.action}
                    style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10, padding:"18px 8px", borderRadius:22, border:"1px solid #ECEAF3", background:"#FFFFFF", boxShadow:"0 1px 2px rgba(30,22,70,.04),0 12px 30px rgba(40,30,90,.06)", cursor:"pointer", fontFamily:"'Hanken Grotesk',sans-serif", transition:"all .15s" }}
                    onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform=""; }}>
                    <span style={{ width:42, height:42, borderRadius:13, background:"#EDE8FF", display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">{item.icon}</svg>
                    </span>
                    <span style={{ fontSize:12, fontWeight:600, color:"#16162A" }}>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PWA install */}
          {showPwaInstall && pwaPrompt && (
            <div style={{ marginBottom:16, background:`${T.accent}10`, border:`1px solid ${T.accent}22`, borderRadius:14, padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize:20 }}>📱</span>
              <div style={{ flex:1 }}>
                <p style={{ fontSize:13, fontWeight:700, color:T.accent, margin:"0 0 2px" }}>Add Empirio to home screen</p>
                <p style={{ fontSize:11, color:T.textMid, margin:0 }}>Practice offline, anytime</p>
              </div>
              <button onClick={installPWA} style={{ padding:"6px 12px", background:T.accent, border:"none", borderRadius:8, color:"white", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Add</button>
              <button onClick={() => setShowPwaInstall(false)} style={{ background:"none", border:"none", color:T.textDim, cursor:"pointer", fontSize:18, padding:4 }}>×</button>
            </div>
          )}

        </div>
      </div>
    </AppShell>
    </>
  );

  // Subject Home
  if (screen === "subjectHome") return (
    <>
    {renderGlobalOverlays()}
    <AppShell {...shellProps}>
      {ent.tier === "expired" && (
        <div role="status" style={{ background:T.accentLo, borderBottom:`1px solid ${T.accent}33`, padding:"12px 24px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <span style={{ fontSize:14, color:T.text, flex:1, minWidth:220 }}><b>Your 7-day free trial has ended.</b> Choose a plan to keep practising and learning — everything you've done is saved.</span>
          <button onClick={() => setShowPricing(true)} style={{ padding:"8px 16px", background:T.accent, border:"none", borderRadius:8, color:"white", fontWeight:800, fontSize:13, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>See plans</button>
        </div>
      )}
      {false && isOnTrial() && (
        <div style={{ margin:"12px 16px 0", background:`${T.gold}15`, border:`1.5px solid ${T.gold}44`, borderRadius:14, padding:"10px 16px", display:"flex", alignItems:"center", gap:10, justifyContent:"space-between" }}>
          <div>
            <p style={{ fontSize:13, fontWeight:800, color:T.gold, margin:"0 0 2px" }}>🎯 Free trial · {ent.trial.daysLeft} day{ent.trial.daysLeft === 1 ? "" : "s"} left</p>
            <p style={{ fontSize:12, color:T.textMid, margin:0 }}>Every exam is open. Choose a plan before it ends to keep going.</p>
          </div>
          <button onClick={() => setShowPricing(true)} style={{ background:T.gold, border:"none", borderRadius:8, padding:"6px 12px", color:"white", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", flexShrink:0 }}>Upgrade</button>
        </div>
      )}
      {!alertDismissed && lumiAlert && <LumiAlertBanner alert={lumiAlert} onDismiss={() => setAlertDismissed(true)} onAction={() => setAlertDismissed(true)} />}
      <div style={{ padding:"0 0 80px" }}>
        <div style={{ padding:"20px 32px 24px", borderBottom:`1px solid ${T.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <button onClick={() => { navigate("yearSelect"); setHomeMode("hub"); }} aria-label="Back to Practice" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>←</button>
            <Badge label={year?.label} color={year?.color} />
            <div style={{ flex:1 }} />
            {/* XP Badge + Leaderboard button */}
            <button onClick={() => setShowLeaderboard(true)} style={{ display:"flex", alignItems:"center", gap:6, background:`linear-gradient(135deg, ${T.gold}22, ${T.gold}11)`, border:`1px solid ${T.gold}55`, borderRadius:10, padding:"6px 12px", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              <span style={{ fontSize:14 }}>🏆</span>
              <div style={{ textAlign:"left" }}>
                <div style={{ fontSize:11, fontWeight:800, color:T.gold }}>Leaderboard</div>
                <div style={{ fontSize:10, color:T.textMid }}>{getXP().monthly.toLocaleString()} XP this month</div>
              </div>
            </button>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:26, color:T.text, marginBottom:4 }}>
              {{"naplan":"NAPLAN","selective_nsw":"NSW Selective","oc":"OC Test","acer":"ACER Scholarship","icas":"ICAS","ignite":"IGNITE (SA)","vic_selective":"VIC Selective","hsc":"NSW HSC","vce":"VIC VCE"}[examProgram]||examProgram} — Subjects
            </h2>
          </div>
          <p style={{ fontSize:13, color:T.textMid }}>Select a subject and study mode to begin.</p>
          {(() => {
            // a running count only: the full banks load per subject, so a "% of the bank" figure was wrong
            const attempted = getExamProgress(examProgram, activeProfile?.id);
            if (attempted === 0) return null;
            return (
              <div style={{ marginTop:12, background:T.surface, borderRadius:10, padding:"10px 14px", border:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:12, color:T.textMid, margin:"0 0 5px", fontWeight:600 }}>
                    📊 {attempted.toLocaleString()} {attempted === 1 ? "question" : "questions"} answered so far
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
        <div style={{ padding:"24px 32px", maxWidth:1100, margin:"0 auto", width:"100%" }}>
          <div style={{ marginBottom:16 }}><GlowUpCard profileId={activeProfile?.id} onOpen={() => navigate("glowUp")} /></div>
          {(() => {
            const papers = mockPapers(examProgram, yearId);
            if (!papers.length) return null;
            const mins = papers.reduce((t, p) => t + (p.minutes || 0), 0);
            return (
              <button onClick={startMock} style={{ width:"100%", display:"flex", alignItems:"center", gap:14, textAlign:"left", cursor:"pointer", marginBottom:16, background:"linear-gradient(135deg,#4F46E5,#7C3AED)", border:"none", borderRadius:14, padding:"16px 20px", color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                <span style={{ fontSize:28 }}>🏁</span>
                <span style={{ flex:1 }}>
                  <span style={{ display:"block", fontSize:11, fontWeight:800, letterSpacing:"0.1em", opacity:0.85 }}>OFFICIAL FORMAT</span>
                  <span style={{ display:"block", fontSize:17, fontWeight:800 }}>Full Mock Exam</span>
                  <span style={{ display:"block", fontSize:12, opacity:0.9 }}>{papers.map(p => p.name).join(" · ")} — {mins} minutes, timed like the real test</span>
                </span>
                <span style={{ fontSize:20 }}>→</span>
              </button>
            );
          })()}
          <SessionPlanner progress={progress} yearId={yearId} onStart={(subject,topic) => { setSubjectId(subject); if(topic){setTopicFilter(topic);startSession(subject,yearId,"practice",topic);}else{startSession(subject,yearId,"practice");}}} />
          {(EXAM_SUBJECTS[examProgram] ? SUBJECTS.filter(s => (EXAM_SUBJECTS[examProgram]||[]).includes(s.id)) : SUBJECTS).map((s,idx) => {
            const progKey = examProgram + '-' + (yearId||'y5') + '-' + s.id;
            const p = progress[progKey] || progress[s.id] || {}; // composite key with fallback
            const pct = p.total > 0 ? Math.round((p.correct/p.total)*100) : null; // show % as soon as any questions attempted
            const used = 0;
            const trialExhausted = !ent.exams; // the trial has ended and there is no plan
            const hasQuestions = !banksReady || (examProgram === "naplan"
              ? (typeof QB !== "undefined" && QB[s.id]?.length > 0)
              : EXAM_QUESTIONS.some(q => q.exam === examProgram && q.subject === s.id) || examExtraCount(examProgram, s.id) > 0
                || (s.id === "writing" && !!EXAM_WRITING[examProgram])); // writing task prompts, not bank questions
            const comingSoon = !hasQuestions;
            return (
              <Card key={s.id} style={{ marginBottom:14, padding:0, overflow:"hidden", animation:`fadeUp ${.15+idx*.1}s ease`, cursor:"default" }} glow={trialExhausted?"#9CA3AF":s.color}>
                <div style={{ padding:"18px 20px 14px", borderBottom:`1px solid ${T.border}` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10 }}>
                    <div style={{ width:44, height:44, borderRadius:12, background:s.lo, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, border:`1px solid ${s.color}33`, opacity:(trialExhausted||comingSoon)?0.4:1 }}>{s.icon}</div>
                    <div style={{ flex:1 }}>
                      <h3 style={{ fontSize:16, fontWeight:700, color:(trialExhausted||comingSoon)?T.textDim:T.text, marginBottom:2 }}>{getSectionName(examProgram, s.id, s.label, yearId)}{comingSoon && <span style={{ fontSize:10, fontWeight:600, color:T.textDim, marginLeft:6, background:T.border, padding:"2px 6px", borderRadius:4 }}>COMING SOON</span>}</h3>
                      <p style={{ fontSize:12, color:T.textMid }}>{trialExhausted ? "Your free trial has ended — choose a plan to continue" : s.desc}</p>
                    </div>
                    {pct!==null && <ProgressRing value={pct} max={100} size={44} color={s.color} label={`${pct}%`} />}
                  </div>
                  {p.total>0 && <p style={{fontSize:11,color:T.textMid,margin:"0 0 4px"}}>{p.correct||0} correct of {p.total} attempted</p>}
                </div>
                <div style={{ padding:"14px 20px", display:"flex", gap:8, flexWrap:"wrap" }}>
                  {comingSoon ? (
                    <div style={{ flex:1, padding:"9px 16px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, textAlign:"center" }}>
                      <span style={{ fontSize:12, color:T.textMid, fontWeight:600 }}>🚧 Coming Soon — Questions being added</span>
                    </div>
                  ) : trialExhausted ? (
                    <button onClick={() => setShowPricing(true)} style={{ flex:1, padding:"9px 4px", background:`${T.accent}15`, border:`1px solid ${T.accent}44`, borderRadius:10, color:T.accent, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>🔓 Upgrade to unlock</button>
                  ) : isWritingSubject(s.id) ? (
                    <>
                      {/* Writing: essays get AI feedback below; NAPLAN also has writing-skills practice (criteria items) */}
                      {examProgram === "naplan" && (
                        <button onClick={() => startSession(s.id,yearId,"practice")} style={{ flex:1, minWidth:80, padding:"9px 4px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:12, fontWeight:600, cursor:"pointer" }}>
                          ✍️ Skills Practice
                        </button>
                      )}
                      {(examProgram === "naplan" || EXAM_WRITING[examProgram]) && s.id === "writing" ? (
                        <button onClick={() => { setSubjectId(s.id); setShowWriting("exam"); }} style={{ flex:1, minWidth:120, padding:"9px 4px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                          📝 Writing Test (timed)
                        </button>
                      ) : (
                        <div style={{ flex:1, minWidth:120, padding:"9px 8px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, textAlign:"center", opacity:0.55 }}>
                          <span style={{ fontSize:12, color:T.textMid, fontWeight:600 }}>📝 Essay Exam — Coming soon</span>
                        </div>
                      )}
{examProgram === "naplan" && (                      <button onClick={() => { setSubjectId(s.id); navigate("topicSelect"); }} style={{ flex:1, minWidth:80, padding:"9px 4px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }} onMouseOver={e=>{e.currentTarget.style.borderColor=T.purple;e.currentTarget.style.color=T.purple;}} onMouseOut={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.text;}}>📚 By topic</button>)}
                    </>
                  ) : (
                    <>
                      {/* Section: quick mixed practice first (starts straight away), then by topic, then a timed section test */}
                      <button onClick={() => startSession(s.id,yearId,"practice")} style={{ flex:"2 1 140px", padding:"10px 8px", background:s.color, border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }} onMouseOver={e=>{e.currentTarget.style.filter="brightness(1.1)";}} onMouseOut={e=>{e.currentTarget.style.filter="";}}>⚡ Quick practice</button>
                      <button onClick={() => { setSubjectId(s.id); navigate("topicSelect"); }} style={{ flex:"1 1 90px", padding:"9px 4px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }} onMouseOver={e=>{e.currentTarget.style.borderColor=T.purple;e.currentTarget.style.color=T.purple;}} onMouseOut={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.text;}}>📚 By topic</button>
                      <button onClick={() => startSession(s.id,yearId,"exam",null,{ paper: availablePaper(examProgram, yearId, s.id) })} style={{ flex:"1 1 90px", padding:"9px 4px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }} onMouseOver={e=>{e.currentTarget.style.borderColor=T.gold;e.currentTarget.style.color=T.gold;}} onMouseOut={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.text;}}>⏱ Section test</button>
                      {isInteractiveEnabled(examProgram, s.id) && interactiveCount(examProgram, yearId, s.id) >= 10 && (
                        <button onClick={async () => {
                          setSubjectId(s.id);
                          const ok = await tryStartInteractive(yearId, s.id, 'exam');
                          if (ok) setShowInteractive(true);
                        }} style={{ flex:1, minWidth:80, padding:"9px 4px", background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, color:T.text, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }} onMouseOver={e=>{e.currentTarget.style.borderColor=T.accent;e.currentTarget.style.color=T.accent;}} onMouseOut={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.text;}}>🎯 Interactive</button>
                      )}
                    </>
                  )}
                  {s.id==="writing" && !trialExhausted && (examProgram === "naplan" || EXAM_WRITING[examProgram]) && ( /* exams without writing yet show only "coming soon" */
                    <button onClick={() => { setSubjectId(s.id); setShowWriting(true); }} style={{ width:"100%", padding:"11px", background:T.purpleLo, border:`1px solid ${T.purple}44`, borderRadius:10, color:T.purple, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginTop:4 }}>
                      {examProgram === "naplan" ? "⚡ AI Writing Feedback — 10 Criteria" : "⚡ Writing practice with AI feedback"}
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </AppShell>
    </>
  );

  // Topic Select
  if (screen === "topicSelect" && subj) {
    const areas = getTopicAreas(subjectId, yearId, examProgram);
    const progKey = examProgram + '-' + (yearId||'y5') + '-' + subjectId;
    const topicProgress = (progress[progKey] || progress[subjectId] || {})?.topics || {};
    const sectionName = getSectionName(examProgram, subjectId, subj.label, yearId);
    return (
      <AppShell {...shellProps}>
        <div style={{ padding:"0 0 100px" }}>
          <div style={{ padding:"20px 24px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:12 }}>
            <button onClick={() => navigate("subjectHome")} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>←</button>
            <div>
              <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:20, color:T.text, margin:0 }}>{subj.icon} {sectionName}</h2>
              <p style={{ fontSize:12, color:T.textMid, margin:0 }}>{year?.label} · {areas.length} topic area{areas.length === 1 ? "" : "s"} · choose an area or a single topic</p>
            </div>
          </div>
          <div style={{ padding:"20px 24px", maxWidth:900, margin:"0 auto" }}>
            {areas.length === 0 && <p style={{ fontSize:14, color:T.textMid }}>Topics for this section are still being added. Quick practice has everything available so far.</p>}
            {areas.map((ar, ai) => (
              <div key={ar.area} style={{ marginBottom:22, animation:`fadeUp ${.15+ai*.06}s ease` }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                  <p style={{ flex:1, fontSize:15, fontWeight:800, color:T.text, margin:0 }}>{ar.area} <span style={{ fontSize:12, fontWeight:600, color:T.textDim }}>· {ar.topics.length} topic{ar.topics.length === 1 ? "" : "s"}</span></p>
                  {ar.topics.length > 1 && (
                    <button onClick={() => startSession(subjectId,yearId,"practice",areaFilter(ar.area))} style={{ background:T.surface, border:`1.5px solid ${subj.color}`, borderRadius:99, padding:"6px 12px", color:subj.color, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Practise all →</button>
                  )}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(240px, 1fr))", gap:10 }}>
                  {ar.topics.map(t => {
                    const tp = topicProgress[t.name];
                    const pct = (tp?.total||0) > 0 ? Math.round((tp.correct/tp.total)*100) : null;
                    return (
                      <Card key={t.name} onClick={() => startSession(subjectId,yearId,"practice",t.name)} style={{ padding:"14px 16px", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <p style={{ fontWeight:600, fontSize:14, color:T.text, margin:0 }}>{t.name}</p>
                          {pct!==null ? (
                            <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6 }}>
                              <ProgressBar value={tp.correct} max={tp.total} color={pct>=70?T.green:pct>=50?T.gold:"#EF4444"} height={4} />
                              <span style={{ fontSize:11, color:pct>=70?T.green:pct>=50?T.gold:"#EF4444", fontWeight:700, flexShrink:0 }}>{pct}%</span>
                            </div>
                          ) : <p style={{ fontSize:11, color:T.textDim, margin:"2px 0 0" }}>{t.count.toLocaleString()} questions</p>}
                        </div>
                        <span style={{ color:T.textDim, fontSize:16 }}>›</span>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  // Question
  if (screen === "question" && questions.length > 0) {
    const q = questions[qIndex];
    if (!q) { navigate("results"); return null; }
    return (
      <AppShell {...shellProps} currentQuestionId={q?.id} examProgram={examProgram}>
        <div style={{ padding:"14px 24px", borderBottom:`1px solid ${T.border}`, background:T.surface, display:"flex", alignItems:"center", gap:10, flexShrink:0, position:"sticky", top:0, zIndex:10 }}>
          <button onClick={() => navigate("subjectHome")} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>←</button>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
              <span style={{ fontSize:14 }}>{subj?.icon}</span>
              <span style={{ fontSize:13, fontWeight:700, color:T.text }}>{subj?.label}</span>
              <Badge label={mode==="exam"?"Section test":topicFilter?filterLabel(topicFilter):mode==="topic"?"Topic":"Practice"} color={mode==="exam"?T.gold:mode==="topic"?T.purple:T.accent} />
              {mode==="practice" && <Badge label={adaptiveState.currentLevel===3?"Your level: 🥇 Gold":adaptiveState.currentLevel===2?"Your level: 🥈 Silver":"Your level: 🥉 Bronze"} color={adaptiveState.currentLevel===3?T.gold:adaptiveState.currentLevel===2?"#94A3B8":T.accent} />}
            </div>
          </div>
          <button onClick={() => setShowScratch(v => !v)} aria-pressed={showScratch} title="Scratch pad for working out" style={{ background:showScratch ? T.accentLo : T.card, border:`1px solid ${showScratch ? T.accent : T.border}`, borderRadius:8, height:32, padding:"0 10px", color:T.text, fontSize:12.5, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>✏️<span className="lumio-hide-narrow"> Scratch pad</span></button>
          {mode==="exam" && <ExamTimer key={questions[0]?.id || "t"} minutes={examMinutes} totalQuestions={examRoute.length ? EXAM_TESTLETS * examTestletSize : questions.length} onTimeUp={() => navigate("results")} />}
        </div>
        <div className={showScratch ? "lumio-q-scroll lumio-scratch-open" : "lumio-q-scroll"} style={{ flex:1, overflowY:"auto" }}>
          <style>{`
            .lumio-scratch-open{padding-bottom:46vh}
            @media (min-width:900px){.lumio-scratch-open{padding-bottom:0;padding-right:min(420px,34vw)}}
            @media (max-width:420px){.lumio-hide-narrow{display:none}}
          `}</style>
          <NaplanItemRenderer
            key={qIndex + ':' + (q?.id || '')}
            question={q}
            // TODO v2: store submittedAt at the moment of submission in parent state, not on render.
            studentAnswer={chosen !== null ? {
              questionId:   q.id,
              questionType: itemType(q),
              rawAnswer:    chosen,
              isCorrect:    isCorrectResponse(q, chosen),
              submittedAt:  new Date().toISOString(),
            } : null}
            state={{ mode, current: qIndex + 1, total: questions.length }}
            showExplanation={chosen !== null && mode !== "exam"}
            onSubmit={(answer) => handleAnswer(answer.rawAnswer)}
            onAskLumi={() => { try {
              const studentAnswerText = chosen != null ? responseText(q, chosen) : "";
              const correctAnswerText = answerText(q);
              const wasWrong = chosen != null && !isCorrectResponse(q, chosen);
              setChatContext({
                subject: subj?.label || "",
                year: year?.label || "",
                question: q?.q || "",
                studentAnswer: studentAnswerText,
                correctAnswer: correctAnswerText,
                context: wasWrong ? "Student answered this question incorrectly. Explain why their answer was wrong and what the correct approach is. Be specific to this question." : "",
              });
              setShowChat(true);
            } catch(e) { console.error("Lumi chat error:", e); } }}
          />
          {/* Navigation — session-level, lives outside the item renderer */}
          <div style={{ display:"flex", gap:12, justifyContent:"center", padding:"0 32px 64px" }}>
            {qIndex > 0 && <Btn variant="ghost" onClick={prevQuestion}>← Previous</Btn>}
            {chosen !== null && <Btn onClick={nextQuestion}>{qIndex < questions.length - 1 ? "Next Question →" : mode === "exam" ? "Submit Exam →" : "See Results →"}</Btn>}
          </div>
        </div>
        {showScratch && <ScratchPad questionId={q.id} onClose={() => setShowScratch(false)} />}
        {confetti && <Confetti />}
      </AppShell>
    );
  }

  if (screen === "mock" && mock) {
    const done = mock.results.length;
    const finished = done >= mock.papers.length;
    const totalMin = mock.papers.reduce((t, p) => t + (p.minutes || 0), 0);
    const bandColor = b => ({ Exceeding: T.green, Strong: T.accent, Developing: T.gold, "Needs Support": T.red }[b] || T.textMid);
    const examName = { naplan:"NAPLAN", selective_nsw:"NSW Selective", oc:"OC Test", acer:"ACER Scholarship", icas:"ICAS", ignite:"IGNITE", vic_selective:"VIC Selective" }[mock.program] || mock.program;
    return (
      <AppShell {...shellProps}>
        <div style={{ flex:1, overflowY:"auto", background:T.bg }}>
          <div style={{ padding:"14px 24px", borderBottom:`1px solid ${T.border}`, background:T.surface, display:"flex", alignItems:"center", gap:10 }}>
            <button onClick={() => { if (finished) setMock(null); navigate("subjectHome"); }} aria-label="Back" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, width:32, height:32, color:T.textMid, cursor:"pointer" }}>←</button>
            <span style={{ fontSize:15, fontWeight:800, color:T.text }}>🏁 {examName} Full Mock Exam · {ALL_YEARS.find(y => y.id === mock.yearId)?.label}</span>
          </div>
          <div style={{ maxWidth:720, margin:"0 auto", padding:"28px 24px 80px" }}>
            <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:30, color:T.text, margin:"0 0 6px" }}>{finished ? "Your mock exam report" : done ? "Keep going — you're doing great" : "The real test, start to finish"}</h2>
            <p style={{ fontSize:14, color:T.textMid, margin:"0 0 22px", lineHeight:1.6 }}>
              {finished ? "Here's how you went on each paper. Every question you got wrong is waiting in Glow Up, ready to fix."
                : `${mock.papers.length} papers in the official order and timing — ${totalMin} minutes of testing in all. Each paper is timed on its own, and you can take a break between papers.`}
            </p>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {mock.papers.map((p, i) => {
                const r = mock.results[i];
                const current = !finished && i === done;
                return (
                  <div key={p.subject + i} style={{ display:"flex", alignItems:"center", gap:12, background:T.card, border:`1.5px solid ${current ? T.accent : T.border}`, borderRadius:12, padding:"14px 16px" }}>
                    <span style={{ width:28, height:28, borderRadius:99, background: r ? T.green : current ? T.accent : T.border, color:"#fff", fontSize:13, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{r ? "✓" : i + 1}</span>
                    <span style={{ flex:1 }}>
                      <span style={{ display:"block", fontSize:15, fontWeight:700, color:T.text }}>{p.name}</span>
                      <span style={{ display:"block", fontSize:12, color:T.textMid }}>{p.minutes} minutes{p.writing ? " · one writing task" : p.showQuestions ? ` · ${p.questions} questions` : mock.program === "naplan" ? " · adapts to your answers, like the real test" : ""}</span>
                    </span>
                    {r && (r.writing
                      ? <span style={{ textAlign:"right" }}><b style={{ fontSize:15, color:T.text }}>{r.score}/{r.max}</b><br/><span style={{ fontSize:12, fontWeight:700, color:bandColor(r.band) }}>{r.band}</span></span>
                      : <span style={{ textAlign:"right" }}><b style={{ fontSize:15, color:T.text }}>{r.correct}/{r.total}</b><br/><span style={{ fontSize:12, fontWeight:700, color:bandColor(r.proficiency) }}>{r.proficiency}</span></span>)}
                  </div>
                );
              })}
            </div>
            <div style={{ display:"flex", gap:12, justifyContent:"center", marginTop:26, flexWrap:"wrap" }}>
              {!finished && <Btn onClick={() => runMockPaper(done)}>{done ? `Start ${mock.papers[done].name} →` : `Start the mock exam →`}</Btn>}
              {finished && <Btn onClick={() => navigate("glowUp")}>✨ Fix my mistakes in Glow Up</Btn>}
              {finished && <Btn variant="ghost" onClick={() => { setMock(null); navigate("subjectHome"); }}>Done</Btn>}
              {!finished && done > 0 && <Btn variant="ghost" onClick={() => { setMock(null); navigate("subjectHome"); }}>Leave mock exam</Btn>}
            </div>
            {!finished && <p style={{ fontSize:12, color:T.textDim, textAlign:"center", marginTop:14 }}>Like the real test, you won't see answers until a paper is finished.</p>}
          </div>
        </div>
      </AppShell>
    );
  }

  if (screen === "glowUp") return (
    <AppShell {...shellProps}>
      <GlowUp
        profileId={activeProfile?.id}
        onExit={() => navigate("yearSelect")}
        onAskLumi={(gq) => { setChatContext({ subject: gq?.subject || "", year: Array.isArray(gq?.year) ? gq.year[0] : (gq?.year || ""), question: gq?.q || "" }); setShowChat(true); }}
      />
    </AppShell>
  );

  // Results
  if (screen === "results") return (
    <AppShell {...shellProps}>
      <div style={{ flex:1, overflowY:"auto" }}>
        {mock && mode === "exam" && mock.idx < mock.papers.length && (
          <div style={{ maxWidth:860, margin:"16px auto 0", padding:"0 24px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", background:T.accentLo, border:`1.5px solid ${T.accent}55`, borderRadius:12, padding:"12px 16px" }}>
              <span style={{ flex:1, minWidth:200, fontSize:14, color:T.text }}><b>Mock exam · paper {mock.idx + 1} of {mock.papers.length} done.</b> {mock.papers[mock.idx + 1] ? `Next: ${mock.papers[mock.idx + 1].name} (${mock.papers[mock.idx + 1].minutes} min).` : "That was the last paper."}</span>
              <Btn onClick={continueMock}>{mock.papers[mock.idx + 1] ? "Continue mock exam →" : "See mock exam report →"}</Btn>
            </div>
          </div>
        )}
        {goalJustMet != null && (
          <div style={{ maxWidth:860, margin:"16px auto 0", padding:"0 24px" }}>
            <div role="status" style={{ display:"flex", alignItems:"center", gap:12, background:"#FFF6E0", border:"1.5px solid #F3D58A", borderRadius:12, padding:"12px 16px" }}>
              <span style={{ fontSize:26 }}>🔥</span>
              <span style={{ flex:1, fontSize:14, color:T.text }}><b>Daily goal done!</b> {goalJustMet > 1 ? `That's a ${goalJustMet}-day streak. See you tomorrow to make it ${goalJustMet + 1}.` : "Day 1 of your streak. Come back tomorrow to make it 2."}</span>
            </div>
          </div>
        )}
        {(() => {
          const missed = questions.filter((qq, i) => answers[i] !== undefined && !isCorrectResponse(qq, answers[i])).length;
          if (!missed) return null;
          return (
            <div style={{ maxWidth:860, margin:"16px auto 0", padding:"0 24px" }}>
              <button onClick={() => navigate("glowUp")} style={{ width:"100%", display:"flex", alignItems:"center", gap:12, background:T.goldLo, border:`1.5px solid ${T.gold}55`, borderRadius:12, padding:"12px 16px", cursor:"pointer", textAlign:"left", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                <span style={{ fontSize:22 }}>✨</span>
                <span style={{ flex:1, fontSize:14, color:T.text }}><b>{missed} question{missed === 1 ? "" : "s"} saved to Glow Up.</b> Fix {missed === 1 ? "it" : "them"} now and turn mistakes into skills.</span>
                <span style={{ fontSize:13, fontWeight:800, color:T.gold }}>Glow Up →</span>
              </button>
            </div>
          );
        })()}
        <ExamResults questions={questions} answers={answers} subject={subjectId} year={yearId} mode={mode} route={examRoute} testletSize={examTestletSize} onRetry={() => startSession(subjectId,yearId,mode,topicFilter)} onHome={() => navigate("yearSelect")} onMorePractice={() => navigate("subjectHome")} />
      </div>
      {confetti && <Confetti />}
    </AppShell>
  );

  // Dashboard
  if (screen === "me") return (
    <>
    {renderGlobalOverlays()}
    <AppShell {...shellProps}>
      <MeScreen profile={activeProfile}
        daily={(() => { try { const a = JSON.parse(localStorage.getItem(profileKey("lumio_activity", activeProfile?.id)) || "[]"); return dailyGoal(Array.isArray(a) ? a : []); } catch { return dailyGoal([]); } })()}
        onSwitch={learners.length > 1 ? () => setPickLearner(true) : null} onParent={() => navigate("dashboard")} onLiveRank={() => setShowLeaderboard(true)} />
    </AppShell>
    </>
  );

  if (screen === "dashboard") return (
    <AppShell {...shellProps}>
      <div style={{ flex:1, overflowY:"auto" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "16px 24px 0" }}>
          <ProfileSwitcher currentProfileId={activeProfile?.id ?? null} maxProfiles={ent.profiles} onSwitch={(p) => {
            setActiveProfile(p);
            try { setProgress(JSON.parse(localStorage.getItem(profileKey('lumio_progress_v2', p?.id)) || '{}')); } catch (_) { setProgress({}); }
          }} />
        </div>
        <ParentDashboard key={activeProfile?.id ?? 'default'} progress={progress} examProgram={examProgram} yearId={yearId} activeProfile={activeProfile} currentPlan={currentPlan} maxProfiles={ent.profiles} onClose={() => navigate("yearSelect")} onUpgrade={() => setShowPricing(true)} />
      </div>
    </AppShell>
  );

  // ── 404 fallback ─────────────────────────────────────────────
  return (
    <AppShell {...shellProps}>
      <div style={{ minHeight:"100vh", background:T.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:32, textAlign:"center" }}>
        <div style={{ fontSize:72, marginBottom:16 }}>🗺️</div>
        <h1 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:"clamp(28px,6vw,42px)", color:T.text, marginBottom:8 }}>Page not found</h1>
        <p style={{ fontSize:16, color:T.textMid, marginBottom:32, maxWidth:360 }}>This page doesn't exist. You'll be taken back to Home in a moment.</p>
        <button onClick={() => navigate("yearSelect")} style={{ background:T.accent, border:"none", borderRadius:12, color:"white", padding:"14px 28px", fontWeight:800, fontSize:15, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
          Go to Home →
        </button>
        <PageAutoRedirect onRedirect={() => navigate("yearSelect")} />
      </div>
    </AppShell>
  );
}

function PageAutoRedirect({ onRedirect }) {
  React.useEffect(() => {
    const t = setTimeout(onRedirect, 3000);
    return () => clearTimeout(t);
  }, [onRedirect]);
  return null;
}

function TrophyIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink:0 }} aria-hidden="true">
      <path d="M7 4h10v3a5 5 0 0 1-5 5 5 5 0 0 1-5-5V4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M7 5H4a3 3 0 0 0 3 4M17 5h3a3 3 0 0 1-3 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 12v3M9 19h6M10 19v-2a2 2 0 0 1 4 0v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// The four tabs, the same on every screen (2026-11-02): a top bar on a laptop, a bottom bar on a phone.
// Plans, reports and settings live in the parent area, not here.
const STUDENT_TABS = [
  { id: "today", label: "Today" }, { id: "learn", label: "Learn" }, { id: "practice", label: "Practice" }, { id: "me", label: "Me" },
];
function EmpirioTopBar({ tab, onAction, activeProfile, noNav }) {
  const go = (id) => { if (!noNav) onAction(id); };
  return (
    <div style={{ height:64, background:"#FFFFFF", borderBottom:"1px solid #E2E0F0", display:"flex", alignItems:"center", justifyContent:"space-between",
      paddingInline:"clamp(14px, 3vw, 40px)", gap:12, flexShrink:0, position:"sticky", top:0, zIndex:100 }}>
      <button onClick={() => go("today")} aria-label="Empirio: Today" className="lumio-topbar-side" style={{ display:"flex", alignItems:"center", gap:10, background:"none", border:"none", cursor:"pointer", padding:0, minWidth:150 }}>
        <span aria-hidden="true" style={{ width:30, height:30, borderRadius:"50%", flex:"none", position:"relative", display:"block",
          background:"radial-gradient(circle at 34% 30%, #C9C2FF 0, #8E7BFF 26%, #5B45E0 62%, #3A2AA6 100%)", boxShadow:"0 0 0 2px rgba(14,159,143,.45)" }}>
          <span style={{ position:"absolute", top:"40%", left:"34%", width:"12%", height:"22%", borderRadius:6, background:"#FFFFFF" }} />
          <span style={{ position:"absolute", top:"40%", right:"34%", width:"12%", height:"22%", borderRadius:6, background:"#FFFFFF" }} />
        </span>
        <span style={{ fontFamily:"'Space Grotesk',sans-serif", fontWeight:700, fontSize:22, letterSpacing:"-.02em", color:"#16162A" }}>Empirio</span>
      </button>
      <nav aria-label="Main" className="lumio-nav-pills" style={{ display:"flex", gap:4, background:"#F1F0F8", borderRadius:16, padding:4 }}>
        {STUDENT_TABS.map((t) => {
          const on = tab === t.id;
          return (
            <button key={t.id} onClick={() => go(t.id)} aria-current={on ? "page" : undefined} style={{ display:"flex", alignItems:"center", gap:8, minHeight:42, padding:"0 20px",
              borderRadius:12, border:"none", background: on ? "#FFFFFF" : "transparent", color: on ? "#2D2A9E" : "#45435A", fontWeight: on ? 700 : 600, fontSize:15,
              cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow: on ? "0 1px 3px rgba(40,38,90,0.12)" : "none" }}>
              <Icon d={ICONS[t.id]} size={18} />{t.label}
            </button>
          );
        })}
      </nav>
      <button onClick={() => go("me")} className="lumio-topbar-side" style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:10, background:"none", border:"none", cursor:"pointer", padding:0, minWidth:150, fontFamily:"'Plus Jakarta Sans',sans-serif" }}
        aria-label={activeProfile ? `Me: ${activeProfile.name}` : "Me"}>
        {activeProfile && <span className="lumio-me-text" style={{ display:"flex", flexDirection:"column", alignItems:"flex-end" }}><b style={{ fontSize:14, color:"#1B1B23" }}>{activeProfile.name}</b>
          {activeProfile.yearId && <span style={{ fontSize:12, color:"#5E5C70" }}>Year {String(activeProfile.yearId).replace(/^y/, "")}</span>}</span>}
        <span style={{ width:36, height:36, borderRadius:"50%", background:"#ECEBFE", display:"flex", alignItems:"center", justifyContent:"center", fontSize:19 }}>{activeProfile?.avatar || "🙂"}</span>
      </button>
    </div>
  );
}

function EmpirioBottomTabs({ tab, onAction }) {
  return (
    <nav aria-label="Main" className="lumio-bottom-tabs" style={{ position:"fixed", bottom:0, left:0, width:"100%", background:"#FFFFFF", borderTop:"1px solid #E2E0F0",
      display:"flex", paddingBottom:"env(safe-area-inset-bottom)", zIndex:100 }}>
      {STUDENT_TABS.map((t) => {
        const on = tab === t.id;
        return (
          <button key={t.id} onClick={() => onAction(t.id)} aria-current={on ? "page" : undefined} style={{ flex:1, minHeight:62, display:"flex", flexDirection:"column", alignItems:"center",
            justifyContent:"center", gap:4, background:"none", border:"none", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", color: on ? "#2D2A9E" : "#5E5C70", fontSize:12, fontWeight: on ? 700 : 500 }}>
            <span style={{ width:52, height:30, borderRadius:15, background: on ? "#ECEBFE" : "transparent", display:"flex", alignItems:"center", justifyContent:"center" }}><Icon d={ICONS[t.id]} size={20} /></span>
            {t.label}
          </button>
        );
      })}
    </nav>
  );
}

// ── ERROR BOUNDARY ────────────────────────────────────────────────


function AppShell({ children, currentQuestionId, examProgram, noNav, tab,
                    screen, onAction, activeProfile, currentPlan, planTier, navigate: navFn }) {
  return (
    <ErrorBoundary currentQuestionId={currentQuestionId} examProgram={examProgram}>
      <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'Plus Jakarta Sans',sans-serif", userSelect:"auto" }}>
        <style>{GLOBAL_CSS}</style>
        <div style={{ background:T.surface, minHeight:"100vh", display:"flex", flexDirection:"column", position:"relative" }}>
          {!noNav && (
            <EmpirioTopBar
              screen={screen} tab={tab} onAction={onAction} noNav={noNav}
              activeProfile={activeProfile} currentPlan={currentPlan} planTier={planTier}
              navigate={navFn || (() => {})}
            />
          )}
          <div className={noNav ? undefined : "lumio-scroll"} style={{ flex:1, overflowY:"auto" }}>
            {children}
          </div>
          {!noNav && <EmpirioBottomTabs tab={tab} onAction={onAction} />}
        </div>
      </div>
    </ErrorBoundary>
  );
}




